// @ts-ignore
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminApplicationFiles from './AdminApplicationFiles';
import AdminApplicationRecovery from './AdminApplicationRecovery';
import AdminStorageStats from './AdminStorageStats';
import VisitorStatsPage from './VisitorStatsPage';
import DocumentRequestPanel from '../../admin/DocumentRequestPanel';
import AdminChatPanel from '../../admin/AdminChatPanel';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css, createGlobalStyle } from 'styled-components';
import { io, Socket } from 'socket.io-client';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { format } from 'date-fns';
import Settings from './Settings';
import StatCardWithPie from './StatCardWithPie';
import PaidApplicationsCard from './PaidApplicationsCard';
import PaymentToast from './PaymentToast';
import LoginToast from '../../admin/LoginToast';
import SubAdminLoginToast from '../../admin/SubAdminLoginToast';
import PaymentConfetti, { PaymentApplicantInfo } from '../../admin/PaymentConfetti';
import BackupActivityToast from '../../admin/BackupActivityToast';
import { useSessionTimer } from '../../../contexts/SessionTimerContext';
import AdminDocs from './AdminDocs';
import AppMaintenance from './AppMaintenance';
import S3Settings from './S3Settings';
import SessionPlayerModal from '../../admin/SessionPlayerModal';
import LiveSessionModal from '../../admin/LiveSessionModal';
import VisitorLiveModal from '../../admin/VisitorLiveModal';
import TileDetailModal from '../../admin/TileDetailModal';
import type { TileType } from '../../admin/TileDetailModal';
import AreaWiseReportPage from '../../admin/AreaReportModal';
import * as XLSX from 'xlsx';

// API URL from environment variable
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5000');

// Application types
interface Application {
  _id: string;
  applicationId: string;
  email: string;
  formData: any;
  status: string;
  createdAt: string;
  updatedAt: string;
  isComplete: boolean;
  isSubmitted: boolean;
  dealershipGroupId?: string | null; // For linked applications
  isPrimaryApplicant?: boolean; // Primary applicant designation
  userPhoneNumber?: string;
  userPhoneNumberHistory?: { number: string; capturedAt: string }[];
}

interface HistoryEntry {
  event?: string;
  status?: string;
  editor?: string;
  changedBy?: string;
  timestamp?: string;
  changedAt?: string;
  details?: string;
}

interface ApplicationDetail extends Application {
  formData: {
    fullName: string;
    email: string;
    phoneNumber: string;
    address: string;
    city: string;
    state: string;
    zipCode: string;
    vehicleMake: string;
    vehicleModel: string;
    vehicleYear: string;
    vehicleVIN: string;
    fuelType: string;
    conversionType: string;
    kitType: string;
    cylinderSize: string;
    [key: string]: any;
  };
  history?: HistoryEntry[];
  metadata?: { [key: string]: any };
}

interface UserIntentData {
  intent: 'yes' | 'maybe' | 'no' | '';
  reason: string;
  source: 'upload' | 'manual';
  updatedAt: string;
}

interface RegisteredUser {
  _id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  profileImageUrl: string;
  createdAt: string;
  lastLogin: string | null;
  applicationCount: number;
  applications: { applicationId: string; status: string; createdAt: string }[];
  intent: UserIntentData | null;
  paidCount: number;
}

const PIE_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A287E7', '#FF6666', '#F2A258', '#8A2BE2'];

// Interface for server statistics (for header gauges)
interface ServerStats {
  cpu: { usage: string; count: number };
  memory: { usagePercent: string; usedFormatted: string; freeFormatted: string };
  diskSpace?: { used: number; total: number; usedFormatted: string; freeFormatted: string };
}



const pulseAnimation = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0.6); }
  70% { box-shadow: 0 0 0 6px rgba(52, 211, 153, 0); }
  100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
`;

/* ── Header widget: reusable thin separator ── */
const HeaderDivider = styled.div`
  width: 1px;
  height: 28px;
  background: linear-gradient(180deg, transparent 0%, rgba(148,163,184,0.25) 40%, rgba(148,163,184,0.25) 60%, transparent 100%);
  flex-shrink: 0;
`;

const DashboardContainer = styled.div`
  display: flex;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  background: linear-gradient(135deg, #f0f4ff 0%, #e6f0ff 100%);
  font-family: 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  overflow-x: hidden;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div<{ $collapsed?: boolean }>`
  width: ${props => props.$collapsed ? '60px' : '190px'};
  background: linear-gradient(180deg, #2c3e50 0%, #1a2533 100%);
  color: white;
  padding: 1rem 0;
  box-shadow: 4px 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 0 16px 16px 0;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  transition: width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 50;
  
  @media (max-width: 768px) {
    width: 100%;
    border-radius: 0 0 20px 20px;
    padding: 1rem 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 30% 20%, rgba(76, 110, 245, 0.1) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const MainContent = styled.div`
  flex: 1;
  padding: 2.5rem 1.25rem;
  position: relative;
  
  &::after {
    content: '';
    position: fixed;
    bottom: 0;
    right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle at bottom right, rgba(76, 110, 245, 0.05) 0%, transparent 70%);
    pointer-events: none;
    z-index: -1;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
  
  @media (max-width: 480px) {
    padding: 1rem;
  }
`;

/* ── Redesigned Header ── */
const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.75rem;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 16px;
  padding: 0.6rem 1.25rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 4px 24px rgba(76,110,245,0.04);
  gap: 0.5rem;
  flex-wrap: wrap;
  position: relative;
  z-index: 10;

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    margin-bottom: 1.25rem;
    border-radius: 12px;
  }
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
`;

const Title = styled(motion.h1)`
  font-size: 1.45rem;
  font-weight: 750;
  margin: 0;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.3px;
  white-space: nowrap;
  flex-shrink: 0;
  @media (max-width: 768px) { font-size: 1.2rem; }
  @media (max-width: 480px) { font-size: 1.1rem; text-align: center; }
`;

/* Wrapper that groups all the right-side widgets */
const HeaderWidgets = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  @media (max-width: 480px) {
    justify-content: center;
    gap: 8px;
  }
`;

/* ── Online users chip ── */
const OnlineChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid rgba(52, 211, 153, 0.3);
  border-radius: 20px;
  padding: 5px 12px 5px 10px;
  cursor: default;
  user-select: none;
  transition: all 0.2s;
  &:hover { box-shadow: 0 2px 8px rgba(52,211,153,0.18); }
`;

const OnlinePulseDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #34d399;
  flex-shrink: 0;
  animation: ${pulseAnimation} 2s infinite;
`;

/* ── Sub-admin online chip ── */
const SubAdminChip = styled.div<{ $hasOnline: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${p => p.$hasOnline
    ? 'linear-gradient(135deg, #fdf4ff 0%, #f3e8ff 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'};
  border: 1px solid ${p => p.$hasOnline ? 'rgba(168, 85, 247, 0.3)' : 'rgba(148, 163, 184, 0.3)'};
  border-radius: 20px;
  padding: 5px 12px 5px 10px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
  &:hover {
    box-shadow: 0 2px 8px ${p => p.$hasOnline ? 'rgba(168,85,247,0.18)' : 'rgba(100,116,139,0.12)'};
    transform: translateY(-1px);
  }
`;

const SubAdminPulseDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #a855f7;
  flex-shrink: 0;
  animation: ${pulseAnimation} 2s infinite;
`;

const SubAdminDropdown = styled(motion.div)`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  background: white;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 36px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06);
  z-index: 9999;
  overflow: hidden;
`;

const SubAdminDropdownHeader = styled.div`
  padding: 10px 14px;
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
  border-bottom: 1px solid #e9d5ff;
  font-size: 11px;
  font-weight: 700;
  color: #7c3aed;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SubAdminDropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
  &:hover { background: #faf5ff; }
`;

const SubAdminAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #c084fc 0%, #a855f7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
`;

/* ── Session timer badge ── */
const SessionTimerBadge = styled.div<{ isWarning: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  background: ${p => p.isWarning
    ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)'
    : 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)'};
  border: 1px solid ${p => p.isWarning ? 'rgba(245,158,11,0.35)' : 'rgba(99,102,241,0.25)'};
  color: ${p => p.isWarning ? '#92400e' : '#4338ca'};
  padding: 5px 12px 5px 9px;
  border-radius: 20px;
  font-size: 12.5px;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px ${p => p.isWarning ? 'rgba(245,158,11,0.15)' : 'rgba(99,102,241,0.12)'};
  }
  svg { width: 14px; height: 14px; flex-shrink: 0; }
`;

/* ── Logout button ── */
const LogoutButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: #dc2626;
  border: 1px solid rgba(220,38,38,0.2);
  border-radius: 10px;
  padding: 6px 14px;
  font-size: 0.8rem;
  font-weight: 650;
  cursor: pointer;
  transition: all 0.25s;
  svg { width: 15px; height: 15px; flex-shrink: 0; }
  &:hover {
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    color: #fff;
    border-color: transparent;
    transform: translateY(-1px);
    box-shadow: 0 4px 14px rgba(220,38,38,0.25);
  }
  &:active { transform: translateY(0); box-shadow: 0 1px 4px rgba(220,38,38,0.18); }
  &:focus { outline: none; box-shadow: 0 0 0 3px rgba(220,38,38,0.2); }
`;

/* ── Mini vitals bar ── */
const VitalsBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(248, 250, 252, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.5);
  border-radius: 10px;
  padding: 4px 10px;
`;

/* Single vital metric inline */
const VitalItem = ({ percent, color, label }: { percent: number; color: string; label: string }) => {
  const barWidth = 32;
  const fillWidth = Math.max(1, (percent / 100) * barWidth);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'default' }} title={`${label}: ${percent}%`}>
      <span style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', width: '24px' }}>{label}</span>
      <div style={{
        width: `${barWidth}px`, height: '6px', borderRadius: '3px',
        background: 'rgba(226,232,240,0.7)', overflow: 'hidden', position: 'relative'
      }}>
        <div style={{
          width: `${fillWidth}px`, height: '100%', borderRadius: '3px',
          background: color, transition: 'width 0.6s cubic-bezier(0.4,0,0.2,1)'
        }} />
      </div>
      <span style={{ fontSize: '10px', fontWeight: 700, color: '#334155', fontVariantNumeric: 'tabular-nums', width: '26px', textAlign: 'right' }}>{percent}%</span>
    </div>
  );
};

/* ── Clock widget ── */
const ClockWidget = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  user-select: none;
  line-height: 1.2;
`;

const Logo = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0.75rem 0.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 2px;
    background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
    border-radius: 2px;
  }

  img {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    object-fit: contain;
    flex-shrink: 0;
  }
  
  .logo-text {
    font-size: 1.3rem;
    font-weight: 700;
    color: white;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
    letter-spacing: -0.5px;
    line-height: 1.2;
  }
  
  span {
    display: block;
    font-size: 0.7rem;
    font-weight: 400;
    opacity: 0.7;
    margin-top: 2px;
    letter-spacing: 0;
    color: white;
  }
`;

const NavMenu = styled.ul<{ $collapsed?: boolean }>`
  list-style: none;
  padding: 0;
  margin: ${props => props.$collapsed ? '0 0.25rem' : '0 0.8rem'};
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition: margin 0.25s ease;
`;

const NavItem = styled(motion.div)<{ $active: boolean; $collapsed?: boolean }>`
  padding: ${props => props.$collapsed ? '0.7rem 0' : '0.7rem 0.75rem'};
  cursor: pointer;
  background-color: ${props => props.$active ? 'rgba(76, 110, 245, 0.2)' : 'transparent'};
  transition: all 0.3s ease;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: ${props => props.$collapsed ? 'center' : 'flex-start'};
  gap: 8px;
  font-weight: ${props => props.$active ? '600' : '500'};
  font-size: 0.85rem;
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  margin: ${props => props.$collapsed ? '0 0.25rem' : '0 0.5rem'};
  
  &:hover {
    background-color: rgba(76, 110, 245, 0.15);
    transform: ${props => props.$collapsed ? 'none' : 'translateX(5px)'};
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
    opacity: ${props => props.$active ? '1' : '0'};
    border-radius: 0 4px 4px 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 1;
  }
  
  svg {
    width: 18px;
    height: 18px;
    opacity: 0.9;
    flex-shrink: 0;
  }
`;

const Card = styled(motion.div)`
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(76, 110, 245, 0.1);
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(76, 110, 245, 0.15);
  }
  
  h2 {
    font-size: 1.5rem;
    margin-top: 0;
    margin-bottom: 1.5rem;
    color: #2d3748;
    font-weight: 700;
    display: flex;
    align-items: center;
    gap: 10px;
    
    svg {
      color: #4c6ef5;
    }
  }
`;

const CardTitle = styled.h2`
  font-size: 1.4rem;
  color: #2d3748;
  margin-top: 0;
  margin-bottom: 1.2rem;
  font-weight: 700;
  position: relative;
  padding-bottom: 0.8rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    width: 24px;
    height: 24px;
    color: #4c6ef5;
    flex-shrink: 0;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 40px;
    height: 3px;
    background: linear-gradient(90deg, #4c6ef5, #6e8ffb);
    border-radius: 3px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.2rem;
  color: #2d3748;
  margin: 0 0 1rem 0;
  font-weight: 600;
`;

const PaginationContainer = styled.div<{ $isFooter?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin: ${props => props.$isFooter ? '0' : '1.5rem 0'};
  padding: ${props => props.$isFooter ? '1.25rem 0 0.5rem' : '1rem 0'};
  border-top: 1px solid rgba(226, 232, 240, 0.8);
  border-bottom: ${props => props.$isFooter ? 'none' : '1px solid rgba(226, 232, 240, 0.8)'};
`;

const PaginationInfo = styled.div`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 500;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PageButton = styled.button<{ $active?: boolean; $disabled?: boolean; $isNav?: boolean }>`
  width: ${props => props.$isNav ? '38px' : '36px'};
  height: ${props => props.$isNav ? '38px' : '36px'};
  padding: 0;
  border: ${props => props.$active ? '2px solid #4c6ef5' : props.$isNav ? '1px solid rgba(226, 232, 240, 0.9)' : '1px solid rgba(226, 232, 240, 0.8)'};
  background: ${props => props.$active ? 'linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%)' : props.$isNav ? '#f8fafc' : 'white'};
  color: ${props => props.$active ? 'white' : props.$isNav ? '#475569' : '#64748b'};
  border-radius: 50%;
  font-size: 0.85rem;
  font-weight: ${props => props.$active ? '700' : '500'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? '0.4' : '1'};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.$active ? '0 3px 12px rgba(76, 110, 245, 0.35)' : 'none'};

  &:hover:not(:disabled) {
    background: ${props => props.$active ? 'linear-gradient(135deg, #3b5bdb 0%, #2f4ccc 100%)' : 'rgba(76, 110, 245, 0.08)'};
    border-color: ${props => props.$active ? '#3b5bdb' : '#4c6ef5'};
    color: ${props => props.$active ? 'white' : '#4c6ef5'};
    transform: translateY(-1px);
    box-shadow: ${props => props.$active ? '0 4px 14px rgba(76, 110, 245, 0.4)' : '0 2px 8px rgba(76, 110, 245, 0.15)'};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const PageEllipsis = styled.span`
  color: #94a3b8;
  padding: 0 0.15rem;
  font-weight: 500;
  font-size: 0.85rem;
  user-select: none;
`;

const PageIndicator = styled.span`
  font-size: 0.875rem;
  color: #64748b;
  font-weight: 500;
  padding: 0 0.5rem;
`;

const LocationManagementContainer = styled.div`
  margin-top: 2rem;
`;

const LocationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const LocationItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background-color: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #cbd5e0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

const LocationName = styled.span<{ enabled: boolean }>`
  font-size: 0.95rem;
  font-weight: 500;
  color: ${props => props.enabled ? '#2d3748' : '#a0aec0'};
  text-decoration: ${props => props.enabled ? 'none' : 'line-through'};
`;

const LocationActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const ToggleSlider = styled.span<{ checked: boolean }>`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: ${props => props.checked ? '#48bb78' : '#cbd5e0'};
  transition: 0.3s;
  border-radius: 24px;
  
  &:before {
    position: absolute;
    content: '';
    height: 18px;
    width: 18px;
    left: ${props => props.checked ? '28px' : '3px'};
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const DeleteLocationButton = styled.button`
  padding: 0.4rem 0.75rem;
  background-color: #fff5f5;
  color: #c53030;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #fed7d7;
    border-color: #fc8181;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddLocationForm = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  padding: 1rem;
  background-color: #f7fafc;
  border-radius: 8px;
  border: 1px dashed #cbd5e0;
`;

const LocationInput = styled.input`
  flex: 1;
  padding: 0.6rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #4c6ef5;
    box-shadow: 0 0 0 3px rgba(76, 110, 245, 0.1);
  }
`;

const AddLocationButton = styled.button`
  padding: 0.6rem 1.25rem;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(76, 110, 245, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.2rem;
  margin-bottom: 2.5rem;
`;

const MiniStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.8rem;
  margin-bottom: 0.8rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const MiniStatCard = styled.div<{ color?: string }>`
  background: white;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.2s ease;
  position: relative;
  overflow: visible;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const MiniStatIcon = styled.div<{ bgColor?: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: ${props => props.bgColor || '#f0f9ff'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MiniStatContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const MiniStatValue = styled.span`
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
`;

const MiniStatLabel = styled.span`
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LastRowWrapper = styled.div`
  grid-column: 1 / -1; /* Span full width of the parent StatsGrid */
  display: grid;
  /* Adjust column ratio as needed: here, stat card gets 1 part, trend chart gets 2 parts */
  grid-template-columns: minmax(150px, 1fr) minmax(300px, 2fr);
  gap: 1.2rem; /* Consistent gap with StatsGrid */
  align-items: stretch; /* Make items in the row stretch to the same height */
  margin-top: 1.2rem; /* Add margin if it's not the very first row of StatsGrid items */

  /* On smaller screens, stack them if they don't fit side-by-side comfortably */
  @media (max-width: 768px) {
    grid-template-columns: 1fr; /* Stack on smaller screens */
  }
`;

const StatCard = styled(motion.div)`
  cursor: pointer;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(76, 110, 245, 0.08);
  padding: 1rem;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(226, 232, 240, 0.8);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 18px 40px rgba(76, 110, 245, 0.15);
    border-color: rgba(76, 110, 245, 0.4);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 120px;
    height: 120px;
    background: linear-gradient(225deg, rgba(76, 110, 245, 0.05) 0%, rgba(76, 110, 245, 0) 50%);
    border-radius: 0 0 0 120px;
  }
  
  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0.75rem 0 0.5rem;
    color: #2d3748;
  }
  
  p {
    color: #718096;
    font-size: 0.75rem;
    margin: 0;
  }
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => {
    switch (props.color) {
      case 'blue': return 'rgba(76, 110, 245, 0.1)';
      case 'green': return 'rgba(46, 204, 113, 0.1)';
      case 'orange': return 'rgba(243, 156, 18, 0.1)';
      case 'red': return 'rgba(231, 76, 60, 0.1)';
      default: return 'rgba(76, 110, 245, 0.1)';
    }
  }};
  color: ${props => {
    switch (props.color) {
      case 'blue': return '#4c6ef5';
      case 'green': return '#27ae60';
      case 'orange': return '#f39c12';
      case 'red': return '#e74c3c';
      default: return '#4c6ef5';
    }
  }};
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const StatValue = styled.div`
  font-size: 2.2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
  margin-bottom: 0.8rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    width: 28px;
    height: 28px;
    color: #4c6ef5;
  }
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #4a5568;
  font-weight: 500;
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
    border-radius: 50%;
    margin-right: 4px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(76, 110, 245, 0.08);
  margin-bottom: 2rem;
`;

const Th = styled.th<{width?: string}>`
  padding: 0.75rem 1rem;
  text-align: left;
  font-weight: 600;
  border-bottom: 2px solid #e2e8f0;
  color: #4a5568;
  background-color: #f7fafc;
  white-space: nowrap;
  font-size: 0.95rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  position: relative;
  width: ${props => props.width || 'auto'};

  &:first-child {
    border-top-left-radius: 10px;
  }

  &:last-child {
    border-top-right-radius: 10px;
    padding-right: 0.5rem;
  }
`;

const Td = styled.td`
  padding: 0.6rem 0.9rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  color: #2d3748;
  font-size: 0.88rem;
  transition: background-color 0.2s ease;
  background-color: white;

  &:last-child {
    padding-right: 0.4rem;
  }

  &:first-child {
    font-weight: 500;
  }

  tr:last-child & {
    border-bottom: none;
  }

  tr:last-child &:first-child {
    border-bottom-left-radius: 10px;
  }

  tr:last-child &:last-child {
    border-bottom-right-radius: 10px;
  }
  
  tr:hover & {
    background-color: #f8faff;
  }
`;

const Tr = styled.tr`
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(237, 242, 247, 0.5);
  }
  
  &:last-child td {
    border-bottom: none;
  }
`;

const Badge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0.35rem 0.8rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'approved': return 'rgba(46, 204, 113, 0.15)';
      case 'payment_complete': return 'rgba(16, 185, 129, 0.15)';
      case 'pending': return 'rgba(243, 156, 18, 0.15)';
      case 'rejected': return 'rgba(231, 76, 60, 0.15)';
      default: return 'rgba(149, 165, 166, 0.15)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'approved': return '#1d8348';
      case 'payment_complete': return '#059669';
      case 'pending': return '#9a5f00';
      case 'rejected': return '#922b21';
      default: return '#5d6d7e';
    }
  }};
  box-shadow: 0 2px 5px ${props => {
    switch (props.status) {
      case 'approved': return 'rgba(46, 204, 113, 0.2)';
      case 'payment_complete': return 'rgba(16, 185, 129, 0.2)';
      case 'pending': return 'rgba(243, 156, 18, 0.2)';
      case 'rejected': return 'rgba(231, 76, 60, 0.2)';
      default: return 'rgba(149, 165, 166, 0.2)';
    }
  }};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px ${props => {
      switch (props.status) {
        case 'approved': return 'rgba(46, 204, 113, 0.3)';
        case 'pending': return 'rgba(243, 156, 18, 0.3)';
        case 'rejected': return 'rgba(231, 76, 60, 0.3)';
        default: return 'rgba(149, 165, 166, 0.3)';
      }
    }};
  }
  
  &::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => {
      switch (props.status) {
        case 'approved': return '#2ecc71';
        case 'payment_complete': return '#10b981';
        case 'pending': return '#f39c12';
        case 'rejected': return '#e74c3c';
        default: return '#95a5a6';
      }
    }};
  }
`;

const StatusBadge = styled.span<{ status: string, large?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: ${props => props.large ? '0.5rem 1rem' : '0.35rem 0.8rem'};
  border-radius: 9999px;
  font-size: ${props => props.large ? '0.95rem' : '0.85rem'};
  font-weight: 600;
  line-height: 1;
  background-color: ${props => {
    switch (props.status) {
      case 'approved':
        return 'rgba(46, 204, 113, 0.15)';
      case 'payment_complete':
        return 'rgba(16, 185, 129, 0.15)';
      case 'rejected':
        return 'rgba(231, 76, 60, 0.15)';
      case 'pending':
      case 'Queued for deletion':
      default:
        return 'rgba(243, 156, 18, 0.15)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'approved':
        return '#27ae60';
      case 'payment_complete':
        return '#059669';
      case 'rejected':
        return '#e74c3c';
      case 'pending':
      case 'Queued for deletion':
      default:
        return '#f39c12';
    }
  }};
`;

// Compact completion display elements (for Recent Applications card)
const CompletionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const CompletionTrack = styled.div`
  width: 120px;
  height: 6px;
  background-color: #e2e8f0;
  border-radius: 9999px;
  overflow: hidden;
`;

const CompletionFill = styled.div<{ percent: number }>`
  height: 100%;
  width: ${props => Math.max(0, Math.min(100, props.percent))}%;
  background: linear-gradient(135deg, #4c6ef5 0%, #6e8ffb 100%);
  transition: width 0.4s ease;
`;

const CompletionText = styled.span`
  font-size: 0.8rem;
  color: #4a5568;
  font-weight: 600;
  min-width: 36px;
  text-align: right;
`;

const ActionButton = styled(motion.button)`
  background-color: transparent;
  border: none;
  color: #4c6ef5;
  font-size: 0.9rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0.4rem 0.8rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(76, 110, 245, 0.1);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #4a5568;
  
  p {
    margin-top: 1rem;
    font-size: 1rem;
  }
`;

const ErrorText = styled.div`
  display: flex;
  align-items: center;
  color: #e53e3e; // Red color for errors
  background-color: #fff5f5; // Light red background
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #f56565;
  margin: 1rem 0;
  font-size: 0.95rem;
  svg {
    margin-right: 0.75rem;
    stroke-width: 2;
  }
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(76, 110, 245, 0.1);
  border-top: 4px solid #4c6ef5;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const IntentTooltipCSS = createGlobalStyle`
  td:hover .intent-tooltip { opacity: 1 !important; pointer-events: auto !important; }
  .intent-drop-zone { transition: all 0.3s ease; }
  .intent-drop-zone.dragging { border-color: #6366f1 !important; background: #eef2ff !important; }

  @keyframes copiedFadeUp {
    0%   { opacity: 0; transform: translate(-50%, 4px) scale(0.85); }
    15%  { opacity: 1; transform: translate(-50%, -8px) scale(1); }
    70%  { opacity: 1; transform: translate(-50%, -8px) scale(1); }
    100% { opacity: 0; transform: translate(-50%, -18px) scale(0.9); }
  }
  .copied-tooltip {
    position: fixed;
    pointer-events: none;
    z-index: 99999;
    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
    color: #4ade80;
    font-size: 11px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.22);
    white-space: nowrap;
    animation: copiedFadeUp 1.1s ease forwards;
    letter-spacing: 0.3px;
  }
  .copied-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #334155;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const BackButton = styled.button`
  background-color: transparent;
  border: none;
  color: #4c6ef5;
  font-size: 0.95rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(76, 110, 245, 0.1);
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
    margin-bottom: 0;
  }
`;

const DetailSectionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1.2rem;
  position: relative;
  padding-left: 1rem;
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: linear-gradient(135deg, #4c6ef5 0%, #3b5bdb 100%);
    border-radius: 4px;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailLabel = styled.span`
  font-size: 0.85rem;
  color: #718096;
  margin-bottom: 0.4rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1.25rem; /* Increased padding */
  margin-bottom: 1.5rem; /* Keep existing margin */
  border: 1px solid #b0bec5; /* Darker, more prominent border */
  border-radius: 0.625rem; /* More rounded corners (10px) */
  font-size: 1rem; /* Larger font size */
  color: #2d3748; /* Darker text color for better contrast */
  background-color: #f7fafc; /* Subtle background to lift it */
  box-sizing: border-box;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); /* Subtle shadow for depth */

  &::placeholder {
    color: #718096; /* Slightly darker placeholder */
  }

  &:focus {
    outline: none;
    border-color: #4299e1; /* Brighter blue for focus */
    background-color: #ffffff; /* White background on focus */
    box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.3), 0 1px 3px rgba(0,0,0,0.05); /* Enhanced focus shadow + persistent depth shadow */
  }
`;

const DetailValue = styled.span`
  font-size: 1rem;
  color: #2d3748;
  font-weight: 500;
`;

const StatusButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const StatusButton = styled.button<{ status: string, active: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.7rem 1.2rem;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  background-color: ${props => {
    if (props.active) {
      switch (props.status) {
        case 'approved': return 'rgba(46, 204, 113, 0.15)';
        case 'rejected': return 'rgba(231, 76, 60, 0.15)';
        case 'pending': 
        case 'Queued for deletion':
        default: return 'rgba(243, 156, 18, 0.15)';
      }
    }
    return 'transparent';
  }};
  color: ${props => {
    switch (props.status) {
      case 'approved': return '#27ae60';
      case 'rejected': return '#e74c3c';
      case 'pending': 
      case 'Queued for deletion':
      default: return '#f39c12';
    }
  }};
  border-color: ${props => {
    switch (props.status) {
      case 'approved': return 'rgba(46, 204, 113, 0.3)';
      case 'rejected': return 'rgba(231, 76, 60, 0.3)';
      case 'pending': 
      case 'Queued for deletion':
      default: return 'rgba(243, 156, 18, 0.3)';
    }
  }};
  
  &:hover {
    background-color: ${props => {
      switch (props.status) {
        case 'approved': return 'rgba(46, 204, 113, 0.2)';
        case 'rejected': return 'rgba(231, 76, 60, 0.2)';
        case 'pending': 
        case 'Queued for deletion':
        default: return 'rgba(243, 156, 18, 0.2)';
      }
    }};
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MetaInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
  background-color: rgba(237, 242, 247, 0.5);
  padding: 1.2rem;
  border-radius: 10px;
  margin-top: 2rem;
`;

const MetaInfoItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const MetaInfoLabel = styled.span`
  font-size: 0.8rem;
  color: #718096;
  margin-bottom: 0.3rem;
`;

const MetaInfoValue = styled.span`
  font-size: 0.9rem;
  color: #2d3748;
  font-weight: 500;
`;

const ErrorMessage = styled.div`
  background-color: rgba(231, 76, 60, 0.1);
  border-left: 4px solid #e74c3c;
  color: #c0392b;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const TabContent = styled(motion.div)`
  width: 100%;
`;

// Modal component for confirmations
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  width: 90%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.3rem;
  margin: 0;
  color: #c53030;
`;

const ModalText = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #2d3748;
`;

const ModalButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const CancelButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #e2e8f0;
  color: #4a5568;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  &:hover {
    background-color: #cbd5e0;
  }
`;

const DeleteButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #c53030;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease-in-out, opacity 0.2s ease-in-out;

  &:hover {
    background-color: #9b2c2c;
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const ArchiveButton = styled.button`
  padding: 0.5rem 1rem;
  background-color: #3182ce;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s ease-in-out, opacity 0.2s ease-in-out;

  &:hover {
    background-color: #2b6cb0;
  }

  &:disabled {
    background-color: #ccc;
    color: #666;
    cursor: not-allowed;
    opacity: 0.7;
  }
`;



const BulkActionBar = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background-color: #f7fafc;
  border-radius: 8px;
  margin-bottom: 1rem;
  align-items: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const SelectAllCheckbox = styled.input`
  margin-right: 0.5rem;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
  padding: 0 1rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const DateFilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
`;

const DateInput = styled.input`
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.9rem;
`;

const DateRangeSeparator = styled.span`
  font-size: 0.9rem;
  color: #718096;
`;

const FilterButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'secondary' ? css`
    background-color: #e2e8f0;
    color: #4a5568;
    &:hover:not(:disabled) {
      background-color: #cbd5e0;
    }
  ` : css`
    background-color: #4c6ef5;
    color: white;
    &:hover:not(:disabled) {
      background-color: #3b5bdb;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExportContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ExportButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? css`
    background-color: #38a169;
    color: white;
    &:hover:not(:disabled) {
      background-color: #2f855a;
    }
  ` : css`
    background-color: #4c6ef5;
    color: white;
    &:hover:not(:disabled) {
      background-color: #3b5bdb;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ExportFormatSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-size: 0.9rem;
  background-color: white;
`;

const AlertMessageBase = styled.div<{type: 'success' | 'error' | 'warning'}>`
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 4px;
  background-color: ${props => 
    props.type === 'success' ? '#c6f6d5' : 
    props.type === 'error' ? '#fed7d7' : 
    '#fefcbf'};
  color: ${props => 
    props.type === 'success' ? '#22543d' : 
    props.type === 'error' ? '#822727' : 
    '#744210'};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// Create a motion version of AlertMessage
const AlertMessage = motion(AlertMessageBase);

// Document checklist rows configuration for Page4
const DOCUMENT_ROWS = [
  { id: 'doc1', label: 'Certified copy of Ownership / Title Document' },
  { id: 'doc2', label: 'Notarized Affidavit of Title of Ownership (Appendix D) on Rs. 500 stamp paper' },
  { id: 'doc3', label: 'Property Card or 7/12 Extract of recent date (Original)' },
  { id: 'doc4', label: 'Ferfar / Mutation Record / Entry (Original)' },
  { id: 'doc5', label: 'Previous 5 years\' Property Tax / Dues payment receipts along with latest Tax NOC (Original)' },
  { id: 'doc6a', label: 'Partnership Firm: a) Registered Partnership Deed along with Certificate of Registration' },
  { id: 'doc6b', label: 'Partnership Firm: b) Proposed Partnership Deed duly notarized' },
  { id: 'doc7', label: 'Certificate of Registration for Co-operatives / Corporate Bodies' },
  { id: 'doc8', label: 'Document clearly indicating Commercial Status of the offered site' },
  { id: 'doc9', label: 'Land Lease Agreement between MIDC & Applicant (as applicable)' },
  { id: 'doc10', label: 'Survey Plan issued by MIDC and Plot Handover Letter (Applicable for MIDC areas)' },
  { id: 'doc11', label: 'NOC from Landlord in case of Leasehold Land' },
  { id: 'doc12', label: 'NOC from MHADA / MIDC / Municipal Corporation or applicable Local Authority' },
  { id: 'doc13', label: 'NA Order, as applicable' },
  { id: 'doc14', label: 'Partition / Distribution Deed (in case of Family Partition / Distribution)' },
  { id: 'doc15', label: 'Firm "Allotment Letter" issued by Govt. / Semi-Govt. bodies' },
  { id: 'doc16', label: 'If Applicant is a Registered POA Holder: Notarized Affidavit from all Owners' },
  { id: 'doc17', label: 'Proof of Date of Birth' },
  { id: 'doc18', label: 'Proof of Residence' },
  { id: 'doc19', label: 'Proof of Educational Qualification' },
  { id: 'doc20', label: 'Proof of Photo ID & Address (Aadhaar)' },
];

const APPLICATION_TYPES = ['Individual', 'Partnership', 'Pvt Ltd', 'Public Ltd', 'Non Individual applicant entity'];

// Page4 Row Selections Component
const Page4RowSelectionsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [selectedAppType, setSelectedAppType] = useState<string>('Individual');
  const [visibility, setVisibility] = useState<{ [key: string]: string[] }>({});
  
  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchVisibilitySettings();
  }, []);

  const fetchVisibilitySettings = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/admin/page4-row-visibility`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.visibility) {
          setVisibility(data.visibility);
        }
      }
    } catch (error) {
      console.error('Error fetching page4 row visibility:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRow = (rowId: string) => {
    setVisibility(prev => {
      const currentRows = prev[selectedAppType] || [];
      const newRows = currentRows.includes(rowId)
        ? currentRows.filter(id => id !== rowId)
        : [...currentRows, rowId];
      return { ...prev, [selectedAppType]: newRows };
    });
  };

  const handleSelectAll = () => {
    setVisibility(prev => ({
      ...prev,
      [selectedAppType]: DOCUMENT_ROWS.map(r => r.id)
    }));
  };

  const handleDeselectAll = () => {
    setVisibility(prev => ({
      ...prev,
      [selectedAppType]: []
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const response = await fetch(`${API_BASE}/admin/page4-row-visibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ visibility })
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setSaveStatus({ type: 'success', message: 'Settings saved successfully!' });
      } else {
        setSaveStatus({ type: 'error', message: data.message || 'Failed to save settings' });
      }
    } catch (error) {
      setSaveStatus({ type: 'error', message: 'Network error while saving settings' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  const currentVisibleRows = visibility[selectedAppType] || [];

  if (loading) {
    return (
      <Card>
        <CardTitle>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '24px', height: '24px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
          Page4 Document Checklist Row Selections
        </CardTitle>
        <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>Loading settings...</div>
      </Card>
    );
  }

  return (
    <Card>
      <CardTitle>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '24px', height: '24px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
        Page4 Document Checklist Row Selections
      </CardTitle>
      
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
        <p style={{ margin: 0, color: '#0369a1', fontSize: '0.9em' }}>
          <strong>📋 Configure which document rows appear in Page 4's Document Checklist</strong> based on the Application Type selected in Page 1.
          Select an application type below, then check/uncheck the documents that should be visible for that type.
        </p>
      </div>

      {/* Application Type Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {APPLICATION_TYPES.map(type => (
          <button
            key={type}
            onClick={() => setSelectedAppType(type)}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9em',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: selectedAppType === type ? '#3b82f6' : '#e2e8f0',
              color: selectedAppType === type ? 'white' : '#475569'
            }}
          >
            {type}
            <span style={{ 
              marginLeft: '8px', 
              padding: '2px 8px', 
              borderRadius: '12px',
              fontSize: '0.8em',
              backgroundColor: selectedAppType === type ? 'rgba(255,255,255,0.2)' : '#cbd5e1'
            }}>
              {(visibility[type] || []).length}/{DOCUMENT_ROWS.length}
            </span>
          </button>
        ))}
      </div>

      {/* Select All / Deselect All Buttons */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <button
          onClick={handleSelectAll}
          style={{
            padding: '8px 16px',
            border: '1px solid #22c55e',
            borderRadius: '6px',
            backgroundColor: '#f0fdf4',
            color: '#16a34a',
            fontSize: '0.85em',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          ✓ Select All
        </button>
        <button
          onClick={handleDeselectAll}
          style={{
            padding: '8px 16px',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            fontSize: '0.85em',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          ✗ Deselect All
        </button>
      </div>

      {/* Document Rows Checkboxes */}
      <div style={{ 
        maxHeight: '400px', 
        overflowY: 'auto', 
        border: '1px solid #e2e8f0', 
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        {DOCUMENT_ROWS.map((row, index) => (
          <label
            key={row.id}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '12px 16px',
              borderBottom: index < DOCUMENT_ROWS.length - 1 ? '1px solid #e2e8f0' : 'none',
              cursor: 'pointer',
              backgroundColor: currentVisibleRows.includes(row.id) ? '#f0fdf4' : 'white',
              transition: 'background-color 0.2s'
            }}
          >
            <input
              type="checkbox"
              checked={currentVisibleRows.includes(row.id)}
              onChange={() => handleToggleRow(row.id)}
              style={{ 
                width: '18px', 
                height: '18px', 
                marginTop: '2px',
                accentColor: '#22c55e'
              }}
            />
            <div>
              <span style={{ 
                fontWeight: 600, 
                color: '#1e40af',
                fontSize: '0.85em',
                marginRight: '8px'
              }}>
                {row.id.replace('doc', 'Doc ')}
              </span>
              <span style={{ color: '#374151', fontSize: '0.9em' }}>{row.label}</span>
            </div>
          </label>
        ))}
      </div>

      {/* Save Button and Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '12px 30px',
            backgroundColor: saving ? '#94a3b8' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1em',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {saving ? '⏳ Saving...' : '💾 Save Settings'}
        </button>
        
        {saveStatus && (
          <div style={{
            padding: '10px 16px',
            borderRadius: '6px',
            fontSize: '0.9em',
            fontWeight: 500,
            backgroundColor: saveStatus.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: saveStatus.type === 'success' ? '#166534' : '#991b1b'
          }}>
            {saveStatus.type === 'success' ? '✓' : '✗'} {saveStatus.message}
          </div>
        )}
      </div>
    </Card>
  );
};

// Appendix Logic Component - Controls which appendixes are visible per Application Type
const APPENDIX_LIST = [
  { key: 'A', title: 'Appendix A', subtitle: 'Affidavit Cum Undertaking' },
  { key: 'B', title: 'Appendix B', subtitle: 'Affidavit for Individual & Partners' },
  { key: 'C', title: 'Appendix C', subtitle: 'Affidavit for Non-Individual Entity' },
  { key: 'D', title: 'Appendix D', subtitle: 'Ownership/Land Undertaking' },
  { key: 'E', title: 'Appendix E', subtitle: 'Advocate Certificate/Legal Opinion' },
  { key: 'F', title: 'Appendix F', subtitle: 'Land Owner Consent/NOC' }
];

const AppendixLogicTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [selectedAppType, setSelectedAppType] = useState<string>('Individual');
  const [appendixLogic, setAppendixLogic] = useState<{ [key: string]: string[] }>({});
  
  const RAW_API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? 'https://backend.mngldodo.site' : 'http://localhost:5000');
  const API_BASE = RAW_API_URL.endsWith('/api') ? RAW_API_URL.slice(0, -4) : RAW_API_URL;

  useEffect(() => {
    fetchAppendixLogic();
  }, []);

  const fetchAppendixLogic = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/appendix-logic`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.logic) {
          setAppendixLogic(data.logic);
        }
      }
    } catch (error) {
      console.error('Error fetching appendix logic:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAppendix = (appendixKey: string) => {
    setAppendixLogic(prev => {
      const currentAppendixes = prev[selectedAppType] || [];
      const newAppendixes = currentAppendixes.includes(appendixKey)
        ? currentAppendixes.filter(key => key !== appendixKey)
        : [...currentAppendixes, appendixKey].sort();
      return { ...prev, [selectedAppType]: newAppendixes };
    });
  };

  const handleSelectAll = () => {
    setAppendixLogic(prev => ({
      ...prev,
      [selectedAppType]: APPENDIX_LIST.map(a => a.key)
    }));
  };

  const handleDeselectAll = () => {
    setAppendixLogic(prev => ({
      ...prev,
      [selectedAppType]: []
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus(null);
    try {
      const response = await fetch(`${API_BASE}/api/admin/appendix-logic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ logic: appendixLogic })
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setSaveStatus({ type: 'success', message: 'Appendix logic settings saved successfully!' });
      } else {
        setSaveStatus({ type: 'error', message: data.message || 'Failed to save settings' });
      }
    } catch (error) {
      setSaveStatus({ type: 'error', message: 'Network error while saving settings' });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  const currentVisibleAppendixes = appendixLogic[selectedAppType] || [];

  if (loading) {
    return (
      <Card>
        <CardTitle>
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '24px', height: '24px' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Appendix Logic Settings
        </CardTitle>
        <div style={{ padding: '40px', textAlign: 'center', color: '#718096' }}>Loading settings...</div>
      </Card>
    );
  }

  return (
    <Card>
      <CardTitle>
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ width: '24px', height: '24px' }}>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        Appendix Logic Settings
      </CardTitle>
      
      <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fcd34d' }}>
        <p style={{ margin: 0, color: '#92400e', fontSize: '0.9em' }}>
          <strong>📄 Configure which Appendix documents are required</strong> for each Application Type.
          This controls which appendixes appear in:
          <br />• <strong>Page 1</strong> - Financial Capability Document Checklist (for main applicant and partners/directors)
          <br />• <strong>Page A</strong> - Appendix Documents upload section
        </p>
      </div>

      {/* Application Type Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {APPLICATION_TYPES.map(type => (
          <button
            key={type}
            onClick={() => setSelectedAppType(type)}
            style={{
              padding: '10px 16px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9em',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              backgroundColor: selectedAppType === type ? '#8b5cf6' : '#e2e8f0',
              color: selectedAppType === type ? 'white' : '#475569'
            }}
          >
            {type}
            <span style={{ 
              marginLeft: '8px', 
              padding: '2px 8px', 
              borderRadius: '12px',
              fontSize: '0.8em',
              backgroundColor: selectedAppType === type ? 'rgba(255,255,255,0.2)' : '#cbd5e1'
            }}>
              {(appendixLogic[type] || []).length}/{APPENDIX_LIST.length}
            </span>
          </button>
        ))}
      </div>

      {/* Select All / Deselect All Buttons */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
        <button
          onClick={handleSelectAll}
          style={{
            padding: '8px 16px',
            border: '1px solid #22c55e',
            borderRadius: '6px',
            backgroundColor: '#f0fdf4',
            color: '#16a34a',
            fontSize: '0.85em',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          ✓ Select All
        </button>
        <button
          onClick={handleDeselectAll}
          style={{
            padding: '8px 16px',
            border: '1px solid #ef4444',
            borderRadius: '6px',
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            fontSize: '0.85em',
            fontWeight: 500,
            cursor: 'pointer'
          }}
        >
          ✗ Deselect All
        </button>
      </div>

      {/* Appendix Checkboxes */}
      <div style={{ 
        border: '1px solid #e2e8f0', 
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        {APPENDIX_LIST.map((appendix, index) => (
          <label
            key={appendix.key}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '16px',
              borderBottom: index < APPENDIX_LIST.length - 1 ? '1px solid #e2e8f0' : 'none',
              cursor: 'pointer',
              backgroundColor: currentVisibleAppendixes.includes(appendix.key) ? '#faf5ff' : 'white',
              transition: 'background-color 0.2s'
            }}
          >
            <input
              type="checkbox"
              checked={currentVisibleAppendixes.includes(appendix.key)}
              onChange={() => handleToggleAppendix(appendix.key)}
              style={{ 
                width: '20px', 
                height: '20px', 
                marginTop: '2px',
                accentColor: '#8b5cf6'
              }}
            />
            <div>
              <span style={{ 
                fontWeight: 700, 
                color: '#7c3aed',
                fontSize: '1em',
                marginRight: '10px'
              }}>
                {appendix.title}
              </span>
              <span style={{ color: '#6b7280', fontSize: '0.9em' }}>- {appendix.subtitle}</span>
            </div>
          </label>
        ))}
      </div>

      {/* Save Button and Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '12px 30px',
            backgroundColor: saving ? '#94a3b8' : '#8b5cf6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '1em',
            fontWeight: 600,
            cursor: saving ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {saving ? '⏳ Saving...' : '💾 Save Appendix Logic'}
        </button>
        
        {saveStatus && (
          <div style={{
            padding: '10px 16px',
            borderRadius: '6px',
            fontSize: '0.9em',
            fontWeight: 500,
            backgroundColor: saveStatus.type === 'success' ? '#dcfce7' : '#fee2e2',
            color: saveStatus.type === 'success' ? '#166534' : '#991b1b'
          }}>
            {saveStatus.type === 'success' ? '✓' : '✗'} {saveStatus.message}
          </div>
        )}
      </div>
    </Card>
  );
};

// Security APIs Settings Component
const SecurityAPIsSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [testingVT, setTestingVT] = useState(false);
  const [testingMD, setTestingMD] = useState(false);
  const [vtTestResult, setVtTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [mdTestResult, setMdTestResult] = useState<{ success: boolean; message: string } | null>(null);
  
  const [virusTotalEnabled, setVirusTotalEnabled] = useState(false);
  const [virusTotalApiKey, setVirusTotalApiKey] = useState('');
  const [virusTotalApiKeySet, setVirusTotalApiKeySet] = useState(false);
  const [clearVirusTotalKey, setClearVirusTotalKey] = useState(false);
  
  const [metadefenderEnabled, setMetadefenderEnabled] = useState(false);
  const [metadefenderApiKey, setMetadefenderApiKey] = useState('');
  const [metadefenderApiKeySet, setMetadefenderApiKeySet] = useState(false);
  const [clearMetadefenderKey, setClearMetadefenderKey] = useState(false);
  
  const [aiDocumentValidationEnabled, setAiDocumentValidationEnabled] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchSecuritySettings();
  }, []);

  const fetchSecuritySettings = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/security-settings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setVirusTotalEnabled(data.virusTotalEnabled || false);
        setVirusTotalApiKeySet(data.virusTotalApiKeySet || false);
        setMetadefenderEnabled(data.metadefenderEnabled || false);
        setMetadefenderApiKeySet(data.metadefenderApiKeySet || false);
        setAiDocumentValidationEnabled(data.aiDocumentValidationEnabled || false);
      }
    } catch (error) {
      console.error('Error fetching security settings:', error);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setSaveStatus(null);
    try {
      const response = await fetch(`${API_BASE}/admin/security-settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          virusTotalEnabled,
          virusTotalApiKey: virusTotalApiKey || undefined,
          clearVirusTotalKey,
          metadefenderEnabled,
          metadefenderApiKey: metadefenderApiKey || undefined,
          clearMetadefenderKey,
          aiDocumentValidationEnabled
        })
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setSaveStatus({ type: 'success', message: 'Settings saved successfully!' });
        setVirusTotalApiKey('');
        setMetadefenderApiKey('');
        setClearVirusTotalKey(false);
        setClearMetadefenderKey(false);
        fetchSecuritySettings();
      } else {
        setSaveStatus({ type: 'error', message: data.message || 'Failed to save settings' });
      }
    } catch (error) {
      setSaveStatus({ type: 'error', message: 'Network error while saving settings' });
    } finally {
      setLoading(false);
    }
  };

  const testVirusTotal = async () => {
    setTestingVT(true);
    setVtTestResult(null);
    try {
      const response = await fetch(`${API_BASE}/admin/test-virustotal`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      setVtTestResult({ success: data.success, message: data.message });
    } catch (error) {
      setVtTestResult({ success: false, message: 'Network error during test' });
    } finally {
      setTestingVT(false);
    }
  };

  const testMetadefender = async () => {
    setTestingMD(true);
    setMdTestResult(null);
    try {
      const response = await fetch(`${API_BASE}/admin/test-metadefender`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      setMdTestResult({ success: data.success, message: data.message });
    } catch (error) {
      setMdTestResult({ success: false, message: 'Network error during test' });
    } finally {
      setTestingMD(false);
    }
  };

  return (
    <Card>
      <CardTitle>Malware Scanning APIs</CardTitle>
      <div style={{ padding: '1.5rem' }}>
        {/* VirusTotal Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#2d3748' }}>VirusTotal Integration</h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
              <input 
                type="checkbox" 
                checked={virusTotalEnabled} 
                onChange={(e) => setVirusTotalEnabled(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: virusTotalEnabled ? '#4c6ef5' : '#ccc', borderRadius: '26px', transition: '0.3s'
              }}>
                <span style={{
                  position: 'absolute', height: '20px', width: '20px', left: virusTotalEnabled ? '26px' : '3px',
                  bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                }}></span>
              </span>
            </label>
            <span style={{ color: '#4a5568' }}>Enable VirusTotal scanning</span>
          </div>
          
          <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }}>🔑</span>
            <input
              type="password"
              placeholder={virusTotalApiKeySet ? "VirusTotal API key (leave blank to keep existing)" : "VirusTotal API key"}
              value={virusTotalApiKey}
              onChange={(e) => setVirusTotalApiKey(e.target.value)}
              style={{
                width: '100%', padding: '0.75rem 0.75rem 0.75rem 2.5rem', border: '1px solid #e2e8f0',
                borderRadius: '6px', fontSize: '0.95rem', backgroundColor: '#f8fafc'
              }}
            />
          </div>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#718096', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={clearVirusTotalKey} onChange={(e) => setClearVirusTotalKey(e.target.checked)} />
            Clear stored VirusTotal key
          </label>
        </div>
        
        {/* Metadefender Section */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#2d3748' }}>Metadefender Integration</h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
              <input 
                type="checkbox" 
                checked={metadefenderEnabled} 
                onChange={(e) => setMetadefenderEnabled(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: metadefenderEnabled ? '#4c6ef5' : '#ccc', borderRadius: '26px', transition: '0.3s'
              }}>
                <span style={{
                  position: 'absolute', height: '20px', width: '20px', left: metadefenderEnabled ? '26px' : '3px',
                  bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                }}></span>
              </span>
            </label>
            <span style={{ color: '#4a5568' }}>Enable Metadefender (OPSWAT) scanning</span>
          </div>
          
          <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }}>🔑</span>
            <input
              type="password"
              placeholder={metadefenderApiKeySet ? "Metadefender API key (leave blank to keep existing)" : "Metadefender API key"}
              value={metadefenderApiKey}
              onChange={(e) => setMetadefenderApiKey(e.target.value)}
              style={{
                width: '100%', padding: '0.75rem 0.75rem 0.75rem 2.5rem', border: '1px solid #e2e8f0',
                borderRadius: '6px', fontSize: '0.95rem', backgroundColor: '#f8fafc'
              }}
            />
          </div>
          
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#718096', fontSize: '0.9rem' }}>
            <input type="checkbox" checked={clearMetadefenderKey} onChange={(e) => setClearMetadefenderKey(e.target.checked)} />
            Clear stored Metadefender key
          </label>
        </div>
        
        {/* AI Document Validation Section */}
        <div style={{ marginBottom: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#2d3748' }}>AI Document Validation</h3>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
              <input 
                type="checkbox" 
                checked={aiDocumentValidationEnabled} 
                onChange={(e) => setAiDocumentValidationEnabled(e.target.checked)}
                style={{ opacity: 0, width: 0, height: 0 }}
              />
              <span style={{
                position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: aiDocumentValidationEnabled ? '#4c6ef5' : '#ccc', borderRadius: '26px', transition: '0.3s'
              }}>
                <span style={{
                  position: 'absolute', height: '20px', width: '20px', left: aiDocumentValidationEnabled ? '26px' : '3px',
                  bottom: '3px', backgroundColor: 'white', borderRadius: '50%', transition: '0.3s'
                }}></span>
              </span>
            </label>
            <span style={{ color: '#4a5568' }}>Enable AI-powered document type validation</span>
          </div>
          
          <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '0' }}>
            When enabled, uploads are validated using AI to ensure the document matches the expected type 
            (e.g., PAN card upload actually contains a PAN card). Requires OpenRouter API key in Settings.
          </p>
        </div>
        
        <p style={{ color: '#718096', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
          API keys are stored encrypted and are optional. When enabled, they may be used for deeper scanning.
        </p>
        
        {saveStatus && (
          <div style={{
            padding: '0.75rem 1rem', borderRadius: '6px', marginBottom: '1rem',
            backgroundColor: saveStatus.type === 'success' ? '#c6f6d5' : '#fed7d7',
            color: saveStatus.type === 'success' ? '#22543d' : '#c53030'
          }}>
            {saveStatus.message}
          </div>
        )}
        
        <button
          onClick={handleSave}
          disabled={loading}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem',
            backgroundColor: '#4c6ef5', color: 'white', border: 'none', borderRadius: '6px',
            cursor: loading ? 'not-allowed' : 'pointer', fontSize: '0.95rem', fontWeight: 500,
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? '⏳' : '💾'} Save API Settings
        </button>
        
        {/* Test Section */}
        <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '1rem', color: '#2d3748' }}>Test API Keys</h3>
          
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <button
              onClick={testVirusTotal}
              disabled={testingVT || !virusTotalApiKeySet}
              style={{
                padding: '0.75rem 2rem', backgroundColor: 'transparent', color: '#38a169',
                border: '2px solid #38a169', borderRadius: '6px', cursor: testingVT || !virusTotalApiKeySet ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem', fontWeight: 500, opacity: !virusTotalApiKeySet ? 0.5 : 1
              }}
            >
              {testingVT ? '⏳ Testing...' : '🔒 Test VirusTotal'}
            </button>
            
            <button
              onClick={testMetadefender}
              disabled={testingMD || !metadefenderApiKeySet}
              style={{
                padding: '0.75rem 2rem', backgroundColor: 'transparent', color: '#805ad5',
                border: '2px solid #805ad5', borderRadius: '6px', cursor: testingMD || !metadefenderApiKeySet ? 'not-allowed' : 'pointer',
                fontSize: '0.95rem', fontWeight: 500, opacity: !metadefenderApiKeySet ? 0.5 : 1
              }}
            >
              {testingMD ? '⏳ Testing...' : '🔒 Test Metadefender'}
            </button>
          </div>
          
          {vtTestResult && (
            <div style={{
              marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '6px',
              backgroundColor: vtTestResult.success ? '#c6f6d5' : '#fed7d7',
              color: vtTestResult.success ? '#22543d' : '#c53030'
            }}>
              <strong>VirusTotal:</strong> {vtTestResult.message}
            </div>
          )}
          
          {mdTestResult && (
            <div style={{
              marginTop: '0.5rem', padding: '0.75rem 1rem', borderRadius: '6px',
              backgroundColor: mdTestResult.success ? '#c6f6d5' : '#fed7d7',
              color: mdTestResult.success ? '#22543d' : '#c53030'
            }}>
              <strong>Metadefender:</strong> {mdTestResult.message}
            </div>
          )}
          
          <p style={{ color: '#a0aec0', fontSize: '0.85rem', marginTop: '1rem' }}>
            Click to verify your API keys are valid and working.
          </p>
        </div>
      </div>
    </Card>
  );
};

const SubAdminWaterfallStyles = createGlobalStyle`
  @keyframes subAdminFadeSlideIn {
    0% {
      opacity: 0;
      transform: translateY(12px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes chatDotPulse {
    0%, 100% {
      transform: scale(1);
      box-shadow: 0 0 4px rgba(34, 197, 94, 0.4);
    }
    50% {
      transform: scale(1.35);
      box-shadow: 0 0 10px rgba(34, 197, 94, 0.7);
    }
  }
  @keyframes chatNavPulse {
    0%, 100% {
      background: rgba(34, 197, 94, 0.06);
      box-shadow: inset 0 0 0 1px rgba(34, 197, 94, 0.12);
    }
    50% {
      background: rgba(34, 197, 94, 0.14);
      box-shadow: inset 0 0 0 1.5px rgba(34, 197, 94, 0.28);
    }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(16px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes slideInRight {
    from { opacity: 0; transform: translateX(100%); }
    to { opacity: 1; transform: translateX(0); }
  }
  @keyframes nameFixGlow {
    0% { background: rgba(34, 197, 94, 0.25); box-shadow: inset 0 0 0 2px rgba(34,197,94,0.6), 0 0 12px rgba(34,197,94,0.3); }
    50% { background: rgba(34, 197, 94, 0.08); box-shadow: inset 0 0 0 2px rgba(34,197,94,0.3), 0 0 6px rgba(34,197,94,0.15); }
    100% { background: transparent; box-shadow: none; }
  }
`;

const AnimatedCounter: React.FC<{ value: number; duration?: number }> = ({ value, duration = 2000 }) => {
  const [display, setDisplay] = React.useState(0);
  const prev = React.useRef(0);
  const raf = React.useRef<number>();
  React.useEffect(() => {
    const start = prev.current;
    const diff = value - start;
    const t0 = performance.now();
    const tick = (now: number) => {
      const p = Math.min((now - t0) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(start + diff * ease));
      if (p < 1) { raf.current = requestAnimationFrame(tick); } else { prev.current = value; }
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [value, duration]);
  return <>{display}</>;
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { timeLeft, isWarning, formatTime, setShowModal } = useSessionTimer();
  
  // Initialize activeTab from sessionStorage to persist on refresh
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = sessionStorage.getItem('adminActiveTab');
    return savedTab || 'dashboard';
  });
  
  // Persist activeTab to sessionStorage when it changes
  React.useEffect(() => {
    sessionStorage.setItem('adminActiveTab', activeTab);
  }, [activeTab]);

  // ── "Copied!" tooltip helper ──
  const showCopiedTooltip = React.useCallback((e: React.MouseEvent) => {
    const el = document.createElement('div');
    el.className = 'copied-tooltip';
    el.textContent = 'Copied!';
    el.style.left = e.clientX + 'px';
    el.style.top = (e.clientY - 6) + 'px';
    document.body.appendChild(el);
    setTimeout(() => { if (el.parentNode) el.parentNode.removeChild(el); }, 1150);
  }, []);

  // Sidebar collapse state
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = sessionStorage.getItem('sidebarPinned');
    return saved === null ? true : saved === 'true';
  });
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const sidebarCollapsed = !sidebarPinned && !sidebarHovered;

  React.useEffect(() => {
    sessionStorage.setItem('sidebarPinned', String(sidebarPinned));
  }, [sidebarPinned]);

  // Digital clock state
  const [clockNow, setClockNow] = useState(new Date());
  React.useEffect(() => {
    const tid = setInterval(() => setClockNow(new Date()), 1000);
    return () => clearInterval(tid);
  }, []);
  
  // Fetch server stats for header gauges (CPU, RAM, Disk) - auto-refresh every 5 seconds
  React.useEffect(() => {
    const fetchHeaderStats = async () => {
      try {
        const [serverRes, storageRes] = await Promise.all([
          axios.get(`${API_BASE}/api/admin/server/stats`, { withCredentials: true }),
          axios.get(`${API_BASE}/api/admin/storage/stats`, { withCredentials: true })
        ]);
        
        if (serverRes.data.success) {
          const stats: ServerStats = {
            cpu: serverRes.data.serverStats.cpu,
            memory: serverRes.data.serverStats.memory,
            diskSpace: storageRes.data.success ? storageRes.data.stats.diskSpace : undefined
          };
          setHeaderServerStats(stats);
        }
      } catch (err) {
        console.error('Error fetching header server stats:', err);
      }
    };
    
    fetchHeaderStats();
    const interval = setInterval(fetchHeaderStats, 5000);
    return () => clearInterval(interval);
  }, []);
  
  // Tab options including deletion logs and controls
  const tabOptions = ['dashboard', 'applications', 'controls'];
  // Controls state for Fill Dummy Data button visibility (default OFF)
  const [showDummyDataButton, setShowDummyDataButton] = useState<boolean>(false);
  // Controls state for Linked Applications navigation visibility
  const [showLinkedApplicationsNav, setShowLinkedApplicationsNav] = useState<boolean>(true);
  // Application form heading/subheading controls
  const [formHeading, setFormHeading] = useState<string>('APPLICATION FOR OFFER OF OPEN PLOTS TO SET UP A CNG STATION BY WAY OF LONG-TERM LEASE');
  const [formSubheading, setFormSubheading] = useState<string>('(Landowners shall be awarded Dealership of the CNG Station based on their suitability)');
  
  // Application Lock Settings state
  const [autoLockPaidApplications, setAutoLockPaidApplications] = useState<boolean>(true);
  const [lockApplicationIds, setLockApplicationIds] = useState<string>('');
  const [manuallyLockedIds, setManuallyLockedIds] = useState<string[]>([]);
  const [manuallyUnlockedIds, setManuallyUnlockedIds] = useState<string[]>([]);
  const [lockActionMessage, setLockActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Fetch lock settings from backend
  const fetchLockSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/application-lock-settings`);
      if (response.data.success) {
        setAutoLockPaidApplications(response.data.autoLockPaidApplications);
        setManuallyLockedIds(response.data.lockedApplicationIds || []);
        setManuallyUnlockedIds(response.data.unlockedApplicationIds || []);
      }
    } catch (error) {
      console.error('Error fetching lock settings:', error);
    }
  };
  
  // Google Analytics configuration state (for Controls tab)
  const [gaMeasurementId, setGaMeasurementId] = useState<string>('');
  const [gaTestingInProgress, setGaTestingInProgress] = useState(false);
  const [gaTestResult, setGaTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [gaSaving, setGaSaving] = useState(false);
  const [gaToast, setGaToast] = useState<{ show: boolean; success: boolean; message: string }>({ show: false, success: false, message: '' });

  // OpenRouter AI configuration state (for Controls tab)
  const [openRouterApiKey, setOpenRouterApiKey] = useState<string>('');
  const [openRouterDefaultModel, setOpenRouterDefaultModel] = useState<string>('');
  const [openRouterCustomModels, setOpenRouterCustomModels] = useState<{ label: string; modelId: string }[]>([]);
  const [newCustomModelLabel, setNewCustomModelLabel] = useState<string>('');
  const [newCustomModelId, setNewCustomModelId] = useState<string>('');
  const [isTestingOpenRouterKey, setIsTestingOpenRouterKey] = useState<boolean>(false);
  const [openRouterTestMessage, setOpenRouterTestMessage] = useState<string | null>(null);
  const [openRouterTestSuccess, setOpenRouterTestSuccess] = useState<boolean | null>(null);

  // MSG91 SMS Integration state
  const [msg91AuthKey, setMsg91AuthKey] = useState<string>('');
  const [msg91SenderId, setMsg91SenderId] = useState<string>('');
  const [msg91SmsEnabled, setMsg91SmsEnabled] = useState<boolean>(false);
  const [msg91WhatsAppEnabled, setMsg91WhatsAppEnabled] = useState<boolean>(false);
  const [msg91ServerLoadAlertEnabled, setMsg91ServerLoadAlertEnabled] = useState<boolean>(false);
  const [msg91ServerLoadThreshold, setMsg91ServerLoadThreshold] = useState<number>(80);
  const [smsPaymentTemplate, setSmsPaymentTemplate] = useState<string>('');
  const [smsApplicationTemplate, setSmsApplicationTemplate] = useState<string>('');
  const [smsServerLoadTemplate, setSmsServerLoadTemplate] = useState<string>('');
  const [waPaymentTemplate, setWaPaymentTemplate] = useState<string>('');
  const [waApplicationTemplate, setWaApplicationTemplate] = useState<string>('');
  const [adminAlertNumbers, setAdminAlertNumbers] = useState<string[]>([]);
  const [newAdminNumber, setNewAdminNumber] = useState<string>('');
  const [testSmsNumber, setTestSmsNumber] = useState<string>('');
  const [testSmsType, setTestSmsType] = useState<string>('payment');
  const [isTestingSms, setIsTestingSms] = useState<boolean>(false);
  const [smsTestMessage, setSmsTestMessage] = useState<string | null>(null);
  const [smsTestSuccess, setSmsTestSuccess] = useState<boolean | null>(null);
  const [isSavingSmsSettings, setIsSavingSmsSettings] = useState<boolean>(false);

  // Social Media Share Links state (for Controls tab)
  const [socialShareLinks, setSocialShareLinks] = useState<{
    whatsapp: string; facebook: string; twitter: string; reddit: string; instagram: string; email: string;
  }>({
    whatsapp: '', facebook: '', twitter: '', reddit: '', instagram: '', email: '',
  });
  const [isSavingSocialLinks, setIsSavingSocialLinks] = useState(false);
  const [socialLinksSaveMsg, setSocialLinksSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Location management state
  interface LocationItem {
    id: string;
    name: string;
    enabled: boolean;
  }
  
  const [locations, setLocations] = useState<LocationItem[]>([]);
  const [newLocationName, setNewLocationName] = useState('');
  
  // Server stats state for header gauges (CPU, RAM, Disk)
  const [headerServerStats, setHeaderServerStats] = useState<ServerStats | null>(null);
  
  const saveShowDummyDataButtonSetting = async (newValue: boolean) => {
    setShowDummyDataButton(newValue);
    try {
      await axios.post(`${API_BASE}/api/admin/settings`, {
        showDummyDataButton: newValue,
      });
    } catch (error) {
      console.error('Error saving Fill Dummy Data button visibility setting:', error);
    }
  };

  const saveLocationsSetting = async (updatedLocations: LocationItem[]) => {
    setLocations(updatedLocations);
    try {
      await axios.post(`${API_BASE}/api/admin/settings`, {
        cngStationLocations: updatedLocations,
      });
    } catch (error) {
      console.error('Error saving CNG Station Locations setting:', error);
    }
  };

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Sub-admin state
  const [isSubAdmin, setIsSubAdmin] = useState(false);
  const [subAdminPermissions, setSubAdminPermissions] = useState<string[]>([]);
  const [subAdminName, setSubAdminName] = useState<string>('');

  // Chat notification state
  const [hasUnreadChats, setHasUnreadChats] = useState(false);
  const chatSocketRef = useRef<Socket | null>(null);
  
  // Sub-admin management state (for super admin)
  interface SubAdminUser {
    id: string;
    _id?: string;
    email: string;
    name: string;
    permissions: string[];
    isActive: boolean;
    lastLogin: string | null;
    loginCount: number;
    createdAt: string;
  }
  const [subAdmins, setSubAdmins] = useState<SubAdminUser[]>([]);
  const [isLoadingSubAdmins, setIsLoadingSubAdmins] = useState(false);
  const [showCreateSubAdminModal, setShowCreateSubAdminModal] = useState(false);
  const [showEditSubAdminModal, setShowEditSubAdminModal] = useState(false);
  const [editingSubAdmin, setEditingSubAdmin] = useState<SubAdminUser | null>(null);
  const [newSubAdminEmail, setNewSubAdminEmail] = useState('');
  const [newSubAdminName, setNewSubAdminName] = useState('');
  const [newSubAdminPassword, setNewSubAdminPassword] = useState('');
  const [newSubAdminPermissions, setNewSubAdminPermissions] = useState<string[]>(['dashboard', 'applications']);
  const [subAdminError, setSubAdminError] = useState<string | null>(null);
  const [subAdminSuccess, setSubAdminSuccess] = useState<string | null>(null);
  const [confettiTestTrigger, setConfettiTestTrigger] = useState(0);
  const [confettiTestInfo, setConfettiTestInfo] = useState<PaymentApplicantInfo | null>(null);
  
  // All available nav permissions
  const ALL_NAV_PERMISSIONS = [
    { key: 'dashboard', label: 'Dashboard' },
    { key: 'area-report', label: 'Area Wise Report' },
    { key: 'applications', label: 'Applications' },
    { key: 'application-files', label: 'Application Files' },
    { key: 'application-recovery', label: 'App Recovery' },
    { key: 'registered-users', label: 'Users' },
    { key: 'user-intent', label: 'User Intent' },
    { key: 'locations', label: 'Locations' },
    { key: 'storage-stats', label: 'Server Stats' },
    { key: 'visitor-stats', label: 'Visitor Stats' },
    { key: 'controls', label: 'Controls' },
    { key: 'security-apis', label: 'Security APIs' },
    { key: 'page4-rows', label: 'Page4 Row Selections' },
    { key: 'appendix-logic', label: 'Appendix Logic' },
    { key: 'settings', label: 'Settings' },
    { key: 'docs', label: 'Docs' },
    { key: 'session-playback', label: 'Session Playback' },
    { key: 'app-maintenance', label: 'App Maintenance' },
  ];
  
  const [locationsUploadFile, setLocationsUploadFile] = useState<File | null>(null);
  const [isLocationsUploading, setIsLocationsUploading] = useState(false);
  const [locationsUploadStatus, setLocationsUploadStatus] = useState<
    { message: string; type: 'success' | 'error' | 'info' } | null
  >(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Recent Applications pagination state
  const [recentAppsPage, setRecentAppsPage] = useState(1);
  const [recentAppsPerPage] = useState(5);
  
  const [applications, setApplications] = useState<Application[]>([]);
  const [selectedApplication, setSelectedApplication] = useState<ApplicationDetail | null>(null);
  const [isViewingDetails, setIsViewingDetails] = useState(false);

  // Compute an approximate completion percentage from formData
  const computeCompletion = (app: Application): number => {
    try {
      if (!app) return 0;
      if (app.status && app.status.toLowerCase() === 'payment_complete') return 100;
      if ((app as any).isComplete || (app as any).isSubmitted) return 100;
      const fd: any = app.formData || {};
      const ignore = new Set(['errors']);
      const entries = Object.entries(fd).filter(([k]) => !ignore.has(k));
      if (entries.length === 0) return 0;
      let filled = 0;
      let total = 0;
      for (const [, v] of entries) {
        total++;
        if (v === null || v === undefined) continue;
        if (typeof v === 'string') { if (v.trim() !== '') filled++; }
        else if (typeof v === 'boolean') { if (v) filled++; }
        else if (typeof v === 'number') { filled++; }
        else if (Array.isArray(v)) { if (v.length > 0) filled++; }
        else if (typeof v === 'object') { if (Object.keys(v).length > 0) filled++; }
      }
      const pct = Math.round((filled / Math.max(1, total)) * 100);
      return Math.max(0, Math.min(100, pct));
    } catch {
      return 0;
    }
  };
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [showDateRangeCal, setShowDateRangeCal] = useState(false);
  const [calViewDate, setCalViewDate] = useState(new Date());
  const [rangePhase, setRangePhase] = useState<'start' | 'end'>('start');
  const [hoverDate, setHoverDate] = useState<string>('');
  const appDatePickerRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<'excel' | 'pdf'>('excel');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const statusFilterFromTileRef = React.useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showQuickPeriod, setShowQuickPeriod] = useState(false);
  const quickPeriodRef = useRef<HTMLDivElement>(null);
  const [sortBy, setSortBy] = useState<string>('creation_desc');
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const [sortSubMenu, setSortSubMenu] = useState<string | null>(null); // 'status' or 'type'
  const [filterByStatus, setFilterByStatus] = useState<string | null>(null);
  const [filterByType, setFilterByType] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  
  // Compute time-based application stats
  const getTimeBasedStats = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    
    let applicationsToday = 0;
    let applicationsYesterday = 0;
    let applicantsThisWeek = 0;
    let applicantsThisMonth = 0;
    
    applications.forEach(app => {
      const createdAt = new Date(app.createdAt);
      const hasValidAppId = app.applicationId && app.applicationId.startsWith('CNG-');
      if (createdAt >= today && hasValidAppId) {
        applicationsToday++;
      }
      if (createdAt >= yesterday && createdAt < today && hasValidAppId) {
        applicationsYesterday++;
      }
      if (createdAt >= weekAgo) {
        applicantsThisWeek++;
      }
      if (createdAt >= monthAgo) {
        applicantsThisMonth++;
      }
    });
    
    return { applicationsToday, applicationsYesterday, applicantsThisWeek, applicantsThisMonth };
  };
  
  const timeStats = getTimeBasedStats();
  
  // Visitor stats state
  const [visitorStats, setVisitorStats] = useState({
    uniqueVisitors: { today: 0, yesterday: 0, thisWeek: 0, thisMonth: 0, total: 0 },
    pageViews: { today: 0, yesterday: 0, thisWeek: 0, thisMonth: 0, total: 0 }
  });
  // Online users count for header indicator
  const [onlineUsersCount, setOnlineUsersCount] = useState<number>(0);

  // Sub-admin online tracking
  const [onlineSubAdmins, setOnlineSubAdmins] = useState<{ id: string; email: string; name: string; sessions: number; connectedAt: number; devices: { device: string; browser: string; ip: string }[] }[]>([]);
  const [subAdminDropdownOpen, setSubAdminDropdownOpen] = useState(false);
  const subAdminDropdownRef = useRef<HTMLDivElement>(null);
  
  // Fetch visitor stats
  const fetchVisitorStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/visitors/stats`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setVisitorStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch visitor stats:', err);
    }
  };

  // Registered users stats state
  const [registeredUsersStats, setRegisteredUsersStats] = useState({
    totalUsers: 0,
    registeredToday: 0,
    registeredYesterday: 0
  });

  // Fetch registered users stats
  const fetchRegisteredUsersStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/registered-users-stats`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setRegisteredUsersStats({
          totalUsers: data.totalUsers,
          registeredToday: data.registeredToday,
          registeredYesterday: data.registeredYesterday
        });
      }
    } catch (err) {
      console.error('Failed to fetch registered users stats:', err);
    }
  };

  // Registered users list state (for the registered-users tab)
  const [registeredUsersList, setRegisteredUsersList] = useState<RegisteredUser[]>([]);
  const [regUsersPage, setRegUsersPage] = useState(1);
  const [regUsersTotalPages, setRegUsersTotalPages] = useState(1);
  const [regUsersTotal, setRegUsersTotal] = useState(0);
  const [regUsersSearch, setRegUsersSearch] = useState('');
  const [regUsersLoading, setRegUsersLoading] = useState(false);
  const [regUsersSortBy, setRegUsersSortBy] = useState<string>('createdAt');
  const [regUsersSortOrder, setRegUsersSortOrder] = useState<'asc' | 'desc'>('desc');
  const [regUsersAppsModal, setRegUsersAppsModal] = useState<RegisteredUser | null>(null);
  const [appsModalPage, setAppsModalPage] = useState(1);
  const appsModalPageSize = 5;
  // Profile image viewer/crop modal state
  const [profileImgModal, setProfileImgModal] = useState<{ userId: string; imageUrl: string; userName: string } | null>(null);
  const [profileImgZoom, setProfileImgZoom] = useState(1);
  const [profileImgPos, setProfileImgPos] = useState({ x: 0, y: 0 });
  const [profileImgDragging, setProfileImgDragging] = useState(false);
  const [profileImgDragStart, setProfileImgDragStart] = useState({ x: 0, y: 0 });
  const [profileImgCropping, setProfileImgCropping] = useState(false);
  const [profileImgCropRect, setProfileImgCropRect] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [profileImgCropDragging, setProfileImgCropDragging] = useState(false);
  const [profileImgSaving, setProfileImgSaving] = useState(false);
  const [profileImgNaturalSize, setProfileImgNaturalSize] = useState({ w: 0, h: 0 });
  const profileImgContainerRef = useRef<HTMLDivElement>(null);
  // Crop undo/redo history
  type CropRectType = { startX: number; startY: number; endX: number; endY: number };
  const [profileImgCropHistory, setProfileImgCropHistory] = useState<CropRectType[]>([]);
  const [profileImgCropRedoStack, setProfileImgCropRedoStack] = useState<CropRectType[]>([]);
  const [profileImgShowPreview, setProfileImgShowPreview] = useState(false);
  const [profileImgRotation, setProfileImgRotation] = useState(0); // 0, 90, 180, 270
  const [profileImgRotSaving, setProfileImgRotSaving] = useState(false);
  const regUsersPageSize = 15;
  const regUsersSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const regUsersPageRef = useRef(1);
  const regUsersSortByRef = useRef('createdAt');
  const regUsersSortOrderRef = useRef<'asc' | 'desc'>('desc');

  // ── Find & Fix User Names state ──
  interface NameFixUser { _id: string; fullName: string; email: string; suggestedName?: string; status?: 'pending' | 'guessing' | 'fixing' | 'fixed' | 'skipped' | 'error'; type: 'newUser' | 'badCase' }
  interface NameFixIgnoredUser { _id: string; fullName: string; email: string }
  const [nameFixModalOpen, setNameFixModalOpen] = useState(false);
  const [nameFixScanning, setNameFixScanning] = useState(false);
  const [nameFixUsers, setNameFixUsers] = useState<NameFixUser[]>([]);
  const [nameFixIgnoredUsers, setNameFixIgnoredUsers] = useState<NameFixIgnoredUser[]>([]);
  const [nameFixProcessing, setNameFixProcessing] = useState(false);
  const [nameFixToast, setNameFixToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' | 'info' }>({ show: false, message: '', type: 'info' });
  const [nameFixGlowId, setNameFixGlowId] = useState<string | null>(null);
  const [nameFixTab, setNameFixTab] = useState<'active' | 'ignored'>('active');
  const nameFixAbortRef = useRef(false);

  const showNameFixToast = (message: string, type: 'success' | 'error' | 'info' = 'info', duration = 4000) => {
    setNameFixToast({ show: true, message, type });
    setTimeout(() => setNameFixToast(prev => ({ ...prev, show: false })), duration);
  };

  const handleScanUserNames = async () => {
    setNameFixScanning(true);
    setNameFixUsers([]);
    setNameFixIgnoredUsers([]);
    setNameFixTab('active');
    try {
      const resp = await fetch(`${API_BASE}/api/admin/scan-user-names`, { credentials: 'include' });
      const data = await resp.json();
      if (!data.success) {
        showNameFixToast(data.message || 'Failed to scan user names.', 'error');
        setNameFixScanning(false);
        return;
      }

      const users: NameFixUser[] = [];
      data.newUsers.forEach((u: any) => users.push({ ...u, type: 'newUser', status: 'pending' }));
      data.badCaseUsers.forEach((u: any) => {
        const corrected = (u.fullName || '').trim().split(/\s+/).map((w: string) =>
          w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
        ).join(' ');
        users.push({ ...u, type: 'badCase', suggestedName: corrected, status: 'pending' });
      });

      setNameFixIgnoredUsers(data.ignoredUsers || []);

      if (users.length === 0) {
        if ((data.ignoredCount || 0) > 0) {
          setNameFixUsers([]);
          setNameFixModalOpen(true);
          showNameFixToast(`No new issues found. ${data.ignoredCount} ignored entries exist.`, 'info', 4000);
        } else {
          showNameFixToast('All user names look good! No issues found.', 'success');
        }
        setNameFixScanning(false);
        return;
      }

      setNameFixUsers(users);

      const parts: string[] = [];
      if (data.newUserCount > 0) parts.push(`${data.newUserCount} user${data.newUserCount > 1 ? 's' : ''} with "New User" name`);
      if (data.badCaseCount > 0) parts.push(`${data.badCaseCount} user${data.badCaseCount > 1 ? 's' : ''} with improper name formatting`);
      showNameFixToast(`Found: ${parts.join(', ')}.`, 'info', 5000);

      setNameFixModalOpen(true);
    } catch (err) {
      showNameFixToast('Failed to scan user names. Please try again.', 'error');
    }
    setNameFixScanning(false);
  };

  // AI guess for a single user
  const handleGuessNameAI = async (userId: string) => {
    const user = nameFixUsers.find(u => u._id === userId);
    if (!user) return;
    setNameFixUsers(prev => prev.map(u => u._id === userId ? { ...u, status: 'guessing' } : u));
    try {
      const aiResp = await fetch(`${API_BASE}/api/admin/ai-guess-name-from-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: user.email }),
      });
      const aiData = await aiResp.json();
      if (!aiData.success) {
        showNameFixToast(aiData.message || 'AI name guessing failed.', 'error', 5000);
        setNameFixUsers(prev => prev.map(u => u._id === userId ? { ...u, status: 'error' } : u));
        return;
      }
      if (aiData.guessedName) {
        setNameFixUsers(prev => prev.map(u => u._id === userId ? { ...u, suggestedName: aiData.guessedName, status: 'pending' } : u));
      } else {
        setNameFixUsers(prev => prev.map(u => u._id === userId ? { ...u, suggestedName: undefined, status: 'pending' } : u));
        showNameFixToast('Could not guess name from this email.', 'info', 3000);
      }
    } catch {
      showNameFixToast('OpenRouter AI is not properly configured.', 'error', 5000);
      setNameFixUsers(prev => prev.map(u => u._id === userId ? { ...u, status: 'error' } : u));
    }
  };

  // Fix a single user name
  const handleFixSingleName = async (userId: string) => {
    const user = nameFixUsers.find(u => u._id === userId);
    if (!user || !user.suggestedName) return;
    setNameFixUsers(prev => prev.map(u => u._id === userId ? { ...u, status: 'fixing' } : u));
    try {
      const resp = await fetch(`${API_BASE}/api/admin/update-user-name`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId: user._id, newName: user.suggestedName }),
      });
      const data = await resp.json();
      if (data.success) {
        setNameFixUsers(prev => prev.map(u => u._id === userId ? { ...u, status: 'fixed', fullName: user.suggestedName! } : u));
        setNameFixGlowId(userId);
        setRegisteredUsersList(prev => prev.map(u => u._id === userId ? { ...u, fullName: user.suggestedName! } : u));
        setTimeout(() => setNameFixGlowId(null), 4000);
      } else {
        setNameFixUsers(prev => prev.map(u => u._id === userId ? { ...u, status: 'error' } : u));
      }
    } catch {
      setNameFixUsers(prev => prev.map(u => u._id === userId ? { ...u, status: 'error' } : u));
    }
  };

  // Fix all bad format names (batch)
  const handleFixAllBadFormat = async () => {
    setNameFixProcessing(true);
    nameFixAbortRef.current = false;
    const toFix = nameFixUsers.filter(u => u.type === 'badCase' && u.suggestedName && u.status === 'pending');
    for (const user of toFix) {
      if (nameFixAbortRef.current) break;
      setNameFixUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'fixing' } : u));
      try {
        const resp = await fetch(`${API_BASE}/api/admin/update-user-name`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ userId: user._id, newName: user.suggestedName }),
        });
        const data = await resp.json();
        if (data.success) {
          setNameFixUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'fixed', fullName: user.suggestedName! } : u));
          setNameFixGlowId(user._id);
          setRegisteredUsersList(prev => prev.map(u => u._id === user._id ? { ...u, fullName: user.suggestedName! } : u));
          await new Promise(resolve => setTimeout(resolve, 4000));
          setNameFixGlowId(null);
        } else {
          setNameFixUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'error' } : u));
        }
      } catch {
        setNameFixUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'error' } : u));
      }
    }
    setNameFixProcessing(false);
    const fixedCount = toFix.length;
    showNameFixToast(`Bad format correction complete! ${fixedCount} name${fixedCount !== 1 ? 's' : ''} updated.`, 'success', 5000);
  };

  // Fix all names that have suggestions ready
  const handleFixAllNames = async () => {
    setNameFixProcessing(true);
    nameFixAbortRef.current = false;
    const usersToFix = nameFixUsers.filter(u => u.suggestedName && u.status === 'pending');
    for (const user of usersToFix) {
      if (nameFixAbortRef.current) break;
      setNameFixUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'fixing' } : u));
      try {
        const resp = await fetch(`${API_BASE}/api/admin/update-user-name`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
          body: JSON.stringify({ userId: user._id, newName: user.suggestedName }),
        });
        const data = await resp.json();
        if (data.success) {
          setNameFixUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'fixed', fullName: user.suggestedName! } : u));
          setNameFixGlowId(user._id);
          setRegisteredUsersList(prev => prev.map(u => u._id === user._id ? { ...u, fullName: user.suggestedName! } : u));
          await new Promise(resolve => setTimeout(resolve, 4000));
          setNameFixGlowId(null);
        } else {
          setNameFixUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'error' } : u));
        }
      } catch {
        setNameFixUsers(prev => prev.map(u => u._id === user._id ? { ...u, status: 'error' } : u));
      }
    }
    setNameFixProcessing(false);
    const fixedCount = usersToFix.length;
    showNameFixToast(`Name correction complete! ${fixedCount} name${fixedCount !== 1 ? 's' : ''} updated.`, 'success', 5000);
  };

  // Ignore a user
  const handleIgnoreUser = async (userId: string) => {
    try {
      const resp = await fetch(`${API_BASE}/api/admin/namefix-ignore`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      const data = await resp.json();
      if (data.success) {
        const user = nameFixUsers.find(u => u._id === userId);
        if (user) {
          setNameFixIgnoredUsers(prev => [...prev, { _id: user._id, fullName: user.fullName, email: user.email }]);
          setNameFixUsers(prev => prev.filter(u => u._id !== userId));
        }
      }
    } catch {
      showNameFixToast('Failed to ignore user.', 'error');
    }
  };

  // Unignore a user
  const handleUnignoreUser = async (userId: string) => {
    try {
      const resp = await fetch(`${API_BASE}/api/admin/namefix-unignore`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      const data = await resp.json();
      if (data.success) {
        const user = nameFixIgnoredUsers.find(u => u._id === userId);
        if (user) {
          const isNewUser = /^new\s*user$/i.test(user.fullName);
          setNameFixUsers(prev => [...prev, { ...user, type: isNewUser ? 'newUser' : 'badCase', status: 'pending' as const,
            suggestedName: isNewUser ? undefined : (user.fullName || '').trim().split(/\s+/).map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
          }]);
          setNameFixIgnoredUsers(prev => prev.filter(u => u._id !== userId));
        }
      }
    } catch {
      showNameFixToast('Failed to unignore user.', 'error');
    }
  };

  // Keep refs in sync with state for socket listener access
  useEffect(() => { regUsersPageRef.current = regUsersPage; }, [regUsersPage]);
  useEffect(() => { regUsersSortByRef.current = regUsersSortBy; }, [regUsersSortBy]);
  useEffect(() => { regUsersSortOrderRef.current = regUsersSortOrder; }, [regUsersSortOrder]);

  // Active online users map: email -> visitorId (for "View Live" in registered users)
  const [onlineUserEmailMap, setOnlineUserEmailMap] = useState<Record<string, string>>({});
  const [regUsersLiveVisitorId, setRegUsersLiveVisitorId] = useState<string | null>(null);
  const [regUsersLiveLabel, setRegUsersLiveLabel] = useState<string>('');
  const [isGlideAnimating, setIsGlideAnimating] = useState(false);

  // Applications glide animation — bubble live applications to top
  const [isAppGlideAnimating, setIsAppGlideAnimating] = useState(false);
  const prevLiveAppIdsRef = useRef<string>('');

  // Recovery exclude from Registered Users page
  const [recoveryExcludeUser, setRecoveryExcludeUser] = useState<RegisteredUser | null>(null);
  const [recoveryExcludeReason, setRecoveryExcludeReason] = useState('');
  const [recoveryExcludeNote, setRecoveryExcludeNote] = useState('');
  const [recoveryExcludeLoading, setRecoveryExcludeLoading] = useState(false);
  const [recoveryExcludedMap, setRecoveryExcludedMap] = useState<Record<string, boolean>>({});
  const [showExcludedUsers, setShowExcludedUsers] = useState(true);
  const [showQualifiedOnly, setShowQualifiedOnly] = useState(false);
  // Track recently-excluded user IDs for strike-through animation
  const [recentlyExcludedIds, setRecentlyExcludedIds] = useState<Set<string>>(new Set());
  // Total excluded count across all pages (fetched from server)
  const [excludedTotalCount, setExcludedTotalCount] = useState(0);

  // ── User Intent state ──
  const [intentEditUser, setIntentEditUser] = useState<{ email: string; current: string; reason: string } | null>(null);
  const [intentEditValue, setIntentEditValue] = useState('');
  const [intentEditReason, setIntentEditReason] = useState('');
  const [intentEditSaving, setIntentEditSaving] = useState(false);
  // User Intent tab state
  const [intentTabStats, setIntentTabStats] = useState<{ yes: number; maybe: number; no: number; total: number } | null>(null);
  const [intentTabList, setIntentTabList] = useState<any[]>([]);
  const [intentTabLoading, setIntentTabLoading] = useState(false);
  const [intentTabPage, setIntentTabPage] = useState(1);
  const [intentTabTotal, setIntentTabTotal] = useState(0);
  const [intentTabTotalPages, setIntentTabTotalPages] = useState(1);
  const [intentTabSearch, setIntentTabSearch] = useState('');
  const [intentUploadDragging, setIntentUploadDragging] = useState(false);
  const [intentUploading, setIntentUploading] = useState(false);
  const [intentUploadResult, setIntentUploadResult] = useState<any>(null);
  const intentSearchTimer = useRef<any>(null);

  // User selection for delete
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  // Date range picker state
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [dateRangeFrom, setDateRangeFrom] = useState<string>('');
  const [dateRangeTo, setDateRangeTo] = useState<string>('');
  const [dateRangeCount, setDateRangeCount] = useState<number | null>(null);
  const [dateRangeLoading, setDateRangeLoading] = useState(false);
  const [datePickerMonth, setDatePickerMonth] = useState(() => { const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() }; });
  const [datePickerSelecting, setDatePickerSelecting] = useState<'from' | 'to'>('from');
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Session player modal state
  const [sessionPlayAppId, setSessionPlayAppId] = useState<string | null>(null);
  const [recordingInfoMap, setRecordingInfoMap] = useState<Record<string, { totalSessions: number; totalEvents: number; totalDataSize: number; totalChunks: number; storagePath: string }>>({});

  // Live session viewing state
  const [liveSessionAppId, setLiveSessionAppId] = useState<string | null>(null);
  const [liveSessionEmail, setLiveSessionEmail] = useState<string>('');
  const [liveSessionsMap, setLiveSessionsMap] = useState<Record<string, { userEmail: string; startedAt: string; currentPage: string }>>({});

  // Delete session recording handler
  const handleDeleteSession = async (applicationId: string) => {
    if (!window.confirm(`Delete ALL session recordings for ${applicationId}? This cannot be undone.`)) return;
    try {
      const res = await axios.delete(`${API_BASE}/api/admin/session-recording/${encodeURIComponent(applicationId)}`);
      if (res.data.success) {
        setRecordingInfoMap(prev => {
          const next = { ...prev };
          delete next[applicationId];
          return next;
        });
      }
    } catch (err) {
      console.error('[DeleteSession] Error:', err);
      alert('Failed to delete session recording.');
    }
  };

  // Tile detail modal state
  const [activeTileModal, setActiveTileModal] = useState<TileType | null>(null);

  // Growth & Insights chart view: 'bars' | 'radar' | 'donut'
  const [growthChartView, setGrowthChartView] = useState<'bars' | 'radar' | 'donut'>('bars');

  // Restore panel state
  const [showRestorePanel, setShowRestorePanel] = useState(false);
  const [deletedUsersList, setDeletedUsersList] = useState<any[]>([]);
  const [deletedUsersLoading, setDeletedUsersLoading] = useState(false);
  const [selectedRestoreIds, setSelectedRestoreIds] = useState<Set<string>>(new Set());
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [restoreResult, setRestoreResult] = useState<{ restored: any[]; skipped: any[] } | null>(null);

  // Toggle user selection
  const toggleUserSelect = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedUserIds.size === registeredUsersList.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(registeredUsersList.map(u => u._id)));
    }
  };

  // Soft delete users
  const handleDeleteUsers = async () => {
    if (deleteConfirmText !== 'DELETE' || selectedUserIds.size === 0) return;
    setDeleteInProgress(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/delete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userIds: Array.from(selectedUserIds) })
      });
      const data = await response.json();
      if (data.success) {
        setSelectedUserIds(new Set());
        setShowDeleteModal(false);
        setDeleteConfirmText('');
        setRegUsersPage(1);
        fetchRegisteredUsersList(1, regUsersSearch);
        fetchRegisteredUsersStats();
        if (dateRangeFrom && dateRangeTo) fetchDateRangeCount(dateRangeFrom, dateRangeTo);
      } else {
        alert(data.message || 'Failed to delete users');
      }
    } catch (err) {
      console.error('Delete users error:', err);
      alert('Failed to delete users');
    } finally {
      setDeleteInProgress(false);
    }
  };

  // Fetch date range count
  const fetchDateRangeCount = async (from: string, to: string) => {
    if (!from || !to) return;
    setDateRangeLoading(true);
    try {
      const params = new URLSearchParams({ startDate: from, endDate: to });
      const response = await fetch(`${API_BASE}/api/admin/registered-users-daterange-count?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) setDateRangeCount(data.count);
    } catch (err) {
      console.error('Date range count error:', err);
    } finally {
      setDateRangeLoading(false);
    }
  };

  // Fetch ALL user IDs in a date range (for select-all across pages)
  const selectAllUsersInDateRange = async () => {
    if (!dateRangeFrom || !dateRangeTo) return;
    try {
      const params = new URLSearchParams({ page: '1', limit: '10000', sortBy: 'createdAt', sortOrder: 'desc', startDate: dateRangeFrom, endDate: dateRangeTo });
      const response = await fetch(`${API_BASE}/api/admin/registered-users?${params}`, { credentials: 'include' });
      const data = await response.json();
      if (data.success && data.users) {
        setSelectedUserIds(new Set(data.users.map((u: any) => u._id)));
      }
    } catch (err) {
      console.error('Select all in date range error:', err);
    }
    setShowDateRangePicker(false);
  };

  // Fetch deleted users for restore
  const fetchDeletedUsers = async () => {
    setDeletedUsersLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/deleted?limit=100`, { credentials: 'include' });
      const data = await response.json();
      if (data.success) setDeletedUsersList(data.deletedUsers || []);
    } catch (err) {
      console.error('Fetch deleted users error:', err);
    } finally {
      setDeletedUsersLoading(false);
    }
  };

  // Restore selected users
  const handleRestoreUsers = async () => {
    if (selectedRestoreIds.size === 0) return;
    setRestoreInProgress(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/restore`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deletedUserIds: Array.from(selectedRestoreIds) })
      });
      const data = await response.json();
      if (data.success) {
        setRestoreResult({ restored: data.restored || [], skipped: data.skipped || [] });
        setSelectedRestoreIds(new Set());
        fetchDeletedUsers();
        fetchRegisteredUsersList(regUsersPage, regUsersSearch);
        fetchRegisteredUsersStats();
      }
    } catch (err) {
      console.error('Restore users error:', err);
    } finally {
      setRestoreInProgress(false);
    }
  };

  // Close date picker on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(e.target as Node)) {
        setShowDateRangePicker(false);
      }
    };
    if (showDateRangePicker) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDateRangePicker]);

  // Close sub-admin dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (subAdminDropdownRef.current && !subAdminDropdownRef.current.contains(e.target as Node)) {
        setSubAdminDropdownOpen(false);
      }
    };
    if (subAdminDropdownOpen) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [subAdminDropdownOpen]);

  const fetchRegisteredUsersList = async (page = 1, search = '', sortBy = regUsersSortBy, sortOrder = regUsersSortOrder, fromDate = dateRangeFrom, toDate = dateRangeTo, hideExcluded?: boolean, qualifiedFilter?: boolean) => {
    setRegUsersLoading(true);
    setSelectedUserIds(new Set());
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(regUsersPageSize), sortBy, sortOrder });
      if (search) params.append('search', search);
      if (fromDate && toDate) { params.append('startDate', fromDate); params.append('endDate', toDate); }
      // When hideExcluded is explicitly passed use it, otherwise derive from current state
      const shouldExclude = hideExcluded !== undefined ? hideExcluded : !showExcludedUsers;
      if (shouldExclude) params.append('excludeRecovery', 'true');
      // Qualified users filter
      const isQualified = qualifiedFilter !== undefined ? qualifiedFilter : showQualifiedOnly;
      if (isQualified) params.append('qualifiedOnly', 'true');
      const response = await fetch(`${API_BASE}/api/admin/registered-users?${params.toString()}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setRegisteredUsersList(data.users);
        setRegUsersTotal(data.total);
        setRegUsersTotalPages(data.totalPages);
        setRegUsersPage(data.page);
      }
    } catch (err) {
      console.error('Failed to fetch registered users list:', err);
    } finally {
      setRegUsersLoading(false);
    }
  };

  const handleRegUsersSort = (field: string) => {
    const newOrder = regUsersSortBy === field && regUsersSortOrder === 'asc' ? 'desc' : 'asc';
    setRegUsersSortBy(field);
    setRegUsersSortOrder(newOrder);
    fetchRegisteredUsersList(1, regUsersSearch, field, newOrder);
    setRegUsersPage(1);
  };

  const regUsersSortIcon = (field: string) => {
    if (regUsersSortBy !== field) return ' ↕';
    return regUsersSortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  // Handle exclude user from recovery (from Registered Users page)
  const handleRecoveryExclude = async () => {
    if (!recoveryExcludeUser) return;
    setRecoveryExcludeLoading(true);
    try {
      await axios.post(`${API_BASE}/api/admin/recovery/exclude-by-userid`, {
        userId: recoveryExcludeUser._id,
        reason: recoveryExcludeReason || 'other',
        note: recoveryExcludeNote,
      }, { withCredentials: true });
      const excludedId = recoveryExcludeUser._id;
      setRecoveryExcludedMap(prev => ({ ...prev, [excludedId]: true }));
      // Trigger strike-through animation
      setRecentlyExcludedIds(prev => new Set(prev).add(excludedId));
      setTimeout(() => setRecentlyExcludedIds(prev => { const next = new Set(prev); next.delete(excludedId); return next; }), 1500);
      setRecoveryExcludeUser(null);
      setRecoveryExcludeReason('');
      setRecoveryExcludeNote('');
    } catch (err: any) {
      alert('Failed to exclude user from recovery');
    } finally {
      setRecoveryExcludeLoading(false);
    }
  };

  // Re-include an excluded user back into recovery
  const [recoveryIncludeLoading, setRecoveryIncludeLoading] = useState<string | null>(null);
  const handleRecoveryInclude = async (userId: string) => {
    setRecoveryIncludeLoading(userId);
    try {
      await axios.post(`${API_BASE}/api/admin/recovery/include-by-userid`, { userId }, { withCredentials: true });
      setRecoveryExcludedMap(prev => { const next = { ...prev }; delete next[userId]; return next; });
      setExcludedTotalCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to re-include user');
    } finally {
      setRecoveryIncludeLoading(null);
    }
  };

  // Fetch recovery exclusion status for visible users
  useEffect(() => {
    if (activeTab !== 'registered-users' || registeredUsersList.length === 0) return;
    const checkExclusions = async () => {
      const map: Record<string, boolean> = {};
      // Batch check — use Promise.all for speed
      await Promise.all(registeredUsersList.map(async (u) => {
        try {
          const res = await axios.get(`${API_BASE}/api/admin/recovery/check-excluded/${u._id}`, { withCredentials: true });
          if (res.data?.excluded) map[u._id] = true;
        } catch { /* ignore */ }
      }));
      setRecoveryExcludedMap(prev => ({ ...prev, ...map }));
    };
    checkExclusions();
  }, [registeredUsersList, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch total excluded count across all pages (for badge)
  const fetchExcludedCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/admin/registered-users/excluded-count`, { withCredentials: true });
      if (res.data?.success) setExcludedTotalCount(res.data.count || 0);
    } catch { /* ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'registered-users') fetchExcludedCount();
  }, [activeTab, fetchExcludedCount]);

  // Also refresh excluded count after excluding a user
  useEffect(() => {
    if (Object.values(recoveryExcludedMap).some(Boolean)) fetchExcludedCount();
  }, [recoveryExcludedMap, fetchExcludedCount]);

  // Export registered users to XLSX — selected only (if any) or all
  const [regUsersExporting, setRegUsersExporting] = useState(false);
  const handleExportUsersXlsx = async () => {
    setRegUsersExporting(true);
    try {
      const hasSelection = selectedUserIds.size > 0;
      let usersToExport: RegisteredUser[] = [];

      if (hasSelection) {
        // Export only selected users — they are already loaded on the current page
        usersToExport = registeredUsersList.filter(u => selectedUserIds.has(u._id));
      } else {
        // Export ALL users across all pages (fetch up to 100k)
        const params = new URLSearchParams({ page: '1', limit: '100000', sortBy: regUsersSortBy, sortOrder: regUsersSortOrder });
        if (regUsersSearch) params.append('search', regUsersSearch);
        if (dateRangeFrom && dateRangeTo) { params.append('startDate', dateRangeFrom); params.append('endDate', dateRangeTo); }
        if (!showExcludedUsers) params.append('excludeRecovery', 'true');
        if (showQualifiedOnly) params.append('qualifiedOnly', 'true');
        const response = await fetch(`${API_BASE}/api/admin/registered-users?${params.toString()}`, { credentials: 'include' });
        const data = await response.json();
        if (!data.success || !data.users) { alert('Failed to fetch users for export'); return; }
        usersToExport = data.users;
      }

      const rows = usersToExport.map((u: RegisteredUser, idx: number) => ({
        '#': idx + 1,
        'Name': u.fullName || '—',
        'Email': u.email,
        'Phone': u.phoneNumber || '—',
        'Intent': u.intent?.intent ? u.intent.intent.charAt(0).toUpperCase() + u.intent.intent.slice(1) : '—',
        'Registered On': new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
        'Applications': u.applicationCount,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 5 }, { wch: 30 }, { wch: 35 }, { wch: 14 }, { wch: 10 }, { wch: 16 }, { wch: 13 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, showQualifiedOnly ? 'Qualified Users' : 'Registered Users');
      const dateStr = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
      const prefix = hasSelection ? `Registered_Users_Selected_${selectedUserIds.size}` : showQualifiedOnly ? 'Qualified_Users' : 'Registered_Users';
      XLSX.writeFile(wb, `${prefix}_${dateStr}.xlsx`);
    } catch (err) {
      console.error('Export XLSX error:', err);
      alert('Failed to export users.');
    } finally {
      setRegUsersExporting(false);
    }
  };

  // Export users without applications to XLSX
  const [regUsersNoAppsExporting, setRegUsersNoAppsExporting] = useState(false);
  const handleExportUsersWithoutAppsXlsx = async () => {
    setRegUsersNoAppsExporting(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/registered-users-without-applications`, { credentials: 'include' });
      const data = await response.json();
      if (!data.success || !data.users) { alert('Failed to fetch users without applications'); return; }
      if (data.users.length === 0) { alert('No users without applications found.'); return; }
      const rows = data.users.map((u: { fullName: string; email: string; phoneNumber: string }, idx: number) => ({
        '#': idx + 1,
        'Name': u.fullName || '—',
        'Email': u.email || '—',
        'Phone': u.phoneNumber || '—',
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 5 }, { wch: 30 }, { wch: 35 }, { wch: 16 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Users Without Applications');
      const dateStr = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
      XLSX.writeFile(wb, `Users_Without_Applications_${dateStr}.xlsx`);
    } catch (err) {
      console.error('Export Users w/o Apps XLSX error:', err);
      alert('Failed to export users without applications.');
    } finally {
      setRegUsersNoAppsExporting(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // User Intent — inline edit + tab data
  // ═══════════════════════════════════════════════════════════════════════

  const handleIntentSave = async (email: string, intent: string, reason: string) => {
    setIntentEditSaving(true);
    try {
      await axios.put(`${API_BASE}/api/admin/user-intent/${encodeURIComponent(email)}`, { intent, reason }, { withCredentials: true });
      // Update the local registeredUsersList to reflect the change immediately
      setRegisteredUsersList(prev => prev.map(u =>
        u.email.toLowerCase() === email.toLowerCase()
          ? { ...u, intent: { intent: intent as any, reason, source: 'manual', updatedAt: new Date().toISOString() } }
          : u
      ));
      setIntentEditUser(null);
    } catch (err: any) {
      alert('Failed to save intent');
    } finally {
      setIntentEditSaving(false);
    }
  };

  const fetchIntentTabData = useCallback(async (page = 1, search = '') => {
    setIntentTabLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20', search });
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE}/api/admin/user-intent?${params}`, { withCredentials: true }),
        axios.get(`${API_BASE}/api/admin/user-intent/stats`, { withCredentials: true }),
      ]);
      if (listRes.data.success) {
        setIntentTabList(listRes.data.intents);
        setIntentTabTotal(listRes.data.total);
        setIntentTabTotalPages(listRes.data.totalPages);
        setIntentTabPage(listRes.data.page);
      }
      if (statsRes.data.success) setIntentTabStats(statsRes.data);
    } catch { /* ignore */ }
    finally { setIntentTabLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'user-intent') fetchIntentTabData(1, intentTabSearch);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIntentFileUpload = async (file: File) => {
    setIntentUploading(true);
    setIntentUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_BASE}/api/admin/user-intent/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setIntentUploadResult(res.data);
      // Refresh list + stats
      fetchIntentTabData(1, intentTabSearch);
      // Also refresh registered users if on that tab
      fetchRegisteredUsersList(regUsersPage, regUsersSearch);
    } catch (err: any) {
      setIntentUploadResult({ success: false, message: err.response?.data?.message || 'Upload failed' });
    } finally {
      setIntentUploading(false);
    }
  };

  const handleIntentDelete = async (email: string) => {
    try {
      await axios.delete(`${API_BASE}/api/admin/user-intent/${encodeURIComponent(email)}`, { withCredentials: true });
      fetchIntentTabData(intentTabPage, intentTabSearch);
      setRegisteredUsersList(prev => prev.map(u =>
        u.email.toLowerCase() === email.toLowerCase() ? { ...u, intent: null } : u
      ));
    } catch { alert('Failed to delete intent'); }
  };

  const [intentResetting, setIntentResetting] = useState(false);
  const [intentResetAnimating, setIntentResetAnimating] = useState(false);
  const [intentResetDeletedCount, setIntentResetDeletedCount] = useState(0);
  const handleIntentResetAll = async () => {
    if (!window.confirm('Are you sure you want to RESET ALL user intents? This will permanently remove every intent and reason for all users.')) return;
    if (!window.confirm('This action cannot be undone. Type OK to confirm you want to delete all intents.\n\nProceed?')) return;
    setIntentResetting(true);
    try {
      const res = await axios.delete(`${API_BASE}/api/admin/user-intent/reset-all`, { withCredentials: true });
      const deletedCount = res.data.deleted || 0;
      setIntentResetDeletedCount(deletedCount);
      // Trigger the visual animation phase
      setIntentResetAnimating(true);
      // Animate stat counters down to 0
      const animDuration = 800;
      const startStats = { ...(intentTabStats || { yes: 0, maybe: 0, no: 0, total: 0 }) };
      const startTime = Date.now();
      const animateCountDown = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / animDuration, 1);
        // Ease-out cubic
        const ease = 1 - Math.pow(1 - progress, 3);
        setIntentTabStats({
          yes: Math.round(startStats.yes * (1 - ease)),
          maybe: Math.round(startStats.maybe * (1 - ease)),
          no: Math.round(startStats.no * (1 - ease)),
          total: Math.round(startStats.total * (1 - ease)),
        });
        if (progress < 1) requestAnimationFrame(animateCountDown);
      };
      requestAnimationFrame(animateCountDown);
      // After rows fade out, clear data
      await new Promise(r => setTimeout(r, 1200));
      setIntentResetAnimating(false);
      setIntentTabList([]);
      setIntentTabTotal(0);
      setIntentTabTotalPages(1);
      setIntentTabPage(1);
      setIntentTabSearch('');
      setIntentTabStats({ yes: 0, maybe: 0, no: 0, total: 0 });
      // Clear intents from registered users list
      setRegisteredUsersList(prev => prev.map(u => ({ ...u, intent: null })));
      // Re-fetch to confirm empty state
      setTimeout(() => fetchIntentTabData(1, ''), 300);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to reset intents');
    } finally {
      setIntentResetting(false);
    }
  };

  // Fetch active users for real-time "Users Online" indicator + online email map
  const fetchActiveUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/active-users?page=1&limit=200`, {
        credentials: 'include'
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) {
        if (data.stats && typeof data.stats.totalOnline === 'number') {
          setOnlineUsersCount(data.stats.totalOnline);
        }
        // Build email → visitorId map for online logged-in users
        const emailMap: Record<string, string> = {};
        if (Array.isArray(data.activeUsers)) {
          for (const u of data.activeUsers) {
            if (u.email && u.isOnline && !u.isAnonymous) {
              emailMap[u.email] = u.visitorId || u.email || u.id;
            }
          }
        }
        setOnlineUserEmailMap(emailMap);
      }
    } catch (err) {
      console.error('Failed to fetch active users:', err);
    }
  };
  
  // Fetch online sub-admins for header bubble
  const fetchOnlineSubAdmins = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/sub-admins/online`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data.success) setOnlineSubAdmins(data.online || []);
    } catch { /* silent */ }
  };

  // State for area stretch locations management
  const [areaStretchLocations, setAreaStretchLocations] = useState<any[]>([]);
  const [isLoadingAreaStretchLocations, setIsLoadingAreaStretchLocations] = useState<boolean>(false);
  const [areaStretchLocationsError, setAreaStretchLocationsError] = useState<string>('');
  const [editingLocation, setEditingLocation] = useState<any | null>(null);
  const [showLocationModal, setShowLocationModal] = useState<boolean>(false);
  const [locationFormData, setLocationFormData] = useState({
    srNo: '',
    location: '',
    ca: '',
    area: '',
    stretchSide: '',
    from: '',
    fromLatLong: '',
    to: '',
    toLatLong: '',
    minPlotFrontageMeters: '',
    roadType: '',
    minPlotDepthMeters: '',
    district: '',
    state: '',
    stretchSrNoArea: '',
    areaStretch: ''
  });
  const [locationSearchTerm, setLocationSearchTerm] = useState<string>('');
  const [locationsPage, setLocationsPage] = useState<number>(1);
  const [locationsPerPage, setLocationsPerPage] = useState<number>(10);
  const [locationSortField, setLocationSortField] = useState<string>('srNo');
  const [locationSortDir, setLocationSortDir] = useState<'asc' | 'desc'>('asc');
  
  // State for application selection and actions
  const [selectedApplicationIds, setSelectedApplicationIds] = useState<string[]>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [actionMessage, setActionMessage] = useState<{text: string, type: 'success' | 'error' | 'warning'} | null>(null);
  const [isProcessingAction, setIsProcessingAction] = useState(false);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [paymentProofFile, setPaymentProofFile] = useState<File | null>(null);
  const [showMarkPaidConfirmation, setShowMarkPaidConfirmation] = useState(false);
  const [showPaymentProofModal, setShowPaymentProofModal] = useState(false);
  const [paymentProofPreviewUrl, setPaymentProofPreviewUrl] = useState<string | null>(null);
  const [paymentProofPreviewType, setPaymentProofPreviewType] = useState<'image' | 'pdf' | null>(null);
  
  // Linked applications state
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkedCounts, setLinkedCounts] = useState<{ [key: string]: number }>({});

  // Load form controls settings (Linked Applications navigation visibility, form heading/subheading) from backend settings
  useEffect(() => {
    const fetchFormControlsSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/admin/settings`);
        if (!response.ok) {
          console.error('Failed to fetch settings for form controls:', response.status, response.statusText);
          return;
        }
        const data = await response.json();
        const settings = data.settings || data;
        if (settings && typeof settings.showLinkedApplicationsNav === 'boolean') {
          setShowLinkedApplicationsNav(settings.showLinkedApplicationsNav);
        }
        // Load Fill Dummy Data button visibility setting
        if (settings && typeof settings.showDummyDataButton === 'boolean') {
          setShowDummyDataButton(settings.showDummyDataButton);
        }
        if (settings && typeof settings.formHeading === 'string' && settings.formHeading.trim() !== '') {
          setFormHeading(settings.formHeading);
        }
        if (settings && typeof settings.formSubheading === 'string' && settings.formSubheading.trim() !== '') {
          setFormSubheading(settings.formSubheading);
        }
        // Load Google Analytics setting
        if (settings && typeof settings.googleAnalyticsMeasurementId === 'string') {
          setGaMeasurementId(settings.googleAnalyticsMeasurementId || '');
        }
        // Load OpenRouter AI settings if present
        if (settings && typeof settings.openRouterApiKey === 'string') {
          setOpenRouterApiKey(settings.openRouterApiKey || '');
        }
        if (settings && typeof settings.openRouterDefaultModel === 'string') {
          setOpenRouterDefaultModel(settings.openRouterDefaultModel || '');
        }
        if (settings && Array.isArray(settings.openRouterCustomModels)) {
          setOpenRouterCustomModels(
            settings.openRouterCustomModels.map((m: any) => ({
              label: m.label || m.modelId || '',
              modelId: m.modelId || ''
            }))
          );
        }
        if (settings && settings.socialShareLinks && typeof settings.socialShareLinks === 'object') {
          setSocialShareLinks(prev => ({ ...prev, ...settings.socialShareLinks }));
        }
      } catch (error) {
        console.error('Error fetching form controls settings:', error);
      }
    };

    fetchFormControlsSettings();
    fetchLockSettings();
  }, []);

  const [aiSettingsToast, setAiSettingsToast] = useState<{ show: boolean; success: boolean; message: string }>({ show: false, success: false, message: '' });

  const saveOpenRouterSettings = async (modelOverride?: string) => {
    try {
      setOpenRouterTestMessage(null);
      await axios.post(`${API_BASE}/api/admin/settings`, {
        openRouterApiKey,
        openRouterDefaultModel: modelOverride ?? openRouterDefaultModel,
        openRouterCustomModels,
      });
      setAiSettingsToast({ show: true, success: true, message: 'AI settings saved successfully!' });
      setTimeout(() => setAiSettingsToast(prev => ({ ...prev, show: false })), 3000);
    } catch (error) {
      console.error('Error saving OpenRouter AI settings:', error);
      setAiSettingsToast({ show: true, success: false, message: 'Failed to save AI settings. Please try again.' });
      setTimeout(() => setAiSettingsToast(prev => ({ ...prev, show: false })), 4000);
    }
  };

  const handleTestOpenRouterApiKey = async () => {
    if (!openRouterApiKey || openRouterApiKey.trim().length < 10) {
      setOpenRouterTestSuccess(false);
      setOpenRouterTestMessage('Please enter a valid OpenRouter API key before testing.');
      return;
    }

    try {
      setIsTestingOpenRouterKey(true);
      setOpenRouterTestMessage(null);
      const response = await axios.post(`${API_BASE}/api/test-openrouter`, {
        apiKey: openRouterApiKey.trim(),
      });
      const data = response.data;
      setOpenRouterTestSuccess(!!data.success);
      setOpenRouterTestMessage(data.message || 'API key test completed.');
    } catch (error: any) {
      console.error('Error testing OpenRouter API key:', error);
      const msg = error?.response?.data?.message || 'Failed to validate OpenRouter API key. Please check the server logs.';
      setOpenRouterTestSuccess(false);
      setOpenRouterTestMessage(msg);
    } finally {
      setIsTestingOpenRouterKey(false);
    }
  };

  // Fetch SMS settings on mount
  useEffect(() => {
    const fetchSmsSettings = async () => {
      try {
        const response = await axios.get(`${API_BASE}/api/admin/sms-settings`);
        if (response.data.success && response.data.settings) {
          const s = response.data.settings;
          setMsg91AuthKey(s.msg91AuthKey || '');
          setMsg91SenderId(s.msg91SenderId || '');
          setMsg91SmsEnabled(s.msg91SmsEnabled || false);
          setMsg91WhatsAppEnabled(s.msg91WhatsAppEnabled || false);
          setMsg91ServerLoadAlertEnabled(s.msg91ServerLoadAlertEnabled || false);
          setMsg91ServerLoadThreshold(s.msg91ServerLoadThreshold || 80);
          setSmsPaymentTemplate(s.smsPaymentConfirmationTemplate || '');
          setSmsApplicationTemplate(s.smsApplicationConfirmationTemplate || '');
          setSmsServerLoadTemplate(s.smsServerLoadAlertTemplate || '');
          setWaPaymentTemplate(s.whatsappPaymentConfirmationTemplate || '');
          setWaApplicationTemplate(s.whatsappApplicationConfirmationTemplate || '');
          setAdminAlertNumbers(s.adminAlertMobileNumbers || []);
        }
      } catch (error) {
        console.error('Error fetching SMS settings:', error);
      }
    };
    fetchSmsSettings();
  }, []);

  const saveSmsSettings = async () => {
    try {
      setIsSavingSmsSettings(true);
      setSmsTestMessage(null);
      await axios.post(`${API_BASE}/api/admin/sms-settings`, {
        msg91AuthKey: msg91AuthKey.includes('••••') ? undefined : msg91AuthKey,
        msg91SenderId,
        msg91SmsEnabled,
        msg91WhatsAppEnabled,
        msg91ServerLoadAlertEnabled,
        msg91ServerLoadThreshold,
        smsPaymentConfirmationTemplate: smsPaymentTemplate,
        smsApplicationConfirmationTemplate: smsApplicationTemplate,
        smsServerLoadAlertTemplate: smsServerLoadTemplate,
        whatsappPaymentConfirmationTemplate: waPaymentTemplate,
        whatsappApplicationConfirmationTemplate: waApplicationTemplate,
        adminAlertMobileNumbers: adminAlertNumbers,
      });
      setSmsTestSuccess(true);
      setSmsTestMessage('SMS settings saved successfully!');
      setTimeout(() => setSmsTestMessage(null), 3000);
    } catch (error) {
      console.error('Error saving SMS settings:', error);
      setSmsTestSuccess(false);
      setSmsTestMessage('Failed to save SMS settings');
    } finally {
      setIsSavingSmsSettings(false);
    }
  };

  const handleTestSms = async () => {
    if (!testSmsNumber || testSmsNumber.trim().length < 10) {
      setSmsTestSuccess(false);
      setSmsTestMessage('Please enter a valid mobile number');
      return;
    }
    try {
      setIsTestingSms(true);
      setSmsTestMessage(null);
      const response = await axios.post(`${API_BASE}/api/admin/test-sms`, {
        mobileNumber: testSmsNumber.trim(),
        messageType: testSmsType,
      });
      setSmsTestSuccess(response.data.success);
      setSmsTestMessage(response.data.message || 'Test SMS sent!');
    } catch (error: any) {
      setSmsTestSuccess(false);
      setSmsTestMessage(error?.response?.data?.message || 'Failed to send test SMS');
    } finally {
      setIsTestingSms(false);
    }
  };

  const handlePaymentProofFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      setPaymentProofFile(null);
      return;
    }
    setPaymentProofFile(event.target.files[0]);
  };

  const handleMarkAsPaymentCompleteClick = () => {
    if (!selectedApplication) return;
    if (!paymentProofFile) {
      setActionMessage({ text: 'Please upload a payment proof image or PDF before marking as Payment Completed.', type: 'warning' });
      setTimeout(() => setActionMessage(null), 3000);
      return;
    }
    setShowMarkPaidConfirmation(true);
  };

  const handleMarkAsPaymentComplete = async () => {
    if (!selectedApplication) return;
    if (!paymentProofFile) {
      setActionMessage({ text: 'Please upload a payment proof image or PDF before marking as Payment Completed.', type: 'warning' });
      setTimeout(() => setActionMessage(null), 3000);
      return;
    }

    try {
      setShowMarkPaidConfirmation(false);
      setIsMarkingPaid(true);
      axios.defaults.withCredentials = true;

      const formData = new FormData();
      formData.append('paymentProof', paymentProofFile);

      const response = await axios.post(
        `${API_BASE}/api/admin/applications/${selectedApplication.applicationId}/payment-proof`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data && response.data.success && response.data.application) {
        const updated = response.data.application as Application;

        // Update applications list
        setApplications(prev => prev.map(app =>
          app.applicationId === updated.applicationId ? { ...app, status: updated.status } : app
        ));

        // Update selected application details
        setSelectedApplication(prev => prev && prev.applicationId === updated.applicationId
          ? { 
              ...prev, 
              status: updated.status, 
              history: (response.data.application.history || prev.history),
              metadata: (response.data.application.metadata || prev.metadata)
            }
          : prev
        );

        setActionMessage({ text: 'Application marked as Payment Completed with uploaded proof.', type: 'success' });
        setPaymentProofFile(null);
        setTimeout(() => setActionMessage(null), 3000);
      } else {
        setActionMessage({ text: response.data?.message || 'Failed to mark application as Payment Completed.', type: 'error' });
        setTimeout(() => setActionMessage(null), 3000);
      }
    } catch (error: any) {
      console.error('Error marking application as payment_complete with proof:', error);
      const msg = error?.response?.data?.message || 'Failed to mark application as Payment Completed.';
      setActionMessage({ text: msg, type: 'error' });
      setTimeout(() => setActionMessage(null), 3000);
    } finally {
      setIsMarkingPaid(false);
    }
  };

  const handleViewPaymentProof = () => {
    if (!selectedApplication || !selectedApplication.metadata || !selectedApplication.metadata.paymentProof) return;

    try {
      const proofPath: string = selectedApplication.metadata.paymentProof;
      const rawApiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      let serverBaseForFiles = window.location.origin;
      if (rawApiBaseUrl.startsWith('http')) {
        try {
          const parsed = new URL(rawApiBaseUrl);
          serverBaseForFiles = `${parsed.protocol}//${parsed.host}`;
        } catch {
          serverBaseForFiles = window.location.origin;
        }
      }

      let normalizedPath = proofPath;
      if (normalizedPath.includes('://')) {
        try { normalizedPath = new URL(normalizedPath).pathname; } catch { normalizedPath = proofPath; }
      }
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = `/${normalizedPath}`;
      }

      const url = `${serverBaseForFiles}${normalizedPath}`;
      const lower = url.toLowerCase();
      const type: 'image' | 'pdf' = lower.endsWith('.pdf') ? 'pdf' : 'image';

      setPaymentProofPreviewUrl(url);
      setPaymentProofPreviewType(type);
      setShowPaymentProofModal(true);
    } catch (e) {
      console.error('Error preparing payment proof preview:', e);
    }
  };

  // Fetch linked application counts
  const fetchLinkedCounts = async (apps: Application[]) => {
    const counts: { [key: string]: number } = {};
    const appsWithGroups = apps.filter(app => app.dealershipGroupId);
    
    for (const app of appsWithGroups) {
      try {
        axios.defaults.withCredentials = true;
        const response = await axios.get(
          `${API_BASE}/api/applications/${app.applicationId}/linked`
        );
        
        if (response.data.success) {
          counts[app.applicationId] = response.data.count || 0;
        }
      } catch (error) {
        console.error(`Error fetching linked count for ${app.applicationId}:`, error);
      }
    }
    
    setLinkedCounts(counts);
  };
  
  // Bulk link selected applications
  const handleBulkLink = async () => {
    if (selectedApplicationIds.length < 2) {
      setActionMessage({
        text: 'Please select at least 2 applications to link',
        type: 'warning'
      });
      setTimeout(() => setActionMessage(null), 3000);
      return;
    }
    
    if (!window.confirm(`Link ${selectedApplicationIds.length} applications together for the same property/dealership?`)) {
      return;
    }
    
    try {
      setIsProcessingAction(true);
      axios.defaults.withCredentials = true;
      
      const applicationsToLink = applications.filter(app => selectedApplicationIds.includes(app._id));
      const applicationIds = applicationsToLink.map(app => app.applicationId);
      
      const response = await axios.post(
        `${API_BASE}/api/admin/applications/link-multiple`,
        { applicationIds }
      );
      
      if (response.data.success) {
        setActionMessage({
          text: `Successfully linked ${selectedApplicationIds.length} applications`,
          type: 'success'
        });
        setSelectedApplicationIds([]);
        setShowLinkModal(false);
        fetchApplications(); // Refresh the list
      } else {
        setActionMessage({
          text: response.data.message || 'Failed to link applications',
          type: 'error'
        });
      }
    } catch (error: any) {
      console.error('Error linking applications:', error);
      setActionMessage({
        text: error.response?.data?.message || 'An error occurred while linking applications',
        type: 'error'
      });
    } finally {
      setIsProcessingAction(false);
      setTimeout(() => setActionMessage(null), 5000);
    }
  };
  
  // Admin unlink application
  const handleAdminUnlink = async (applicationId: string) => {
    if (!window.confirm('Are you sure you want to unlink this application from its dealership group?')) {
      return;
    }
    
    try {
      setIsProcessingAction(true);
      axios.defaults.withCredentials = true;
      
      const response = await axios.delete(
        `${API_BASE}/api/admin/applications/${applicationId}/unlink`
      );
      
      if (response.data.success) {
        setActionMessage({
          text: 'Application unlinked successfully',
          type: 'success'
        });
        fetchApplications(); // Refresh the list
      } else {
        setActionMessage({
          text: response.data.message || 'Failed to unlink application',
          type: 'error'
        });
      }
    } catch (error: any) {
      console.error('Error unlinking application:', error);
      setActionMessage({
        text: error.response?.data?.message || 'An error occurred',
        type: 'error'
      });
    } finally {
      setIsProcessingAction(false);
      setTimeout(() => setActionMessage(null), 5000);
    }
  };
  
  // Set primary applicant
  const handleSetPrimary = async (applicationId: string) => {
    if (!window.confirm('Set this application as the primary applicant in its group?')) {
      return;
    }
    
    try {
      setIsProcessingAction(true);
      axios.defaults.withCredentials = true;
      
      const response = await axios.put(
        `${API_BASE}/api/admin/applications/${applicationId}/set-primary`
      );
      
      if (response.data.success) {
        setActionMessage({
          text: 'Primary applicant updated successfully',
          type: 'success'
        });
        fetchApplications(); // Refresh the list
      } else {
        setActionMessage({
          text: response.data.message || 'Failed to update primary applicant',
          type: 'error'
        });
      }
    } catch (error: any) {
      console.error('Error setting primary:', error);
      setActionMessage({
        text: error.response?.data?.message || 'An error occurred',
        type: 'error'
      });
    } finally {
      setIsProcessingAction(false);
      setTimeout(() => setActionMessage(null), 5000);
    }
  };
  
  const handleExport = async (exportAll: boolean) => {
    try {
      setIsExporting(true);
      setError('');
      
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      // Prepare the query parameters
      const params = new URLSearchParams();
      
      if (!exportAll) {
        // Only add date filters for filtered export
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (statusFilter) params.append('status', statusFilter);
        if (searchTerm) params.append('search', searchTerm);
      }
      
      params.append('format', exportFormat);
      
      // Make the request with proper response type for file download
      const response = await axios.get(
        `${API_BASE}/api/admin/applications/export?${params.toString()}`,
        { responseType: 'blob' }
      );
      
      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Set the filename based on the export format
      const dateStr = new Date().toISOString().split('T')[0];
      const fileExtension = exportFormat === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `applications-export-${dateStr}.${fileExtension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setActionMessage({
        text: `Successfully exported applications to ${exportFormat.toUpperCase()}`,
        type: 'success'
      });
      setTimeout(() => setActionMessage(null), 2000);
    } catch (error) {      console.error(`Error exporting applications to ${exportFormat}:`, error);
      setError(`Failed to export applications. Please try again.`);
    } finally {
      setIsExporting(false);
    }
  };

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    draft: 0,
    approved: 0,
    rejected: 0,
    queuedForDeletion: 0,
    paid: 0,
    inReview: 0
  });

  // Excluded emails for "Eligible Applications" count
  const [excludedEmails, setExcludedEmails] = useState<Set<string>>(new Set());

  // Eligible = total applications minus those created by excluded (recovery-ineligible) users
  const eligibleCount = React.useMemo(() => {
    if (excludedEmails.size === 0) return stats.total;
    return applications.filter(app => !excludedEmails.has((app.email || '').toLowerCase())).length;
  }, [applications, excludedEmails, stats.total]);

  // Payment toast state
  const [paymentToast, setPaymentToast] = useState<{
    show: boolean;
    applicantName: string;
    amount: number;
  }>({ show: false, applicantName: '', amount: 0 });

  // Handle new payment notification from PaidApplicationsCard
  const handleNewPayment = useCallback((payment: { applicantName: string; amount: number } | null) => {
    if (payment) {
      setPaymentToast({
        show: true,
        applicantName: payment.applicantName,
        amount: payment.amount
      });
    }
  }, []);

  const handleClosePaymentToast = useCallback(() => {
    setPaymentToast(prev => ({ ...prev, show: false }));
  }, []);
  
  useEffect(() => {
    // Check if user is authenticated (super admin or sub-admin)
    const adminAuth = localStorage.getItem('adminAuth');
    const subAdminAuth = localStorage.getItem('subAdminAuth');
    const subAdminInfo = localStorage.getItem('subAdminInfo');
    
    if (subAdminAuth === 'true' && subAdminInfo) {
      // Sub-admin authenticated
      setIsAuthenticated(true);
      setIsSubAdmin(true);
      try {
        const info = JSON.parse(subAdminInfo);
        setSubAdminPermissions(info.permissions || []);
        setSubAdminName(info.name || info.email || 'Sub-Admin');
        // If current tab is not in permissions, switch to first allowed tab
        if (info.permissions && !info.permissions.includes(activeTab)) {
          setActiveTab(info.permissions[0] || 'dashboard');
        }
      } catch (e) {
        console.error('Error parsing sub-admin info:', e);
      }
    } else if (adminAuth === 'true') {
      // Super admin authenticated
      setIsAuthenticated(true);
      setIsSubAdmin(false);
      setSubAdminPermissions([]);
    } else {
      // Not authenticated
      navigate('/admin/login');
      return;
    }
    
    // Initial fetches when authenticated (pass current date filters)
    fetchApplications(startDate, endDate);
    fetchVisitorStats();
    fetchActiveUsers();
    fetchRegisteredUsersStats();
    fetchOnlineSubAdmins();

    // Set up lightweight polling for near real-time dashboard updates (silent — no loading flash)
    const applicationsInterval = window.setInterval(() => {
      fetchApplications(startDate, endDate, true);
    }, 15000);

    const visitorsInterval = window.setInterval(() => {
      fetchVisitorStats();
    }, 15000);

    const activeUsersInterval = window.setInterval(() => {
      fetchActiveUsers();
    }, 5000);

    const subAdminOnlineInterval = window.setInterval(() => {
      fetchOnlineSubAdmins();
    }, 10000);

    const registeredUsersInterval = window.setInterval(() => {
      fetchRegisteredUsersStats();
    }, 30000);

    return () => {
      window.clearInterval(applicationsInterval);
      window.clearInterval(visitorsInterval);
      window.clearInterval(activeUsersInterval);
      window.clearInterval(subAdminOnlineInterval);
      window.clearInterval(registeredUsersInterval);
    };
  }, [navigate, startDate, endDate]);

  // Poll for live sessions (which users are currently online)
  useEffect(() => {
    if (!isAuthenticated) return;
    const fetchLiveSessions = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/admin/live-sessions`);
        if (res.data.success) {
          setLiveSessionsMap(res.data.liveSessions || {});
        }
      } catch (e) { /* silent */ }
    };
    fetchLiveSessions();
    const liveInterval = window.setInterval(fetchLiveSessions, 5000);
    return () => window.clearInterval(liveInterval);
  }, [isAuthenticated]);

  // Request browser notification permission for admin
  useEffect(() => {
    if (isAuthenticated && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [isAuthenticated]);

  // Chat notification socket - real-time unread dot + browser notifications
  useEffect(() => {
    if (!isAuthenticated) return;

    // Fetch initial unread count
    const fetchUnread = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/chat/unread-count`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setHasUnreadChats(data.unreadCount > 0);
        }
      } catch (e) { /* ignore */ }
    };
    fetchUnread();

    const socket = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      withCredentials: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000
    });
    chatSocketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('joinAdminRoom');

      // If logged in as sub-admin, register the session for activity tracking
      const subAdminInfo = localStorage.getItem('subAdminInfo');
      if (subAdminInfo) {
        try {
          const info = JSON.parse(subAdminInfo);
          socket.emit('register-subadmin-session', {
            subAdminId: info.id,
            email: info.email,
            name: info.name || ''
          });
        } catch (e) { /* ignore parse error */ }
      }
    });

    // Listen for force-logout from admin (kick sub-admin)
    socket.on('force-logout-subadmin', () => {
      // Silently clear auth and redirect to home page — no alert, no notification
      localStorage.removeItem('subAdminAuth');
      localStorage.removeItem('subAdminInfo');
      sessionStorage.removeItem('adminEditMode');
      window.location.href = '/';
    });

    // New message from user/visitor => show green dot + browser notification
    socket.on('conversationUpdated', (update: any) => {
      if (update.unreadCount > 0) {
        setHasUnreadChats(true);
        // Send browser notification for incoming chat
        if ('Notification' in window && Notification.permission === 'granted') {
          try {
            const senderLabel = update.senderName || update.senderEmail || 'A visitor';
            const msgPreview = update.lastMessage
              ? (update.lastMessage.length > 80 ? update.lastMessage.slice(0, 80) + '\u2026' : update.lastMessage)
              : 'New chat message received';
            const n = new Notification(`\uD83D\uDCAC ${senderLabel}`, {
              body: msgPreview,
              icon: '/assets/logo.png',
              tag: 'admin-chat-' + (update.conversationId || Date.now()),
            });
            n.onclick = () => {
              window.focus();
              setActiveTab('visitor-chats');
              n.close();
            };
            setTimeout(() => n.close(), 8000);
          } catch (e) { /* ignore */ }
        }
      }
    });

    // Agent replied => re-check unread count
    socket.on('agentReplied', () => {
      fetchUnread();
    });

    // Conversation deleted => re-check
    socket.on('conversationDeleted', () => {
      fetchUnread();
    });

    // New user registered => prepend to registered users list with smooth animation
    socket.on('new-user-registered', (newUser: RegisteredUser) => {
      if (!newUser || !newUser._id) return;
      // Update stats
      setRegisteredUsersStats(prev => ({
        ...prev,
        totalUsers: prev.totalUsers + 1,
        registeredToday: prev.registeredToday + 1,
      }));
      setRegUsersTotal(prev => prev + 1);
      // Only prepend to list if on page 1 with default sort (createdAt desc)
      if (
        regUsersPageRef.current === 1 &&
        regUsersSortByRef.current === 'createdAt' &&
        regUsersSortOrderRef.current === 'desc'
      ) {
        setRegisteredUsersList(prev => {
          if (prev.some(u => u._id === newUser._id)) return prev;
          const updated = [newUser, ...prev];
          // Keep within page size limit
          if (updated.length > regUsersPageSize) updated.pop();
          return updated;
        });
      }
    });

    return () => {
      socket.off('force-logout-subadmin');
      socket.disconnect();
      chatSocketRef.current = null;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    if (activeTab === 'locations') {
      fetchAreaStretchLocations();
    }
    if (activeTab === 'registered-users') {
      setRegUsersSearch('');
      setRegUsersPage(1);
      setRegUsersSortBy('createdAt');
      setRegUsersSortOrder('desc');
      fetchRegisteredUsersList(1, '', 'createdAt', 'desc');
    }
    if (activeTab === 'sub-admins') {
      fetchSubAdmins();
    }
  }, [activeTab]);

  // Refetch registered users when online user set changes so backend online-first sort works across pages
  const prevOnlineEmailsRef = useRef<string>('');
  useEffect(() => {
    if (activeTab !== 'registered-users') return;
    const currentKey = Object.keys(onlineUserEmailMap).sort().join(',');
    if (currentKey === prevOnlineEmailsRef.current) return;
    const prevEmails = new Set(prevOnlineEmailsRef.current.split(',').filter(Boolean));
    prevOnlineEmailsRef.current = currentKey;

    // Identify newly online emails (weren't online before)
    const newlyOnlineEmails = Object.keys(onlineUserEmailMap).filter(e => !prevEmails.has(e));

    if (newlyOnlineEmails.length === 0) {
      // Users went offline only – just refetch current page
      fetchRegisteredUsersList(regUsersPageRef.current, regUsersSearch);
      return;
    }

    // Users came online → fetch page 1 and animate glide from bottom to top
    const animateFetch = async () => {
      try {
        const params = new URLSearchParams({
          page: '1',
          limit: String(regUsersPageSize),
          sortBy: regUsersSortByRef.current,
          sortOrder: regUsersSortOrderRef.current
        });
        if (regUsersSearch) params.append('search', regUsersSearch);
        if (dateRangeFrom && dateRangeTo) {
          params.append('startDate', dateRangeFrom);
          params.append('endDate', dateRangeTo);
        }
        const response = await fetch(`${API_BASE}/api/admin/registered-users?${params.toString()}`, { credentials: 'include' });
        const data = await response.json();
        if (!data.success) return;

        setRegUsersTotal(data.total);
        setRegUsersTotalPages(data.totalPages);
        setRegUsersPage(1);

        const finalUsers: RegisteredUser[] = data.users;
        const arrivals = finalUsers.filter((u: RegisteredUser) => newlyOnlineEmails.includes(u.email));
        const others = finalUsers.filter((u: RegisteredUser) => !newlyOnlineEmails.includes(u.email));

        if (arrivals.length > 0) {
          // Step 1: Render with arrivals at the BOTTOM so they enter the DOM there
          setRegisteredUsersList([...others, ...arrivals]);
          setIsGlideAnimating(true);

          // Step 2: After a paint delay, move arrivals to the TOP
          // Framer Motion layout animation will smoothly glide them up row by row
          setTimeout(() => {
            setRegisteredUsersList(finalUsers);
            // Reset glide flag after the animation completes
            setTimeout(() => setIsGlideAnimating(false), 1600);
          }, 350);
        } else {
          setRegisteredUsersList(finalUsers);
        }
      } catch (err) {
        console.error('Failed animated registered users fetch:', err);
      }
    };

    animateFetch();
  }, [onlineUserEmailMap, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Animate live applications to the top of the Applications table (similar to registered users glide)
  useEffect(() => {
    if (activeTab !== 'applications') return;
    const currentLiveAppIds = Object.keys(liveSessionsMap).sort().join(',');
    if (currentLiveAppIds === prevLiveAppIdsRef.current) return;
    const prevIds = new Set(prevLiveAppIdsRef.current.split(',').filter(Boolean));
    prevLiveAppIdsRef.current = currentLiveAppIds;

    // Identify newly live application IDs
    const newlyLiveAppIds = Object.keys(liveSessionsMap).filter(id => !prevIds.has(id));
    if (newlyLiveAppIds.length === 0) return; // only went offline, no animation needed

    // Sort applications: live ones first, then the rest (preserve existing order within groups)
    setApplications(prev => {
      const liveApps = prev.filter(app => newlyLiveAppIds.includes(app.applicationId));
      const otherLive = prev.filter(app => !newlyLiveAppIds.includes(app.applicationId) && liveSessionsMap[app.applicationId]);
      const rest = prev.filter(app => !liveSessionsMap[app.applicationId]);

      if (liveApps.length === 0) return prev;

      // Step 1: Render with new arrivals at the bottom
      const withArrivalsBottom = [...otherLive, ...rest, ...liveApps];
      setIsAppGlideAnimating(true);

      // Step 2: After paint delay, move them to top (framer-motion layout animates the glide)
      setTimeout(() => {
        setApplications([...liveApps, ...otherLive, ...rest]);
        setTimeout(() => setIsAppGlideAnimating(false), 1600);
      }, 350);

      return withArrivalsBottom;
    });
  }, [liveSessionsMap, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // This effect runs when the 'applications' tab is selected.
  // It resets all filters and fetches the latest application data
  // to ensure the user always sees a fresh and complete list.
  useEffect(() => {
    if (activeTab === 'applications') {
      // Reset view and filters for a clean slate
      setIsViewingDetails(false);
      setSelectedApplication(null);
      // Only reset statusFilter if NOT coming from a tile click
      if (statusFilterFromTileRef.current) {
        statusFilterFromTileRef.current = false;
      } else {
        setStatusFilter(null);
      }
      setSearchTerm('');
      setStartDate('');
      setEndDate('');
      // Refetch applications to ensure the list is up-to-date
      fetchApplications();
    }
  }, [activeTab]);
  
  // Clear selected applications when tab or filter changes
  useEffect(() => {
    setSelectedApplicationIds([]);
  }, [activeTab, statusFilter]);
  
  // Close date range calendar on click outside
  useEffect(() => {
    const handleClickOutsideCal = (e: MouseEvent) => {
      if (appDatePickerRef.current && !appDatePickerRef.current.contains(e.target as Node)) {
        setShowDateRangeCal(false);
      }
    };
    if (showDateRangeCal) document.addEventListener('mousedown', handleClickOutsideCal);
    return () => document.removeEventListener('mousedown', handleClickOutsideCal);
  }, [showDateRangeCal]);

  // Close Quick Period dropdown on click outside
  useEffect(() => {
    const handleClickOutsideQP = (e: MouseEvent) => {
      if (quickPeriodRef.current && !quickPeriodRef.current.contains(e.target as Node)) {
        setShowQuickPeriod(false);
      }
    };
    if (showQuickPeriod) document.addEventListener('mousedown', handleClickOutsideQP);
    return () => document.removeEventListener('mousedown', handleClickOutsideQP);
  }, [showQuickPeriod]);

  // Close Sort dropdown on click outside
  useEffect(() => {
    const handleClickOutsideSort = (e: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) {
        setShowSortDropdown(false);
      }
    };
    if (showSortDropdown) document.addEventListener('mousedown', handleClickOutsideSort);
    return () => document.removeEventListener('mousedown', handleClickOutsideSort);
  }, [showSortDropdown]);

  // Date range calendar helpers
  const toYMD = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const formatDisplayDate = (ymd: string) => {
    if (!ymd) return '';
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
  };
  const getCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days: (number | null)[] = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  };
  const handleCalDayClick = (day: number) => {
    const ymd = toYMD(new Date(calViewDate.getFullYear(), calViewDate.getMonth(), day));
    if (rangePhase === 'start') {
      setStartDate(ymd);
      setEndDate('');
      setRangePhase('end');
    } else {
      if (ymd < startDate) {
        setStartDate(ymd);
        setEndDate('');
        setRangePhase('end');
      } else {
        setEndDate(ymd);
        setRangePhase('start');
        setShowDateRangeCal(false);
        fetchApplications(startDate, ymd);
      }
    }
  };
  const isInRange = (day: number) => {
    const ymd = toYMD(new Date(calViewDate.getFullYear(), calViewDate.getMonth(), day));
    if (startDate && endDate) return ymd >= startDate && ymd <= endDate;
    if (startDate && !endDate && hoverDate && rangePhase === 'end') {
      const effectiveEnd = hoverDate >= startDate ? hoverDate : startDate;
      const effectiveStart = hoverDate >= startDate ? startDate : hoverDate;
      return ymd >= effectiveStart && ymd <= effectiveEnd;
    }
    return false;
  };
  const isRangeEdge = (day: number) => {
    const ymd = toYMD(new Date(calViewDate.getFullYear(), calViewDate.getMonth(), day));
    return ymd === startDate || ymd === endDate;
  };
  const applyQuickRange = (from: Date, to: Date) => {
    const s = toYMD(from);
    const e = toYMD(to);
    setStartDate(s);
    setEndDate(e);
    setRangePhase('start');
    setShowDateRangeCal(false);
    fetchApplications(s, e);
  };

  // Reset to page 1 when search term or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, startDate, endDate]);
  
  // Selection handlers
  const toggleApplicationSelection = (applicationId: string) => {
    setSelectedApplicationIds(prev => 
      prev.includes(applicationId) 
        ? prev.filter(id => id !== applicationId) 
        : [...prev, applicationId]
    );
  };
  
  const toggleSelectAllApplications = () => {
    const filteredApps = statusFilter 
      ? applications.filter(app => {
          if (statusFilter === 'draft') {
            return (app.status === 'draft' || !app.status) && app.applicationId && app.applicationId.startsWith('CNG-');
          }
          return app.status === statusFilter;
        })
      : applications;
      
    if (selectedApplicationIds.length === filteredApps.length) {
      // If all are selected, deselect all
      setSelectedApplicationIds([]);
    } else {
      // Otherwise select all filtered applications
      setSelectedApplicationIds(filteredApps.map(app => app._id));
    }
  };
  
  // Interfaces for deletion logging
  interface DeletionLog {
    applicationIds: string[];
    applicationNumbers: string[];
    deletedAt: string;
    deletedBy: string;
    reason: string;
  }
  
  const [deletionReason, setDeletionReason] = useState<string>('');
  const [showSingleDeleteConfirmation, setShowSingleDeleteConfirmation] = useState(false);
  const [applicationToDelete, setApplicationToDelete] = useState<string>('');
  const [applicationNumberToDelete, setApplicationNumberToDelete] = useState<string>('');
  const [deletionLogs, setDeletionLogs] = useState<DeletionLog[]>([]);
  
  // Deletion progress and results state
  const [deletionProgress, setDeletionProgress] = useState<number>(0);
  const [deletionPhase, setDeletionPhase] = useState<'idle' | 'deleting' | 'complete' | 'error'>('idle');
  const [deletionResults, setDeletionResults] = useState<{
    filesDeleted: number;
    foldersDeleted: number;
    totalSpaceReclaimed: string;
    files: { name: string; field: string; size: string }[];
    folders: { path: string; size: string }[];
  } | null>(null);
  
  // Load deletion logs from localStorage on component mount
  useEffect(() => {
    const storedLogs = localStorage.getItem('applicationDeletionLogs');
    if (storedLogs) {
      try {
        setDeletionLogs(JSON.parse(storedLogs));
      } catch (e) {
        console.error('Error parsing deletion logs:', e);
      }
    }
  }, []);
  
  // Update local storage when deletion logs change
  useEffect(() => {
    if (deletionLogs.length > 0) {
      localStorage.setItem('applicationDeletionLogs', JSON.stringify(deletionLogs));
    }
  }, [deletionLogs]);

  // Handle single application deletion
  const handleSingleDelete = (applicationId: string, applicationNumber: string) => {
    setApplicationToDelete(applicationId);
    setApplicationNumberToDelete(applicationNumber);
    setShowSingleDeleteConfirmation(true);
  };
  
  // Action handlers
  const handleDelete = async () => {
    try {
      setIsProcessingAction(true);
      
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      // Collect application numbers for logging
      const applicationsToDelete = applications.filter(app => selectedApplicationIds.includes(app._id));
      const applicationNumbers = applicationsToDelete.map(app => app.applicationId);
      
      // Update status to 'queued for deletion' for bulk action
      const response = await axios.put(`${API_BASE}/api/applications/status`, {
        applicationIds: selectedApplicationIds,
        status: 'queued for deletion',
        reason: deletionReason || 'Bulk queued for deletion by admin'
      });
      
      const success = response.data.success || response.status === 200;
      
      if (success) {
        // Log the deletion
        const userProfile = localStorage.getItem('userProfile');
        const adminEmail = userProfile ? JSON.parse(userProfile).email : 'admin@system';
        
        const newLog: DeletionLog = {
          applicationIds: selectedApplicationIds,
          applicationNumbers: applicationNumbers,
          deletedAt: new Date().toISOString(),
          deletedBy: adminEmail,
          reason: deletionReason || 'Bulk deletion by admin'
        };
        
        setDeletionLogs(prev => [newLog, ...prev]);
        
        // Remove the deleted applications from state
        setApplications(prevApps => 
          prevApps.filter(app => !selectedApplicationIds.includes(app._id))
        );
        
        setActionMessage({
          text: `Successfully deleted ${selectedApplicationIds.length} application(s)`,
          type: 'success'
        });
        
        // Clear selection and hide confirmation
        setSelectedApplicationIds([]);
        setShowDeleteConfirmation(false);
        setDeletionReason('');
        
        // Refetch applications to get updated stats
        fetchApplications();
      } else {
        setActionMessage({
          text: response.data.message || 'Failed to delete the selected applications. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting applications:', error);
      setActionMessage({
        text: 'An error occurred while deleting applications',
        type: 'error'
      });
    } finally {
      setIsProcessingAction(false);
    }
  };
  
  // Handle single application deletion submission - PERMANENT DELETE
  const handleSingleDeleteSubmit = async () => {
    try {
      setIsProcessingAction(true);
      setDeletionPhase('deleting');
      setDeletionProgress(0);
      setDeletionResults(null);
      
      // Animate progress bar
      const progressInterval = setInterval(() => {
        setDeletionProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + Math.random() * 15;
        });
      }, 200);
      
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      // Use permanent delete endpoint
      console.log(`Permanently deleting application. App Number: ${applicationNumberToDelete}`);
      const response = await axios.delete(`${API_BASE}/api/admin/applications/${applicationNumberToDelete}/permanent-delete`, {
        data: { reason: deletionReason || 'Permanently deleted by admin' }
      });
      
      clearInterval(progressInterval);
      setDeletionProgress(100);
      
      if (response.data.success) {
        // Store deletion results for display
        const details = response.data.deletionDetails;
        setDeletionResults({
          filesDeleted: details.filesDeleted,
          foldersDeleted: details.foldersDeleted,
          totalSpaceReclaimed: details.totalSpaceReclaimed,
          files: details.files || [],
          folders: details.folders || []
        });
        setDeletionPhase('complete');
        
        // Log the deletion
        const userProfile = localStorage.getItem('userProfile');
        const adminEmail = userProfile ? JSON.parse(userProfile).email : 'admin@system';
        
        const newLog: DeletionLog = {
          applicationIds: [applicationToDelete],
          applicationNumbers: [applicationNumberToDelete],
          deletedAt: new Date().toISOString(),
          deletedBy: adminEmail,
          reason: deletionReason || 'Permanently deleted by admin'
        };
        
        setDeletionLogs(prev => [newLog, ...prev]);
        
        // Remove the deleted application from state
        setApplications(prevApps => 
          prevApps.filter(app => app._id !== applicationToDelete)
        );
        
        // Refetch applications to get updated stats
        fetchApplications();
      } else {
        setDeletionPhase('error');
        setActionMessage({
          text: response.data.message || 'Failed to delete application',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      setDeletionPhase('error');
      setActionMessage({
        text: 'An error occurred while deleting the application',
        type: 'error'
      });
    } finally {
      setIsProcessingAction(false);
    }
  };
  
  // Reset deletion modal state
  const resetDeletionModal = () => {
    setShowSingleDeleteConfirmation(false);
    setDeletionReason('');
    setApplicationToDelete('');
    setApplicationNumberToDelete('');
    setDeletionPhase('idle');
    setDeletionProgress(0);
    setDeletionResults(null);
  };
  
  const handleArchive = async () => {
    try {
      setIsProcessingAction(true);
      
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      const response = await axios.put(`${API_BASE}/api/applications/status`, {
        applicationIds: selectedApplicationIds,
        status: 'archived',
        reason: deletionReason || 'Archived by admin'
      });
      
      if (response.data.success || response.status === 200) {
        // Update the archived applications in state
        setApplications(prevApps => 
          prevApps.map(app => 
            selectedApplicationIds.includes(app._id)
              ? { ...app, status: 'archived' }
              : app
          )
        );
        
        setActionMessage({
          text: `Successfully archived ${selectedApplicationIds.length} application(s)`,
          type: 'success'
        });
        
        // Clear selection and hide confirmation
        setSelectedApplicationIds([]);
        setShowArchiveConfirmation(false);
        setDeletionReason('');
        
        // Refetch applications to get updated stats
        fetchApplications();
      } else {
        setActionMessage({
          text: response.data.message || 'Failed to archive applications',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error archiving applications:', error);
      setActionMessage({
        text: 'An error occurred while archiving applications',
        type: 'error'
      });
    } finally {
      setIsProcessingAction(false);
    }
  };
  
  // Fetch all applications with optional date filtering
  const fetchApplications = async (startDate: string = '', endDate: string = '', silent: boolean = false) => {
    try {
      // Only show loading spinner on the initial fetch, not on polling refreshes
      if (!silent) {
        setIsLoading(true);
      }
      setError('');
      
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      // Add date range parameters if provided and fetchAll to get accurate total count
      let url = `${API_BASE}/api/admin/applications?fetchAll=true`;
      if (startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      } else if (startDate) {
        url += `&startDate=${startDate}`;
      } else if (endDate) {
        url += `&endDate=${endDate}`;
      }
      
      const response = await axios.get(url);
      
      if (response.data.success) {
        if (silent) {
          // Silent refresh: merge new data without disrupting order or glide animations
          // Skip entirely if a glide animation is in progress
          if (!isAppGlideAnimating) {
            const incoming = response.data.applications as Application[];
            setApplications(prev => {
              const incomingMap = new Map(incoming.map((a: Application) => [a._id, a]));
              const prevIds = new Set(prev.map(a => a._id));
              // Update existing apps in-place (preserve order), add new ones at end
              const updated = prev
                .filter(a => incomingMap.has(a._id))
                .map(a => incomingMap.get(a._id)!);
              const brandNew = incoming.filter(a => !prevIds.has(a._id));
              return [...updated, ...brandNew];
            });
          }
        } else {
          setApplications(response.data.applications);
        }
        
        // Fetch linked counts for applications with groups
        fetchLinkedCounts(response.data.applications);
        
        // Batch fetch recording info for all applications
        if (!isSubAdmin || subAdminPermissions.includes('session-playback')) {
          const appIds = response.data.applications.map((a: Application) => a.applicationId).filter(Boolean);
          if (appIds.length > 0) {
            try {
              const recRes = await axios.post(`${API_BASE}/api/admin/session-recording/batch-info`, { applicationIds: appIds });
              if (recRes.data.success) {
                setRecordingInfoMap(recRes.data.recordings || {});
              }
            } catch (e) { /* silent */ }
          }
        }

        // Get the total count directly from the response
        const total = response.data.total || response.data.applications.length;
        
        // Calculate other stats
        const pending = response.data.applications.filter((app: Application) => 
          app.status === 'pending').length;
        const draft = response.data.applications.filter((app: Application) => 
          (app.status === 'draft' || !app.status) && app.applicationId && app.applicationId.startsWith('CNG-')).length;
        const approved = response.data.applications.filter((app: Application) => 
          app.status === 'approved').length;
        const rejected = response.data.applications.filter((app: Application) => 
          app.status === 'rejected').length;
        const queuedForDeletion = response.data.applications.filter((app: Application) => 
          app.status === 'Queued for deletion').length;
        const paid = response.data.applications.filter((app: Application) => 
          app.status === 'payment_complete').length;
        const inReview = response.data.applications.filter((app: Application) => 
          app.status === 'in-review').length;
        
        setStats({ total, pending, draft, approved, rejected, queuedForDeletion, paid, inReview });

        // Fetch excluded emails for Eligible Applications count
        try {
          const exclRes = await axios.get(`${API_BASE}/api/admin/recovery/excluded-emails`, { withCredentials: true });
          if (exclRes.data.success && exclRes.data.emails) {
            setExcludedEmails(new Set(exclRes.data.emails));
          }
        } catch (_) { /* silent — eligible count will just equal total */ }
      } else {
        setError('Failed to fetch applications');
      }
    } catch (error: any) {
      console.error('Error fetching applications:', error);

      // If the server reports that the admin session is no longer valid, show a clear
      // session-expired message and log out after a short delay.
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        const wasSubAdmin = error?.response?.data?.isSubAdmin || localStorage.getItem('subAdminAuth') === 'true';
        setError('Your admin session has expired. You will be redirected to the login page.');
        setTimeout(() => {
          localStorage.removeItem('adminAuth');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('subAdminAuth');
          localStorage.removeItem('subAdminInfo');
          navigate(wasSubAdmin ? '/su-admin/login' : '/admin/login');
        }, 3000);
      } else {
        setError('Failed to fetch applications. Please try again.');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  // Fetch approved dealers
  // Fetch area stretch locations for admin management
  const fetchAreaStretchLocations = async () => {
    try {
      setIsLoadingAreaStretchLocations(true);
      setAreaStretchLocationsError('');
      const response = await axios.get(`${API_BASE}/api/locations`);
      if (response.data.success) {
        setAreaStretchLocations(response.data.locations);
      } else {
        setAreaStretchLocationsError('Failed to fetch locations');
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      setAreaStretchLocationsError('Failed to fetch locations. Please try again.');
    } finally {
      setIsLoadingAreaStretchLocations(false);
    }
  };

  // Reset location form data
  const resetLocationForm = () => {
    setLocationFormData({
      srNo: '',
      location: '',
      ca: '',
      area: '',
      stretchSide: '',
      from: '',
      fromLatLong: '',
      to: '',
      toLatLong: '',
      minPlotFrontageMeters: '',
      roadType: '',
      minPlotDepthMeters: '',
      district: '',
      state: '',
      stretchSrNoArea: '',
      areaStretch: ''
    });
    setEditingLocation(null);
  };

  // Handle location form input change
  const handleLocationFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setLocationFormData(prev => ({ ...prev, [name]: value }));
  };

  // Open modal for adding new location
  const handleAddLocation = () => {
    resetLocationForm();
    setShowLocationModal(true);
  };

  // Open modal for editing location
  const handleEditLocation = (location: any) => {
    setEditingLocation(location);
    setLocationFormData({
      srNo: location.srNo || '',
      location: location.location || '',
      ca: location.ca || '',
      area: location.area || '',
      stretchSide: location.stretchSide || '',
      from: location.from || '',
      fromLatLong: location.fromLatLong || '',
      to: location.to || '',
      toLatLong: location.toLatLong || '',
      minPlotFrontageMeters: location.minPlotFrontageMeters || '',
      roadType: location.roadType || '',
      minPlotDepthMeters: location.minPlotDepthMeters || '',
      district: location.district || '',
      state: location.state || '',
      stretchSrNoArea: location.stretchSrNoArea || '',
      areaStretch: location.areaStretch || ''
    });
    setShowLocationModal(true);
  };

  // Save location (create or update)
  const handleSaveLocation = async () => {
    try {
      axios.defaults.withCredentials = true;
      if (editingLocation) {
        // Update existing location
        const response = await axios.put(`${API_BASE}/api/admin/locations/${editingLocation._id}`, locationFormData);
        if (response.data.success) {
          setActionMessage({ text: 'Location updated successfully!', type: 'success' });
          fetchAreaStretchLocations();
        } else {
          setActionMessage({ text: 'Failed to update location', type: 'error' });
        }
      } else {
        // Create new location
        const response = await axios.post(`${API_BASE}/api/admin/locations`, locationFormData);
        if (response.data.success) {
          setActionMessage({ text: 'Location created successfully!', type: 'success' });
          fetchAreaStretchLocations();
        } else {
          setActionMessage({ text: 'Failed to create location', type: 'error' });
        }
      }
      setShowLocationModal(false);
      resetLocationForm();
    } catch (error) {
      console.error('Error saving location:', error);
      setActionMessage({ text: 'Error saving location. Please try again.', type: 'error' });
    }
  };

  // Delete location
  const handleDeleteLocation = async (locationId: string) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      axios.defaults.withCredentials = true;
      const response = await axios.delete(`${API_BASE}/api/admin/locations/${locationId}`);
      if (response.data.success) {
        setActionMessage({ text: 'Location deleted successfully!', type: 'success' });
        fetchAreaStretchLocations();
      } else {
        setActionMessage({ text: 'Failed to delete location', type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      setActionMessage({ text: 'Error deleting location. Please try again.', type: 'error' });
    }
  };

  // Filter and sort area stretch locations
  const filteredAreaStretchLocations = areaStretchLocations.filter(loc => {
    const term = locationSearchTerm.toLowerCase();
    return (
      loc.location?.toLowerCase().includes(term) ||
      loc.from?.toLowerCase().includes(term) ||
      loc.to?.toLowerCase().includes(term) ||
      loc.area?.toLowerCase().includes(term) ||
      loc.ca?.toLowerCase().includes(term) ||
      loc.srNo?.toLowerCase().includes(term)
    );
  }).sort((a: any, b: any) => {
    const field = locationSortField;
    let aVal = a[field] ?? '';
    let bVal = b[field] ?? '';
    // Numeric sort for srNo, minPlotFrontageMeters, minPlotDepthMeters
    if (field === 'srNo' || field === 'minPlotFrontageMeters' || field === 'minPlotDepthMeters') {
      aVal = parseFloat(aVal) || 0;
      bVal = parseFloat(bVal) || 0;
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    if (aVal < bVal) return locationSortDir === 'asc' ? -1 : 1;
    if (aVal > bVal) return locationSortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const handleLocationSort = (field: string) => {
    if (locationSortField === field) {
      setLocationSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setLocationSortField(field);
      setLocationSortDir('asc');
    }
    setLocationsPage(1);
  };
  
  // Fetch application details
  const fetchApplicationDetails = async (applicationId: string) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      const response = await axios.get(`${API_BASE}/api/admin/applications/${applicationId}`);
      
      if (response.data.success) {
        setSelectedApplication(response.data.application);
        setIsViewingDetails(true);
      } else {
        setError('Failed to fetch application details');
      }
    } catch (error) {
      console.error('Error fetching application details:', error);
      setError('Failed to fetch application details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete application permanently
  const deleteApplication = async (applicationId: string) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      const response = await axios.put(`${API_BASE}/api/admin/applications/${applicationId}/status`, {
        status: 'delete'
      });
      
      if (response.data.success || response.status === 200 || response.status === 204) {
        // Remove application from the list
        setApplications(applications.filter(app => app.applicationId !== applicationId));
        
        // Close details view if the deleted application was being viewed
        if (selectedApplication && selectedApplication.applicationId === applicationId) {
          setSelectedApplication(null);
          setIsViewingDetails(false);
        }
        
        // Recalculate stats from the updated list
        const updatedApplications = applications.filter(app => app.applicationId !== applicationId);
        const total = updatedApplications.length;
        const pending = updatedApplications.filter(app => app.status === 'pending').length;
        const draft = updatedApplications.filter(app => app.status === 'draft' || !app.status).length;
        const approved = updatedApplications.filter(app => app.status === 'approved').length;
        const rejected = updatedApplications.filter(app => app.status === 'rejected').length;
        const queuedForDeletion = updatedApplications.filter(app => app.status === 'Queued for deletion').length;
        const paid = updatedApplications.filter(app => app.status === 'payment_complete').length;
        const inReview = updatedApplications.filter(app => app.status === 'in-review').length;
        setStats({ total, pending, draft, approved, rejected, queuedForDeletion, paid, inReview });

        // Log the deletion
        const userProfile = localStorage.getItem('userProfile');
        const adminEmail = userProfile ? JSON.parse(userProfile).email : 'admin@system';
        const appDetails = applications.find(app => app.applicationId === applicationId);

        const newLog: DeletionLog = {
          applicationIds: [appDetails?._id || applicationId], // Use _id if found, else applicationId as fallback
          applicationNumbers: [applicationId],
          deletedAt: new Date().toISOString(),
          deletedBy: adminEmail,
          reason: "Admin confirmed deletion for 'Queued for deletion' status"
        };
        setDeletionLogs(prev => [newLog, ...prev]);

        setActionMessage({
          text: `Successfully deleted application ${applicationId}`,
          type: 'success'
        });

      } else {
        setActionMessage({
          text: response.data.message || 'Failed to delete application',
          type: 'error'
        });
        setError('Failed to delete application'); // Keep original setError for potential other uses if any
      }
    } catch (err: any) {
      console.error('Error deleting application:', err);
      const errorMsg = err.response?.data?.message || 'Failed to delete application. Please try again.';
      setActionMessage({
        text: errorMsg,
        type: 'error'
      });
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Update application status
  const updateApplicationStatus = async (applicationId: string, newStatus: string) => {
    try {
      setIsLoading(true);
      setError('');
      
      // Configure axios to send credentials (cookies)
      axios.defaults.withCredentials = true;
      
      const response = await axios.put(`${API_BASE}/api/admin/applications/${applicationId}/status`, {
        status: newStatus
      });
      
      if (response.data.success) {
        // Update application in the list
        setApplications(applications.map(app => 
          app.applicationId === applicationId ? { ...app, status: newStatus } : app
        ));
        
        // Update selected application if viewing details
        if (selectedApplication && selectedApplication.applicationId === applicationId) {
          setSelectedApplication({ ...selectedApplication, status: newStatus });
        }
        
        // Recalculate stats
        const total = applications.length;
        const pending = applications.filter(app => 
          (app.applicationId === applicationId ? newStatus === 'pending' : app.status === 'pending')).length;
        const draft = applications.filter(app => 
          (app.applicationId === applicationId ? newStatus === 'draft' : (app.status === 'draft' || !app.status))).length;
        const approved = applications.filter(app => 
          (app.applicationId === applicationId ? newStatus === 'approved' : app.status === 'approved')).length;
        const rejected = applications.filter(app => 
          (app.applicationId === applicationId ? newStatus === 'rejected' : app.status === 'rejected')).length;
        const queuedForDeletion = applications.filter(app => 
          (app.applicationId === applicationId ? newStatus === 'Queued for deletion' : app.status === 'Queued for deletion')).length;
        const paid = applications.filter(app => 
          (app.applicationId === applicationId ? newStatus === 'payment_complete' : app.status === 'payment_complete')).length;
        const inReview = applications.filter(app => 
          (app.applicationId === applicationId ? newStatus === 'in-review' : app.status === 'in-review')).length;
        
        setStats({ total, pending, draft, approved, rejected, queuedForDeletion, paid, inReview });
      } else {
        setError('Failed to update application status');
      }
    } catch (error) {
      console.error('Error updating application status:', error);
      setError('Failed to update application status. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadLocationsTemplate = async () => {
    try {
      setLocationsUploadStatus(null);

      const response = await axios.get(`${API_BASE}/api/admin/locations/template`, {
        responseType: 'blob',
        withCredentials: true,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'location-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      console.error('Error downloading locations XLSX template:', error);
      const msg = error?.response?.data?.message || 'Failed to download template. Please try again.';
      setLocationsUploadStatus({ message: msg, type: 'error' });
    }
  };

  const handleExportLocationsXlsx = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/admin/locations/export`, {
        responseType: 'blob',
        withCredentials: true,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `locations-export-${dateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      console.error('Error exporting locations to XLSX:', error);
      alert('Failed to export locations. Please try again.');
    }
  };

  const handleUploadLocationsXlsx = async () => {
    if (!locationsUploadFile) {
      setLocationsUploadStatus({ message: 'Please select an .xlsx file to upload.', type: 'error' });
      return;
    }

    try {
      setIsLocationsUploading(true);
      setLocationsUploadStatus({ message: 'Uploading and validating file, please wait...', type: 'info' });

      const formData = new FormData();
      formData.append('file', locationsUploadFile);

      const response = await axios.post(`${API_BASE}/api/admin/locations/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      if (response.data && response.data.success) {
        const count = typeof response.data.records === 'number' ? response.data.records : 0;
        setLocationsUploadStatus({
          message: `File uploaded and validated successfully. Records found: ${count}.`,
          type: 'success',
        });
      } else {
        const msg = response.data?.message || 'Failed to validate uploaded file.';
        setLocationsUploadStatus({ message: msg, type: 'error' });
      }
    } catch (error: any) {
      console.error('Error uploading locations XLSX:', error);
      const msg = error?.response?.data?.message ||
        'Failed to upload or validate file. Please check the template and try again.';
      setLocationsUploadStatus({ message: msg, type: 'error' });
    } finally {
      setIsLocationsUploading(false);
    }
  };

  const handleLogout = async () => {
    // VULN-015 FIX: Call backend to clear httpOnly cookies (JS cannot clear httpOnly cookies)
    try {
      await fetch(`${API_BASE}/api/logout`, {
        method: 'POST',
        credentials: 'include', // Send cookies so backend can identify and clear them
        headers: { 'Accept': 'application/json' },
      });
    } catch (error) {
      console.error('[AdminDashboard] Backend logout failed:', error);
      // Continue with client-side cleanup even if backend call fails
    }

    // Clear both super admin and sub-admin auth from localStorage
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminUser');
    localStorage.removeItem('subAdminAuth');
    localStorage.removeItem('subAdminInfo');
    // Clear any session storage items
    sessionStorage.removeItem('adminEditMode');

    // Redirect to appropriate login page
    if (isSubAdmin) {
      navigate('/su-admin/login');
    } else {
      navigate('/admin/login');
    }
  };
  
  // Helper to check if a nav item should be visible
  const canAccessTab = (tabKey: string): boolean => {
    if (!isSubAdmin) return true; // Super admin can access all
    return subAdminPermissions.includes(tabKey);
  };
  
  // Sub-admin management functions (super admin only)
  const fetchSubAdmins = async () => {
    if (isSubAdmin) return; // Only super admin can fetch
    setIsLoadingSubAdmins(true);
    try {
      const response = await axios.get(`${API_BASE}/api/su-admin/list`, { withCredentials: true });
      if (response.data.success) {
        setSubAdmins(response.data.subAdmins.map((sa: any) => ({ ...sa, id: sa._id })));
      }
    } catch (error) {
      console.error('Error fetching sub-admins:', error);
    } finally {
      setIsLoadingSubAdmins(false);
    }
  };
  
  const handleCreateSubAdmin = async () => {
    if (!newSubAdminEmail || !newSubAdminName) {
      setSubAdminError('Name and email are required');
      return;
    }
    setSubAdminError(null);
    try {
      const response = await axios.post(`${API_BASE}/api/su-admin/create`, {
        email: newSubAdminEmail,
        name: newSubAdminName,
        permissions: newSubAdminPermissions
      }, { withCredentials: true });
      
      if (response.data.success) {
        setSubAdminSuccess('Sub-admin created successfully');
        setShowCreateSubAdminModal(false);
        setNewSubAdminEmail('');
        setNewSubAdminName('');
        setNewSubAdminPassword('');
        setNewSubAdminPermissions(['dashboard', 'applications']);
        fetchSubAdmins();
        setTimeout(() => setSubAdminSuccess(null), 3000);
      }
    } catch (error: any) {
      setSubAdminError(error.response?.data?.message || 'Failed to create sub-admin');
    }
  };
  
  const handleUpdateSubAdmin = async () => {
    if (!editingSubAdmin) return;
    setSubAdminError(null);
    try {
      const payload: any = {
        name: editingSubAdmin.name,
        permissions: editingSubAdmin.permissions,
        isActive: editingSubAdmin.isActive
      };
      
      const response = await axios.put(`${API_BASE}/api/su-admin/${editingSubAdmin.id || editingSubAdmin._id}`, payload, { withCredentials: true });
      
      if (response.data.success) {
        setSubAdminSuccess('Sub-admin updated successfully');
        setShowEditSubAdminModal(false);
        setEditingSubAdmin(null);
        setNewSubAdminPassword('');
        fetchSubAdmins();
        setTimeout(() => setSubAdminSuccess(null), 3000);
      }
    } catch (error: any) {
      setSubAdminError(error.response?.data?.message || 'Failed to update sub-admin');
    }
  };
  
  const handleDeleteSubAdmin = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this sub-admin?')) return;
    try {
      const response = await axios.delete(`${API_BASE}/api/su-admin/${id}`, { withCredentials: true });
      if (response.data.success) {
        setSubAdminSuccess('Sub-admin deleted successfully');
        fetchSubAdmins();
        setTimeout(() => setSubAdminSuccess(null), 3000);
      }
    } catch (error: any) {
      setSubAdminError(error.response?.data?.message || 'Failed to delete sub-admin');
    }
  };

  const handleToggleSubAdminActive = async (sa: any) => {
    const newStatus = !sa.isActive;
    const action = newStatus ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} ${sa.name}?`)) return;
    try {
      const response = await axios.put(`${API_BASE}/api/su-admin/${sa.id || sa._id}`, {
        name: sa.name,
        permissions: sa.permissions,
        isActive: newStatus
      }, { withCredentials: true });
      if (response.data.success) {
        setSubAdminSuccess(`Sub-admin ${newStatus ? 'activated' : 'deactivated'} successfully`);
        fetchSubAdmins();
        setTimeout(() => setSubAdminSuccess(null), 3000);
      }
    } catch (error: any) {
      setSubAdminError(error.response?.data?.message || `Failed to ${action} sub-admin`);
    }
  };
  
  const toggleSubAdminPermission = (permission: string, isEditing: boolean = false) => {
    if (isEditing && editingSubAdmin) {
      const perms = editingSubAdmin.permissions.includes(permission)
        ? editingSubAdmin.permissions.filter(p => p !== permission)
        : [...editingSubAdmin.permissions, permission];
      setEditingSubAdmin({ ...editingSubAdmin, permissions: perms });
    } else {
      setNewSubAdminPermissions(prev => 
        prev.includes(permission) ? prev.filter(p => p !== permission) : [...prev, permission]
      );
    }
  };

  if (!isAuthenticated) {
    return null; // Don't render anything until authentication check is complete
  }

  return (
    <DashboardContainer>
      <IntentTooltipCSS />
      <SubAdminWaterfallStyles />
      {/* Login Toast Notifications */}
      <LoginToast />
      <SubAdminLoginToast />
      <PaymentConfetti testTrigger={confettiTestTrigger} testApplicantInfo={confettiTestInfo} />
      <BackupActivityToast />
      
      <Sidebar
        $collapsed={sidebarCollapsed}
        onMouseEnter={() => setSidebarHovered(true)}
        onMouseLeave={() => setSidebarHovered(false)}
      >
        {/* Pin/Unpin toggle */}
        <button
          onClick={() => setSidebarPinned(prev => !prev)}
          title={sidebarPinned ? 'Unpin sidebar (collapse on mouse leave)' : 'Pin sidebar (keep expanded)'}
          style={{
            position: 'absolute',
            top: 8,
            right: sidebarCollapsed ? '50%' : 8,
            transform: sidebarCollapsed ? 'translateX(50%)' : 'none',
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: 'none',
            background: sidebarPinned ? 'rgba(76, 110, 245, 0.3)' : 'rgba(255,255,255,0.1)',
            color: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.25s ease',
            zIndex: 5,
            opacity: sidebarCollapsed ? 0.6 : 0.8,
            padding: 0,
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill={sidebarPinned ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
            {sidebarPinned ? (
              <path d="M12 2l3 9h9l-7 5 3 9-8-6-8 6 3-9-7-5h9z" />
            ) : (
              <path d="M4 12h16M12 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
            )}
          </svg>
        </button>
        <Logo
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          whileHover={{ scale: 1.03 }}
          style={sidebarCollapsed ? { padding: '0.75rem 0', justifyContent: 'center' } : undefined}
        >
          <img src="/assets/logo_square.png" alt="CNG Logo" style={{ width: sidebarCollapsed ? 30 : 36, height: sidebarCollapsed ? 30 : 36, transition: 'all 0.25s ease' }} />
          {!sidebarCollapsed && (
            <div className="logo-text">
              CNG Admin
              <span>Dealership Portal</span>
            </div>
          )}
        </Logo>
        <NavMenu $collapsed={sidebarCollapsed}>
          {/* Sub-admin indicator */}
          {isSubAdmin && !sidebarCollapsed && (
            <div style={{ padding: '8px 12px', marginBottom: '8px', background: 'rgba(245, 158, 11, 0.15)', borderRadius: '8px', fontSize: '0.75rem', color: '#f59e0b', textAlign: 'center' }}>
              Sub-Admin: {subAdminName}
            </div>
          )}
          {canAccessTab('dashboard') && (
          <NavItem 
            $active={activeTab === 'dashboard'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('dashboard')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Dashboard"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
            {!sidebarCollapsed && 'Dashboard'}
          </NavItem>
          )}
          {canAccessTab('area-report') && (
          <NavItem
            $active={activeTab === 'area-report'}
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('area-report')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Area Wise Report"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {!sidebarCollapsed && 'Area Wise Report'}
          </NavItem>
          )}
          {canAccessTab('applications') && (
          <NavItem 
            $active={activeTab === 'applications'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('applications')} 
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Applications"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {!sidebarCollapsed && 'Applications'}
          </NavItem>
          )}
          {canAccessTab('application-files') && (
          <NavItem 
            $active={activeTab === 'application-files'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('application-files')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Application Files"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
            {!sidebarCollapsed && 'Application Files'}
          </NavItem>
          )}
          {canAccessTab('application-recovery') && (
          <NavItem
            $active={activeTab === 'application-recovery'}
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('application-recovery')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="App Recovery"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {!sidebarCollapsed && 'App Recovery'}
          </NavItem>
          )}
          {canAccessTab('registered-users') && (
          <NavItem 
            $active={activeTab === 'registered-users'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('registered-users')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Users"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            {!sidebarCollapsed && 'Users'}
            {onlineUsersCount > 0 && (
              <span style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                background: 'rgba(34, 197, 94, 0.15)',
                color: '#22c55e',
                fontSize: '0.7rem',
                fontWeight: 700,
                padding: sidebarCollapsed ? '2px 4px' : '2px 7px',
                borderRadius: '10px',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                ...(sidebarCollapsed ? { position: 'absolute' as const, top: '2px', right: '2px', padding: '1px 4px', fontSize: '0.6rem' } : {}),
              }}>
                <span style={{
                  width: '6px', height: '6px', borderRadius: '50%',
                  background: '#22c55e',
                  boxShadow: '0 0 6px rgba(34, 197, 94, 0.6)',
                  animation: 'chatDotPulse 2s ease-in-out infinite',
                  flexShrink: 0,
                }} />
                {onlineUsersCount}
              </span>
            )}
          </NavItem>
          )}
          {canAccessTab('user-intent') && (
          <NavItem
            $active={activeTab === 'user-intent'}
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('user-intent')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="User Intent"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {!sidebarCollapsed && 'User Intent'}
          </NavItem>
          )}
          {canAccessTab('locations') && (
          <NavItem
            $active={activeTab === 'locations'}
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('locations')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Locations"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {!sidebarCollapsed && 'Locations'}
          </NavItem>
          )}
          <NavItem 
            $active={activeTab === 'visitor-chats'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('visitor-chats')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Visitor Chats"
            style={hasUnreadChats && activeTab !== 'visitor-chats' ? {
              animation: 'chatNavPulse 2.5s ease-in-out infinite',
              borderRadius: '10px',
            } : undefined}
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            {!sidebarCollapsed && 'Visitor Chats'}
            <span style={{
              width: '9px', height: '9px', borderRadius: '50%', marginLeft: sidebarCollapsed ? 0 : '6px', flexShrink: 0,
              background: hasUnreadChats ? '#22c55e' : '#cbd5e1',
              boxShadow: hasUnreadChats ? '0 0 6px rgba(34, 197, 94, 0.6)' : 'none',
              animation: hasUnreadChats ? 'chatDotPulse 2s ease-in-out infinite' : 'none',
              transition: 'background 0.4s ease, box-shadow 0.4s ease',
              position: sidebarCollapsed ? 'absolute' as const : 'static' as const,
              top: sidebarCollapsed ? 6 : undefined,
              right: sidebarCollapsed ? 6 : undefined,
            }} />
          </NavItem>
          {canAccessTab('storage-stats') && (
          <NavItem 
            $active={activeTab === 'storage-stats'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('storage-stats')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Server Stats"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            {!sidebarCollapsed && 'Server Stats'}
          </NavItem>
          )}
          {canAccessTab('visitor-stats') && (
          <NavItem 
            $active={activeTab === 'visitor-stats'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('visitor-stats')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Visitor Stats"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
            {!sidebarCollapsed && 'Visitor Stats'}
          </NavItem>
          )}
          {canAccessTab('controls') && (
          <NavItem 
            $active={activeTab === 'controls'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('controls')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Controls"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
            {!sidebarCollapsed && 'Controls'}
          </NavItem>
          )}
          {canAccessTab('security-apis') && (
          <NavItem 
            $active={activeTab === 'security-apis'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('security-apis')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Security APIs"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            {!sidebarCollapsed && 'Security APIs'}
          </NavItem>
          )}
          {canAccessTab('page4-rows') && (
          <NavItem 
            $active={activeTab === 'page4-rows'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('page4-rows')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Page4 Row Selections"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
            </svg>
            {!sidebarCollapsed && 'Page4 Row Selections'}
          </NavItem>
          )}
          {canAccessTab('appendix-logic') && (
          <NavItem 
            $active={activeTab === 'appendix-logic'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('appendix-logic')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Appendix Logic"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {!sidebarCollapsed && 'Appendix Logic'}
          </NavItem>
          )}
          {canAccessTab('settings') && (
          <NavItem 
            $active={activeTab === 'settings'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('settings')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Settings"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c-.77 1.543-.694 3.413-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c.77-1.543.694-3.413 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {!sidebarCollapsed && 'Settings'}
          </NavItem>
          )}
          {canAccessTab('docs') && (
          <NavItem 
            $active={activeTab === 'docs'} 
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('docs')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Docs"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            {!sidebarCollapsed && 'Docs'}
          </NavItem>
          )}
          {/* Sub-Admin Management - Super Admin Only */}
          {!isSubAdmin && (
          <NavItem 
            $active={activeTab === 'sub-admins'} 
            $collapsed={sidebarCollapsed}
            onClick={() => { setActiveTab('sub-admins'); fetchSubAdmins(); }}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="Sub-Admins"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            {!sidebarCollapsed && 'Sub-Admins'}
          </NavItem>
          )}
          {!isSubAdmin && (
          <NavItem
            $active={activeTab === 'app-maintenance'}
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('app-maintenance')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="App Maintenance"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            {!sidebarCollapsed && 'Maintenance'}
          </NavItem>
          )}
          {!isSubAdmin && (
          <NavItem
            $active={activeTab === 's3-settings'}
            $collapsed={sidebarCollapsed}
            onClick={() => setActiveTab('s3-settings')}
            whileHover={sidebarCollapsed ? undefined : { x: 5 }}
            whileTap={{ scale: 0.95 }}
            title="S3 & Backups"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
            </svg>
            {!sidebarCollapsed && 'S3 & Backups'}
          </NavItem>
          )}
        </NavMenu>
        {!sidebarCollapsed && (
        <div style={{
          position: 'absolute', bottom: '10px', left: 0, right: 0, textAlign: 'center',
          fontSize: '8.5px', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Helvetica Neue', sans-serif",
          fontWeight: 500, letterSpacing: '0.3px', lineHeight: 1.3, userSelect: 'none',
          color: 'transparent',
          textShadow: '0px 1px 1px rgba(255,255,255,0.08), 0px -1px 1px rgba(0,0,0,0.5)',
          WebkitBackgroundClip: 'text', backgroundClip: 'text',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.06) 100%)',
          WebkitTextFillColor: 'rgba(255,255,255,0.10)',
        }}>
          © {new Date().getFullYear()} By Neelabh for MNGL
        </div>
        )}
      </Sidebar>
      
      <MainContent>
        <Header>
          {/* ── Left: Page title ── */}
          <Title
            key={activeTab}
            initial={{ opacity: 0, x: -12 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.25 }}
          >
            {activeTab === 'dashboard' && 'Dashboard'}
            {activeTab === 'area-report' && 'Area Wise Report'}
            {activeTab === 'applications' && 'Applications'}
            {activeTab === 'locations' && 'Locations Management'}
            {activeTab === 'visitor-chats' && 'Visitor Chats'}
            {activeTab === 'application-files' && 'Application Files'}
            {activeTab === 'application-recovery' && 'App Recovery'}
            {activeTab === 'storage-stats' && 'Server Statistics'}
            {activeTab === 'visitor-stats' && 'Visitor Stats'}
            {activeTab === 'controls' && 'Controls'}
            {activeTab === 'security-apis' && 'Security APIs'}
            {activeTab === 'page4-rows' && 'Page4 Row Selections'}
            {activeTab === 'appendix-logic' && 'Appendix Logic'}
            {activeTab === 'settings' && 'Settings'}
            {activeTab === 'docs' && 'Documentation'}
            {activeTab === 'sub-admins' && 'Sub-Admin Management'}
            {activeTab === 'registered-users' && 'Registered Users'}
            {activeTab === 'user-intent' && 'User Intent Management'}
            {activeTab === 'app-maintenance' && 'App Maintenance'}
            {activeTab === 's3-settings' && 'S3 & Backups'}
          </Title>

          {/* ── Right: All widgets ── */}
          <HeaderWidgets>
            {/* Online users */}
            <OnlineChip>
              <OnlinePulseDot />
              <span style={{ fontSize: '11.5px', fontWeight: 650, color: '#065f46', whiteSpace: 'nowrap' }}>
                {onlineUsersCount} Online
              </span>
            </OnlineChip>

            {/* Sub-admin online bubble */}
            {onlineSubAdmins.length > 0 && (
              <SubAdminChip
                ref={subAdminDropdownRef}
                $hasOnline={true}
                onClick={() => setSubAdminDropdownOpen(p => !p)}
                title={`${onlineSubAdmins.length} sub-admin${onlineSubAdmins.length > 1 ? 's' : ''} online`}
              >
                <SubAdminPulseDot />
                <span style={{ fontSize: '11.5px', fontWeight: 650, color: '#6b21a8', whiteSpace: 'nowrap' }}>
                  {onlineSubAdmins.length} Sub-Admin{onlineSubAdmins.length > 1 ? 's' : ''}
                </span>
                <AnimatePresence>
                  {subAdminDropdownOpen && (
                    <SubAdminDropdown
                      initial={{ opacity: 0, y: -8, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.95 }}
                      transition={{ duration: 0.15 }}
                    >
                      <SubAdminDropdownHeader>
                        {onlineSubAdmins.length} Sub-Admin{onlineSubAdmins.length > 1 ? 's' : ''} Online
                      </SubAdminDropdownHeader>
                      {onlineSubAdmins.map(sa => (
                        <SubAdminDropdownItem key={sa.id}>
                          <SubAdminAvatar>
                            {(sa.name || sa.email || '?')[0].toUpperCase()}
                          </SubAdminAvatar>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '12.5px', fontWeight: 650, color: '#1e1b4b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {sa.name || sa.email?.split('@')[0] || 'Sub-Admin'}
                            </div>
                            <div style={{ fontSize: '10.5px', color: '#7c3aed', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {sa.email}
                            </div>
                            <div style={{ fontSize: '10px', color: '#94a3b8', marginTop: 2 }}>
                              {sa.sessions > 1 ? `${sa.sessions} sessions · ` : ''}
                              {sa.devices?.[0]?.browser || ''} · {sa.devices?.[0]?.device || ''}
                              {sa.connectedAt && (() => {
                                const mins = Math.round((Date.now() - sa.connectedAt) / 60000);
                                return mins < 1 ? ' · just now' : ` · ${mins}m ago`;
                              })()}
                            </div>
                          </div>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />
                        </SubAdminDropdownItem>
                      ))}
                    </SubAdminDropdown>
                  )}
                </AnimatePresence>
              </SubAdminChip>
            )}

            <HeaderDivider />

            {/* Session timer */}
            <SessionTimerBadge
              isWarning={isWarning}
              onClick={() => setShowModal(true)}
              title="Session Time Remaining — Click to extend"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>
              </svg>
              {formatTime(timeLeft)}
            </SessionTimerBadge>

            <HeaderDivider />

            {/* System vitals */}
            {headerServerStats && (
              <>
                <VitalsBar>
                  <VitalItem
                    percent={Math.round(parseFloat(headerServerStats.cpu.usage))}
                    color={parseFloat(headerServerStats.cpu.usage) > 80 ? '#ef4444' : parseFloat(headerServerStats.cpu.usage) > 60 ? '#f59e0b' : '#6366f1'}
                    label="CPU"
                  />
                  <VitalItem
                    percent={Math.round(parseFloat(headerServerStats.memory.usagePercent))}
                    color={parseFloat(headerServerStats.memory.usagePercent) > 80 ? '#ef4444' : parseFloat(headerServerStats.memory.usagePercent) > 60 ? '#f59e0b' : '#10b981'}
                    label="RAM"
                  />
                  {headerServerStats.diskSpace && (
                    <VitalItem
                      percent={Math.round((headerServerStats.diskSpace.used / headerServerStats.diskSpace.total) * 100)}
                      color={(headerServerStats.diskSpace.used / headerServerStats.diskSpace.total) > 0.9 ? '#ef4444' : (headerServerStats.diskSpace.used / headerServerStats.diskSpace.total) > 0.7 ? '#f59e0b' : '#8b5cf6'}
                      label="DISK"
                    />
                  )}
                </VitalsBar>
                <HeaderDivider />
              </>
            )}

            {/* Clock */}
            <ClockWidget>
              <span style={{
                fontSize: '0.92rem', fontWeight: 700, color: '#1e293b',
                letterSpacing: '1px', fontFamily: "'SF Mono', 'Cascadia Code', 'Consolas', monospace",
                fontVariantNumeric: 'tabular-nums',
              }}>
                {clockNow.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).toUpperCase()}
              </span>
              <span style={{
                fontSize: '0.58rem', fontWeight: 600, color: '#94a3b8', letterSpacing: '0.5px',
              }}>
                {clockNow.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase()}
              </span>
            </ClockWidget>

            <HeaderDivider />

            {/* Logout */}
            <LogoutButton
              onClick={handleLogout}
              whileTap={{ scale: 0.96 }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" /><polyline points="16,17 21,12 16,7" /><line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </LogoutButton>
          </HeaderWidgets>
        </Header>

        {activeTab === 'application-files' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="application-files"
          >
            <AdminApplicationFiles />
          </TabContent>
        )}

        {activeTab === 'application-recovery' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="application-recovery"
          >
            <AdminApplicationRecovery />
          </TabContent>
        )}

        {activeTab === 'storage-stats' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="storage-stats"
          >
            <AdminStorageStats />
          </TabContent>
        )}

        {activeTab === 'visitor-stats' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="visitor-stats"
          >
            <VisitorStatsPage />
          </TabContent>
        )}

        {activeTab === 'registered-users' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="registered-users"
          >
            <div style={{ padding: '0.5rem 0' }}>
              {/* ── Row 1: Total count + Date Range  |  Search box ── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    {dateRangeFrom && dateRangeTo ? (
                      <>Showing <strong>{regUsersTotal}</strong> {showQualifiedOnly ? 'qualified ' : ''}users in date range</>
                    ) : showQualifiedOnly ? (
                      <>Showing <strong style={{ color: '#15803d' }}>{regUsersTotal}</strong> qualified users (Intent: Yes/Maybe/—, with progress)</>
                    ) : (
                      <>Total: <strong>{regUsersTotal}</strong> registered users</>
                    )}
                  </span>

                  {/* Airline-style Date Range Picker */}
                  <div style={{ position: 'relative' }} ref={datePickerRef}>
                    <button
                      onClick={() => { setShowDateRangePicker(!showDateRangePicker); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '5px 12px', border: '1px solid #e2e8f0', borderRadius: '8px',
                        background: (dateRangeFrom && dateRangeTo) ? 'linear-gradient(135deg, #eff6ff, #f0fdf4)' : '#fff',
                        cursor: 'pointer', fontSize: '0.78rem', color: '#475569',
                        transition: 'all 0.2s', boxShadow: showDateRangePicker ? '0 0 0 2px rgba(74,144,226,0.3)' : 'none'
                      }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      {dateRangeFrom && dateRangeTo ? (
                        <span>
                          {new Date(dateRangeFrom).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                          {' → '}
                          {new Date(dateRangeTo).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {dateRangeCount !== null && (
                            <span style={{ marginLeft: '6px', background: '#dbeafe', color: '#1d4ed8', padding: '1px 7px', borderRadius: '10px', fontWeight: 700, fontSize: '0.72rem' }}>
                              {dateRangeLoading ? '...' : dateRangeCount}
                            </span>
                          )}
                        </span>
                      ) : 'Date Range'}
                    </button>

                    {showDateRangePicker && (() => {
                      const { year, month } = datePickerMonth;
                      const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                      const daysInMonth = new Date(year, month + 1, 0).getDate();
                      const firstDayOfWeek = new Date(year, month, 1).getDay();
                      const today = new Date(); today.setHours(0,0,0,0);
                      const prevMonth = () => setDatePickerMonth(p => p.month === 0 ? { year: p.year - 1, month: 11 } : { ...p, month: p.month - 1 });
                      const nextMonth = () => setDatePickerMonth(p => p.month === 11 ? { year: p.year + 1, month: 0 } : { ...p, month: p.month + 1 });
                      const handleDayClick = (day: number) => {
                        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        if (datePickerSelecting === 'from') {
                          setDateRangeFrom(dateStr);
                          setDateRangeTo('');
                          setDateRangeCount(null);
                          setDatePickerSelecting('to');
                        } else {
                          if (dateStr < dateRangeFrom) {
                            setDateRangeFrom(dateStr);
                            setDateRangeTo('');
                            setDateRangeCount(null);
                            setDatePickerSelecting('to');
                          } else {
                            setDateRangeTo(dateStr);
                            setDatePickerSelecting('from');
                            fetchDateRangeCount(dateRangeFrom, dateStr);
                            setRegUsersPage(1);
                            fetchRegisteredUsersList(1, regUsersSearch, regUsersSortBy, regUsersSortOrder, dateRangeFrom, dateStr);
                          }
                        }
                      };
                      const isInRange = (day: number) => {
                        if (!dateRangeFrom || !dateRangeTo) return false;
                        const d = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                        return d >= dateRangeFrom && d <= dateRangeTo;
                      };
                      const isFrom = (day: number) => dateRangeFrom === `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const isTo = (day: number) => dateRangeTo === `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                      const isFuture = (day: number) => new Date(year, month, day) > today;
                      return (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, marginTop: '6px', zIndex: 9999,
                          background: '#fff', borderRadius: '14px', boxShadow: '0 12px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                          padding: '0', width: '320px', overflow: 'hidden',
                          animation: 'subAdminFadeSlideIn 0.2s ease'
                        }}>
                          {/* From / To indicator */}
                          <div style={{ display: 'flex', borderBottom: '1px solid #f1f5f9' }}>
                            <div
                              onClick={() => setDatePickerSelecting('from')}
                              style={{
                                flex: 1, padding: '12px 16px', cursor: 'pointer', textAlign: 'center',
                                background: datePickerSelecting === 'from' ? '#eff6ff' : '#fff',
                                borderBottom: datePickerSelecting === 'from' ? '2px solid #3b82f6' : '2px solid transparent',
                                transition: 'all 0.2s'
                              }}
                            >
                              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>From</div>
                              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: dateRangeFrom ? '#1e293b' : '#cbd5e1', marginTop: '2px' }}>
                                {dateRangeFrom ? new Date(dateRangeFrom + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Select date'}
                              </div>
                            </div>
                            <div style={{ width: '1px', background: '#f1f5f9' }} />
                            <div
                              onClick={() => { if (dateRangeFrom) setDatePickerSelecting('to'); }}
                              style={{
                                flex: 1, padding: '12px 16px', cursor: dateRangeFrom ? 'pointer' : 'default', textAlign: 'center',
                                background: datePickerSelecting === 'to' ? '#f0fdf4' : '#fff',
                                borderBottom: datePickerSelecting === 'to' ? '2px solid #22c55e' : '2px solid transparent',
                                transition: 'all 0.2s', opacity: dateRangeFrom ? 1 : 0.5
                              }}
                            >
                              <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>To</div>
                              <div style={{ fontSize: '0.9rem', fontWeight: 600, color: dateRangeTo ? '#1e293b' : '#cbd5e1', marginTop: '2px' }}>
                                {dateRangeTo ? new Date(dateRangeTo + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Select date'}
                              </div>
                            </div>
                          </div>
                          {/* Month navigation */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px' }}>
                            <button onClick={prevMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontSize: '1rem', color: '#64748b', borderRadius: '6px' }} onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')} onMouseLeave={e => (e.currentTarget.style.background = 'none')}>&lsaquo;</button>
                            <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>{monthNames[month]} {year}</span>
                            <button onClick={nextMonth} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontSize: '1rem', color: '#64748b', borderRadius: '6px' }} onMouseEnter={e => (e.currentTarget.style.background = '#f1f5f9')} onMouseLeave={e => (e.currentTarget.style.background = 'none')}>&rsaquo;</button>
                          </div>
                          {/* Day headers */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 12px', gap: '0' }}>
                            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                              <div key={d} style={{ textAlign: 'center', fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', padding: '4px 0' }}>{d}</div>
                            ))}
                          </div>
                          {/* Days grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', padding: '0 12px 12px', gap: '2px' }}>
                            {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`empty-${i}`} />)}
                            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
                              const inRange = isInRange(day);
                              const isStart = isFrom(day);
                              const isEnd = isTo(day);
                              const future = isFuture(day);
                              return (
                                <div
                                  key={day}
                                  onClick={() => !future && handleDayClick(day)}
                                  style={{
                                    textAlign: 'center', padding: '7px 0', fontSize: '0.8rem',
                                    cursor: future ? 'not-allowed' : 'pointer',
                                    borderRadius: isStart ? '8px 0 0 8px' : isEnd ? '0 8px 8px 0' : (isStart && isEnd) ? '8px' : '0',
                                    background: isStart ? '#3b82f6' : isEnd ? '#22c55e' : inRange ? '#eff6ff' : 'transparent',
                                    color: (isStart || isEnd) ? '#fff' : future ? '#d1d5db' : inRange ? '#1d4ed8' : '#334155',
                                    fontWeight: (isStart || isEnd) ? 700 : inRange ? 600 : 400,
                                    transition: 'all 0.15s',
                                    opacity: future ? 0.4 : 1
                                  }}
                                  onMouseEnter={e => { if (!future && !isStart && !isEnd) e.currentTarget.style.background = inRange ? '#dbeafe' : '#f8fafc'; }}
                                  onMouseLeave={e => { if (!future && !isStart && !isEnd) e.currentTarget.style.background = inRange ? '#eff6ff' : 'transparent'; }}
                                >
                                  {day}
                                </div>
                              );
                            })}
                          </div>
                          {/* Result + Clear */}
                          {(dateRangeFrom && dateRangeTo) && (
                            <div style={{ padding: '8px 16px 12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <button
                                onClick={selectAllUsersInDateRange}
                                disabled={dateRangeLoading || dateRangeCount === null || dateRangeCount === 0}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '6px',
                                  padding: '5px 12px', border: '1px solid #c7d2fe', borderRadius: '8px',
                                  background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', color: '#4338ca',
                                  cursor: (dateRangeLoading || !dateRangeCount) ? 'default' : 'pointer',
                                  fontSize: '0.78rem', fontWeight: 700, transition: 'all 0.2s',
                                  opacity: (dateRangeLoading || !dateRangeCount) ? 0.5 : 1
                                }}
                                onMouseEnter={e => { if (dateRangeCount) e.currentTarget.style.background = 'linear-gradient(135deg, #e0e7ff, #c7d2fe)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #eef2ff, #e0e7ff)'; }}
                              >
                                {dateRangeLoading ? 'Counting...' : dateRangeCount !== null ? (
                                  <>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                                    Select all {dateRangeCount} users
                                  </>
                                ) : ''}
                              </button>
                              <button
                                onClick={() => { setDateRangeFrom(''); setDateRangeTo(''); setDateRangeCount(null); setDatePickerSelecting('from'); setRegUsersPage(1); fetchRegisteredUsersList(1, regUsersSearch, regUsersSortBy, regUsersSortOrder, '', ''); }}
                                style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: '0.72rem' }}
                              >Clear</button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                </div>
                {/* Search box */}
                <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <input
                    type="text"
                    placeholder="Search by name, email, phone... (comma for multiple)"
                    value={regUsersSearch}
                    onChange={(e) => {
                      const val = e.target.value;
                      setRegUsersSearch(val);
                      if (regUsersSearchTimer.current) clearTimeout(regUsersSearchTimer.current);
                      regUsersSearchTimer.current = setTimeout(() => {
                        setRegUsersPage(1);
                        fetchRegisteredUsersList(1, val);
                      }, 350);
                    }}
                    style={{ padding: '0.4rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', width: '280px' }}
                  />
                  {regUsersSearch && (
                    <button
                      onClick={() => { setRegUsersSearch(''); setRegUsersPage(1); fetchRegisteredUsersList(1, ''); }}
                      style={{ padding: '0.4rem 0.65rem', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer' }}
                    >&times;</button>
                  )}
                </div>
              </div>

              {/* ── Row 2: Actions + Exports  |  Pagination ── */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '0.5rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {/* Delete (conditional) */}
                  {selectedUserIds.size > 0 && (
                    <button
                      onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '5px',
                        padding: '5px 12px', border: '1px solid #fca5a5', borderRadius: '8px',
                        background: '#fef2f2', color: '#dc2626', cursor: 'pointer',
                        fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.2s'
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fef2f2'; }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                      Delete ({selectedUserIds.size})
                    </button>
                  )}
                  {/* Restore */}
                  <button
                    onClick={() => { setShowRestorePanel(true); setRestoreResult(null); fetchDeletedUsers(); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '5px 12px', border: '1px solid #86efac', borderRadius: '8px',
                      background: '#f0fdf4', color: '#16a34a', cursor: 'pointer',
                      fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.2s'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                    Restore
                  </button>
                  {/* Divider */}
                  <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }} />
                  {/* Export XLSX */}
                  <button
                    onClick={handleExportUsersXlsx}
                    disabled={regUsersExporting || regUsersTotal === 0}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '5px 12px', border: '1px solid #93c5fd', borderRadius: '8px',
                      background: 'linear-gradient(135deg, #eff6ff, #dbeafe)', color: '#1d4ed8',
                      cursor: (regUsersExporting || regUsersTotal === 0) ? 'not-allowed' : 'pointer',
                      fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.2s',
                      opacity: (regUsersExporting || regUsersTotal === 0) ? 0.5 : 1,
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={e => { if (!regUsersExporting && regUsersTotal > 0) e.currentTarget.style.background = 'linear-gradient(135deg, #dbeafe, #bfdbfe)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #eff6ff, #dbeafe)'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    {regUsersExporting
                      ? 'Exporting...'
                      : selectedUserIds.size > 0
                        ? `Export ${selectedUserIds.size} Selected XLSX`
                        : `Export All ${regUsersTotal} XLSX`}
                  </button>
                  {/* Export Users w/o Applications */}
                  <button
                    onClick={handleExportUsersWithoutAppsXlsx}
                    disabled={regUsersNoAppsExporting}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '5px 12px', border: '1px solid #fdba74', borderRadius: '8px',
                      background: 'linear-gradient(135deg, #fff7ed, #ffedd5)', color: '#c2410c',
                      cursor: regUsersNoAppsExporting ? 'not-allowed' : 'pointer',
                      fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.2s',
                      opacity: regUsersNoAppsExporting ? 0.5 : 1,
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={e => { if (!regUsersNoAppsExporting) e.currentTarget.style.background = 'linear-gradient(135deg, #ffedd5, #fed7aa)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fff7ed, #ffedd5)'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    {regUsersNoAppsExporting ? 'Exporting...' : 'Export Users w/o Applications'}
                  </button>
                  {/* Divider */}
                  <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }} />
                  {/* Show/Hide Excluded Users toggle */}
                  <button
                    onClick={() => {
                      const newVal = !showExcludedUsers;
                      setShowExcludedUsers(newVal);
                      // Re-fetch from page 1 with server-side exclusion filter
                      setRegUsersPage(1);
                      fetchRegisteredUsersList(1, regUsersSearch, regUsersSortBy, regUsersSortOrder, dateRangeFrom, dateRangeTo, !newVal);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '5px 12px', border: `1px solid ${showExcludedUsers ? '#fca5a5' : '#86efac'}`, borderRadius: '8px',
                      background: showExcludedUsers ? 'linear-gradient(135deg, #fef2f2, #fee2e2)' : 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                      color: showExcludedUsers ? '#dc2626' : '#16a34a',
                      cursor: 'pointer',
                      fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.2s',
                      whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      {showExcludedUsers ? (
                        <><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></>
                      ) : (
                        <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                      )}
                    </svg>
                    {showExcludedUsers ? 'Hide Excluded' : 'Show Excluded'}
                    {excludedTotalCount > 0 && (
                      <span style={{
                        background: showExcludedUsers ? '#dc2626' : '#16a34a', color: '#fff',
                        fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: '8px', minWidth: '16px', textAlign: 'center'
                      }}>
                        {excludedTotalCount}
                      </span>
                    )}
                  </button>
                  {/* Show Qualified Users toggle */}
                  <button
                    onClick={() => {
                      const newVal = !showQualifiedOnly;
                      setShowQualifiedOnly(newVal);
                      // When qualified is ON, also force-hide excluded users
                      if (newVal && showExcludedUsers) {
                        setShowExcludedUsers(false);
                      }
                      setRegUsersPage(1);
                      fetchRegisteredUsersList(1, regUsersSearch, regUsersSortBy, regUsersSortOrder, dateRangeFrom, dateRangeTo, newVal ? true : undefined, newVal);
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      padding: '5px 12px', border: `1px solid ${showQualifiedOnly ? '#86efac' : '#cbd5e1'}`, borderRadius: '8px',
                      background: showQualifiedOnly ? 'linear-gradient(135deg, #f0fdf4, #dcfce7)' : '#fff',
                      color: showQualifiedOnly ? '#15803d' : '#64748b',
                      cursor: 'pointer',
                      fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                      boxShadow: showQualifiedOnly ? '0 0 0 1px rgba(34,197,94,0.2)' : 'none'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.opacity = '0.85'; }}
                    onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      {showQualifiedOnly ? (
                        <><path d="M22 11.08V12a10 10 0 11-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>
                      ) : (
                        <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>
                      )}
                    </svg>
                    {showQualifiedOnly ? 'Qualified Users' : 'Show Qualified'}
                  </button>
                  {/* Find & Fix User Names — admin only */}
                  {!isSubAdmin && (
                    <>
                      <div style={{ width: '1px', height: '20px', background: '#e2e8f0' }} />
                      <button
                        onClick={handleScanUserNames}
                        disabled={nameFixScanning}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '5px',
                          padding: '5px 12px', border: '1px solid #c4b5fd', borderRadius: '8px',
                          background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', color: '#7c3aed',
                          cursor: nameFixScanning ? 'not-allowed' : 'pointer',
                          fontSize: '0.78rem', fontWeight: 600, transition: 'all 0.2s',
                          opacity: nameFixScanning ? 0.6 : 1,
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={e => { if (!nameFixScanning) e.currentTarget.style.background = 'linear-gradient(135deg, #ede9fe, #ddd6fe)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #f5f3ff, #ede9fe)'; }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>
                        {nameFixScanning ? 'Scanning...' : 'Find & Fix User Names'}
                      </button>
                    </>
                  )}
                </div>
                {/* Pagination */}
                {regUsersTotalPages > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
                    <button
                      disabled={regUsersPage <= 1}
                      onClick={() => { const p = regUsersPage - 1; setRegUsersPage(p); fetchRegisteredUsersList(p, regUsersSearch); }}
                      style={{ padding: '0.3rem 0.55rem', border: '1px solid #e2e8f0', borderRadius: '5px', background: regUsersPage <= 1 ? '#f8fafc' : '#fff', color: regUsersPage <= 1 ? '#cbd5e1' : '#475569', cursor: regUsersPage <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.75rem' }}
                    >&laquo;</button>
                    {Array.from({ length: Math.min(5, regUsersTotalPages) }, (_, i) => {
                      let pageNum: number;
                      if (regUsersTotalPages <= 5) { pageNum = i + 1; }
                      else if (regUsersPage <= 3) { pageNum = i + 1; }
                      else if (regUsersPage >= regUsersTotalPages - 2) { pageNum = regUsersTotalPages - 4 + i; }
                      else { pageNum = regUsersPage - 2 + i; }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => { setRegUsersPage(pageNum); fetchRegisteredUsersList(pageNum, regUsersSearch); }}
                          style={{ padding: '0.3rem 0.55rem', border: pageNum === regUsersPage ? '1px solid #4A90E2' : '1px solid #e2e8f0', borderRadius: '5px', background: pageNum === regUsersPage ? '#4A90E2' : '#fff', color: pageNum === regUsersPage ? '#fff' : '#475569', cursor: 'pointer', fontSize: '0.75rem', fontWeight: pageNum === regUsersPage ? 600 : 400, minWidth: '28px' }}
                        >{pageNum}</button>
                      );
                    })}
                    <button
                      disabled={regUsersPage >= regUsersTotalPages}
                      onClick={() => { const p = regUsersPage + 1; setRegUsersPage(p); fetchRegisteredUsersList(p, regUsersSearch); }}
                      style={{ padding: '0.3rem 0.55rem', border: '1px solid #e2e8f0', borderRadius: '5px', background: regUsersPage >= regUsersTotalPages ? '#f8fafc' : '#fff', color: regUsersPage >= regUsersTotalPages ? '#cbd5e1' : '#475569', cursor: regUsersPage >= regUsersTotalPages ? 'not-allowed' : 'pointer', fontSize: '0.75rem' }}
                    >&raquo;</button>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8', marginLeft: '0.25rem' }}>{regUsersPage}/{regUsersTotalPages}</span>
                  </div>
                )}
              </div>

              {regUsersLoading ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading...</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '0.5rem 0.5rem', textAlign: 'center', width: '36px' }}>
                          <input
                            type="checkbox"
                            checked={registeredUsersList.length > 0 && selectedUserIds.size === registeredUsersList.length}
                            onChange={toggleSelectAll}
                            style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#4A90E2' }}
                          />
                        </th>
                        <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>#</th>
                        <th style={{ padding: '0.5rem 0.25rem', width: '36px' }}></th>
                        <th onClick={() => handleRegUsersSort('fullName')} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Name{regUsersSortIcon('fullName')}</th>
                        <th onClick={() => handleRegUsersSort('paidCount')} style={{ padding: '0.5rem 0.4rem', textAlign: 'center', fontWeight: 600, color: '#475569', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', width: '40px' }} title="Paid Applications">P{regUsersSortIcon('paidCount')}</th>
                        <th onClick={() => handleRegUsersSort('email')} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Email / Phone{regUsersSortIcon('email')}</th>
                        <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap', width: '80px' }}>Intent</th>
                        <th onClick={() => handleRegUsersSort('createdAt')} style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, color: '#475569', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Registered On{regUsersSortIcon('createdAt')}</th>
                        <th onClick={() => handleRegUsersSort('applicationCount')} style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600, color: '#475569', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Applications{regUsersSortIcon('applicationCount')}</th>
                        <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap', width: '60px' }}></th>
                      </tr>
                    </thead>
                    <AnimatePresence mode="popLayout">
                    <tbody>
                      {registeredUsersList.length === 0 ? (
                        <tr><td colSpan={10} style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>No registered users found.</td></tr>
                      ) : registeredUsersList.map((user, renderIdx) => {
                          const isOnline = !!onlineUserEmailMap[user.email];
                          const visitorKey = onlineUserEmailMap[user.email];
                          const isExcluded = !!recoveryExcludedMap[user._id];
                          const isRecentlyExcluded = recentlyExcludedIds.has(user._id);
                          return (
                        <motion.tr
                          key={user._id}
                          layout
                          initial={isRecentlyExcluded ? { opacity: 1, y: 0 } : { opacity: 0, y: 15 }}
                          animate={{
                            opacity: isExcluded ? 0.45 : 1,
                            y: 0,
                            transition: {
                              layout: { duration: isGlideAnimating ? 1.5 : 0.8, ease: [0.4, 0, 0.2, 1] },
                              opacity: { duration: isRecentlyExcluded ? 0.8 : 0.4, delay: isRecentlyExcluded ? 0 : renderIdx * 0.04 },
                              y: { duration: 0.4, delay: isRecentlyExcluded ? 0 : renderIdx * 0.04 },
                            }
                          }}
                          exit={{ opacity: 0, height: 0, transition: { duration: 0.35, ease: 'easeInOut' } }}
                          style={{
                            borderBottom: '1px solid #f1f5f9',
                            background: isExcluded
                              ? 'repeating-linear-gradient(135deg, transparent, transparent 10px, rgba(220,38,38,0.02) 10px, rgba(220,38,38,0.02) 20px)'
                              : selectedUserIds.has(user._id) ? '#eff6ff' : isOnline ? 'rgba(34,197,94,0.04)' : 'transparent',
                            position: 'relative',
                          }}
                          onMouseEnter={e => { if (!selectedUserIds.has(user._id) && !isExcluded) (e.currentTarget as HTMLElement).style.background = isOnline ? 'rgba(34,197,94,0.08)' : '#f8fafc'; }}
                          onMouseLeave={e => { if (!selectedUserIds.has(user._id) && !isExcluded) (e.currentTarget as HTMLElement).style.background = isOnline ? 'rgba(34,197,94,0.04)' : 'transparent'; }}
                        >
                          <td style={{ padding: '0.5rem 0.5rem', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedUserIds.has(user._id)}
                              onChange={() => toggleUserSelect(user._id)}
                              style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: '#4A90E2' }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', color: '#94a3b8' }}>{(regUsersPage - 1) * regUsersPageSize + renderIdx + 1}</td>
                          <td style={{ padding: '0.5rem 0.25rem', textAlign: 'center' }}>
                            {user.profileImageUrl ? (
                              <img
                                src={user.profileImageUrl}
                                alt=""
                                onClick={() => setProfileImgModal({ userId: user._id, imageUrl: user.profileImageUrl, userName: user.fullName || user.email })}
                                style={{
                                  width: '28px', height: '28px', borderRadius: '50%',
                                  objectFit: 'cover', display: 'block', margin: '0 auto',
                                  border: '1.5px solid rgba(74, 144, 226, 0.35)',
                                  boxShadow: '0 0 0 0.5px rgba(74, 144, 226, 0.1)',
                                  cursor: 'pointer', transition: 'transform 0.15s, box-shadow 0.15s',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.2)'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(74, 144, 226, 0.4)'; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 0 0 0.5px rgba(74, 144, 226, 0.1)'; }}
                              />
                            ) : (
                              <svg width="28" height="28" viewBox="0 0 28 28" fill="none" style={{ display: 'block', margin: '0 auto' }}>
                                <circle cx="14" cy="14" r="13" fill="#f1f5f9" stroke="rgba(148, 163, 184, 0.4)" strokeWidth="1.5" />
                                <circle cx="14" cy="11" r="4.5" fill="#cbd5e1" />
                                <path d="M6.5 23.5c0-4.14 3.36-7.5 7.5-7.5s7.5 3.36 7.5 7.5" fill="#cbd5e1" />
                              </svg>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', textDecoration: isExcluded ? 'line-through' : 'none', textDecorationColor: 'rgba(220,38,38,0.4)', transition: 'text-decoration 0.4s ease, color 0.4s ease', color: isExcluded ? '#94a3b8' : undefined }}>
                            <div style={{ fontWeight: 500 }}>{user.fullName || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>—</span>}</div>
                            <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '1px' }}>
                              last logged-in: {user.lastLogin
                                ? new Date(user.lastLogin).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' + new Date(user.lastLogin).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })
                                : '—'}
                            </div>
                            {isOnline && (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Prefer LiveSessionModal if user has an active application session (higher fidelity: HTTP+Socket dual recording)
                                  const matchedAppId = Object.keys(liveSessionsMap).find(
                                    appId => liveSessionsMap[appId].userEmail?.toLowerCase() === user.email?.toLowerCase()
                                  );
                                  if (matchedAppId) {
                                    setLiveSessionAppId(matchedAppId);
                                    setLiveSessionEmail(user.email);
                                  } else {
                                    // Fallback to VisitorLiveModal (user is browsing, not in application form)
                                    setRegUsersLiveVisitorId(visitorKey);
                                    setRegUsersLiveLabel(user.email);
                                  }
                                }}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                                  fontSize: '0.68rem', fontWeight: 600, color: '#16a34a',
                                  cursor: 'pointer', padding: '2px 8px', borderRadius: '10px',
                                  background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                                  transition: 'all 0.2s', marginTop: '3px',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34,197,94,0.2)'; e.currentTarget.style.borderColor = 'rgba(34,197,94,0.6)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(34,197,94,0.1)'; e.currentTarget.style.borderColor = 'rgba(34,197,94,0.3)'; }}
                              >
                                <span style={{
                                  width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e',
                                  boxShadow: '0 0 6px #22c55e',
                                  animation: 'liveDot 1.5s ease-in-out infinite',
                                }} />
                                View Live
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem 0.4rem', textAlign: 'center' }}>
                            {user.paidCount > 0 ? (
                              <span
                                title={`${user.paidCount} paid application${user.paidCount > 1 ? 's' : ''}`}
                                style={{
                                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 2,
                                  minWidth: 26, height: 26, borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                  color: '#fff', fontSize: '0.68rem', fontWeight: 700,
                                  boxShadow: '0 2px 6px rgba(34,197,94,0.35)',
                                  letterSpacing: '-0.3px',
                                  transition: 'transform 0.15s, box-shadow 0.15s',
                                  cursor: 'default',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.18)'; e.currentTarget.style.boxShadow = '0 3px 10px rgba(34,197,94,0.45)'; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(34,197,94,0.35)'; }}
                              >
                                {user.paidCount}P
                              </span>
                            ) : (
                              <span style={{ color: '#e2e8f0', fontSize: '0.7rem' }}>—</span>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', color: isExcluded ? '#94a3b8' : '#475569', textDecoration: isExcluded ? 'line-through' : 'none', textDecorationColor: 'rgba(220,38,38,0.4)', transition: 'all 0.4s ease' }}>
                            <div>{user.email}</div>
                            {user.phoneNumber && (
                              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: '1px' }}>{user.phoneNumber}</div>
                            )}
                          </td>
                          <td style={{ padding: '0.5rem 0.5rem', textAlign: 'center' }}>
                            {(() => {
                              const intentVal = user.intent?.intent || '';
                              const intentReason = user.intent?.reason || '';
                              const colors: Record<string, { bg: string; border: string; text: string; label: string }> = {
                                yes: { bg: '#dcfce7', border: '#86efac', text: '#166534', label: 'Yes' },
                                maybe: { bg: '#fef9c3', border: '#fde047', text: '#854d0e', label: 'Maybe' },
                                no: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', label: 'No' },
                              };
                              const c = colors[intentVal];
                              return (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  <button
                                    onClick={() => {
                                      setIntentEditUser({ email: user.email, current: intentVal, reason: intentReason });
                                      setIntentEditValue(intentVal);
                                      setIntentEditReason(intentReason);
                                    }}
                                    title={intentReason ? `${intentReason}\n\nClick to edit` : 'Click to set intent'}
                                    style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: c ? '2px 10px' : '2px 8px', borderRadius: 12,
                                      border: c ? `1px solid ${c.border}` : '1px dashed #cbd5e1',
                                      background: c ? c.bg : 'transparent',
                                      color: c ? c.text : '#94a3b8',
                                      fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer',
                                      transition: 'all 0.2s', whiteSpace: 'nowrap',
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = 'none'; }}
                                  >
                                    {c ? (
                                      <>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.text, opacity: 0.7 }} />
                                        {c.label}
                                      </>
                                    ) : (
                                      <span style={{ fontSize: '0.65rem' }}>—</span>
                                    )}
                                  </button>
                                  {intentReason && (
                                    <div style={{
                                      position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
                                      background: '#1e293b', color: '#f1f5f9', padding: '6px 10px', borderRadius: 8,
                                      fontSize: '0.68rem', lineHeight: 1.4, whiteSpace: 'pre-wrap', maxWidth: 240, minWidth: 120,
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.2)', pointerEvents: 'none', opacity: 0,
                                      transition: 'opacity 0.2s', zIndex: 50, marginBottom: 6,
                                    }}
                                      className="intent-tooltip"
                                    >
                                      {intentReason}
                                      <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '5px solid #1e293b' }} />
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', whiteSpace: 'nowrap' }}>
                            <div style={{ color: '#64748b' }}>{new Date(user.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                            <div style={{ fontSize: '0.65rem', color: '#94a3b8', marginTop: '1px' }}>{new Date(user.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</div>
                          </td>
                          <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                            <span
                              onClick={() => { if (user.applicationCount > 0) { setAppsModalPage(1); setRegUsersAppsModal(user); } }}
                              style={{
                                display: 'inline-block',
                                background: user.applicationCount > 0 ? '#dbeafe' : '#f1f5f9',
                                color: user.applicationCount > 0 ? '#2563eb' : '#94a3b8',
                                padding: '0.2rem 0.6rem',
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                cursor: user.applicationCount > 0 ? 'pointer' : 'default',
                                transition: 'transform 0.15s',
                              }}
                              onMouseEnter={e => { if (user.applicationCount > 0) (e.currentTarget as HTMLElement).style.transform = 'scale(1.15)'; }}
                              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'scale(1)'; }}
                            >
                              {user.applicationCount}
                            </span>
                          </td>
                          <td style={{ padding: '0.5rem 0.5rem', textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', alignItems: 'center' }}>
                            {recoveryExcludedMap[user._id] ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.6rem', color: '#dc2626', fontWeight: 600, padding: '2px 6px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', whiteSpace: 'nowrap' }} title="Excluded from App Recovery">
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                                  Excluded
                                </span>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Re-include "${user.fullName || user.email}" back into App Recovery?`)) {
                                      handleRecoveryInclude(user._id);
                                    }
                                  }}
                                  disabled={recoveryIncludeLoading === user._id}
                                  title="Re-include in App Recovery"
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 3,
                                    fontSize: '0.58rem', color: '#059669', fontWeight: 600,
                                    padding: '2px 7px', borderRadius: 8,
                                    background: '#ecfdf5', border: '1px solid #a7f3d0',
                                    cursor: recoveryIncludeLoading === user._id ? 'wait' : 'pointer',
                                    whiteSpace: 'nowrap', transition: 'all 0.2s',
                                    opacity: recoveryIncludeLoading === user._id ? 0.6 : 1,
                                  }}
                                  onMouseEnter={e => { e.currentTarget.style.background = '#d1fae5'; e.currentTarget.style.borderColor = '#6ee7b7'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background = '#ecfdf5'; e.currentTarget.style.borderColor = '#a7f3d0'; }}
                                >
                                  {recoveryIncludeLoading === user._id ? (
                                    <span style={{ width: 8, height: 8, border: '1.5px solid #a7f3d0', borderTopColor: '#059669', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} />
                                  ) : (
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                                  )}
                                  Re-include
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => { setRecoveryExcludeUser(user); setRecoveryExcludeReason(''); setRecoveryExcludeNote(''); }}
                                title="Exclude from App Recovery"
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                                  color: '#cbd5e1', borderRadius: '4px', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.color = '#f97316'; e.currentTarget.style.background = '#fff7ed'; }}
                                onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.background = 'none'; }}
                              >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                              </button>
                            )}
                            <button
                              onClick={() => { setSelectedUserIds(new Set([user._id])); setDeleteConfirmText(''); setShowDeleteModal(true); }}
                              title="Delete user"
                              style={{
                                background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                                color: '#cbd5e1', borderRadius: '4px', transition: 'all 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center'
                              }}
                              onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.background = 'none'; }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                            </button>
                            </div>
                          </td>
                        </motion.tr>
                          );
                        })}
                    </tbody>
                    </AnimatePresence>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {regUsersTotalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                  <button
                    disabled={regUsersPage <= 1}
                    onClick={() => { const p = regUsersPage - 1; setRegUsersPage(p); fetchRegisteredUsersList(p, regUsersSearch); }}
                    style={{ padding: '0.35rem 0.7rem', border: '1px solid #e2e8f0', borderRadius: '6px', background: regUsersPage <= 1 ? '#f8fafc' : '#fff', color: regUsersPage <= 1 ? '#cbd5e1' : '#475569', cursor: regUsersPage <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}
                  >&laquo; Prev</button>
                  {Array.from({ length: Math.min(7, regUsersTotalPages) }, (_, i) => {
                    let pageNum: number;
                    if (regUsersTotalPages <= 7) {
                      pageNum = i + 1;
                    } else if (regUsersPage <= 4) {
                      pageNum = i + 1;
                    } else if (regUsersPage >= regUsersTotalPages - 3) {
                      pageNum = regUsersTotalPages - 6 + i;
                    } else {
                      pageNum = regUsersPage - 3 + i;
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => { setRegUsersPage(pageNum); fetchRegisteredUsersList(pageNum, regUsersSearch); }}
                        style={{
                          padding: '0.35rem 0.65rem',
                          border: pageNum === regUsersPage ? '1px solid #4A90E2' : '1px solid #e2e8f0',
                          borderRadius: '6px',
                          background: pageNum === regUsersPage ? '#4A90E2' : '#fff',
                          color: pageNum === regUsersPage ? '#fff' : '#475569',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: pageNum === regUsersPage ? 600 : 400
                        }}
                      >{pageNum}</button>
                    );
                  })}
                  <button
                    disabled={regUsersPage >= regUsersTotalPages}
                    onClick={() => { const p = regUsersPage + 1; setRegUsersPage(p); fetchRegisteredUsersList(p, regUsersSearch); }}
                    style={{ padding: '0.35rem 0.7rem', border: '1px solid #e2e8f0', borderRadius: '6px', background: regUsersPage >= regUsersTotalPages ? '#f8fafc' : '#fff', color: regUsersPage >= regUsersTotalPages ? '#cbd5e1' : '#475569', cursor: regUsersPage >= regUsersTotalPages ? 'not-allowed' : 'pointer', fontSize: '0.8rem' }}
                  >Next &raquo;</button>
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8', marginLeft: '0.5rem' }}>Page {regUsersPage} of {regUsersTotalPages}</span>
                </div>
              )}

              {/* Applications Modal */}
              {regUsersAppsModal && (() => {
                const apps = regUsersAppsModal.applications;
                const totalModalPages = Math.ceil(apps.length / appsModalPageSize);
                const pagedApps = apps.slice((appsModalPage - 1) * appsModalPageSize, appsModalPage * appsModalPageSize);
                return (
                  <div
                    onClick={() => setRegUsersAppsModal(null)}
                    onKeyDown={e => { if (e.key === 'Escape') setRegUsersAppsModal(null); }}
                    tabIndex={-1}
                    ref={el => el?.focus()}
                    style={{
                      position: 'fixed', inset: 0, zIndex: 99999,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)',
                      animation: 'fadeIn 0.2s ease', outline: 'none'
                    }}
                  >
                    <div
                      onClick={e => e.stopPropagation()}
                      style={{
                        background: '#fff', borderRadius: '16px', padding: '0',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.18)', width: '94%', maxWidth: '720px',
                        animation: 'slideUp 0.25s ease', overflow: 'hidden'
                      }}
                    >
                      <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
                            {regUsersAppsModal.fullName || 'User'}'s Applications
                            <span style={{ fontWeight: 400, fontSize: '0.82rem', color: '#94a3b8', marginLeft: '8px' }}>({apps.length})</span>
                          </h3>
                          <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>{regUsersAppsModal.email}</p>
                        </div>
                        <button
                          onClick={() => setRegUsersAppsModal(null)}
                          style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#94a3b8', cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}
                        >&times;</button>
                      </div>
                      <div style={{ padding: '12px 24px 16px', overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                              <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>#</th>
                              <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Application ID</th>
                              <th style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Date</th>
                              <th style={{ padding: '6px 10px', textAlign: 'center', fontWeight: 600, color: '#64748b', fontSize: '0.75rem' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pagedApps.map((app, aIdx) => (
                              <tr key={aIdx} style={{ borderBottom: '1px solid #f8fafc' }}>
                                <td style={{ padding: '8px 10px', color: '#94a3b8', fontSize: '0.78rem' }}>{(appsModalPage - 1) * appsModalPageSize + aIdx + 1}</td>
                                <td style={{ padding: '8px 10px' }}>
                                  <span style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#334155', fontWeight: 600 }}>{app.applicationId}</span>
                                </td>
                                <td style={{ padding: '8px 10px', color: '#64748b', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                  {new Date(app.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                                  <span style={{
                                    padding: '3px 10px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: 700,
                                    textTransform: 'uppercase', whiteSpace: 'nowrap', letterSpacing: '0.02em',
                                    background:
                                      app.status === 'approved' ? '#dcfce7' :
                                      app.status === 'rejected' ? '#fee2e2' :
                                      app.status === 'payment_complete' ? '#f3e8ff' :
                                      app.status === 'in-review' ? '#fef3c7' :
                                      app.status === 'draft' || !app.status ? '#e0f2fe' : '#f1f5f9',
                                    color:
                                      app.status === 'approved' ? '#166534' :
                                      app.status === 'rejected' ? '#991b1b' :
                                      app.status === 'payment_complete' ? '#6b21a8' :
                                      app.status === 'in-review' ? '#92400e' :
                                      app.status === 'draft' || !app.status ? '#0369a1' : '#475569'
                                  }}>{app.status || 'draft'}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {totalModalPages > 1 && (
                        <div style={{ padding: '0 24px 16px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
                          <button
                            disabled={appsModalPage <= 1}
                            onClick={() => setAppsModalPage(p => p - 1)}
                            style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', background: appsModalPage <= 1 ? '#f8fafc' : '#fff', color: appsModalPage <= 1 ? '#cbd5e1' : '#475569', cursor: appsModalPage <= 1 ? 'not-allowed' : 'pointer', fontSize: '0.75rem' }}
                          >&laquo; Prev</button>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Page {appsModalPage} of {totalModalPages}</span>
                          <button
                            disabled={appsModalPage >= totalModalPages}
                            onClick={() => setAppsModalPage(p => p + 1)}
                            style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', background: appsModalPage >= totalModalPages ? '#f8fafc' : '#fff', color: appsModalPage >= totalModalPages ? '#cbd5e1' : '#475569', cursor: appsModalPage >= totalModalPages ? 'not-allowed' : 'pointer', fontSize: '0.75rem' }}
                          >Next &raquo;</button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ===== INTENT EDIT MODAL ===== */}
              {intentEditUser && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, backdropFilter: 'blur(2px)' }}
                  onClick={() => setIntentEditUser(null)}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    onClick={e => e.stopPropagation()}
                    style={{ background: '#fff', borderRadius: 16, padding: '24px 28px', width: '380px', maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
                    <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', marginBottom: 4 }}>Set User Intent</div>
                    <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 16 }}>{intentEditUser.email}</div>

                    {/* Intent selection */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                      {[
                        { value: 'yes', label: 'Yes', bg: '#dcfce7', border: '#86efac', text: '#166534', emoji: '✓' },
                        { value: 'maybe', label: 'Maybe', bg: '#fef9c3', border: '#fde047', text: '#854d0e', emoji: '?' },
                        { value: 'no', label: 'No', bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', emoji: '✕' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setIntentEditValue(opt.value)}
                          style={{
                            flex: 1, padding: '10px 8px', borderRadius: 10, cursor: 'pointer',
                            border: intentEditValue === opt.value ? `2px solid ${opt.text}` : `1.5px solid ${opt.border}`,
                            background: intentEditValue === opt.value ? opt.bg : '#fff',
                            color: opt.text, fontWeight: 700, fontSize: '0.85rem',
                            transition: 'all 0.2s', transform: intentEditValue === opt.value ? 'scale(1.05)' : 'scale(1)',
                            boxShadow: intentEditValue === opt.value ? `0 2px 12px ${opt.border}80` : 'none',
                          }}
                        >
                          <div style={{ fontSize: '1.1rem', marginBottom: 2 }}>{opt.emoji}</div>
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {/* Reason */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Reason / Comment</label>
                      <textarea
                        value={intentEditReason}
                        onChange={e => setIntentEditReason(e.target.value)}
                        placeholder="e.g., Interested but waiting for docs, Not picking calls..."
                        rows={3}
                        style={{ width: '100%', padding: '8px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: '0.82rem', resize: 'vertical', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                        onFocus={e => { e.currentTarget.style.borderColor = '#6366f1'; }}
                        onBlur={e => { e.currentTarget.style.borderColor = '#e2e8f0'; }}
                      />
                    </div>

                    {/* Clear option */}
                    {intentEditUser.current && (
                      <button
                        onClick={() => { setIntentEditValue(''); setIntentEditReason(''); }}
                        style={{ fontSize: '0.72rem', color: '#94a3b8', background: 'none', border: 'none', cursor: 'pointer', marginBottom: 12, textDecoration: 'underline' }}
                      >Clear intent</button>
                    )}

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => setIntentEditUser(null)}
                        style={{ padding: '8px 18px', border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', color: '#64748b', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}
                      >Cancel</button>
                      <button
                        onClick={() => handleIntentSave(intentEditUser.email, intentEditValue, intentEditReason)}
                        disabled={intentEditSaving}
                        style={{
                          padding: '8px 22px', border: 'none', borderRadius: 8,
                          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff',
                          fontSize: '0.82rem', fontWeight: 600, cursor: intentEditSaving ? 'wait' : 'pointer',
                          opacity: intentEditSaving ? 0.7 : 1, transition: 'all 0.2s',
                        }}
                      >{intentEditSaving ? 'Saving...' : 'Save'}</button>
                    </div>
                  </motion.div>
                </div>
              )}

              {/* ===== RECOVERY EXCLUDE MODAL ===== */}
              {recoveryExcludeUser && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setRecoveryExcludeUser(null)}>
                  <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: 440, maxWidth: '90vw', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 15, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
                          Exclude from App Recovery
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                          <strong>{recoveryExcludeUser.fullName || '—'}</strong> — {recoveryExcludeUser.email}
                        </div>
                      </div>
                      <button onClick={() => setRecoveryExcludeUser(null)} style={{ background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer', padding: '0 4px' }}>&times;</button>
                    </div>
                    <div style={{ padding: 20 }}>
                      <div style={{ marginBottom: 14 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>
                          Reason for Exclusion <span style={{ color: '#dc2626' }}>*</span>
                        </label>
                        <select value={recoveryExcludeReason} onChange={e => setRecoveryExcludeReason(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc', color: '#334155', outline: 'none' }}>
                          <option value="">Select a reason...</option>
                          <option value="location_not_eligible">Location Not Eligible</option>
                          <option value="not_interested">User Not Interested</option>
                          <option value="duplicate_account">Duplicate Account</option>
                          <option value="already_applied_elsewhere">Already Applied Elsewhere</option>
                          <option value="incomplete_documents">Cannot Provide Required Documents</option>
                          <option value="business_not_eligible">Business/Vehicle Not Eligible</option>
                          <option value="user_requested">User Requested Removal</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Additional Notes (optional)</label>
                        <textarea value={recoveryExcludeNote} onChange={e => setRecoveryExcludeNote(e.target.value)} placeholder="Add context..." rows={2} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc', color: '#334155', resize: 'vertical', fontFamily: 'inherit', outline: 'none' }} />
                      </div>
                      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: '#92400e' }}>
                        This user will not receive any recovery or outreach emails. You can re-include them from the App Recovery page.
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => setRecoveryExcludeUser(null)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
                        <button onClick={handleRecoveryExclude} disabled={!recoveryExcludeReason || recoveryExcludeLoading} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: recoveryExcludeReason ? '#dc2626' : '#fca5a5', color: '#fff', fontSize: 12, fontWeight: 600, cursor: recoveryExcludeReason ? 'pointer' : 'not-allowed', opacity: recoveryExcludeReason ? 1 : 0.6 }}>
                          {recoveryExcludeLoading ? 'Excluding...' : 'Exclude User'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ===== DELETE CONFIRMATION MODAL ===== */}
              {showDeleteModal && (() => {
                const visibleToDelete = registeredUsersList.filter(u => selectedUserIds.has(u._id));
                const totalToDelete = selectedUserIds.size;
                const extraCount = totalToDelete - visibleToDelete.length;
                return (
                  <div
                    onClick={() => { if (!deleteInProgress) { setShowDeleteModal(false); setDeleteConfirmText(''); } }}
                    style={{
                      position: 'fixed', inset: 0, zIndex: 99999,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)',
                      animation: 'fadeIn 0.2s ease'
                    }}
                  >
                    <div
                      onClick={e => e.stopPropagation()}
                      style={{
                        background: '#fff', borderRadius: '16px', width: '94%', maxWidth: '440px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
                        animation: 'slideUp 0.25s ease'
                      }}
                    >
                      {/* Header */}
                      <div style={{ padding: '20px 24px 0', textAlign: 'center' }}>
                        <div style={{
                          width: '48px', height: '48px', borderRadius: '50%', background: '#fef2f2',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px'
                        }}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#1e293b' }}>
                          Delete {totalToDelete} User{totalToDelete > 1 ? 's' : ''}?
                        </h3>
                        <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#64748b', lineHeight: 1.5 }}>
                          This is a soft delete. Users will be backed up and can be restored later.
                        </p>
                      </div>

                      {/* Users list preview */}
                      <div style={{ padding: '12px 24px', maxHeight: '140px', overflowY: 'auto' }}>
                        {visibleToDelete.map(u => (
                          <div key={u._id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', borderBottom: '1px solid #f8fafc' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#dc2626', flexShrink: 0 }}>
                              {(u.fullName || u.email)[0]?.toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.fullName || 'No name'}</div>
                              <div style={{ fontSize: '0.72rem', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
                            </div>
                            {u.applicationCount > 0 && (
                              <span style={{ marginLeft: 'auto', background: '#fef3c7', color: '#92400e', padding: '1px 8px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 600, flexShrink: 0 }}>
                                {u.applicationCount} app{u.applicationCount > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        ))}
                        {extraCount > 0 && (
                          <div style={{ padding: '6px 0', fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic', textAlign: 'center' }}>
                            + {extraCount} more user{extraCount > 1 ? 's' : ''} from other pages
                          </div>
                        )}
                      </div>

                      {/* Type DELETE to confirm */}
                      <div style={{ padding: '0 24px 16px' }}>
                        <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: '6px' }}>
                          Type <span style={{ color: '#dc2626', fontFamily: 'monospace', fontWeight: 700, letterSpacing: '1px' }}>DELETE</span> to confirm
                        </label>
                        <input
                          type="text"
                          value={deleteConfirmText}
                          onChange={e => setDeleteConfirmText(e.target.value)}
                          placeholder="DELETE"
                          autoFocus
                          style={{
                            width: '100%', padding: '10px 14px', border: deleteConfirmText === 'DELETE' ? '2px solid #dc2626' : '1px solid #e2e8f0',
                            borderRadius: '10px', fontSize: '0.9rem', fontFamily: 'monospace', letterSpacing: '2px',
                            textAlign: 'center', outline: 'none', transition: 'border-color 0.2s',
                            background: deleteConfirmText === 'DELETE' ? '#fef2f2' : '#fff'
                          }}
                          onKeyDown={e => { if (e.key === 'Enter' && deleteConfirmText === 'DELETE') handleDeleteUsers(); }}
                        />
                      </div>

                      {/* Actions */}
                      <div style={{ display: 'flex', gap: '8px', padding: '0 24px 20px' }}>
                        <button
                          onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
                          disabled={deleteInProgress}
                          style={{
                            flex: 1, padding: '10px', border: '1px solid #e2e8f0', borderRadius: '10px',
                            background: '#fff', color: '#475569', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600
                          }}
                        >Cancel</button>
                        <button
                          onClick={handleDeleteUsers}
                          disabled={deleteConfirmText !== 'DELETE' || deleteInProgress}
                          style={{
                            flex: 1, padding: '10px', border: 'none', borderRadius: '10px',
                            background: deleteConfirmText === 'DELETE' ? '#dc2626' : '#fca5a5',
                            color: '#fff', cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed',
                            fontSize: '0.85rem', fontWeight: 700, transition: 'all 0.2s',
                            opacity: deleteInProgress ? 0.7 : 1
                          }}
                        >{deleteInProgress ? 'Deleting...' : `Delete ${totalToDelete} User${totalToDelete > 1 ? 's' : ''}`}</button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ===== RESTORE PANEL MODAL ===== */}
              {showRestorePanel && (
                <div
                  onClick={() => { if (!restoreInProgress) { setShowRestorePanel(false); setRestoreResult(null); } }}
                  style={{
                    position: 'fixed', inset: 0, zIndex: 99999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)',
                    animation: 'fadeIn 0.2s ease'
                  }}
                >
                  <div
                    onClick={e => e.stopPropagation()}
                    style={{
                      background: '#fff', borderRadius: '16px', width: '94%', maxWidth: '600px',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
                      animation: 'slideUp 0.25s ease', maxHeight: '80vh', display: 'flex', flexDirection: 'column'
                    }}
                  >
                    {/* Header */}
                    <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                          Restore Deleted Users
                        </h3>
                        <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#94a3b8' }}>{deletedUsersList.length} deleted user{deletedUsersList.length !== 1 ? 's' : ''} available for restoration</p>
                      </div>
                      <button onClick={() => { setShowRestorePanel(false); setRestoreResult(null); }} style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: '#94a3b8', cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}>&times;</button>
                    </div>

                    {/* Restore result banner */}
                    {restoreResult && (
                      <div style={{ padding: '12px 24px', background: '#f0fdf4', borderBottom: '1px solid #bbf7d0' }}>
                        {restoreResult.restored.length > 0 && (
                          <div style={{ fontSize: '0.82rem', color: '#166534', fontWeight: 600, marginBottom: restoreResult.skipped.length > 0 ? '6px' : 0 }}>
                            ✓ Restored {restoreResult.restored.length} user{restoreResult.restored.length > 1 ? 's' : ''}:
                            <span style={{ fontWeight: 400, marginLeft: '6px' }}>
                              {restoreResult.restored.map((u: any) => u.fullName || u.email).join(', ')}
                            </span>
                          </div>
                        )}
                        {restoreResult.skipped.length > 0 && (
                          <div style={{ fontSize: '0.78rem', color: '#92400e', background: '#fef3c7', padding: '4px 8px', borderRadius: '6px', marginTop: '4px' }}>
                            ⚠ Skipped {restoreResult.skipped.length}: {restoreResult.skipped.map((s: any) => `${s.email} (${s.reason})`).join(', ')}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Deleted users list */}
                    <div style={{ overflowY: 'auto', flex: 1, padding: '0' }}>
                      {deletedUsersLoading ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading...</div>
                      ) : deletedUsersList.length === 0 ? (
                        <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" style={{ display: 'block', margin: '0 auto 8px' }}><path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                          No deleted users to restore
                        </div>
                      ) : (
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                          <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', position: 'sticky', top: 0 }}>
                              <th style={{ padding: '8px 12px', textAlign: 'center', width: '36px' }}>
                                <input
                                  type="checkbox"
                                  checked={deletedUsersList.length > 0 && selectedRestoreIds.size === deletedUsersList.length}
                                  onChange={() => {
                                    if (selectedRestoreIds.size === deletedUsersList.length) setSelectedRestoreIds(new Set());
                                    else setSelectedRestoreIds(new Set(deletedUsersList.map((u: any) => u._id)));
                                  }}
                                  style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#16a34a' }}
                                />
                              </th>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Name</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Email</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#475569' }}>Apps</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Deleted On</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>By</th>
                            </tr>
                          </thead>
                          <tbody>
                            {deletedUsersList.map((du: any, idx: number) => (
                              <tr
                                key={du._id}
                                style={{
                                  borderBottom: '1px solid #f1f5f9',
                                  background: selectedRestoreIds.has(du._id) ? '#f0fdf4' : 'transparent',
                                  transition: 'background 0.15s',
                                  opacity: 0, animation: `subAdminFadeSlideIn 0.3s ease forwards`, animationDelay: `${idx * 0.03}s`
                                }}
                                onMouseEnter={e => { if (!selectedRestoreIds.has(du._id)) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { if (!selectedRestoreIds.has(du._id)) e.currentTarget.style.background = 'transparent'; }}
                              >
                                <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedRestoreIds.has(du._id)}
                                    onChange={() => {
                                      setSelectedRestoreIds(prev => {
                                        const next = new Set(prev);
                                        if (next.has(du._id)) next.delete(du._id); else next.add(du._id);
                                        return next;
                                      });
                                    }}
                                    style={{ width: '14px', height: '14px', cursor: 'pointer', accentColor: '#16a34a' }}
                                  />
                                </td>
                                <td style={{ padding: '8px', fontWeight: 500, color: '#1e293b' }}>{du.fullName || <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>—</span>}</td>
                                <td style={{ padding: '8px', color: '#475569', fontSize: '0.78rem' }}>{du.email}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>
                                  <span style={{ background: du.applicationCount > 0 ? '#dbeafe' : '#f1f5f9', color: du.applicationCount > 0 ? '#2563eb' : '#94a3b8', padding: '2px 8px', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 600 }}>{du.applicationCount}</span>
                                </td>
                                <td style={{ padding: '8px', color: '#64748b', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{new Date(du.deletedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</td>
                                <td style={{ padding: '8px', color: '#94a3b8', fontSize: '0.72rem' }}>{du.deletedBy || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Restore action bar */}
                    {deletedUsersList.length > 0 && (
                      <div style={{ padding: '12px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                          {selectedRestoreIds.size > 0 ? (
                            <><strong style={{ color: '#16a34a' }}>{selectedRestoreIds.size}</strong> selected for restoration</>
                          ) : 'Select users to restore'}
                        </span>
                        <button
                          onClick={handleRestoreUsers}
                          disabled={selectedRestoreIds.size === 0 || restoreInProgress}
                          style={{
                            padding: '8px 20px', border: 'none', borderRadius: '10px',
                            background: selectedRestoreIds.size > 0 ? '#16a34a' : '#d1d5db',
                            color: '#fff', cursor: selectedRestoreIds.size > 0 ? 'pointer' : 'not-allowed',
                            fontSize: '0.82rem', fontWeight: 700, transition: 'all 0.2s',
                            opacity: restoreInProgress ? 0.7 : 1
                          }}
                        >
                          {restoreInProgress ? 'Restoring...' : `Restore ${selectedRestoreIds.size > 0 ? selectedRestoreIds.size : ''} User${selectedRestoreIds.size !== 1 ? 's' : ''}`}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ===== PROFILE IMAGE VIEWER / CROP MODAL ===== */}
              {profileImgModal && (() => {
                const closeModal = () => {
                  setProfileImgModal(null);
                  setProfileImgZoom(1);
                  setProfileImgPos({ x: 0, y: 0 });
                  setProfileImgCropping(false);
                  setProfileImgCropRect(null);
                  setProfileImgSaving(false);
                  setProfileImgCropHistory([]);
                  setProfileImgCropRedoStack([]);
                  setProfileImgShowPreview(false);
                  setProfileImgRotation(0);
                  setProfileImgRotSaving(false);
                };

                const handleWheel = (e: React.WheelEvent) => {
                  if (profileImgCropping) return;
                  e.preventDefault();
                  const delta = e.deltaY > 0 ? -0.15 : 0.15;
                  setProfileImgZoom(z => Math.max(0.25, Math.min(8, z + delta)));
                };

                const handlePointerDown = (e: React.PointerEvent) => {
                  if (profileImgCropping) {
                    // Start crop rectangle
                    const rect = profileImgContainerRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    const sx = e.clientX - rect.left;
                    const sy = e.clientY - rect.top;
                    setProfileImgCropRect({ startX: sx, startY: sy, endX: sx, endY: sy });
                    setProfileImgCropDragging(true);
                    (e.target as HTMLElement).setPointerCapture(e.pointerId);
                  } else {
                    // Start panning
                    setProfileImgDragging(true);
                    setProfileImgDragStart({ x: e.clientX - profileImgPos.x, y: e.clientY - profileImgPos.y });
                    (e.target as HTMLElement).setPointerCapture(e.pointerId);
                  }
                };

                const handlePointerMove = (e: React.PointerEvent) => {
                  if (profileImgCropDragging && profileImgCropRect) {
                    const rect = profileImgContainerRef.current?.getBoundingClientRect();
                    if (!rect) return;
                    setProfileImgCropRect(prev => prev ? { ...prev, endX: e.clientX - rect.left, endY: e.clientY - rect.top } : null);
                  } else if (profileImgDragging) {
                    setProfileImgPos({ x: e.clientX - profileImgDragStart.x, y: e.clientY - profileImgDragStart.y });
                  }
                };

                const handlePointerUp = () => {
                  if (profileImgCropDragging && profileImgCropRect) {
                    const w = Math.abs(profileImgCropRect.endX - profileImgCropRect.startX);
                    const h = Math.abs(profileImgCropRect.endY - profileImgCropRect.startY);
                    if (w > 10 && h > 10) {
                      setProfileImgCropHistory(prev => [...prev, profileImgCropRect]);
                      setProfileImgCropRedoStack([]);
                    }
                  }
                  setProfileImgDragging(false);
                  setProfileImgCropDragging(false);
                };

                const handleCropUndo = () => {
                  if (profileImgCropHistory.length === 0) return;
                  const prev = [...profileImgCropHistory];
                  const last = prev.pop()!;
                  setProfileImgCropRedoStack(rs => [...rs, profileImgCropRect || last]);
                  setProfileImgCropRect(prev.length > 0 ? prev[prev.length - 1] : null);
                  setProfileImgCropHistory(prev);
                };

                const handleCropRedo = () => {
                  if (profileImgCropRedoStack.length === 0) return;
                  const stack = [...profileImgCropRedoStack];
                  const next = stack.pop()!;
                  if (profileImgCropRect) {
                    setProfileImgCropHistory(h => [...h, profileImgCropRect]);
                  }
                  setProfileImgCropRect(next);
                  setProfileImgCropRedoStack(stack);
                };

                // Helper: compute the actual visual size & position of the image in the viewport
                const getImageVisualRect = () => {
                  const container = profileImgContainerRef.current;
                  if (!container || profileImgNaturalSize.w === 0) return null;
                  const cRect = container.getBoundingClientRect();
                  const cW = cRect.width;
                  const cH = cRect.height;
                  // The image has maxWidth:100%, maxHeight:100% so it fits within container at zoom=1
                  const fitScale = Math.min(1, cW / profileImgNaturalSize.w, cH / profileImgNaturalSize.h);
                  const baseW = profileImgNaturalSize.w * fitScale;
                  const baseH = profileImgNaturalSize.h * fitScale;
                  // After CSS scale(zoom) around center
                  const visW = baseW * profileImgZoom;
                  const visH = baseH * profileImgZoom;
                  // Visual top-left in viewport coordinates
                  const visLeft = (cW / 2) + profileImgPos.x - (visW / 2);
                  const visTop = (cH / 2) + profileImgPos.y - (visH / 2);
                  return { visW, visH, visLeft, visTop };
                };

                const handleSaveCrop = async () => {
                  if (!profileImgCropRect || !profileImgContainerRef.current || profileImgNaturalSize.w === 0) return;
                  setProfileImgSaving(true);
                  try {
                    const vis = getImageVisualRect();
                    if (!vis) return;

                    // Normalize crop rect (handle any drag direction)
                    const cropLeft = Math.min(profileImgCropRect.startX, profileImgCropRect.endX);
                    const cropTop = Math.min(profileImgCropRect.startY, profileImgCropRect.endY);
                    const cropW = Math.abs(profileImgCropRect.endX - profileImgCropRect.startX);
                    const cropH = Math.abs(profileImgCropRect.endY - profileImgCropRect.startY);

                    // Convert screen crop to natural image pixels
                    const scale = profileImgNaturalSize.w / vis.visW;
                    const natX = (cropLeft - vis.visLeft) * scale;
                    const natY = (cropTop - vis.visTop) * scale;
                    const natW = cropW * scale;
                    const natH = cropH * scale;

                    const response = await fetch(`${API_BASE}/api/admin/users/${profileImgModal.userId}/crop-profile-image`, {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ x: natX, y: natY, width: natW, height: natH, rotation: profileImgRotation }),
                    });
                    const data = await response.json();
                    if (data.success) {
                      // Force refresh the thumbnail by cache-busting
                      const newUrl = profileImgModal.imageUrl.split('?')[0] + '?t=' + Date.now();
                      setProfileImgModal(prev => prev ? { ...prev, imageUrl: newUrl } : null);
                      setProfileImgCropping(false);
                      setProfileImgCropRect(null);
                      setProfileImgZoom(1);
                      setProfileImgPos({ x: 0, y: 0 });
                      setProfileImgCropHistory([]);
                      setProfileImgCropRedoStack([]);
                      setProfileImgShowPreview(false);
                      setProfileImgRotation(0);
                      // Also refresh the list to update thumbnails
                      fetchRegisteredUsersList(regUsersPage, regUsersSearch);
                    } else {
                      alert(data.message || 'Failed to crop image.');
                    }
                  } catch (err) {
                    alert('Failed to save crop. Please try again.');
                  } finally {
                    setProfileImgSaving(false);
                  }
                };

                const handleSaveRotation = async () => {
                  if (profileImgRotation === 0 || !profileImgModal) return;
                  setProfileImgRotSaving(true);
                  try {
                    const response = await fetch(`${API_BASE}/api/admin/users/${profileImgModal.userId}/rotate-profile-image`, {
                      method: 'POST',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ rotation: profileImgRotation }),
                    });
                    const data = await response.json();
                    if (data.success) {
                      const newUrl = profileImgModal.imageUrl.split('?')[0] + '?t=' + Date.now();
                      setProfileImgModal(prev => prev ? { ...prev, imageUrl: newUrl } : null);
                      setProfileImgRotation(0);
                      setProfileImgZoom(1);
                      setProfileImgPos({ x: 0, y: 0 });
                      fetchRegisteredUsersList(regUsersPage, regUsersSearch);
                    } else {
                      alert(data.message || 'Failed to rotate image.');
                    }
                  } catch {
                    alert('Failed to save rotation. Please try again.');
                  } finally {
                    setProfileImgRotSaving(false);
                  }
                };

                // Normalized crop rect for rendering
                const cropBox = profileImgCropRect ? {
                  left: Math.min(profileImgCropRect.startX, profileImgCropRect.endX),
                  top: Math.min(profileImgCropRect.startY, profileImgCropRect.endY),
                  width: Math.abs(profileImgCropRect.endX - profileImgCropRect.startX),
                  height: Math.abs(profileImgCropRect.endY - profileImgCropRect.startY),
                } : null;

                return (
                  <div
                    onClick={closeModal}
                    onKeyDown={e => { if (e.key === 'Escape') closeModal(); }}
                    tabIndex={-1}
                    ref={el => el?.focus()}
                    style={{
                      position: 'fixed', inset: 0, zIndex: 99999,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
                      animation: 'fadeIn 0.2s ease', outline: 'none',
                    }}
                  >
                    <div
                      onClick={e => e.stopPropagation()}
                      style={{
                        background: '#fff', borderRadius: '16px', overflow: 'hidden',
                        boxShadow: '0 24px 80px rgba(0,0,0,0.25)', width: '640px', maxWidth: '94vw',
                        animation: 'slideUp 0.25s ease',
                      }}
                    >
                      {/* Header row — name + close */}
                      <div style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '10px 18px 6px',
                      }}>
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{profileImgModal.userName}</div>
                          <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>Profile Photo</div>
                        </div>
                        <button onClick={closeModal} title="Close"
                          style={{
                            width: '28px', height: '28px', border: 'none', borderRadius: '50%',
                            background: '#f1f5f9', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b',
                            transition: 'all 0.15s', flexShrink: 0,
                          }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#dc2626'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>

                      {/* Toolbar row */}
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: '5px', padding: '6px 18px 10px', borderBottom: '1px solid #f1f5f9',
                        flexWrap: 'wrap',
                      }}>
                        {/* Zoom controls */}
                        <button onClick={() => setProfileImgZoom(z => Math.max(0.25, z - 0.25))} title="Zoom Out"
                          style={{ width: '28px', height: '28px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#475569' }}>−</button>
                        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: '#64748b', minWidth: '32px', textAlign: 'center' }}>{Math.round(profileImgZoom * 100)}%</span>
                        <button onClick={() => setProfileImgZoom(z => Math.min(8, z + 0.25))} title="Zoom In"
                          style={{ width: '28px', height: '28px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#475569' }}>+</button>
                        <button onClick={() => { setProfileImgZoom(1); setProfileImgPos({ x: 0, y: 0 }); setProfileImgRotation(0); }} title="Reset View"
                          style={{ width: '28px', height: '28px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 12a9 9 0 119 9"/><polyline points="3,4 3,12 11,12"/></svg>
                        </button>

                        <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 1px' }} />

                        {/* Download */}
                        <button
                          onClick={async () => {
                            try {
                              const resp = await fetch(profileImgModal.imageUrl);
                              const blob = await resp.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url;
                              const ext = profileImgModal.imageUrl.split('.').pop()?.split('?')[0] || 'jpg';
                              a.download = `${profileImgModal.userName.replace(/\s+/g, '_')}_profile.${ext}`;
                              document.body.appendChild(a);
                              a.click();
                              document.body.removeChild(a);
                              URL.revokeObjectURL(url);
                            } catch { alert('Failed to download image.'); }
                          }}
                          title="Download Image"
                          style={{
                            height: '28px', padding: '0 8px', border: '1px solid #e2e8f0', borderRadius: '8px',
                            background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                            fontSize: '0.7rem', fontWeight: 600, color: '#475569',
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7,10 12,15 17,10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                          Download
                        </button>

                        <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 1px' }} />

                        {/* Rotate buttons (admin only) */}
                        {!isSubAdmin && (
                          <>
                            <button
                              onClick={() => setProfileImgRotation(r => ((r - 90) % 360 + 360) % 360)}
                              title="Rotate Counter-Clockwise"
                              style={{
                                width: '28px', height: '28px', border: '1px solid #e2e8f0', borderRadius: '8px',
                                background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569',
                              }}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3.51 15a9 9 0 105.64-11.36L1 10"/><polyline points="1,4 1,10 7,10"/></svg>
                            </button>
                            <button
                              onClick={() => setProfileImgRotation(r => (r + 90) % 360)}
                              title="Rotate Clockwise"
                              style={{
                                width: '28px', height: '28px', border: '1px solid #e2e8f0', borderRadius: '8px',
                                background: '#f8fafc', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#475569',
                              }}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20.49 15a9 9 0 11-5.64-11.36L23 10"/><polyline points="23,4 23,10 17,10"/></svg>
                            </button>

                            {/* Save Rotation — only when rotated and not cropping */}
                            {profileImgRotation !== 0 && !profileImgCropping && (
                              <button
                                onClick={handleSaveRotation}
                                disabled={profileImgRotSaving}
                                title="Save Rotation"
                                style={{
                                  height: '28px', padding: '0 8px', border: '1px solid #86efac',
                                  borderRadius: '8px', background: profileImgRotSaving ? '#bbf7d0' : '#f0fdf4',
                                  cursor: profileImgRotSaving ? 'not-allowed' : 'pointer',
                                  display: 'flex', alignItems: 'center', gap: '4px',
                                  fontSize: '0.7rem', fontWeight: 600, color: '#16a34a',
                                }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                                {profileImgRotSaving ? 'Saving...' : `Save ${profileImgRotation}°`}
                              </button>
                            )}

                            <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 1px' }} />
                          </>
                        )}

                        {/* Crop toggle (admin only) */}
                        {!isSubAdmin && (
                          <button
                            onClick={() => {
                              const newCropping = !profileImgCropping;
                              setProfileImgCropping(newCropping);
                              setProfileImgCropRect(null);
                              if (!newCropping) {
                                setProfileImgCropHistory([]);
                                setProfileImgCropRedoStack([]);
                                setProfileImgShowPreview(false);
                              }
                            }}
                            title={profileImgCropping ? 'Cancel Crop' : 'Crop Image'}
                            style={{
                              height: '28px', padding: '0 8px', border: '1px solid', borderRadius: '8px',
                              background: profileImgCropping ? '#fef2f2' : '#f8fafc',
                              borderColor: profileImgCropping ? '#fca5a5' : '#e2e8f0',
                              color: profileImgCropping ? '#dc2626' : '#475569',
                              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.7rem', fontWeight: 600,
                            }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 2v14a2 2 0 002 2h14"/><path d="M18 22V8a2 2 0 00-2-2H2"/></svg>
                            {profileImgCropping ? 'Cancel' : 'Crop'}
                          </button>
                        )}

                        {/* Undo / Redo (only when cropping) */}
                        {!isSubAdmin && profileImgCropping && (
                          <>
                            <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 1px' }} />
                            <button onClick={handleCropUndo} disabled={profileImgCropHistory.length === 0} title="Undo crop"
                              style={{
                                width: '28px', height: '28px', border: '1px solid #e2e8f0', borderRadius: '8px',
                                background: profileImgCropHistory.length > 0 ? '#f8fafc' : '#f1f5f9',
                                cursor: profileImgCropHistory.length > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: profileImgCropHistory.length > 0 ? '#475569' : '#cbd5e1',
                                opacity: profileImgCropHistory.length > 0 ? 1 : 0.5,
                              }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="1,4 1,10 7,10"/><path d="M3.51 15a9 9 0 105.64-11.36L1 10"/></svg>
                            </button>
                            <button onClick={handleCropRedo} disabled={profileImgCropRedoStack.length === 0} title="Redo crop"
                              style={{
                                width: '28px', height: '28px', border: '1px solid #e2e8f0', borderRadius: '8px',
                                background: profileImgCropRedoStack.length > 0 ? '#f8fafc' : '#f1f5f9',
                                cursor: profileImgCropRedoStack.length > 0 ? 'pointer' : 'not-allowed',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: profileImgCropRedoStack.length > 0 ? '#475569' : '#cbd5e1',
                                opacity: profileImgCropRedoStack.length > 0 ? 1 : 0.5,
                              }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="23,4 23,10 17,10"/><path d="M20.49 15a9 9 0 11-5.64-11.36L23 10"/></svg>
                            </button>
                          </>
                        )}
                      </div>

                      {/* Image viewport */}
                      <div
                        ref={profileImgContainerRef}
                        onWheel={handleWheel}
                        onPointerDown={handlePointerDown}
                        onPointerMove={handlePointerMove}
                        onPointerUp={handlePointerUp}
                        style={{
                          width: '100%', height: '380px', overflow: 'hidden', position: 'relative',
                          background: 'repeating-conic-gradient(#f1f5f9 0% 25%, #fff 0% 50%) 50% / 20px 20px',
                          cursor: profileImgCropping ? 'crosshair' : (profileImgDragging ? 'grabbing' : 'grab'),
                          userSelect: 'none', touchAction: 'none',
                        }}
                      >
                        <img
                          src={profileImgModal.imageUrl}
                          alt=""
                          draggable={false}
                          onLoad={(e) => {
                            const img = e.currentTarget;
                            setProfileImgNaturalSize({ w: img.naturalWidth, h: img.naturalHeight });
                          }}
                          style={{
                            position: 'absolute',
                            left: '50%', top: '50%',
                            transform: `translate(calc(-50% + ${profileImgPos.x}px), calc(-50% + ${profileImgPos.y}px)) scale(${profileImgZoom}) rotate(${profileImgRotation}deg)`,
                            transformOrigin: 'center center',
                            maxWidth: '100%', maxHeight: '100%',
                            transition: profileImgDragging || profileImgCropDragging ? 'none' : 'transform 0.15s ease',
                            pointerEvents: 'none',
                          }}
                        />

                        {/* Crop overlay */}
                        {profileImgCropping && cropBox && cropBox.width > 2 && cropBox.height > 2 && (
                          <>
                            {/* Dim outside crop area */}
                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', pointerEvents: 'none', clipPath: `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%, ${cropBox.left}px ${cropBox.top}px, ${cropBox.left}px ${cropBox.top + cropBox.height}px, ${cropBox.left + cropBox.width}px ${cropBox.top + cropBox.height}px, ${cropBox.left + cropBox.width}px ${cropBox.top}px, ${cropBox.left}px ${cropBox.top}px)` }} />
                            {/* Crop border */}
                            <div style={{
                              position: 'absolute',
                              left: cropBox.left, top: cropBox.top,
                              width: cropBox.width, height: cropBox.height,
                              border: '2px dashed #fff',
                              boxShadow: '0 0 0 1px rgba(0,0,0,0.3)',
                              pointerEvents: 'none',
                            }}>
                              {/* Corner handles */}
                              {[[0,0],[1,0],[0,1],[1,1]].map(([cx,cy], i) => (
                                <div key={i} style={{
                                  position: 'absolute',
                                  left: cx ? 'calc(100% - 4px)' : '-4px',
                                  top: cy ? 'calc(100% - 4px)' : '-4px',
                                  width: '8px', height: '8px',
                                  background: '#fff', border: '1.5px solid #3b82f6',
                                  borderRadius: '2px', pointerEvents: 'none',
                                }} />
                              ))}
                              {/* Dimension label */}
                              <div style={{
                                position: 'absolute', bottom: '-22px', left: '50%', transform: 'translateX(-50%)',
                                background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '2px 8px', borderRadius: '4px',
                                fontSize: '10px', fontWeight: 600, whiteSpace: 'nowrap', pointerEvents: 'none',
                              }}>
                                {Math.round(cropBox.width)} × {Math.round(cropBox.height)}
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Footer with crop preview + save */}
                      {profileImgCropping && cropBox && cropBox.width > 10 && cropBox.height > 10 && (() => {
                        // Compute natural-pixel crop coordinates
                        const vis = getImageVisualRect();
                        let prevContainerW = 0, prevContainerH = 0;
                        let imgInPrevW = 0, imgInPrevH = 0, imgInPrevX = 0, imgInPrevY = 0;
                        if (vis && profileImgNaturalSize.w > 0) {
                          const pixToNat = profileImgNaturalSize.w / vis.visW;
                          const natX = (cropBox.left - vis.visLeft) * pixToNat;
                          const natY = (cropBox.top - vis.visTop) * pixToNat;
                          const natW = cropBox.width * pixToNat;
                          const natH = cropBox.height * pixToNat;
                          // Scale natural crop to fit preview box (max 120×90)
                          const prevScale = Math.min(120 / natW, 90 / natH, 1);
                          prevContainerW = Math.round(natW * prevScale);
                          prevContainerH = Math.round(natH * prevScale);
                          // Position the full natural image inside the preview so the crop region aligns
                          imgInPrevW = profileImgNaturalSize.w * prevScale;
                          imgInPrevH = profileImgNaturalSize.h * prevScale;
                          imgInPrevX = -natX * prevScale;
                          imgInPrevY = -natY * prevScale;
                        }
                        return (
                          <div style={{
                            padding: '10px 18px', borderTop: '1px solid #f1f5f9',
                            display: 'flex', alignItems: 'center', gap: '12px',
                          }}>
                            {/* Preview thumbnail */}
                            <div style={{
                              display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 auto',
                            }}>
                              <div style={{
                                width: `${prevContainerW}px`, height: `${prevContainerH}px`,
                                overflow: 'hidden', borderRadius: '6px', position: 'relative',
                                border: '2px solid #3b82f6', background: 'repeating-conic-gradient(#f1f5f9 0% 25%, #fff 0% 50%) 50% / 10px 10px',
                                flexShrink: 0,
                              }}>
                                <img
                                  src={profileImgModal.imageUrl}
                                  alt="Crop preview"
                                  draggable={false}
                                  style={{
                                    position: 'absolute',
                                    left: `${imgInPrevX}px`,
                                    top: `${imgInPrevY}px`,
                                    width: `${imgInPrevW}px`,
                                    height: `${imgInPrevH}px`,
                                    pointerEvents: 'none',
                                  }}
                                />
                              </div>
                              <div style={{ fontSize: '0.68rem', color: '#64748b', lineHeight: 1.4 }}>
                                <div style={{ fontWeight: 600, color: '#475569', marginBottom: '2px' }}>Preview</div>
                                <div>{Math.round(cropBox.width)} × {Math.round(cropBox.height)}px</div>
                              </div>
                            </div>
                            {/* Action buttons */}
                            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                              <button
                                onClick={() => { setProfileImgCropRect(null); }}
                                style={{ padding: '7px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', color: '#475569', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                              >Clear</button>
                              <button
                                onClick={handleSaveCrop}
                                disabled={profileImgSaving}
                                style={{
                                  padding: '7px 20px', border: 'none', borderRadius: '8px',
                                  background: profileImgSaving ? '#93c5fd' : '#3b82f6', color: '#fff',
                                  cursor: profileImgSaving ? 'not-allowed' : 'pointer',
                                  fontSize: '0.78rem', fontWeight: 700, transition: 'all 0.15s',
                                }}
                              >{profileImgSaving ? 'Saving...' : 'Save Crop'}</button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                );
              })()}

              {/* ── Find & Fix User Names Modal ── */}
              {nameFixModalOpen && (
                <div
                  style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)', zIndex: 9998,
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    animation: 'fadeIn 0.2s ease',
                  }}
                  onClick={(e) => { if (e.target === e.currentTarget && !nameFixProcessing) { setNameFixModalOpen(false); nameFixAbortRef.current = true; } }}
                >
                  <div
                    style={{
                      background: '#fff', borderRadius: '16px', width: '94%', maxWidth: '1050px',
                      maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                      animation: 'slideUp 0.3s ease',
                    }}
                  >
                    {/* Header */}
                    <div style={{
                      padding: '1.25rem 1.5rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#1e293b' }}>
                          Find & Fix User Names
                        </h3>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#64748b' }}>
                          {nameFixUsers.filter(u => u.type === 'newUser' && u.status !== 'fixed').length} "New User"
                          {' | '}{nameFixUsers.filter(u => u.type === 'badCase' && u.status !== 'fixed').length} bad format
                          {' | '}{nameFixUsers.filter(u => u.status === 'fixed').length} fixed
                          {nameFixIgnoredUsers.length > 0 && ` | ${nameFixIgnoredUsers.length} ignored`}
                        </p>
                      </div>
                      <button
                        onClick={() => { if (!nameFixProcessing) { setNameFixModalOpen(false); nameFixAbortRef.current = true; } }}
                        disabled={nameFixProcessing}
                        style={{
                          width: '32px', height: '32px', borderRadius: '50%', border: '1px solid #e2e8f0',
                          background: '#f8fafc', cursor: nameFixProcessing ? 'not-allowed' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1.1rem', color: '#64748b', transition: 'all 0.2s',
                        }}
                      >&times;</button>
                    </div>

                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: 0, padding: '0.75rem 1.5rem 0', borderBottom: '1px solid #e2e8f0' }}>
                      <button
                        onClick={() => setNameFixTab('active')}
                        style={{
                          padding: '0.5rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer',
                          fontSize: '0.85rem', fontWeight: 600,
                          color: nameFixTab === 'active' ? '#7c3aed' : '#94a3b8',
                          borderBottom: nameFixTab === 'active' ? '2px solid #7c3aed' : '2px solid transparent',
                          transition: 'all 0.2s', marginBottom: '-1px',
                        }}
                      >
                        Active ({nameFixUsers.length})
                      </button>
                      <button
                        onClick={() => setNameFixTab('ignored')}
                        style={{
                          padding: '0.5rem 1.25rem', border: 'none', background: 'none', cursor: 'pointer',
                          fontSize: '0.85rem', fontWeight: 600,
                          color: nameFixTab === 'ignored' ? '#7c3aed' : '#94a3b8',
                          borderBottom: nameFixTab === 'ignored' ? '2px solid #7c3aed' : '2px solid transparent',
                          transition: 'all 0.2s', marginBottom: '-1px',
                        }}
                      >
                        Ignored ({nameFixIgnoredUsers.length})
                      </button>
                    </div>

                    {/* Body — scrollable */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1.5rem' }}>
                      {nameFixTab === 'active' ? (
                        nameFixUsers.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No users to fix.</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, color: '#475569', width: '36px' }}>#</th>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, color: '#475569', minWidth: '100px' }}>Current Name</th>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, color: '#475569', minWidth: '160px' }}>Email</th>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, color: '#475569', minWidth: '120px' }}>Corrected Name</th>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'center', fontWeight: 600, color: '#475569', width: '90px' }}>Type</th>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'center', fontWeight: 600, color: '#475569', width: '180px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {nameFixUsers.map((user, idx) => (
                                <tr
                                  key={user._id}
                                  style={{
                                    borderBottom: '1px solid #f1f5f9',
                                    background: nameFixGlowId === user._id
                                      ? 'rgba(34, 197, 94, 0.12)'
                                      : user.status === 'fixing' ? 'rgba(59, 130, 246, 0.06)' : 'transparent',
                                    transition: 'background 0.5s ease',
                                    boxShadow: nameFixGlowId === user._id ? 'inset 0 0 0 2px rgba(34,197,94,0.4)' : 'none',
                                    animation: nameFixGlowId === user._id ? 'nameFixGlow 2s ease-in-out' : 'none',
                                  }}
                                >
                                  <td style={{ padding: '0.5rem 0.5rem', color: '#94a3b8' }}>{idx + 1}</td>
                                  <td style={{ padding: '0.5rem 0.5rem' }}>
                                    <span style={{
                                      color: user.type === 'newUser' ? '#dc2626' : '#ea580c',
                                      fontWeight: 500,
                                      textDecoration: user.status === 'fixed' ? 'line-through' : 'none',
                                      opacity: user.status === 'fixed' ? 0.5 : 1,
                                    }}>
                                      {user.fullName}
                                    </span>
                                  </td>
                                  <td style={{ padding: '0.5rem 0.5rem', color: '#475569', fontSize: '0.8rem', wordBreak: 'break-all' }}>{user.email}</td>
                                  <td style={{ padding: '0.5rem 0.5rem' }}>
                                    {user.suggestedName ? (
                                      <span style={{
                                        fontWeight: 600,
                                        color: user.status === 'fixed' ? '#16a34a' : '#7c3aed',
                                        background: user.status === 'fixed' ? 'rgba(34,197,94,0.1)' : 'rgba(124,58,237,0.08)',
                                        padding: '2px 8px', borderRadius: '6px', display: 'inline-block',
                                      }}>
                                        {user.suggestedName}
                                      </span>
                                    ) : user.status === 'guessing' ? (
                                      <span style={{ color: '#3b82f6', fontStyle: 'italic', fontSize: '0.78rem' }}>Guessing via AI...</span>
                                    ) : user.status === 'error' ? (
                                      <span style={{ color: '#dc2626', fontStyle: 'italic', fontSize: '0.78rem' }}>AI unavailable</span>
                                    ) : (
                                      <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.78rem' }}>&mdash;</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '0.5rem 0.5rem', textAlign: 'center' }}>
                                    <span style={{
                                      fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', whiteSpace: 'nowrap',
                                      background: user.type === 'newUser' ? '#fef2f2' : '#fff7ed',
                                      color: user.type === 'newUser' ? '#dc2626' : '#c2410c',
                                      border: user.type === 'newUser' ? '1px solid #fca5a5' : '1px solid #fdba74',
                                    }}>
                                      {user.type === 'newUser' ? 'New User' : 'Bad Format'}
                                    </span>
                                  </td>
                                  <td style={{ padding: '0.4rem 0.5rem', textAlign: 'center' }}>
                                    {user.status === 'fixed' ? (
                                      <span style={{ color: '#16a34a', fontWeight: 700, fontSize: '0.85rem' }}>&#10003; Fixed</span>
                                    ) : user.status === 'fixing' ? (
                                      <span style={{ color: '#3b82f6', fontSize: '0.78rem', fontWeight: 600 }}>Fixing...</span>
                                    ) : user.status === 'guessing' ? (
                                      <span style={{ color: '#3b82f6', fontSize: '0.78rem', fontWeight: 600 }}>Guessing...</span>
                                    ) : (
                                      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                        {/* Guess & Correct for newUser without suggestion */}
                                        {user.type === 'newUser' && !user.suggestedName && user.status !== 'error' && (
                                          <button
                                            onClick={() => handleGuessNameAI(user._id)}
                                            style={{
                                              padding: '3px 8px', borderRadius: '5px', border: '1px solid #c4b5fd',
                                              background: '#f5f3ff', color: '#7c3aed', fontSize: '0.7rem', fontWeight: 600,
                                              cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = '#ede9fe'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = '#f5f3ff'; }}
                                          >
                                            Guess Name
                                          </button>
                                        )}
                                        {/* Fix button when suggestion is ready */}
                                        {user.suggestedName && (
                                          <button
                                            onClick={() => handleFixSingleName(user._id)}
                                            style={{
                                              padding: '3px 8px', borderRadius: '5px', border: '1px solid #86efac',
                                              background: '#f0fdf4', color: '#16a34a', fontSize: '0.7rem', fontWeight: 600,
                                              cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; }}
                                          >
                                            Fix
                                          </button>
                                        )}
                                        {/* Retry for error */}
                                        {user.status === 'error' && user.type === 'newUser' && (
                                          <button
                                            onClick={() => handleGuessNameAI(user._id)}
                                            style={{
                                              padding: '3px 8px', borderRadius: '5px', border: '1px solid #c4b5fd',
                                              background: '#f5f3ff', color: '#7c3aed', fontSize: '0.7rem', fontWeight: 600,
                                              cursor: 'pointer', whiteSpace: 'nowrap',
                                            }}
                                          >
                                            Retry
                                          </button>
                                        )}
                                        {/* Ignore button */}
                                        <button
                                          onClick={() => handleIgnoreUser(user._id)}
                                          style={{
                                            padding: '3px 8px', borderRadius: '5px', border: '1px solid #e2e8f0',
                                            background: '#f8fafc', color: '#94a3b8', fontSize: '0.7rem', fontWeight: 600,
                                            cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                                          }}
                                          onMouseEnter={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
                                          onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.color = '#94a3b8'; }}
                                        >
                                          Ignore
                                        </button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      ) : (
                        /* Ignored tab */
                        nameFixIgnoredUsers.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No ignored users.</div>
                        ) : (
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                            <thead>
                              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, color: '#475569', width: '36px' }}>#</th>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Name</th>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Email</th>
                                <th style={{ padding: '0.5rem 0.5rem', textAlign: 'center', fontWeight: 600, color: '#475569', width: '100px' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {nameFixIgnoredUsers.map((user, idx) => (
                                <tr key={user._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <td style={{ padding: '0.5rem 0.5rem', color: '#94a3b8' }}>{idx + 1}</td>
                                  <td style={{ padding: '0.5rem 0.5rem', color: '#64748b' }}>{user.fullName}</td>
                                  <td style={{ padding: '0.5rem 0.5rem', color: '#475569', fontSize: '0.8rem' }}>{user.email}</td>
                                  <td style={{ padding: '0.5rem 0.5rem', textAlign: 'center' }}>
                                    <button
                                      onClick={() => handleUnignoreUser(user._id)}
                                      style={{
                                        padding: '3px 10px', borderRadius: '5px', border: '1px solid #93c5fd',
                                        background: '#eff6ff', color: '#2563eb', fontSize: '0.7rem', fontWeight: 600,
                                        cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                                      }}
                                      onMouseEnter={e => { e.currentTarget.style.background = '#dbeafe'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = '#eff6ff'; }}
                                    >
                                      Restore
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )
                      )}
                    </div>

                    {/* Footer — only on active tab */}
                    {nameFixTab === 'active' && (
                      <div style={{
                        padding: '0.75rem 1.5rem', borderTop: '1px solid #e2e8f0',
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      }}>
                        <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                          {nameFixUsers.filter(u => u.suggestedName && u.status === 'pending').length} ready to fix
                        </span>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          {nameFixProcessing && (
                            <button
                              onClick={() => { nameFixAbortRef.current = true; }}
                              style={{
                                padding: '0.4rem 1rem', borderRadius: '8px',
                                border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626',
                                fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                              }}
                            >
                              Stop
                            </button>
                          )}
                          {/* Fix All Bad Format */}
                          {nameFixUsers.some(u => u.type === 'badCase' && u.suggestedName && u.status === 'pending') && !nameFixProcessing && (
                            <button
                              onClick={handleFixAllBadFormat}
                              style={{
                                padding: '0.4rem 1rem', borderRadius: '8px', border: '1px solid #fdba74',
                                background: 'linear-gradient(135deg, #fff7ed, #ffedd5)', color: '#c2410c',
                                fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #ffedd5, #fed7aa)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fff7ed, #ffedd5)'; }}
                            >
                              Fix All Bad Format ({nameFixUsers.filter(u => u.type === 'badCase' && u.suggestedName && u.status === 'pending').length})
                            </button>
                          )}
                          {/* Fix All (both types) */}
                          <button
                            onClick={handleFixAllNames}
                            disabled={nameFixProcessing || nameFixUsers.filter(u => u.suggestedName && u.status === 'pending').length === 0}
                            style={{
                              padding: '0.4rem 1.2rem', borderRadius: '8px', border: 'none',
                              background: nameFixProcessing ? '#94a3b8' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                              color: '#fff', fontSize: '0.8rem', fontWeight: 600,
                              cursor: nameFixProcessing || nameFixUsers.filter(u => u.suggestedName && u.status === 'pending').length === 0 ? 'not-allowed' : 'pointer',
                              opacity: nameFixUsers.filter(u => u.suggestedName && u.status === 'pending').length === 0 && !nameFixProcessing ? 0.5 : 1,
                              transition: 'all 0.2s',
                            }}
                          >
                            {nameFixProcessing ? 'Fixing Names...' : `Fix All Ready (${nameFixUsers.filter(u => u.suggestedName && u.status === 'pending').length})`}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Floating toast for name fix operations */}
              {nameFixToast.show && (
                <div
                  style={{
                    position: 'fixed', top: '1.5rem', right: '1.5rem', zIndex: 10000,
                    padding: '0.85rem 1.5rem', borderRadius: '10px',
                    fontSize: '0.9rem', fontWeight: 500, maxWidth: '480px',
                    backgroundColor: nameFixToast.type === 'success' ? '#f0fff4' : nameFixToast.type === 'error' ? '#fff5f5' : '#eff6ff',
                    color: nameFixToast.type === 'success' ? '#22543d' : nameFixToast.type === 'error' ? '#742a2a' : '#1e40af',
                    border: nameFixToast.type === 'success' ? '1px solid #9ae6b4' : nameFixToast.type === 'error' ? '1px solid #feb2b2' : '1px solid #93c5fd',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    animation: 'slideInRight 0.3s ease-out',
                  }}
                >
                  {nameFixToast.message}
                </div>
              )}

            </div>
          </TabContent>
        )}

        {activeTab === 'dashboard' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="dashboard"
          >
            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}

            {/* Mini Stats Cards */}
            {(() => {
              const pctChange = (today: number, yesterday: number) => {
                if (yesterday === 0) return today > 0 ? 100 : 0;
                return Math.round(((today - yesterday) / yesterday) * 100);
              };
              const appPct = pctChange(timeStats.applicationsToday, timeStats.applicationsYesterday);
              const visPct = pctChange(visitorStats.uniqueVisitors.today, visitorStats.uniqueVisitors.yesterday);
              const regPct = pctChange(registeredUsersStats.registeredToday, registeredUsersStats.registeredYesterday);
              const pvPct = pctChange(visitorStats.pageViews.today, visitorStats.pageViews.yesterday);

              const PctBadge: React.FC<{ pct: number }> = ({ pct }) => {
                if (pct === 0) return <span style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 600, marginLeft: 4 }}>0%</span>;
                const isUp = pct > 0;
                return (
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 1,
                    fontSize: '0.62rem', fontWeight: 700, marginLeft: 4,
                    color: isUp ? '#16a34a' : '#dc2626',
                    background: isUp ? 'rgba(22,163,74,0.08)' : 'rgba(220,38,38,0.08)',
                    padding: '1px 5px', borderRadius: 6,
                  }}>
                    <svg width="9" height="9" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2">
                      {isUp
                        ? <polyline points="1 7 5 3 9 7" />
                        : <polyline points="1 3 5 7 9 3" />
                      }
                    </svg>
                    {isUp ? '+' : ''}{pct}%
                  </span>
                );
              };

              const blueBubble = (label: string) => (
                <span style={{
                  position: 'absolute', top: -7, right: -5,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: '#fff', fontSize: '0.55rem', fontWeight: 700,
                  padding: '2px 7px', borderRadius: 10,
                  boxShadow: '0 2px 6px rgba(59,130,246,0.35)',
                  letterSpacing: '0.02em', whiteSpace: 'nowrap',
                }}>{label}</span>
              );

              const greenBubble = (label: string) => (
                <span style={{
                  position: 'absolute', top: -7, right: -5,
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  color: '#fff', fontSize: '0.55rem', fontWeight: 700,
                  padding: '2px 7px', borderRadius: 10,
                  boxShadow: '0 2px 6px rgba(34,197,94,0.35)',
                  letterSpacing: '0.02em', whiteSpace: 'nowrap',
                }}>{label}</span>
              );

              return (
              <>
            {/* Row 1: Applications & Registrations */}
            <MiniStatsGrid>
              <MiniStatCard onClick={() => setActiveTileModal('applications-today')} style={{ cursor: 'pointer' }}>
                <MiniStatIcon bgColor="#e0f2fe">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#0284c7" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue style={{ display: 'flex', alignItems: 'center' }}>
                    <AnimatedCounter value={timeStats.applicationsToday} />
                    <PctBadge pct={appPct} />
                  </MiniStatValue>
                  <MiniStatLabel>Applications Today<br/><span style={{ fontSize: '0.58rem', color: '#94a3b8', fontWeight: 400 }}>since 12:00 AM today</span></MiniStatLabel>
                </MiniStatContent>
              </MiniStatCard>

              <MiniStatCard onClick={() => setActiveTileModal('applications-yesterday')} style={{ cursor: 'pointer' }}>
                <MiniStatIcon bgColor="#fff7ed">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#f97316" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue><AnimatedCounter value={timeStats.applicationsYesterday} /></MiniStatValue>
                  <MiniStatLabel>Apps Yesterday</MiniStatLabel>
                </MiniStatContent>
              </MiniStatCard>

              <MiniStatCard onClick={() => setActiveTileModal('registered-today')} style={{ cursor: 'pointer' }}>
                <MiniStatIcon bgColor="#dbeafe">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#3b82f6" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue style={{ display: 'flex', alignItems: 'center' }}>
                    <AnimatedCounter value={registeredUsersStats.registeredToday} />
                    <PctBadge pct={regPct} />
                  </MiniStatValue>
                  <MiniStatLabel>Registered Today</MiniStatLabel>
                </MiniStatContent>
                {greenBubble('Users')}
              </MiniStatCard>

              <MiniStatCard onClick={() => setActiveTileModal('registered-yesterday')} style={{ cursor: 'pointer' }}>
                <MiniStatIcon bgColor="#fce7f3">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#ec4899" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue><AnimatedCounter value={registeredUsersStats.registeredYesterday} /></MiniStatValue>
                  <MiniStatLabel>Registered Yesterday</MiniStatLabel>
                </MiniStatContent>
                {greenBubble('Users')}
              </MiniStatCard>

              <MiniStatCard onClick={() => setActiveTileModal('this-week')} style={{ cursor: 'pointer' }}>
                <MiniStatIcon bgColor="#d1fae5">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#10b981" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue><AnimatedCounter value={timeStats.applicantsThisWeek} /></MiniStatValue>
                  <MiniStatLabel>This Week</MiniStatLabel>
                </MiniStatContent>
                {blueBubble('Applications')}
              </MiniStatCard>

              <MiniStatCard onClick={() => setActiveTileModal('this-month')} style={{ cursor: 'pointer' }}>
                <MiniStatIcon bgColor="#ede9fe">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#8b5cf6" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue><AnimatedCounter value={timeStats.applicantsThisMonth} /></MiniStatValue>
                  <MiniStatLabel>This Month</MiniStatLabel>
                </MiniStatContent>
                {blueBubble('Applications')}
              </MiniStatCard>
            </MiniStatsGrid>

            {/* Row 2: Visitors */}
            <MiniStatsGrid style={{ marginBottom: '1.5rem' }}>
              <MiniStatCard>
                <MiniStatIcon bgColor="#fef3c7">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#f59e0b" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue style={{ display: 'flex', alignItems: 'center' }}>
                    <AnimatedCounter value={visitorStats.uniqueVisitors.today} />
                    <PctBadge pct={visPct} />
                  </MiniStatValue>
                  <MiniStatLabel>Visitors Today</MiniStatLabel>
                </MiniStatContent>
              </MiniStatCard>

              <MiniStatCard>
                <MiniStatIcon bgColor="#fef9c3">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#ca8a04" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue><AnimatedCounter value={visitorStats.uniqueVisitors.yesterday} /></MiniStatValue>
                  <MiniStatLabel>Visitors Yesterday</MiniStatLabel>
                </MiniStatContent>
              </MiniStatCard>

              <MiniStatCard>
                <MiniStatIcon bgColor="#ecfdf5">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#059669" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue><AnimatedCounter value={visitorStats.uniqueVisitors.thisWeek} /></MiniStatValue>
                  <MiniStatLabel>Visitors This Week</MiniStatLabel>
                </MiniStatContent>
              </MiniStatCard>

              <MiniStatCard>
                <MiniStatIcon bgColor="#f0fdf4">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#15803d" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue><AnimatedCounter value={visitorStats.uniqueVisitors.thisMonth} /></MiniStatValue>
                  <MiniStatLabel>Visitors This Month</MiniStatLabel>
                </MiniStatContent>
              </MiniStatCard>

              <MiniStatCard>
                <MiniStatIcon bgColor="#ede9fe">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#7c3aed" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue><AnimatedCounter value={visitorStats.uniqueVisitors.total} /></MiniStatValue>
                  <MiniStatLabel>Total Visitors</MiniStatLabel>
                </MiniStatContent>
              </MiniStatCard>

              <MiniStatCard>
                <MiniStatIcon bgColor="#fce4ec">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#e11d48" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </MiniStatIcon>
                <MiniStatContent>
                  <MiniStatValue style={{ display: 'flex', alignItems: 'center' }}>
                    <AnimatedCounter value={visitorStats.pageViews.today} />
                    <PctBadge pct={pvPct} />
                  </MiniStatValue>
                  <MiniStatLabel>Page Views Today</MiniStatLabel>
                </MiniStatContent>
              </MiniStatCard>
            </MiniStatsGrid>
              </>
              );
            })()}

            {/* ── Top Row: PaidApplicationsCard (left) + 4 stat tiles (right, 2×2) ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) minmax(300px, 2fr)', gap: '1.2rem', marginBottom: '1.2rem' }}>
              <PaidApplicationsCard
                totalApplications={eligibleCount}
                onNewPayment={handleNewPayment}
                onClick={() => {
                  statusFilterFromTileRef.current = true;
                  setStatusFilter('payment_complete');
                  setActiveTab('applications');
                }}
                tooltip={`Paid Applications tracks payments against Eligible Applications only (${eligibleCount}), excluding users removed from recovery. Conversion = Paid / Eligible.`}
              />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.2rem' }}>
                <StatCardWithPie
                  onClick={() => { setActiveTab('registered-users'); }}
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#6366f1" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
                  title="Registered Users"
                  value={registeredUsersStats.totalUsers}
                  tooltip="Total users who have registered on the portal via OTP login. Includes all users regardless of application status."
                  pieData={[
                    { name: 'Registered Today', value: registeredUsersStats.registeredToday, fill: PIE_COLORS[0] },
                    { name: 'Registered Yesterday', value: registeredUsersStats.registeredYesterday, fill: PIE_COLORS[1] },
                    ...( (registeredUsersStats.totalUsers - registeredUsersStats.registeredToday - registeredUsersStats.registeredYesterday) > 0 ? [{ name: 'Earlier', value: registeredUsersStats.totalUsers - registeredUsersStats.registeredToday - registeredUsersStats.registeredYesterday, fill: PIE_COLORS[4] }] : [])
                  ].filter(item => item.value > 0)}
                />
                <StatCardWithPie
                  onClick={() => { statusFilterFromTileRef.current = true; setStatusFilter(null); setActiveTab('applications'); }}
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#4A90E2" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
                  title="Total Applications"
                  value={stats.total}
                  tooltip="All applications ever created on the portal, including drafts, paid, approved, rejected, and in-review. This is the raw count before any exclusions."
                  pieData={[
                    { name: 'Approved', value: stats.approved, fill: PIE_COLORS[0] },
                    { name: 'Draft', value: stats.draft, fill: PIE_COLORS[1] },
                    { name: 'Rejected', value: stats.rejected, fill: PIE_COLORS[2] },
                    { name: 'In-review', value: stats.inReview, fill: PIE_COLORS[3] },
                    { name: 'Paid', value: stats.paid, fill: PIE_COLORS[5] },
                    ...( (stats.total - (stats.approved + stats.draft + stats.rejected + stats.inReview + stats.paid)) > 0 ? [{ name: 'Other', value: (stats.total - (stats.approved + stats.draft + stats.rejected + stats.inReview + stats.paid)), fill: PIE_COLORS[4] }] : [])
                  ].filter(item => item.value > 0)}
                />
                <StatCardWithPie
                  onClick={() => { statusFilterFromTileRef.current = true; setStatusFilter('payment_complete'); setActiveTab('applications'); }}
                  icon={<span style={{ color: '#6A0DAD', fontSize: '24px' }}>₹</span>}
                  title="Paid Applications"
                  value={stats.paid}
                  tooltip={`Applications with completed payment. Pie chart shows paid (${stats.paid}) vs eligible unpaid (${eligibleCount - stats.paid}) applications.`}
                  pieData={[
                    { name: 'Paid', value: stats.paid, fill: PIE_COLORS[5] },
                    { name: 'Eligible Unpaid', value: Math.max(0, eligibleCount - stats.paid), fill: PIE_COLORS[4] },
                  ].filter(item => item.value > 0)}
                />
                <StatCardWithPie
                  onClick={() => { statusFilterFromTileRef.current = true; setStatusFilter('draft'); setActiveTab('applications'); }}
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#F5A623" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                  title="Draft Applications"
                  value={stats.draft}
                  tooltip={`Applications that are still in progress and not yet submitted for review. Pie shows drafts (${stats.draft}) vs all other applications (${stats.total - stats.draft}).`}
                  pieData={[
                    { name: 'Draft', value: stats.draft, fill: PIE_COLORS[1] },
                    { name: 'Other', value: stats.total - stats.draft, fill: PIE_COLORS[4] },
                  ].filter(item => item.value > 0)}
                />
              </div>
            </div>

            {/* ── Bottom Row: Approved + In-review (left) | Growth & Insights (right) ── */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) minmax(300px, 2fr)', gap: '1.2rem', marginBottom: '2.5rem', alignItems: 'stretch' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.2rem' }}>
                <StatCardWithPie
                  onClick={() => { statusFilterFromTileRef.current = true; setStatusFilter(null); setActiveTab('applications'); }}
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#7ED321" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
                  title="Eligible Applications"
                  value={eligibleCount}
                  tooltip={`Total Applications (${stats.total}) minus applications from users excluded from App Recovery (${stats.total - eligibleCount} excluded). Only eligible applications are considered for conversion metrics.`}
                  pieData={[
                    { name: 'Eligible', value: eligibleCount, fill: PIE_COLORS[0] },
                    { name: 'Excluded', value: Math.max(0, stats.total - eligibleCount), fill: PIE_COLORS[2] },
                  ].filter(item => item.value > 0)}
                />
                <StatCardWithPie
                  onClick={() => { statusFilterFromTileRef.current = true; setStatusFilter('in-review'); setActiveTab('applications'); }}
                  icon={<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#F59E0B" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z" /></svg>}
                  title="In-review (DD Manual)"
                  value={stats.inReview}
                  tooltip="Applications currently under manual due diligence review. These are submitted applications awaiting admin approval or rejection."
                  pieData={[
                    { name: 'In-review', value: stats.inReview, fill: PIE_COLORS[3] },
                    { name: 'Other', value: stats.total - stats.inReview, fill: PIE_COLORS[4] },
                  ].filter(item => item.value > 0)}
                />
              </div>
                {/* Growth & Insights Card — 3 chart views */}
                {(() => {
                  const metrics = [
                    { label: 'Total Applications', value: stats.total, color: '#4A90E2', bar: Math.min(100, stats.total > 0 ? 100 : 0), sub: `+${timeStats.applicationsToday} today` },
                    { label: 'Paid Applications', value: stats.paid, color: '#22c55e', bar: stats.total > 0 ? Math.round((stats.paid / stats.total) * 100) : 0, sub: `${stats.total > 0 ? Math.round((stats.paid / stats.total) * 100) : 0}% conversion` },
                    { label: 'Draft In Progress', value: stats.draft, color: '#f59e0b', bar: stats.total > 0 ? Math.round((stats.draft / stats.total) * 100) : 0, sub: `${stats.total > 0 ? Math.round((stats.draft / stats.total) * 100) : 0}% of total` },
                    { label: 'Unique Visitors', value: visitorStats.uniqueVisitors.total, color: '#8b5cf6', bar: Math.min(100, visitorStats.uniqueVisitors.total > 0 ? 100 : 0), sub: `${visitorStats.uniqueVisitors.today} today` },
                    { label: 'Registered Users', value: registeredUsersStats.totalUsers, color: '#6366f1', bar: Math.min(100, registeredUsersStats.totalUsers > 0 ? 100 : 0), sub: `+${registeredUsersStats.registeredToday} today` },
                  ];
                  const maxVal = Math.max(...metrics.map(m => m.value), 1);

                  const viewBtn = (view: 'bars' | 'radar' | 'donut', icon: React.ReactNode) => (
                    <button
                      onClick={() => setGrowthChartView(view)}
                      style={{
                        width: 28, height: 28, borderRadius: '50%', border: 'none',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                        background: growthChartView === view ? '#3b82f6' : '#f1f5f9',
                        color: growthChartView === view ? '#fff' : '#64748b',
                        transition: 'all 0.2s ease',
                      }}
                    >{icon}</button>
                  );

                  return (
                <div style={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)',
                  borderRadius: 16, padding: '1.5rem',
                  border: '1px solid rgba(226,232,240,0.8)',
                  boxShadow: '0 10px 25px rgba(76,110,245,0.08)',
                  display: 'flex', flexDirection: 'column', gap: '1rem',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#2d3748' }}>Growth & Insights</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {viewBtn('bars', <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12h4v9H3zM10 7h4v14h-4zM17 3h4v18h-4z"/></svg>)}
                      {viewBtn('radar', <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 22 8.5 19 19.5 5 19.5 2 8.5"/><polygon points="12 6 18 10 16 17 8 17 6 10"/></svg>)}
                      {viewBtn('donut', <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/><path d="M12 2v4M22 12h-4"/></svg>)}
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6,
                        fontSize: '0.68rem', fontWeight: 700, color: '#16a34a',
                        background: 'rgba(22,163,74,0.08)', padding: '3px 10px', borderRadius: 20,
                      }}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="#16a34a" strokeWidth="2"><polyline points="1 7 5 3 9 7" /></svg>
                        Uptrend
                      </span>
                    </div>
                  </div>

                  {/* View: Bars */}
                  {growthChartView === 'bars' && metrics.map((m, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 90, fontSize: '0.72rem', color: '#64748b', fontWeight: 500, flexShrink: 0 }}>{m.label}</div>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 4,
                            background: `linear-gradient(90deg, ${m.color}88, ${m.color})`,
                            width: `${m.bar}%`,
                            transition: 'width 1s ease',
                          }} />
                        </div>
                        <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', minWidth: 36, textAlign: 'right' }}>{m.value}</span>
                      </div>
                      <span style={{ fontSize: '0.62rem', color: '#94a3b8', fontWeight: 500, minWidth: 70, textAlign: 'right' }}>{m.sub}</span>
                    </div>
                  ))}

                  {/* View: Radar (Pentagon Spider) */}
                  {growthChartView === 'radar' && (() => {
                    const cx = 110, cy = 105, r = 80;
                    const n = metrics.length;
                    const angleSlice = (Math.PI * 2) / n;
                    const getXY = (i: number, scale: number) => ({
                      x: cx + r * scale * Math.cos(angleSlice * i - Math.PI / 2),
                      y: cy + r * scale * Math.sin(angleSlice * i - Math.PI / 2),
                    });
                    const gridLevels = [0.25, 0.5, 0.75, 1];
                    const dataPoints = metrics.map((m, i) => getXY(i, Math.max(0.05, m.value / maxVal)));
                    const dataPath = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ') + 'Z';

                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                        <svg width="220" height="210" viewBox="0 0 220 210">
                          {gridLevels.map((lv, li) => (
                            <polygon key={li}
                              points={Array.from({ length: n }, (_, i) => { const p = getXY(i, lv); return `${p.x},${p.y}`; }).join(' ')}
                              fill="none" stroke="#e2e8f0" strokeWidth="1"
                            />
                          ))}
                          {Array.from({ length: n }, (_, i) => {
                            const p = getXY(i, 1);
                            return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke="#e2e8f0" strokeWidth="1" />;
                          })}
                          <path d={dataPath} fill="rgba(59,130,246,0.15)" stroke="#3b82f6" strokeWidth="2" />
                          {dataPoints.map((p, i) => (
                            <circle key={i} cx={p.x} cy={p.y} r="4" fill={metrics[i].color} stroke="#fff" strokeWidth="1.5" />
                          ))}
                          {metrics.map((m, i) => {
                            const lp = getXY(i, 1.18);
                            return (
                              <text key={i} x={lp.x} y={lp.y} textAnchor="middle" dominantBaseline="middle"
                                fill="#64748b" fontSize="8" fontWeight="600">{m.label.split(' ')[0]}</text>
                            );
                          })}
                        </svg>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                          {metrics.map((m, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: m.color, flexShrink: 0 }} />
                              <span style={{ fontSize: '0.72rem', color: '#475569', flex: 1 }}>{m.label}</span>
                              <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>{m.value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* View: Donut Rings */}
                  {growthChartView === 'donut' && (() => {
                    const size = 170, ringW = 10, gap = 3;

                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                          {metrics.map((m, i) => {
                            const radius = (size / 2) - (i * (ringW + gap)) - ringW;
                            if (radius <= 0) return null;
                            const circ = 2 * Math.PI * radius;
                            const pct = Math.max(0.02, m.value / maxVal);
                            return (
                              <g key={i}>
                                <circle cx={size / 2} cy={size / 2} r={radius}
                                  fill="none" stroke="#f1f5f9" strokeWidth={ringW} />
                                <circle cx={size / 2} cy={size / 2} r={radius}
                                  fill="none" stroke={m.color} strokeWidth={ringW}
                                  strokeDasharray={`${circ * pct} ${circ * (1 - pct)}`}
                                  strokeDashoffset={circ * 0.25}
                                  strokeLinecap="round"
                                  style={{ transition: 'stroke-dasharray 1s ease' }}
                                />
                              </g>
                            );
                          })}
                        </svg>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                          {metrics.map((m, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{
                                width: 10, height: 10, borderRadius: '50%',
                                border: `3px solid ${m.color}`, background: 'transparent', flexShrink: 0,
                              }} />
                              <span style={{ fontSize: '0.72rem', color: '#475569', flex: 1 }}>{m.label}</span>
                              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>{m.value}</span>
                              <span style={{ fontSize: '0.6rem', color: '#94a3b8' }}>{m.sub}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                  );
                })()}
            </div>
          </TabContent>
        )}

        {activeTab === 'area-report' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="area-report"
          >
            <AreaWiseReportPage />
          </TabContent>
        )}

        {activeTab === 'applications' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="applications"
          >
            {isLoading && (
              <LoadingContainer>
                <LoadingSpinner />
                <p>Loading applications...</p>
              </LoadingContainer>
            )}

            {error && (
              <ErrorMessage>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </ErrorMessage>
            )}

            {actionMessage && (
              <AlertMessage 
                type={actionMessage.type}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                {actionMessage.type === 'success' && (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
                {actionMessage.type === 'error' && (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
                {actionMessage.type === 'warning' && (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                )}
                {actionMessage.text}
              </AlertMessage>
            )}
            
            {!isLoading && !error && !isViewingDetails && (
              <Card
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {/* Title row with inline pagination */}
                {(() => {
                  const filteredApplications = (statusFilter ? applications.filter(app => {
                    if (statusFilter === 'draft') {
                      return (app.status === 'draft' || !app.status) && app.applicationId && app.applicationId.startsWith('CNG-');
                    }
                    return app.status === statusFilter;
                  }) : applications)
                    .filter(app => {
                      if (!searchTerm) return true;
                      const lowerSearchTerm = searchTerm.toLowerCase();
                      const fullName = app.formData?.fullName?.toLowerCase() || '';
                      const email = app.email?.toLowerCase() || '';
                      const applicationId = app.applicationId?.toLowerCase() || '';
                      const vehicleMake = app.formData?.vehicleMake?.toLowerCase() || '';
                      const vehicleModel = app.formData?.vehicleModel?.toLowerCase() || '';

                      return (
                        fullName.includes(lowerSearchTerm) ||
                        email.includes(lowerSearchTerm) ||
                        applicationId.includes(lowerSearchTerm) ||
                        vehicleMake.includes(lowerSearchTerm) ||
                        vehicleModel.includes(lowerSearchTerm)
                      );
                    })
                    .filter(app => {
                      if (!filterByStatus) return true;
                      if (filterByStatus === 'draft') return (app.status === 'draft' || !app.status);
                      return app.status === filterByStatus;
                    })
                    .filter(app => {
                      if (!filterByType) return true;
                      const appType = (app.formData?.dealershipType || 'Individual/OPC').toLowerCase();
                      return appType === filterByType.toLowerCase();
                    });

                  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
                  const startIndex = (currentPage - 1) * itemsPerPage + 1;
                  const endIndex = Math.min(currentPage * itemsPerPage, filteredApplications.length);

                  const getPageNumbers = () => {
                    const pages: (number | string)[] = [];
                    const maxVisible = 7;

                    if (totalPages <= maxVisible) {
                      for (let i = 1; i <= totalPages; i++) {
                        pages.push(i);
                      }
                    } else {
                      if (currentPage <= 4) {
                        for (let i = 1; i <= 5; i++) pages.push(i);
                        pages.push('...');
                        pages.push(totalPages);
                      } else if (currentPage >= totalPages - 3) {
                        pages.push(1);
                        pages.push('...');
                        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
                      } else {
                        pages.push(1);
                        pages.push('...');
                        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                        pages.push('...');
                        pages.push(totalPages);
                      }
                    }
                    return pages;
                  };

                  const renderPaginationControls = () => (
                    <PaginationControls>
                      <PageButton $isNav onClick={() => setCurrentPage(1)} $disabled={currentPage === 1} disabled={currentPage === 1} title="First page">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 19l-7-7 7-7M18 19l-7-7 7-7" /></svg>
                      </PageButton>
                      <PageButton $isNav onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} $disabled={currentPage === 1} disabled={currentPage === 1} title="Previous page">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                      </PageButton>
                      {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                          <PageEllipsis key={`ellipsis-${index}`}>•••</PageEllipsis>
                        ) : (
                          <PageButton key={page} onClick={() => setCurrentPage(page as number)} $active={currentPage === page}>{page}</PageButton>
                        )
                      ))}
                      <PageButton $isNav onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} $disabled={currentPage === totalPages || totalPages === 0} disabled={currentPage === totalPages || totalPages === 0} title="Next page">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                      </PageButton>
                      <PageButton $isNav onClick={() => setCurrentPage(totalPages)} $disabled={currentPage === totalPages || totalPages === 0} disabled={currentPage === totalPages || totalPages === 0} title="Last page">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 5l7 7-7 7M6 5l7 7-7 7" /></svg>
                      </PageButton>
                    </PaginationControls>
                  );

                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', marginBottom: '1rem', paddingBottom: '0.7rem', borderBottom: '3px solid transparent', backgroundImage: 'linear-gradient(white, white), linear-gradient(90deg, #4c6ef5, #6e8ffb)', backgroundOrigin: 'padding-box, border-box', backgroundClip: 'padding-box, border-box', borderRadius: '0 0 0 0' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#4c6ef5" width="24" height="24" style={{ flexShrink: 0 }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                        <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#2d3748' }}>All Applications</span>
                        <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500, marginLeft: '4px' }}>
                          Showing {filteredApplications.length === 0 ? 0 : startIndex}–{endIndex} of {filteredApplications.length}
                        </span>
                      </div>
                      {renderPaginationControls()}
                    </div>
                  );
                })()}
                
                <FilterContainer>
                  <DateFilterContainer style={{ alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {/* Compact date range picker trigger */}
                    <div ref={appDatePickerRef} style={{ position: 'relative' }}>
                      <button
                        onClick={() => { setShowDateRangeCal(v => !v); if (!showDateRangeCal) { setCalViewDate(startDate ? new Date(startDate) : new Date()); setRangePhase('start'); } }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '7px 14px', borderRadius: '8px',
                          border: (startDate && endDate) ? '1.5px solid #4c6ef5' : '1px solid #e2e8f0',
                          background: (startDate && endDate) ? '#eef2ff' : '#fff',
                          cursor: 'pointer', fontSize: '0.85rem', color: '#334155',
                          fontWeight: 500, whiteSpace: 'nowrap', transition: 'all 0.15s',
                          boxShadow: showDateRangeCal ? '0 0 0 2px rgba(76,110,245,0.3)' : 'none'
                        }}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                        {startDate && endDate
                          ? <><span style={{ color: '#4c6ef5', fontWeight: 600 }}>{formatDisplayDate(startDate)}</span><span style={{ color: '#94a3b8' }}>→</span><span style={{ color: '#4c6ef5', fontWeight: 600 }}>{formatDisplayDate(endDate)}</span></>
                          : startDate && !endDate
                            ? <><span style={{ color: '#4c6ef5', fontWeight: 600 }}>{formatDisplayDate(startDate)}</span><span style={{ color: '#94a3b8' }}>→ pick end</span></>
                            : <span style={{ color: '#94a3b8' }}>Select date range</span>
                        }
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
                      </button>

                      {/* Calendar dropdown */}
                      {showDateRangeCal && (
                        <div style={{
                          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 9999,
                          background: '#fff', borderRadius: '12px', padding: '14px',
                          boxShadow: '0 12px 40px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0',
                          width: '280px', animation: 'fadeIn 0.15s ease'
                        }}>
                          {/* Month navigation */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                            <button onClick={() => setCalViewDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', color: '#475569', fontSize: '1rem', fontWeight: 700 }} onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>‹</button>
                            <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>
                              {calViewDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </span>
                            <button onClick={() => setCalViewDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px', color: '#475569', fontSize: '1rem', fontWeight: 700 }} onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>›</button>
                          </div>
                          {/* Day headers */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '4px' }}>
                            {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                              <div key={d} style={{ textAlign: 'center', fontSize: '0.68rem', fontWeight: 600, color: '#94a3b8', padding: '2px 0' }}>{d}</div>
                            ))}
                          </div>
                          {/* Day grid */}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                            {getCalendarDays(calViewDate.getFullYear(), calViewDate.getMonth()).map((day, idx) => {
                              if (day === null) return <div key={`e-${idx}`} />;
                              const ymd = toYMD(new Date(calViewDate.getFullYear(), calViewDate.getMonth(), day));
                              const today = toYMD(new Date());
                              const edge = isRangeEdge(day);
                              const inRange = isInRange(day);
                              const isToday = ymd === today;
                              return (
                                <button
                                  key={idx}
                                  onClick={() => handleCalDayClick(day)}
                                  onMouseEnter={() => setHoverDate(ymd)}
                                  onMouseLeave={() => setHoverDate('')}
                                  style={{
                                    width: '34px', height: '32px', border: 'none', cursor: 'pointer',
                                    borderRadius: edge ? '8px' : inRange ? '4px' : '8px',
                                    background: edge ? '#4c6ef5' : inRange ? '#dbeafe' : 'transparent',
                                    color: edge ? '#fff' : inRange ? '#1e40af' : isToday ? '#4c6ef5' : '#334155',
                                    fontWeight: edge ? 700 : isToday ? 700 : 500,
                                    fontSize: '0.8rem', transition: 'all 0.1s',
                                    outline: isToday && !edge ? '1.5px dashed #4c6ef5' : 'none',
                                    outlineOffset: '-1px',
                                    margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center'
                                  }}
                                  onMouseOver={e => { if (!edge && !inRange) e.currentTarget.style.background = '#f1f5f9'; }}
                                  onMouseOut={e => { if (!edge && !inRange) e.currentTarget.style.background = 'transparent'; }}
                                >
                                  {day}
                                </button>
                              );
                            })}
                          </div>
                          {/* Selection hint */}
                          <div style={{ marginTop: '10px', textAlign: 'center', fontSize: '0.72rem', color: '#94a3b8' }}>
                            {rangePhase === 'start' ? 'Click to select start date' : 'Click to select end date'}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Quick Period dropdown */}
                    {(() => {
                      const now = new Date();
                      const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                      const yesterdayStart = new Date(todayStart); yesterdayStart.setDate(yesterdayStart.getDate() - 1);
                      const weekStart = new Date(todayStart); weekStart.setDate(weekStart.getDate() - (todayStart.getDay() || 7) + 1);
                      const prevWeekStart = new Date(weekStart); prevWeekStart.setDate(prevWeekStart.getDate() - 7);
                      const prevWeekEnd = new Date(weekStart); prevWeekEnd.setDate(prevWeekEnd.getDate() - 1);
                      const isActive = (s: Date, e: Date) => startDate === toYMD(s) && endDate === toYMD(e);
                      const presets: { label: string; from: Date; to: Date }[] = [
                        { label: 'Today', from: todayStart, to: todayStart },
                        { label: 'Yesterday', from: yesterdayStart, to: yesterdayStart },
                        { label: 'This Week', from: weekStart, to: todayStart },
                        { label: 'Previous Week', from: prevWeekStart, to: prevWeekEnd }
                      ];
                      const activePreset = presets.find(p => isActive(p.from, p.to));
                      return (
                        <div ref={quickPeriodRef} style={{ position: 'relative' }}>
                          <button
                            onClick={() => setShowQuickPeriod(v => !v)}
                            style={{
                              padding: '6px 14px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 600,
                              border: activePreset ? '1.5px solid #4c6ef5' : '1px solid #e2e8f0',
                              background: activePreset ? '#eef2ff' : '#fff',
                              color: activePreset ? '#4c6ef5' : '#475569',
                              cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                              display: 'flex', alignItems: 'center', gap: '6px',
                              boxShadow: showQuickPeriod ? '0 0 0 2px rgba(76,110,245,0.25)' : 'none'
                            }}
                            onMouseEnter={e => { if (!activePreset) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
                            onMouseLeave={e => { if (!activePreset) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                            {activePreset ? activePreset.label : 'Quick Period'}
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9l6 6 6-6"/></svg>
                          </button>
                          {showQuickPeriod && (
                            <div style={{
                              position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 9999,
                              background: '#fff', borderRadius: '10px', padding: '6px',
                              boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
                              minWidth: '160px', animation: 'fadeIn 0.12s ease'
                            }}>
                              {presets.map(p => (
                                <button
                                  key={p.label}
                                  onClick={() => { applyQuickRange(p.from, p.to); setShowQuickPeriod(false); }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                    padding: '8px 12px', borderRadius: '7px', fontSize: '0.8rem', fontWeight: 500,
                                    border: 'none', cursor: 'pointer', textAlign: 'left',
                                    background: isActive(p.from, p.to) ? '#eef2ff' : 'transparent',
                                    color: isActive(p.from, p.to) ? '#4c6ef5' : '#334155',
                                    transition: 'all 0.1s'
                                  }}
                                  onMouseEnter={e => { if (!isActive(p.from, p.to)) e.currentTarget.style.background = '#f8fafc'; }}
                                  onMouseLeave={e => { if (!isActive(p.from, p.to)) e.currentTarget.style.background = 'transparent'; }}
                                >
                                  {isActive(p.from, p.to) && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4c6ef5" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                                  {p.label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Reset button */}
                    {(startDate || endDate) && (
                      <button
                        onClick={() => {
                          setStartDate('');
                          setEndDate('');
                          setRangePhase('start');
                          fetchApplications();
                        }}
                        disabled={isLoading}
                        style={{
                          padding: '6px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600,
                          border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                          whiteSpace: 'nowrap', transition: 'all 0.15s'
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                        Clear
                      </button>
                    )}

                    {/* Sort & Filter dropdown */}
                    <div ref={sortDropdownRef} style={{ position: 'relative', marginLeft: '4px' }}>
                      <button
                        onClick={() => { setShowSortDropdown(v => !v); setSortSubMenu(null); }}
                        style={{
                          padding: '6px 14px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: 600,
                          border: (sortBy !== 'creation_desc' || filterByStatus || filterByType) ? '1.5px solid #8b5cf6' : '1px solid #e2e8f0',
                          background: (sortBy !== 'creation_desc' || filterByStatus || filterByType) ? '#f5f3ff' : '#fff',
                          color: (sortBy !== 'creation_desc' || filterByStatus || filterByType) ? '#7c3aed' : '#475569',
                          cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.15s',
                          display: 'flex', alignItems: 'center', gap: '6px',
                          boxShadow: showSortDropdown ? '0 0 0 2px rgba(139,92,246,0.25)' : 'none'
                        }}
                        onMouseEnter={e => { if (sortBy === 'creation_desc' && !filterByStatus && !filterByType) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
                        onMouseLeave={e => { if (sortBy === 'creation_desc' && !filterByStatus && !filterByType) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M3 12h12M3 18h6"/></svg>
                        {filterByStatus ? `Status: ${filterByStatus === 'payment_complete' ? 'Paid' : filterByStatus === 'draft' ? 'Draft' : filterByStatus === 'submitted' ? 'Submitted' : filterByStatus === 'in_review' ? 'In Review' : filterByStatus}` : filterByType ? `Type: ${filterByType}` : 'Sort'}
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9l6 6 6-6"/></svg>
                      </button>
                      {showSortDropdown && (
                        <div style={{
                          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 9999,
                          background: '#fff', borderRadius: '10px', padding: '6px',
                          boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '1px solid #e2e8f0',
                          minWidth: '240px', animation: 'fadeIn 0.12s ease'
                        }}>
                          {/* Sort options header */}
                          {!sortSubMenu && (
                            <>
                              <div style={{ padding: '4px 12px 6px', fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sort By</div>
                              {[
                                { value: 'creation_desc', label: 'Creation Date (Newest)' },
                                { value: 'creation_asc', label: 'Creation Date (Oldest)' },
                                { value: 'updated_desc', label: 'Last Updated (Recent)' },
                                { value: 'updated_asc', label: 'Last Updated (Oldest)' },
                                { value: 'completion_desc', label: 'Completion % (High to Low)' },
                                { value: 'completion_asc', label: 'Completion % (Low to High)' },
                              ].map(opt => (
                                <button
                                  key={opt.value}
                                  onClick={() => { setSortBy(opt.value); setShowSortDropdown(false); }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                    padding: '8px 12px', borderRadius: '7px', fontSize: '0.8rem', fontWeight: 500,
                                    border: 'none', cursor: 'pointer', textAlign: 'left',
                                    background: sortBy === opt.value && !filterByStatus && !filterByType ? '#f5f3ff' : 'transparent',
                                    color: sortBy === opt.value && !filterByStatus && !filterByType ? '#7c3aed' : '#334155',
                                    transition: 'all 0.1s'
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = sortBy === opt.value ? '#f5f3ff' : '#f8fafc'}
                                  onMouseLeave={e => e.currentTarget.style.background = sortBy === opt.value && !filterByStatus && !filterByType ? '#f5f3ff' : 'transparent'}
                                >
                                  {sortBy === opt.value && !filterByStatus && !filterByType && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                                  {opt.label}
                                </button>
                              ))}
                              <div style={{ height: '1px', background: '#e2e8f0', margin: '6px 8px' }} />
                              <div style={{ padding: '4px 12px 6px', fontSize: '0.68rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Filter By</div>
                              {/* Application Status - expandable */}
                              <button
                                onClick={() => setSortSubMenu('status')}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                                  padding: '8px 12px', borderRadius: '7px', fontSize: '0.8rem', fontWeight: 500,
                                  border: 'none', cursor: 'pointer', textAlign: 'left',
                                  background: filterByStatus ? '#f5f3ff' : 'transparent',
                                  color: filterByStatus ? '#7c3aed' : '#334155',
                                  transition: 'all 0.1s'
                                }}
                                onMouseEnter={e => { if (!filterByStatus) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { if (!filterByStatus) e.currentTarget.style.background = 'transparent'; }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {filterByStatus && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                                  Application Status
                                  {filterByStatus && <span style={{ fontSize: '0.7rem', background: '#ede9fe', color: '#7c3aed', padding: '1px 6px', borderRadius: '8px', fontWeight: 600 }}>{filterByStatus === 'payment_complete' ? 'Paid' : filterByStatus === 'draft' ? 'Draft' : filterByStatus === 'submitted' ? 'Submitted' : filterByStatus === 'in_review' ? 'In Review' : filterByStatus}</span>}
                                </span>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                              </button>
                              {/* Application Type - expandable */}
                              <button
                                onClick={() => setSortSubMenu('type')}
                                style={{
                                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                                  padding: '8px 12px', borderRadius: '7px', fontSize: '0.8rem', fontWeight: 500,
                                  border: 'none', cursor: 'pointer', textAlign: 'left',
                                  background: filterByType ? '#f5f3ff' : 'transparent',
                                  color: filterByType ? '#7c3aed' : '#334155',
                                  transition: 'all 0.1s'
                                }}
                                onMouseEnter={e => { if (!filterByType) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { if (!filterByType) e.currentTarget.style.background = 'transparent'; }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  {filterByType && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                                  Application Type
                                  {filterByType && <span style={{ fontSize: '0.7rem', background: '#ede9fe', color: '#7c3aed', padding: '1px 6px', borderRadius: '8px', fontWeight: 600 }}>{filterByType}</span>}
                                </span>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5"><path d="M9 18l6-6-6-6"/></svg>
                              </button>
                              {/* Clear all filters */}
                              {(filterByStatus || filterByType || sortBy !== 'creation_desc') && (
                                <>
                                  <div style={{ height: '1px', background: '#e2e8f0', margin: '6px 8px' }} />
                                  <button
                                    onClick={() => { setSortBy('creation_desc'); setFilterByStatus(null); setFilterByType(null); setShowSortDropdown(false); }}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                      padding: '8px 12px', borderRadius: '7px', fontSize: '0.8rem', fontWeight: 600,
                                      border: 'none', cursor: 'pointer', textAlign: 'left',
                                      background: 'transparent', color: '#dc2626', transition: 'all 0.1s'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                  >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                                    Reset Sort & Filters
                                  </button>
                                </>
                              )}
                            </>
                          )}

                          {/* Status sub-menu */}
                          {sortSubMenu === 'status' && (
                            <>
                              <button
                                onClick={() => setSortSubMenu(null)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                                  padding: '6px 12px 8px', borderRadius: '7px', fontSize: '0.75rem', fontWeight: 600,
                                  border: 'none', cursor: 'pointer', textAlign: 'left',
                                  background: 'transparent', color: '#64748b', transition: 'all 0.1s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                                Application Status
                              </button>
                              <div style={{ height: '1px', background: '#e2e8f0', margin: '2px 8px 4px' }} />
                              {/* All (clear filter) */}
                              <button
                                onClick={() => { setFilterByStatus(null); setSortSubMenu(null); setShowSortDropdown(false); setCurrentPage(1); }}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                  padding: '8px 12px', borderRadius: '7px', fontSize: '0.8rem', fontWeight: 500,
                                  border: 'none', cursor: 'pointer', textAlign: 'left',
                                  background: !filterByStatus ? '#f5f3ff' : 'transparent',
                                  color: !filterByStatus ? '#7c3aed' : '#334155', transition: 'all 0.1s'
                                }}
                                onMouseEnter={e => { if (filterByStatus) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { if (filterByStatus) e.currentTarget.style.background = 'transparent'; }}
                              >
                                {!filterByStatus && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                                All Statuses
                              </button>
                              {[
                                { value: 'draft', label: 'Draft', color: '#f59e0b' },
                                { value: 'submitted', label: 'Submitted', color: '#3b82f6' },
                                { value: 'in_review', label: 'In Review', color: '#8b5cf6' },
                                { value: 'payment_complete', label: 'Paid', color: '#22c55e' },
                              ].map(s => (
                                <button
                                  key={s.value}
                                  onClick={() => { setFilterByStatus(s.value); setSortSubMenu(null); setShowSortDropdown(false); setCurrentPage(1); }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                    padding: '8px 12px', borderRadius: '7px', fontSize: '0.8rem', fontWeight: 500,
                                    border: 'none', cursor: 'pointer', textAlign: 'left',
                                    background: filterByStatus === s.value ? '#f5f3ff' : 'transparent',
                                    color: filterByStatus === s.value ? '#7c3aed' : '#334155', transition: 'all 0.1s'
                                  }}
                                  onMouseEnter={e => { if (filterByStatus !== s.value) e.currentTarget.style.background = '#f8fafc'; }}
                                  onMouseLeave={e => { if (filterByStatus !== s.value) e.currentTarget.style.background = 'transparent'; }}
                                >
                                  {filterByStatus === s.value && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: s.color, flexShrink: 0 }} />
                                  {s.label}
                                </button>
                              ))}
                            </>
                          )}

                          {/* Type sub-menu */}
                          {sortSubMenu === 'type' && (
                            <>
                              <button
                                onClick={() => setSortSubMenu(null)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '6px', width: '100%',
                                  padding: '6px 12px 8px', borderRadius: '7px', fontSize: '0.75rem', fontWeight: 600,
                                  border: 'none', cursor: 'pointer', textAlign: 'left',
                                  background: 'transparent', color: '#64748b', transition: 'all 0.1s'
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2.5"><path d="M15 18l-6-6 6-6"/></svg>
                                Application Type
                              </button>
                              <div style={{ height: '1px', background: '#e2e8f0', margin: '2px 8px 4px' }} />
                              <button
                                onClick={() => { setFilterByType(null); setSortSubMenu(null); setShowSortDropdown(false); setCurrentPage(1); }}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                  padding: '8px 12px', borderRadius: '7px', fontSize: '0.8rem', fontWeight: 500,
                                  border: 'none', cursor: 'pointer', textAlign: 'left',
                                  background: !filterByType ? '#f5f3ff' : 'transparent',
                                  color: !filterByType ? '#7c3aed' : '#334155', transition: 'all 0.1s'
                                }}
                                onMouseEnter={e => { if (filterByType) e.currentTarget.style.background = '#f8fafc'; }}
                                onMouseLeave={e => { if (filterByType) e.currentTarget.style.background = 'transparent'; }}
                              >
                                {!filterByType && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                                All Types
                              </button>
                              {[
                                { value: 'Individual/OPC', label: 'Individual / OPC', color: '#059669' },
                                { value: 'Proprietorship', label: 'Proprietorship', color: '#0891b2' },
                                { value: 'Partnership', label: 'Partnership', color: '#059669' },
                                { value: 'LLP', label: 'LLP', color: '#7c3aed' },
                                { value: 'Pvt Ltd', label: 'Pvt Ltd', color: '#7c3aed' },
                                { value: 'Public Ltd', label: 'Public Ltd', color: '#7c3aed' },
                              ].map(t => (
                                <button
                                  key={t.value}
                                  onClick={() => { setFilterByType(t.value); setSortSubMenu(null); setShowSortDropdown(false); setCurrentPage(1); }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                                    padding: '8px 12px', borderRadius: '7px', fontSize: '0.8rem', fontWeight: 500,
                                    border: 'none', cursor: 'pointer', textAlign: 'left',
                                    background: filterByType === t.value ? '#f5f3ff' : 'transparent',
                                    color: filterByType === t.value ? '#7c3aed' : '#334155', transition: 'all 0.1s'
                                  }}
                                  onMouseEnter={e => { if (filterByType !== t.value) e.currentTarget.style.background = '#f8fafc'; }}
                                  onMouseLeave={e => { if (filterByType !== t.value) e.currentTarget.style.background = 'transparent'; }}
                                >
                                  {filterByType === t.value && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="3"><path d="M20 6L9 17l-5-5"/></svg>}
                                  <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                                  {t.label}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </DateFilterContainer>

                  <ExportContainer>
                    <ExportFormatSelect
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value as 'excel' | 'pdf')}
                      disabled={isExporting}
                    >
                      <option value="excel">Excel</option>
                      <option value="pdf">PDF</option>
                    </ExportFormatSelect>
                    <ExportButton 
                      onClick={() => handleExport(false)}
                      disabled={isExporting || applications.length === 0}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16" style={{marginRight: '4px'}}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Export Filtered
                    </ExportButton>
                    <ExportButton 
                      onClick={() => handleExport(true)}
                      disabled={isExporting}
                      variant="primary"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16" style={{marginRight: '4px'}}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Export Master Data
                    </ExportButton>
                  </ExportContainer>
                </FilterContainer>

                <SearchInput
                  type="text"
                  placeholder="Search by Application ID, Name, Email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                
                {/* Bulk action bar */}
                {selectedApplicationIds.length > 0 && (
                  <BulkActionBar>
                    <span style={{ fontWeight: 500 }}>{selectedApplicationIds.length} of {stats.total} application(s) selected</span>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '1rem' }}>
                      <button
                        onClick={handleBulkLink}
                        disabled={isProcessingAction || selectedApplicationIds.length < 2}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: selectedApplicationIds.length < 2 ? '#e0e0e0' : '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          cursor: selectedApplicationIds.length < 2 ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        Link Selected
                      </button>
                      <ArchiveButton 
                        onClick={() => setShowArchiveConfirmation(true)}
                        disabled={isProcessingAction}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16" style={{marginRight: '4px'}}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                        </svg>
                        Archive Selected
                      </ArchiveButton>
                      <DeleteButton 
                        onClick={() => setShowDeleteConfirmation(true)}
                        disabled={isProcessingAction}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="16" height="16" style={{marginRight: '4px'}}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete Selected
                      </DeleteButton>
                    </div>
                  </BulkActionBar>
                )}
                
                <Table>
                  <thead>
                    <tr>
                      <Th width="40px">
                        <SelectAllCheckbox 
                          type="checkbox"
                          checked={applications.length > 0 && selectedApplicationIds.length === (statusFilter ? applications.filter(app => {
                            if (statusFilter === 'draft') {
                              return (app.status === 'draft' || !app.status) && app.applicationId && app.applicationId.startsWith('CNG-');
                            }
                            return app.status === statusFilter;
                          }).length : applications.length)}
                          onChange={toggleSelectAllApplications}
                        />
                      </Th>
                      <Th>Application ID</Th>
                      <Th>Details</Th>
                      <Th>Actions</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(statusFilter ? applications.filter(app => {
                      if (statusFilter === 'draft') {
                        return (app.status === 'draft' || !app.status) && app.applicationId && app.applicationId.startsWith('CNG-');
                      }
                      return app.status === statusFilter;
                    }) : applications)
                      .filter(app => {
                        if (!searchTerm) return true;
                        const lowerSearchTerm = searchTerm.toLowerCase();
                        const fullName = app.formData?.fullName?.toLowerCase() || '';
                        const email = app.email?.toLowerCase() || '';
                        const applicationId = app.applicationId?.toLowerCase() || '';
                        const vehicleMake = app.formData?.vehicleMake?.toLowerCase() || '';
                        const vehicleModel = app.formData?.vehicleModel?.toLowerCase() || '';

                        return (
                          fullName.includes(lowerSearchTerm) ||
                          email.includes(lowerSearchTerm) ||
                          applicationId.includes(lowerSearchTerm) ||
                          vehicleMake.includes(lowerSearchTerm) ||
                          vehicleModel.includes(lowerSearchTerm)
                        );
                      })
                      .sort((a, b) => {
                        // Always sort live/active applications to the top first
                        const aLive = liveSessionsMap[a.applicationId] ? 1 : 0;
                        const bLive = liveSessionsMap[b.applicationId] ? 1 : 0;
                        if (aLive !== bLive) return bLive - aLive;
                        // Then apply user-selected sort
                        if (sortBy === 'creation_asc') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                        if (sortBy === 'creation_desc') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                        if (sortBy === 'updated_desc') return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
                        if (sortBy === 'updated_asc') return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
                        if (sortBy === 'completion_desc') return computeCompletion(b) - computeCompletion(a);
                        if (sortBy === 'completion_asc') return computeCompletion(a) - computeCompletion(b);
                        return 0;
                      })
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((application, renderIdx) => {
                      const isAppLive = !!liveSessionsMap[application.applicationId];
                      return (
                      <motion.tr
                        key={application._id}
                        layout
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                          layout: { duration: isAppGlideAnimating ? 1.5 : 0.8, ease: [0.4, 0, 0.2, 1] },
                          opacity: { duration: 0.4, delay: renderIdx * 0.04 },
                          y: { duration: 0.4, delay: renderIdx * 0.04 },
                        }}
                        style={{
                          background: isAppLive ? 'rgba(34,197,94,0.06)' : selectedApplicationIds.includes(application._id) ? '#eff6ff' : 'transparent',
                          transition: 'background 0.3s ease',
                          borderLeft: isAppLive ? '3px solid #22c55e' : '3px solid transparent',
                        }}>
                        <Td>
                          <input 
                            type="checkbox" 
                            checked={selectedApplicationIds.includes(application._id)}
                            onChange={() => toggleApplicationSelection(application._id)}
                          />
                        </Td>
                        {/* ── Application ID + Status + Date column ── */}
                        <Td style={{ whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                            {/* Status + progress bar — top */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '1px' }}>
                              <StatusBadge status={application.status || 'pending'}>
                                {application.status ? application.status.charAt(0).toUpperCase() + application.status.slice(1) : 'Pending'}
                              </StatusBadge>
                              <CompletionWrapper style={{ flex: 1, maxWidth: '90px' }}>
                                <CompletionTrack>
                                  <CompletionFill percent={computeCompletion(application)} />
                                </CompletionTrack>
                                <CompletionText>{computeCompletion(application)}%</CompletionText>
                              </CompletionWrapper>
                            </div>
                            {/* App ID + copy + badges */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'nowrap' }}>
                              <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', letterSpacing: '-0.01em', fontFamily: "'SF Mono', 'Fira Code', monospace" }}>
                                {application.applicationId}
                              </span>
                              <span
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(application.applicationId || ''); showCopiedTooltip(e); }}
                                style={{ cursor: 'pointer', color: '#b0b8c8', transition: 'color 0.2s', display: 'inline-flex', flexShrink: 0 }}
                                title="Copy Application ID"
                              >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                              </span>
                              {application.dealershipGroupId && (
                                <span style={{ backgroundColor: '#667eea', color: 'white', padding: '1px 6px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: 600, whiteSpace: 'nowrap', lineHeight: '1.5' }}>
                                  🔗{linkedCounts[application.applicationId] || 0}
                                </span>
                              )}
                              {application.isPrimaryApplicant && application.dealershipGroupId && (
                                <span style={{ backgroundColor: '#ffd700', color: '#7c5a00', padding: '1px 5px', borderRadius: '4px', fontSize: '0.62rem', fontWeight: 700, lineHeight: '1.5' }}>
                                  PRI
                                </span>
                              )}
                            </div>
                            {/* Name */}
                            <div style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>
                              {application.formData?.fullName || application.email?.split('@')[0] || 'N/A'}
                            </div>
                            {/* Date + time */}
                            <div style={{ fontSize: '0.76rem', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg>
                              {format(new Date(application.createdAt), 'dd MMM yyyy')}
                              {' · '}
                              {(() => {
                                const appId = application.applicationId || '';
                                const match = appId.match(/(\d{2})(\d{2})(\d{2})$/);
                                if (match) return `${match[1]}:${match[2]}`;
                                return format(new Date(application.createdAt), 'HH:mm');
                              })()}
                            </div>
                            {liveSessionsMap[application.applicationId] && (
                              <span
                                onClick={(e) => { e.stopPropagation(); setLiveSessionAppId(application.applicationId); setLiveSessionEmail(application.email || ''); }}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.68rem', fontWeight: 600, color: '#16a34a', cursor: 'pointer', padding: '2px 8px', borderRadius: '8px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', transition: 'all 0.2s', width: 'fit-content' }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(34,197,94,0.2)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(34,197,94,0.1)'; }}
                              >
                                <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 6px #22c55e', animation: 'liveDot 1.5s ease-in-out infinite' }} />
                                Live
                              </span>
                            )}
                          </div>
                        </Td>
                        {/* ── Details: Phone + Email + Type column ── */}
                        <Td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {/* Phone row */}
                            {(() => {
                              const primaryPhone = application.userPhoneNumber || '';
                              const history = application.userPhoneNumberHistory || [];
                              const hasMultiple = history.length > 1;
                              return primaryPhone ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e40af', letterSpacing: '0.3px' }}>
                                    📞 {primaryPhone}
                                  </span>
                                  <span
                                    onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(primaryPhone); showCopiedTooltip(e); }}
                                    style={{ cursor: 'pointer', color: '#b0b8c8', transition: 'color 0.2s', display: 'inline-flex', flexShrink: 0 }}
                                    title="Copy phone number"
                                  >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                                  </span>
                                  {hasMultiple && (
                                    <span
                                      style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: '8px', fontSize: '0.68rem', fontWeight: 700, cursor: 'pointer' }}
                                      title={history.map((h: any) => `${h.number} (${new Date(h.capturedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })})`).join('\n')}
                                    >
                                      +{history.length - 1}
                                    </span>
                                  )}
                                </div>
                              ) : null;
                            })()}
                            {/* Email row */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.86rem', color: '#475569' }}>
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 6L2 7"/></svg>
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '260px' }}>{application.email}</span>
                              <span
                                onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(application.email || ''); showCopiedTooltip(e); }}
                                style={{ cursor: 'pointer', color: '#b0b8c8', transition: 'color 0.2s', display: 'inline-flex', flexShrink: 0 }}
                                title="Copy email"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
                              </span>
                            </div>
                            {/* Type + Applicants row */}
                            <div style={{ fontSize: '0.78rem', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                              <span style={{
                                color: application.formData?.dealershipType === 'LLP' || application.formData?.dealershipType === 'Pvt Ltd' || application.formData?.dealershipType === 'Public Ltd' ? '#7c3aed' :
                                       application.formData?.dealershipType === 'Proprietorship' ? '#0891b2' : '#059669',
                                fontWeight: 600
                              }}>
                                {application.formData?.dealershipType || 'Individual/OPC'}
                              </span>
                              <span style={{ color: '#cbd5e1' }}>•</span>
                              {(() => {
                                const partners = application.formData?.partnerDirectorDetails || [];
                                const validPartners = partners.filter((p: any) => p && (p.nameInBlocks || p.personalMobile || p.dateOfBirth));
                                const totalParticipants = 1 + validPartners.length;
                                const primaryName = application.formData?.nameInBlocks || application.email?.split('@')[0] || 'Primary Applicant';
                                const participantNames = [`👤 ${primaryName} (Primary)`, ...validPartners.map((p: any, i: number) => `👤 ${p.nameInBlocks || `Partner ${i + 1}`}`)].join('\n');
                                return (
                                  <span style={{ backgroundColor: totalParticipants > 1 ? '#fef3c7' : '#f0f9ff', color: totalParticipants > 1 ? '#92400e' : '#0369a1', padding: '1px 6px', borderRadius: '6px', fontWeight: 600, cursor: 'pointer', lineHeight: '1.6' }} title={participantNames}>
                                    {totalParticipants} applicant{totalParticipants > 1 ? 's' : ''}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </Td>
                        <Td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {/* Row 1: View + User View + Delete Application */}
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                              <ActionButton onClick={() => fetchApplicationDetails(application.applicationId)} style={{ backgroundColor: '#EFF6FF', color: '#1D4ED8', fontSize: '0.78rem' }}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="14" height="14">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                View
                              </ActionButton>
                              <ActionButton
                                onClick={() => { window.open(`/admin/applications/${encodeURIComponent(application.applicationId)}/edit`, '_blank', 'noopener,noreferrer'); }}
                                style={{ backgroundColor: '#EBF8FF', color: '#2B6CB0', fontSize: '0.78rem' }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                                  <path d="M15 3h4a2 2 0 0 1 2 2v4" /><path d="M21 3 14 10" /><path d="M17 21H7a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h4" />
                                </svg>
                                User View
                              </ActionButton>
                              <ActionButton
                                onClick={() => handleSingleDelete(application._id, application.applicationId)}
                                style={{ backgroundColor: '#FEE2E2', color: '#B91C1C', fontSize: '0.78rem' }}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="13" height="13">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Delete Application
                              </ActionButton>
                              {application.dealershipGroupId && !application.isPrimaryApplicant && (
                                <ActionButton
                                  onClick={() => handleSetPrimary(application.applicationId)}
                                  style={{ backgroundColor: '#FFF9E6', color: '#b8860b', fontSize: '0.78rem' }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="13" height="13">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                  </svg>
                                  Set Primary
                                </ActionButton>
                              )}
                              {application.dealershipGroupId && (
                                <ActionButton
                                  onClick={() => handleAdminUnlink(application.applicationId)}
                                  style={{ backgroundColor: '#FFF5F5', color: '#c53030', fontSize: '0.78rem' }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="13" height="13">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                  </svg>
                                  Unlink
                                </ActionButton>
                              )}
                            </div>
                            {/* Row 3: Session recording — inline with Delete Session */}
                            {(!isSubAdmin || subAdminPermissions.includes('session-playback')) && (() => {
                              const hasRecording = !!recordingInfoMap[application.applicationId];
                              return (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                                  <div style={{
                                    background: hasRecording ? 'linear-gradient(135deg, #10b981, #059669)' : 'linear-gradient(135deg, #d1d5db, #9ca3af)',
                                    borderRadius: '6px', padding: '1.5px', opacity: hasRecording ? 1 : 0.6, flexShrink: 0,
                                  }}>
                                    <ActionButton
                                      onClick={() => { if (hasRecording) setSessionPlayAppId(application.applicationId); }}
                                      style={{
                                        backgroundColor: hasRecording ? '#f0fdf4' : '#f3f4f6',
                                        color: hasRecording ? '#15803d' : '#9ca3af',
                                        fontSize: '0.76rem', borderRadius: '5px', border: 'none',
                                        cursor: hasRecording ? 'pointer' : 'not-allowed',
                                      }}
                                      title={hasRecording ? 'Play session recording' : 'No session recording available'}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="12" height="12">
                                        <polygon strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} points="5 3 19 12 5 21 5 3" fill="currentColor" />
                                      </svg>
                                      Play Session
                                    </ActionButton>
                                  </div>
                                  {hasRecording && (
                                    <>
                                      <span style={{ fontSize: '0.68rem', color: '#059669', fontWeight: 600, whiteSpace: 'nowrap' }}>
                                        {(() => {
                                          const size = recordingInfoMap[application.applicationId].totalDataSize;
                                          if (size < 1024) return `${size} B`;
                                          if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
                                          return `${(size / (1024 * 1024)).toFixed(2)} MB`;
                                        })()}
                                        {' · '}{recordingInfoMap[application.applicationId].totalEvents} events
                                      </span>
                                      {!isSubAdmin && (
                                        <button
                                          onClick={(e) => { e.stopPropagation(); handleDeleteSession(application.applicationId); }}
                                          style={{
                                            padding: '2px 7px', fontSize: '0.65rem', fontWeight: 600,
                                            background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626', border: '1px solid rgba(239, 68, 68, 0.2)',
                                            borderRadius: '4px', cursor: 'pointer', transition: 'all 0.15s ease',
                                            display: 'inline-flex', alignItems: 'center', gap: '3px', whiteSpace: 'nowrap',
                                          }}
                                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.15)'; }}
                                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)'; }}
                                          title="Delete session recording"
                                        >
                                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                                          Del Session
                                        </button>
                                      )}
                                    </>
                                  )}
                                  {!hasRecording && (
                                    <span style={{ fontSize: '0.65rem', color: '#9ca3af', fontStyle: 'italic' }}>No recording</span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        </Td>
                      </motion.tr>
                    );
                    })}
                  </tbody>
                </Table>

                {/* Pagination (footer — below table) */}
                {(() => {
                  const filteredApplications = (statusFilter ? applications.filter(app => {
                    if (statusFilter === 'draft') {
                      return (app.status === 'draft' || !app.status) && app.applicationId && app.applicationId.startsWith('CNG-');
                    }
                    return app.status === statusFilter;
                  }) : applications)
                    .filter(app => {
                      if (!searchTerm) return true;
                      const lowerSearchTerm = searchTerm.toLowerCase();
                      const fullName = app.formData?.fullName?.toLowerCase() || '';
                      const email = app.email?.toLowerCase() || '';
                      const applicationId = app.applicationId?.toLowerCase() || '';
                      const vehicleMake = app.formData?.vehicleMake?.toLowerCase() || '';
                      const vehicleModel = app.formData?.vehicleModel?.toLowerCase() || '';
                      return (
                        fullName.includes(lowerSearchTerm) ||
                        email.includes(lowerSearchTerm) ||
                        applicationId.includes(lowerSearchTerm) ||
                        vehicleMake.includes(lowerSearchTerm) ||
                        vehicleModel.includes(lowerSearchTerm)
                      );
                    })
                    .filter(app => {
                      if (!filterByStatus) return true;
                      if (filterByStatus === 'draft') return (app.status === 'draft' || !app.status);
                      return app.status === filterByStatus;
                    })
                    .filter(app => {
                      if (!filterByType) return true;
                      const appType = (app.formData?.dealershipType || 'Individual/OPC').toLowerCase();
                      return appType === filterByType.toLowerCase();
                    });

                  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
                  const startIndex = (currentPage - 1) * itemsPerPage + 1;
                  const endIndex = Math.min(currentPage * itemsPerPage, filteredApplications.length);

                  const getPageNumbers = () => {
                    const pages: (number | string)[] = [];
                    const maxVisible = 7;
                    if (totalPages <= maxVisible) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      if (currentPage <= 4) {
                        for (let i = 1; i <= 5; i++) pages.push(i);
                        pages.push('...');
                        pages.push(totalPages);
                      } else if (currentPage >= totalPages - 3) {
                        pages.push(1);
                        pages.push('...');
                        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
                      } else {
                        pages.push(1);
                        pages.push('...');
                        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
                        pages.push('...');
                        pages.push(totalPages);
                      }
                    }
                    return pages;
                  };

                  return totalPages > 1 ? (
                    <PaginationContainer $isFooter>
                      <PaginationInfo>
                        Showing {filteredApplications.length === 0 ? 0 : startIndex} to {endIndex} of {filteredApplications.length} applications
                      </PaginationInfo>
                      <PaginationControls>
                        <PageButton $isNav onClick={() => setCurrentPage(1)} $disabled={currentPage === 1} disabled={currentPage === 1} title="First page">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 19l-7-7 7-7M18 19l-7-7 7-7" /></svg>
                        </PageButton>
                        <PageButton $isNav onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} $disabled={currentPage === 1} disabled={currentPage === 1} title="Previous page">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                        </PageButton>
                        {getPageNumbers().map((page, index) => (
                          page === '...' ? (
                            <PageEllipsis key={`f-ellipsis-${index}`}>•••</PageEllipsis>
                          ) : (
                            <PageButton key={`f-${page}`} onClick={() => setCurrentPage(page as number)} $active={currentPage === page}>{page}</PageButton>
                          )
                        ))}
                        <PageButton $isNav onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} $disabled={currentPage === totalPages || totalPages === 0} disabled={currentPage === totalPages || totalPages === 0} title="Next page">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                        </PageButton>
                        <PageButton $isNav onClick={() => setCurrentPage(totalPages)} $disabled={currentPage === totalPages || totalPages === 0} disabled={currentPage === totalPages || totalPages === 0} title="Last page">
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 5l7 7-7 7M6 5l7 7-7 7" /></svg>
                        </PageButton>
                      </PaginationControls>
                    </PaginationContainer>
                  ) : null;
                })()}
              </Card>
            )}

            {!isLoading && !error && isViewingDetails && selectedApplication && (
              <Card
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <CardHeader>
                  <BackButton onClick={() => setIsViewingDetails(false)}>
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    </svg>
                    Back to Applications
                  </BackButton>
                  <div style={{ marginLeft: '1rem', fontSize: '0.95rem', fontWeight: 600 }}>
                    Application No: {selectedApplication.applicationId}
                  </div>
                  <StatusBadge status={selectedApplication.status || 'pending'} large>
                    {selectedApplication.status ? selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1) : 'Pending'}
                  </StatusBadge>
                </CardHeader>
                
                <DetailSection>
                  <DetailSectionTitle>Applicant Information</DetailSectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>Full Name</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.fullName || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Email</DetailLabel>
                      <DetailValue>{selectedApplication.email}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Phone Number</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.phoneNumber || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Address</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.address || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>City</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.city || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>State</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.state || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Zip Code</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.zipCode || 'Not provided'}</DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </DetailSection>

                <DetailSection>
                  <DetailSectionTitle>Vehicle Information</DetailSectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <DetailLabel>Vehicle Make</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.vehicleMake || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Vehicle Model</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.vehicleModel || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Vehicle Year</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.vehicleYear || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Vehicle VIN</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.vehicleVIN || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Fuel Type</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.fuelType || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Conversion Type</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.conversionType || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Kit Type</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.kitType || 'Not provided'}</DetailValue>
                    </DetailItem>
                    <DetailItem>
                      <DetailLabel>Cylinder Size</DetailLabel>
                      <DetailValue>{selectedApplication?.formData?.cylinderSize || 'Not provided'}</DetailValue>
                    </DetailItem>
                  </DetailGrid>
                </DetailSection>

                <DetailSection>
                  <DetailSectionTitle>Application Status</DetailSectionTitle>
                  <StatusButtonsContainer>
                    <StatusButton 
                      status="pending" 
                      active={selectedApplication.status === 'pending' || !selectedApplication.status}
                      onClick={() => updateApplicationStatus(selectedApplication.applicationId, 'pending')}
                      disabled={isLoading}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      Mark as Pending
                    </StatusButton>
                    <StatusButton 
                      status="approved" 
                      active={selectedApplication.status === 'payment_complete'}
                      onClick={() => updateApplicationStatus(selectedApplication.applicationId, 'payment_complete')}
                      disabled={isLoading}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      Approve Application
                    </StatusButton>
                    <StatusButton 
                      status="rejected" 
                      active={selectedApplication.status === 'rejected'}
                      onClick={() => updateApplicationStatus(selectedApplication.applicationId, 'rejected')}
                      disabled={isLoading}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                      </svg>
                      Reject Application
                    </StatusButton>
                    {selectedApplication.status === 'Queued for deletion' && (
                      <StatusButton 
                        status="Queued for deletion" 
                        active={selectedApplication.status === 'Queued for deletion'}
                        onClick={() => deleteApplication(selectedApplication.applicationId)}
                        disabled={isLoading}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Confirm Deletion
                      </StatusButton>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginLeft: 'auto' }}>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={handlePaymentProofFileChange}
                        style={{ fontSize: '0.8rem' }}
                      />
                      <StatusButton
                        status="payment_complete"
                        active={selectedApplication.status === 'payment_complete'}
                        onClick={handleMarkAsPaymentCompleteClick}
                        disabled={isLoading || isMarkingPaid || !paymentProofFile}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="2" y="5" width="20" height="14" rx="2" ry="2"></rect>
                          <path d="M2 10h20"></path>
                          <path d="M8 15h2"></path>
                          <path d="M14 15h2"></path>
                        </svg>
                        Mark as Payment Completed
                      </StatusButton>
                      {selectedApplication.metadata && selectedApplication.metadata.paymentProof && (
                        <div style={{ fontSize: '0.8rem' }}>
                          Payment proof: {' '}
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              handleViewPaymentProof();
                            }}
                            style={{ color: '#2563eb', textDecoration: 'underline' }}
                          >
                            View uploaded proof
                          </a>
                        </div>
                      )}
                    </div>
                  </StatusButtonsContainer>
                </DetailSection>

                {/* Document Request Panel */}
                <DocumentRequestPanel
                  applicationId={selectedApplication.applicationId}
                  applicantEmail={selectedApplication.email}
                  applicantName={selectedApplication.formData?.nameInBlocks || selectedApplication.formData?.fullName || ''}
                  partners={selectedApplication.formData?.partnerDirectorDetails || []}
                />

                <MetaInfo>
                  <MetaInfoItem>
                    <MetaInfoLabel>Created At:</MetaInfoLabel>
                    <MetaInfoValue>{format(new Date(selectedApplication.createdAt), 'MMM dd, yyyy HH:mm:ss')}</MetaInfoValue>
                  </MetaInfoItem>
                  <MetaInfoItem>
                    <MetaInfoLabel>Last Updated:</MetaInfoLabel>
                    <MetaInfoValue>{format(new Date(selectedApplication.updatedAt), 'MMM dd, yyyy HH:mm:ss')}</MetaInfoValue>
                  </MetaInfoItem>
                  <MetaInfoItem>
                    <MetaInfoLabel>Completed:</MetaInfoLabel>
                    <MetaInfoValue>{selectedApplication.isComplete ? 'Yes' : 'No'}</MetaInfoValue>
                  </MetaInfoItem>
                  <MetaInfoItem>
                    <MetaInfoLabel>Submitted:</MetaInfoLabel>
                    <MetaInfoValue>{selectedApplication.isSubmitted ? 'Yes' : 'No'}</MetaInfoValue>
                  </MetaInfoItem>
                </MetaInfo>
                <DetailSection>
                  <DetailSectionTitle>Audit Trail</DetailSectionTitle>
                  {Array.isArray(selectedApplication.history) && selectedApplication.history.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                      <Table>
                        <thead>
                          <tr>
                            <Th>Date & Time</Th>
                            <Th>Event</Th>
                            <Th>Status</Th>
                            <Th>Editor</Th>
                            <Th>Details</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedApplication.history.map((entry, idx) => {
                            const ts = (entry && (entry.timestamp || entry.changedAt)) as string | undefined;
                            const when = ts ? new Date(ts).toLocaleString() : new Date(selectedApplication.updatedAt).toLocaleString();
                            const event = (entry && entry.event) || 'Update';
                            const status = (entry && entry.status) || '-';
                            const editor = (entry && (entry.editor || entry.changedBy)) || 'system';
                            const details = (entry && entry.details) || '-';
                            return (
                              <Tr key={idx}>
                                <Td>{when}</Td>
                                <Td>{event}</Td>
                                <Td>{status}</Td>
                                <Td>{editor}</Td>
                                <Td>{details}</Td>
                              </Tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </div>
                  ) : (
                    <p style={{ padding: '1rem', color: '#718096', textAlign: 'center' }}>
                      No audit entries found.
                    </p>
                  )}
                </DetailSection>
              </Card>
            )}
          </TabContent>
        )}

        {/* ══════════════════════════════════════════════════════════════
             User Intent Tab
             ══════════════════════════════════════════════════════════════ */}
        {activeTab === 'user-intent' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="user-intent"
          >
            <div style={{ padding: '0.5rem 0' }}>
              {/* ── Stats Row ── */}
              {intentTabStats && (<>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                  {[
                    { label: 'Total Intents', value: intentTabStats.total, color: '#6366f1', bg: '#eef2ff' },
                    { label: 'Yes', value: intentTabStats.yes, color: '#166534', bg: '#dcfce7' },
                    { label: 'Maybe', value: intentTabStats.maybe, color: '#854d0e', bg: '#fef9c3' },
                    { label: 'No', value: intentTabStats.no, color: '#991b1b', bg: '#fee2e2' },
                  ].map(s => (
                    <div key={s.label} style={{
                      background: s.bg, borderRadius: 12, padding: '14px 16px', textAlign: 'center',
                      transition: 'all 0.4s ease',
                      transform: intentResetAnimating ? 'scale(0.95)' : 'scale(1)',
                      opacity: intentResetAnimating && s.value === 0 ? 0.5 : 1,
                    }}>
                      <div style={{ fontSize: '0.7rem', fontWeight: 600, color: s.color, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{s.label}</div>
                      <div style={{
                        fontSize: '1.5rem', fontWeight: 700, color: s.color,
                        transition: 'all 0.3s ease',
                      }}>{s.value}</div>
                    </div>
                  ))}
                </div>
                {/* Reset success banner */}
                {intentResetAnimating && (
                  <div style={{
                    background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
                    border: '1px solid #fca5a5',
                    borderRadius: 10, padding: '12px 18px', marginBottom: 16,
                    display: 'flex', alignItems: 'center', gap: 10,
                    animation: 'fadeIn 0.4s ease',
                  }}>
                    <span style={{ fontSize: '1.2rem' }}>🗑️</span>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#dc2626' }}>
                      Resetting {intentResetDeletedCount} intents...
                    </span>
                    <span style={{
                      width: 14, height: 14, border: '2px solid #fca5a5', borderTopColor: '#dc2626',
                      borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite',
                    }} />
                  </div>
                )}
              </>)}

              {/* ── Upload Area ── */}
              <Card style={{ marginBottom: 20, overflow: 'hidden' }}>
                <div style={{ padding: '18px 20px 14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>Upload Followup Excel</div>
                    <button
                      onClick={handleIntentResetAll}
                      disabled={intentResetting || (intentTabStats?.total || 0) === 0}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '5px 14px', border: '1px solid #fca5a5', borderRadius: 8,
                        background: 'linear-gradient(135deg, #fef2f2, #fee2e2)', color: '#dc2626',
                        cursor: (intentResetting || (intentTabStats?.total || 0) === 0) ? 'not-allowed' : 'pointer',
                        fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.2s',
                        opacity: (intentResetting || (intentTabStats?.total || 0) === 0) ? 0.4 : 1,
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={e => { if (!intentResetting) e.currentTarget.style.background = 'linear-gradient(135deg, #fee2e2, #fecaca)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #fef2f2, #fee2e2)'; }}
                    >
                      {intentResetting ? (
                        <span style={{ width: 10, height: 10, border: '2px solid #fca5a5', borderTopColor: '#dc2626', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.6s linear infinite' }} />
                      ) : (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                      )}
                      {intentResetting ? 'Resetting...' : 'Reset All Intents'}
                    </button>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 14 }}>
                    Upload the CNG Dealership Followup List (.xlsx). The system auto-detects columns: Email, Conversion Possibility (Yes/Maybe/No), and Reason/Comment. Blank values are skipped.
                  </div>
                  <div
                    className={`intent-drop-zone${intentUploadDragging ? ' dragging' : ''}`}
                    onDragOver={e => { e.preventDefault(); setIntentUploadDragging(true); }}
                    onDragLeave={() => setIntentUploadDragging(false)}
                    onDrop={e => { e.preventDefault(); setIntentUploadDragging(false); const f = e.dataTransfer.files[0]; if (f) handleIntentFileUpload(f); }}
                    style={{
                      border: '2px dashed #cbd5e1', borderRadius: 12, padding: '28px 20px',
                      textAlign: 'center', cursor: 'pointer', transition: 'all 0.3s',
                      background: intentUploadDragging ? '#eef2ff' : '#fafbfc',
                    }}
                    onClick={() => {
                      const inp = document.createElement('input');
                      inp.type = 'file';
                      inp.accept = '.xlsx,.xls';
                      inp.onchange = (ev: any) => { const f = ev.target.files[0]; if (f) handleIntentFileUpload(f); };
                      inp.click();
                    }}
                  >
                    {intentUploading ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 28, height: 28, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                        <span style={{ fontSize: '0.82rem', color: '#6366f1', fontWeight: 600 }}>Processing...</span>
                      </div>
                    ) : (
                      <>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" style={{ marginBottom: 8 }}>
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                        </svg>
                        <div style={{ fontSize: '0.82rem', color: '#475569', fontWeight: 600, marginBottom: 4 }}>
                          Drop .xlsx file here or click to browse
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                          Sheet1 with columns: Email, Conversion Possibility, Reason/Comment
                        </div>
                      </>
                    )}
                  </div>

                  {/* Upload Result */}
                  {intentUploadResult && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      style={{
                        marginTop: 12, padding: '10px 14px', borderRadius: 10,
                        background: intentUploadResult.success ? '#dcfce7' : '#fee2e2',
                        border: `1px solid ${intentUploadResult.success ? '#86efac' : '#fca5a5'}`,
                        color: intentUploadResult.success ? '#166534' : '#991b1b',
                        fontSize: '0.8rem', fontWeight: 500,
                      }}
                    >
                      {intentUploadResult.success ? (
                        <div>
                          <strong>{intentUploadResult.imported}</strong> intents imported, <strong>{intentUploadResult.skipped}</strong> skipped
                          {intentUploadResult.detectedColumns && (
                            <div style={{ fontSize: '0.7rem', color: '#166534', marginTop: 4, opacity: 0.8 }}>
                              Detected: {Object.entries(intentUploadResult.detectedColumns).filter(([,v]) => v).map(([k,v]) => `${k}="${v}"`).join(', ')}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div>{intentUploadResult.message}</div>
                      )}
                    </motion.div>
                  )}
                </div>
              </Card>

              {/* ── Intent List ── */}
              <Card>
                <div style={{ padding: '14px 20px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b' }}>All User Intents ({intentTabTotal})</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="text"
                      placeholder="Search by name, email, phone..."
                      value={intentTabSearch}
                      onChange={e => {
                        const val = e.target.value;
                        setIntentTabSearch(val);
                        if (intentSearchTimer.current) clearTimeout(intentSearchTimer.current);
                        intentSearchTimer.current = setTimeout(() => { setIntentTabPage(1); fetchIntentTabData(1, val); }, 350);
                      }}
                      style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.8rem', width: 220 }}
                    />
                    <button
                      onClick={() => fetchIntentTabData(intentTabPage, intentTabSearch)}
                      style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', color: '#64748b', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}
                    >Refresh</button>
                  </div>
                </div>

                {intentTabLoading ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>Loading...</div>
                ) : intentTabList.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8', fontSize: '0.85rem' }}>
                    No intents found. Upload a followup Excel to get started.
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>#</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>User</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#475569', width: 90 }}>Intent</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#475569' }}>Reason</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#475569', width: 80 }}>Source</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, color: '#475569', width: 60 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {intentTabList.map((item: any, idx: number) => {
                          const colors: Record<string, { bg: string; border: string; text: string; label: string }> = {
                            yes: { bg: '#dcfce7', border: '#86efac', text: '#166534', label: 'Yes' },
                            maybe: { bg: '#fef9c3', border: '#fde047', text: '#854d0e', label: 'Maybe' },
                            no: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', label: 'No' },
                          };
                          const c = colors[item.intent] || null;
                          return (
                            <tr key={item._id || idx} style={{
                              borderBottom: '1px solid #f1f5f9',
                              transition: `all 0.5s ease ${idx * 0.04}s`,
                              opacity: intentResetAnimating ? 0 : 1,
                              transform: intentResetAnimating ? 'translateX(40px)' : 'translateX(0)',
                            }}>
                              <td style={{ padding: '8px 12px', color: '#94a3b8' }}>{(intentTabPage - 1) * 20 + idx + 1}</td>
                              <td style={{ padding: '8px 12px' }}>
                                <div style={{ fontWeight: 500, color: '#1e293b' }}>{item.fullName || '—'}</div>
                                <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{item.email}</div>
                                {item.phoneNumber && <div style={{ fontSize: '0.68rem', color: '#94a3b8' }}>{item.phoneNumber}</div>}
                              </td>
                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                <button
                                  onClick={() => {
                                    setIntentEditUser({ email: item.email, current: item.intent, reason: item.reason || '' });
                                    setIntentEditValue(item.intent || '');
                                    setIntentEditReason(item.reason || '');
                                  }}
                                  style={{
                                    padding: '3px 12px', borderRadius: 12, cursor: 'pointer',
                                    border: c ? `1px solid ${c.border}` : '1px dashed #cbd5e1',
                                    background: c ? c.bg : 'transparent', color: c ? c.text : '#94a3b8',
                                    fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s',
                                  }}
                                >
                                  {c ? c.label : '—'}
                                </button>
                              </td>
                              <td style={{ padding: '8px 12px', color: '#475569', fontSize: '0.78rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.reason}>
                                {item.reason || <span style={{ color: '#cbd5e1' }}>—</span>}
                              </td>
                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                <span style={{
                                  fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase',
                                  padding: '2px 6px', borderRadius: 6,
                                  background: item.source === 'upload' ? '#dbeafe' : '#f3e8ff',
                                  color: item.source === 'upload' ? '#1e40af' : '#7c3aed',
                                }}>{item.source}</span>
                              </td>
                              <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                <button
                                  onClick={() => { if (window.confirm(`Remove intent for ${item.email}?`)) handleIntentDelete(item.email); }}
                                  title="Delete intent"
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', padding: 4, borderRadius: 4, transition: 'all 0.15s' }}
                                  onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
                                  onMouseLeave={e => { e.currentTarget.style.color = '#cbd5e1'; e.currentTarget.style.background = 'none'; }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Pagination */}
                {intentTabTotalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, padding: '12px', flexWrap: 'wrap' }}>
                    <button
                      disabled={intentTabPage <= 1}
                      onClick={() => { setIntentTabPage(p => p - 1); fetchIntentTabData(intentTabPage - 1, intentTabSearch); }}
                      style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#475569', fontSize: '0.75rem', cursor: intentTabPage <= 1 ? 'not-allowed' : 'pointer', opacity: intentTabPage <= 1 ? 0.4 : 1 }}
                    >Prev</button>
                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Page {intentTabPage} of {intentTabTotalPages}</span>
                    <button
                      disabled={intentTabPage >= intentTabTotalPages}
                      onClick={() => { setIntentTabPage(p => p + 1); fetchIntentTabData(intentTabPage + 1, intentTabSearch); }}
                      style={{ padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#fff', color: '#475569', fontSize: '0.75rem', cursor: intentTabPage >= intentTabTotalPages ? 'not-allowed' : 'pointer', opacity: intentTabPage >= intentTabTotalPages ? 0.4 : 1 }}
                    >Next</button>
                  </div>
                )}
              </Card>
            </div>
          </TabContent>
        )}

        {activeTab === 'locations' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="locations"
          >
            <Card style={{ overflow: 'visible' }}>
              {/* Header row: Title + Search + Buttons all inline */}
              <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '1rem' }}>
                <CardTitle style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="24" height="24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Area Stretch Locations ({areaStretchLocations.length})
                </CardTitle>
                <input
                  type="text"
                  placeholder="Search locations..."
                  value={locationSearchTerm}
                  onChange={(e) => { setLocationSearchTerm(e.target.value); setLocationsPage(1); }}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    fontSize: '0.88rem',
                    width: '220px',
                    marginLeft: 'auto'
                  }}
                />
                <ActionButton
                  onClick={handleExportLocationsXlsx}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{ 
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: '#ffffff',
                    fontWeight: '600',
                    padding: '8px 14px',
                    fontSize: '0.82rem',
                    boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="14" height="14" style={{ marginRight: '5px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                  </svg>
                  Export XLSX
                </ActionButton>
                <ActionButton
                  onClick={handleAddLocation}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{ 
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    color: '#ffffff',
                    fontWeight: '600',
                    padding: '8px 14px',
                    fontSize: '0.82rem',
                    boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" width="14" height="14" style={{ marginRight: '5px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                  Add Location
                </ActionButton>
              </div>

              {isLoadingAreaStretchLocations ? (
                <LoadingSpinner />
              ) : areaStretchLocationsError ? (
                <ErrorText>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="20" height="20" style={{ marginRight: '8px' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {areaStretchLocationsError}
                </ErrorText>
              ) : filteredAreaStretchLocations.length === 0 ? (
                <p style={{ color: '#4a5568', lineHeight: '1.6', fontSize: '0.95rem', textAlign: 'center', padding: '2rem' }}>
                  {locationSearchTerm ? 'No locations match your search.' : 'No locations found. Click "Add Location" to create one.'}
                </p>
              ) : (() => {
                const totalFiltered = filteredAreaStretchLocations.length;
                const isShowAll = locationsPerPage === 0;
                const totalPages = isShowAll ? 1 : Math.ceil(totalFiltered / locationsPerPage);
                const safePage = Math.min(locationsPage, totalPages);
                const startIdx = isShowAll ? 0 : (safePage - 1) * locationsPerPage;
                const endIdx = isShowAll ? totalFiltered : Math.min(startIdx + locationsPerPage, totalFiltered);
                const paginatedLocations = filteredAreaStretchLocations.slice(startIdx, endIdx);

                const getPageNumbers = () => {
                  const pages: (number | string)[] = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) pages.push(i);
                  } else {
                    pages.push(1);
                    if (safePage > 3) pages.push('...');
                    const start = Math.max(2, safePage - 1);
                    const end = Math.min(totalPages - 1, safePage + 1);
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (safePage < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };

                const renderPagination = (position: 'top' | 'bottom') => (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: position === 'bottom' ? '1rem' : 0, marginBottom: position === 'top' ? '0.75rem' : 0, flexWrap: 'wrap', gap: '10px' }}>
                    <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
                      Showing {startIdx + 1}-{endIdx} of {totalFiltered} locations
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <select
                        value={locationsPerPage}
                        onChange={(e) => { setLocationsPerPage(Number(e.target.value)); setLocationsPage(1); }}
                        style={{
                          padding: '5px 8px', borderRadius: '6px', border: '1px solid #e2e8f0',
                          fontSize: '0.8rem', color: '#475569', background: '#f8fafc', cursor: 'pointer'
                        }}
                      >
                        {[10, 20, 50, 100].map(n => (
                          <option key={n} value={n}>{n} / page</option>
                        ))}
                        <option value={0}>All</option>
                      </select>
                      <button onClick={() => setLocationsPage(1)} disabled={safePage === 1}
                        style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', background: safePage === 1 ? '#f1f5f9' : '#fff', color: safePage === 1 ? '#cbd5e1' : '#475569', cursor: safePage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>&laquo;</button>
                      <button onClick={() => setLocationsPage(Math.max(1, safePage - 1))} disabled={safePage === 1}
                        style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', background: safePage === 1 ? '#f1f5f9' : '#fff', color: safePage === 1 ? '#cbd5e1' : '#475569', cursor: safePage === 1 ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>&lsaquo;</button>
                      {getPageNumbers().map((p, idx) =>
                        p === '...' ? (
                          <span key={`${position}-ellipsis-${idx}`} style={{ color: '#94a3b8', fontSize: '0.8rem', padding: '0 2px' }}>...</span>
                        ) : (
                          <button key={`${position}-${p}`} onClick={() => setLocationsPage(p as number)}
                            style={{ width: '30px', height: '30px', borderRadius: '50%', border: (p as number) === safePage ? '2px solid #4c6ef5' : '1px solid #e2e8f0', background: (p as number) === safePage ? '#4c6ef5' : '#fff', color: (p as number) === safePage ? '#fff' : '#475569', fontWeight: (p as number) === safePage ? 700 : 500, fontSize: '0.78rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}>{p}</button>
                        )
                      )}
                      <button onClick={() => setLocationsPage(Math.min(totalPages, safePage + 1))} disabled={safePage === totalPages}
                        style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', background: safePage === totalPages ? '#f1f5f9' : '#fff', color: safePage === totalPages ? '#cbd5e1' : '#475569', cursor: safePage === totalPages ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>&rsaquo;</button>
                      <button onClick={() => setLocationsPage(totalPages)} disabled={safePage === totalPages}
                        style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #e2e8f0', background: safePage === totalPages ? '#f1f5f9' : '#fff', color: safePage === totalPages ? '#cbd5e1' : '#475569', cursor: safePage === totalPages ? 'not-allowed' : 'pointer', fontSize: '0.78rem', fontWeight: 600 }}>&raquo;</button>
                    </div>
                  </div>
                );

                return (
                  <>
                    {renderPagination('top')}
                    <div style={{ overflowX: 'auto', margin: '0 -2rem', padding: '0 2rem' }}>
                      <Table style={{ minWidth: '900px', fontSize: '0.85rem' }}>
                        <thead>
                          <tr>
                            {[
                              { key: 'srNo', label: 'Sr.No', sortable: true },
                              { key: 'location', label: 'Location', sortable: true, minWidth: '120px' },
                              { key: 'ca', label: 'CA', sortable: true },
                              { key: 'area', label: 'Area', sortable: true },
                              { key: 'stretchSide', label: 'Side', sortable: true },
                              { key: 'from', label: 'From', sortable: false, minWidth: '100px' },
                              { key: 'to', label: 'To', sortable: false, minWidth: '100px' },
                              { key: 'minPlotFrontageMeters', label: 'Frontage', sortable: true },
                              { key: 'roadType', label: 'Road Type', sortable: false },
                              { key: 'minPlotDepthMeters', label: 'Depth', sortable: true },
                              { key: 'actions', label: 'Actions', sortable: false },
                            ].map(col => (
                              <Th
                                key={col.key}
                                onClick={col.sortable ? () => handleLocationSort(col.key) : undefined}
                                style={{
                                  padding: '0.6rem 0.7rem', fontSize: '0.75rem',
                                  cursor: col.sortable ? 'pointer' : 'default',
                                  userSelect: col.sortable ? 'none' : undefined,
                                  minWidth: (col as any).minWidth || undefined,
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {col.label}
                                {col.sortable && locationSortField === col.key && (
                                  <span style={{ marginLeft: '4px', fontSize: '0.7rem' }}>
                                    {locationSortDir === 'asc' ? '▲' : '▼'}
                                  </span>
                                )}
                                {col.sortable && locationSortField !== col.key && (
                                  <span style={{ marginLeft: '4px', fontSize: '0.65rem', opacity: 0.35 }}>⇅</span>
                                )}
                              </Th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedLocations.map((loc: any) => (
                            <Tr key={loc._id}>
                              <Td style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem' }}><strong>{loc.srNo || '-'}</strong></Td>
                              <Td style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem', maxWidth: '150px', wordBreak: 'break-word' }}>{loc.location || '-'}</Td>
                              <Td style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem' }}>{loc.ca || '-'}</Td>
                              <Td style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem' }}>{loc.area || '-'}</Td>
                              <Td style={{ padding: '0.5rem 0.7rem' }}>
                                <Badge status={loc.stretchSide === 'LHS' ? 'approved' : loc.stretchSide === 'RHS' ? 'pending' : 'draft'}>
                                  {loc.stretchSide || '-'}
                                </Badge>
                              </Td>
                              <Td style={{ padding: '0.5rem 0.7rem', fontSize: '0.8rem', maxWidth: '120px', wordBreak: 'break-word' }}>{loc.from || '-'}</Td>
                              <Td style={{ padding: '0.5rem 0.7rem', fontSize: '0.8rem', maxWidth: '120px', wordBreak: 'break-word' }}>{loc.to || '-'}</Td>
                              <Td style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem', textAlign: 'center' }}>{loc.minPlotFrontageMeters || '-'}</Td>
                              <Td style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem' }}>{loc.roadType || '-'}</Td>
                              <Td style={{ padding: '0.5rem 0.7rem', fontSize: '0.85rem', textAlign: 'center' }}>{loc.minPlotDepthMeters || '-'}</Td>
                              <Td style={{ padding: '0.5rem 0.7rem' }}>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                  <button
                                    onClick={() => handleEditLocation(loc)}
                                    style={{
                                      padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600,
                                      color: '#2563eb', background: '#eff6ff', border: '1px solid #bfdbfe',
                                      borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' as any
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#dbeafe'; e.currentTarget.style.borderColor = '#93c5fd'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#bfdbfe'; }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDeleteLocation(loc._id)}
                                    style={{
                                      padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600,
                                      color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca',
                                      borderRadius: '6px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' as any
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.borderColor = '#fecaca'; }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </Td>
                            </Tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {renderPagination('bottom')}
                  </>
                );
              })()}
            </Card>
          </TabContent>
        )}

        {activeTab === 'visitor-chats' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="visitor-chats"
          >
            <AdminChatPanel apiBase={API_BASE} isSubAdmin={isSubAdmin} />
          </TabContent>
        )}

        {/* Location Add/Edit Modal */}
        {showLocationModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '30px',
              width: '90%',
              maxWidth: '800px',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}>
              <h2 style={{ marginBottom: '20px', color: '#1a202c' }}>
                {editingLocation ? 'Edit Location' : 'Add New Location'}
              </h2>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Sr.No</label>
                  <input type="text" name="srNo" value={locationFormData.srNo} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Location</label>
                  <input type="text" name="location" value={locationFormData.location} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>CA</label>
                  <input type="text" name="ca" value={locationFormData.ca} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Area</label>
                  <input type="text" name="area" value={locationFormData.area} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Stretch Side (LHS/RHS)</label>
                  <select name="stretchSide" value={locationFormData.stretchSide} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <option value="">Select</option>
                    <option value="LHS">LHS</option>
                    <option value="RHS">RHS</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>From</label>
                  <input type="text" name="from" value={locationFormData.from} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Lat/Long (From)</label>
                  <input type="text" name="fromLatLong" value={locationFormData.fromLatLong} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>To</label>
                  <input type="text" name="to" value={locationFormData.to} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Lat/Long (To)</label>
                  <input type="text" name="toLatLong" value={locationFormData.toLatLong} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Min Frontage (m)</label>
                  <input type="text" name="minPlotFrontageMeters" value={locationFormData.minPlotFrontageMeters} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Road Type (NH/STATE/CITY)</label>
                  <input type="text" name="roadType" value={locationFormData.roadType} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Min Depth (m)</label>
                  <input type="text" name="minPlotDepthMeters" value={locationFormData.minPlotDepthMeters} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>District</label>
                  <input type="text" name="district" value={locationFormData.district} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>State</label>
                  <input type="text" name="state" value={locationFormData.state} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Stretch Sr.No Area</label>
                  <input type="text" name="stretchSrNoArea" value={locationFormData.stretchSrNoArea} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 600, color: '#4a5568' }}>Area Stretch</label>
                  <input type="text" name="areaStretch" value={locationFormData.areaStretch} onChange={handleLocationFormChange}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '25px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setShowLocationModal(false); resetLocationForm(); }}
                  style={{
                    padding: '10px 25px',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    background: 'white',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveLocation}
                  style={{
                    padding: '10px 25px',
                    borderRadius: '8px',
                    border: 'none',
                    background: 'linear-gradient(135deg, #4A6CF7 0%, #667eea 100%)',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  {editingLocation ? 'Update Location' : 'Create Location'}
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'controls' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="controls"
          >
            {/* ── Google Analytics Card ── */}
            <Card>
              <SectionTitle>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/>
                  </svg>
                  Google Analytics
                </span>
              </SectionTitle>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#4a5568' }}>
                Add your Google Analytics Measurement ID to track user behaviour across the web app. The tracking code is automatically injected for all visitors.
              </p>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', color: '#2d3748' }}>
                  Measurement ID
                </label>
                <input
                  type="text"
                  value={gaMeasurementId}
                  onChange={e => setGaMeasurementId(e.target.value)}
                  placeholder="G-XXXXXXXXXX"
                  style={{
                    width: '100%', maxWidth: '360px', padding: '0.5rem 0.75rem',
                    borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.9rem',
                    fontFamily: 'monospace',
                  }}
                />
                <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: '#718096' }}>
                  Find your Measurement ID in{' '}
                  <a href="https://analytics.google.com/" target="_blank" rel="noreferrer" style={{ color: '#3182ce' }}>
                    Google Analytics
                  </a>{' '}
                  &rarr; Admin &rarr; Data Streams &rarr; Web. Format: <code>G-XXXXXXXXXX</code>
                </p>
              </div>

              {/* Action buttons */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <button
                  type="button"
                  onClick={async () => {
                    if (!gaMeasurementId.trim()) {
                      setGaTestResult({ success: false, message: 'Please enter a Measurement ID first.' });
                      return;
                    }
                    setGaTestingInProgress(true);
                    setGaTestResult(null);
                    try {
                      const resp = await fetch(`${API_BASE}/api/admin/test-google-analytics`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ measurementId: gaMeasurementId.trim() }),
                      });
                      const data = await resp.json();
                      setGaTestResult({ success: !!data.success, message: data.message || 'Test completed.' });
                    } catch {
                      setGaTestResult({ success: false, message: 'Failed to reach server for GA test.' });
                    } finally {
                      setGaTestingInProgress(false);
                    }
                  }}
                  disabled={gaTestingInProgress || !gaMeasurementId.trim()}
                  style={{
                    padding: '0.5rem 1rem', borderRadius: '6px',
                    border: '1px solid #d69e2e', backgroundColor: gaTestingInProgress ? '#fefcbf' : '#f6e05e',
                    color: '#744210', fontSize: '0.85rem', fontWeight: 600,
                    cursor: gaTestingInProgress || !gaMeasurementId.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  {gaTestingInProgress ? 'Testing...' : 'Test GA'}
                </button>
              </div>

              {gaTestResult && (
                <div style={{
                  marginBottom: '1rem', padding: '0.6rem 0.9rem', borderRadius: '6px', fontSize: '0.85rem',
                  backgroundColor: gaTestResult.success ? '#f0fff4' : '#fff5f5',
                  color: gaTestResult.success ? '#22543d' : '#742a2a',
                  border: gaTestResult.success ? '1px solid #9ae6b4' : '1px solid #feb2b2',
                }}>
                  {gaTestResult.message}
                </div>
              )}

              {/* GA Status indicator */}
              <div style={{
                padding: '0.6rem 0.9rem', borderRadius: '8px', fontSize: '0.82rem',
                background: gaMeasurementId.trim() ? '#f0fff4' : '#f7fafc',
                border: gaMeasurementId.trim() ? '1px solid #c6f6d5' : '1px solid #e2e8f0',
                display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem',
              }}>
                <div style={{
                  width: '8px', height: '8px', borderRadius: '50%',
                  background: gaMeasurementId.trim() ? '#48bb78' : '#a0aec0',
                }} />
                <span style={{ color: gaMeasurementId.trim() ? '#276749' : '#718096' }}>
                  {gaMeasurementId.trim()
                    ? `Tracking active — ${gaMeasurementId.trim()} is injected on all pages`
                    : 'No Measurement ID configured — analytics tracking is disabled'}
                </span>
              </div>

              {/* Save button at bottom */}
              <div style={{ marginTop: '0.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={async () => {
                    setGaSaving(true);
                    try {
                      await axios.post(`${API_BASE}/api/admin/settings`, {
                        googleAnalyticsMeasurementId: gaMeasurementId.trim(),
                      });
                      setGaToast({ show: true, success: true, message: 'Google Analytics settings saved! Tracking will take effect on next page load.' });
                      setTimeout(() => setGaToast(prev => ({ ...prev, show: false })), 4000);
                    } catch {
                      setGaToast({ show: true, success: false, message: 'Failed to save Google Analytics settings.' });
                      setTimeout(() => setGaToast(prev => ({ ...prev, show: false })), 4000);
                    } finally {
                      setGaSaving(false);
                    }
                  }}
                  disabled={gaSaving}
                  style={{
                    padding: '0.6rem 1.5rem', borderRadius: '6px',
                    border: '1px solid #d69e2e', backgroundColor: '#ecc94b',
                    color: '#744210', fontSize: '0.9rem', fontWeight: 600,
                    cursor: gaSaving ? 'not-allowed' : 'pointer',
                  }}
                >
                  {gaSaving ? 'Saving...' : 'Save GA Settings'}
                </button>
              </div>

              {/* Floating toast */}
              {gaToast.show && (
                <div style={{
                  position: 'fixed', top: '1.5rem', right: '1.5rem', zIndex: 9999,
                  padding: '0.85rem 1.5rem', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 500,
                  backgroundColor: gaToast.success ? '#f0fff4' : '#fff5f5',
                  color: gaToast.success ? '#22543d' : '#742a2a',
                  border: gaToast.success ? '1px solid #9ae6b4' : '1px solid #feb2b2',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  animation: 'slideInRight 0.3s ease-out',
                }}>
                  {gaToast.message}
                </div>
              )}
            </Card>

            <Card>
              <SectionTitle>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span role="img" aria-label="AI">⚙️</span> AI Configuration (OpenRouter)
                </span>
              </SectionTitle>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#4a5568' }}>
                Configure OpenRouter API key and models for AI‑powered features. The API key is stored in settings; do not hardcode it.
              </p>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.25rem', color: '#2d3748' }}>
                  OpenRouter API Key
                </label>
                <input
                  type="password"
                  value={openRouterApiKey}
                  onChange={(e) => setOpenRouterApiKey(e.target.value)}
                  placeholder="Enter your OpenRouter API key"
                  style={{
                    width: '100%',
                    maxWidth: '420px',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e0',
                    fontSize: '0.9rem',
                  }}
                />
                <p style={{ margin: '0.35rem 0 0.5rem 0', fontSize: '0.8rem', color: '#718096' }}>
                  Get your API key from <a href="https://openrouter.ai/keys" target="_blank" rel="noreferrer" style={{ color: '#3182ce' }}>openrouter.ai/keys</a>.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.75rem', marginTop: '0.25rem' }}>
                  <button
                    type="button"
                    onClick={handleTestOpenRouterApiKey}
                    disabled={isTestingOpenRouterKey || !openRouterApiKey}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '6px',
                      border: '1px solid #3182ce',
                      backgroundColor: isTestingOpenRouterKey ? '#90cdf4' : '#3182ce',
                      color: 'white',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      cursor: isTestingOpenRouterKey || !openRouterApiKey ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isTestingOpenRouterKey ? 'Testing...' : 'Test API Key'}
                  </button>
                </div>

                {/* Floating toast for AI settings */}
                {aiSettingsToast.show && (
                  <div
                    style={{
                      position: 'fixed',
                      top: '1.5rem',
                      right: '1.5rem',
                      zIndex: 9999,
                      padding: '0.85rem 1.5rem',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      backgroundColor: aiSettingsToast.success ? '#f0fff4' : '#fff5f5',
                      color: aiSettingsToast.success ? '#22543d' : '#742a2a',
                      border: aiSettingsToast.success ? '1px solid #9ae6b4' : '1px solid #feb2b2',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      animation: 'slideInRight 0.3s ease-out',
                    }}
                  >
                    {aiSettingsToast.message}
                  </div>
                )}
                {openRouterTestMessage && (
                  <div
                    style={{
                      marginTop: '0.75rem',
                      padding: '0.6rem 0.9rem',
                      borderRadius: '6px',
                      fontSize: '0.85rem',
                      backgroundColor: openRouterTestSuccess ? '#f0fff4' : '#fff5f5',
                      color: openRouterTestSuccess ? '#22543d' : '#742a2a',
                      border: openRouterTestSuccess ? '1px solid #9ae6b4' : '1px solid #feb2b2',
                    }}
                  >
                    {openRouterTestMessage}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '1.25rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', fontWeight: 600, color: '#2d3748' }}>AI Model</h3>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#718096' }}>
                  Select the primary model that will be used by default for AI features.
                </p>
                <select
                  value={openRouterDefaultModel}
                  onChange={(e) => {
                    const newModel = e.target.value;
                    setOpenRouterDefaultModel(newModel);
                    if (newModel) {
                      saveOpenRouterSettings(newModel);
                    }
                  }}
                  style={{
                    width: '100%',
                    maxWidth: '420px',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '6px',
                    border: '1px solid #cbd5e0',
                    fontSize: '0.9rem',
                    backgroundColor: '#f7fafc',
                  }}
                >
                  <option value="">Select a default model</option>
                  <option value="google/gemini-2.0-flash">Google Gemini 2.0 Flash</option>
                  <option value="gpt-4.1">OpenAI GPT‑4.1</option>
                  <option value="gpt-4.1-mini">OpenAI GPT‑4.1 Mini</option>
                  {openRouterCustomModels.map((model) => (
                    <option key={model.modelId} value={model.modelId}>
                      {model.label || model.modelId}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginTop: '1.5rem' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '0.95rem', fontWeight: 600, color: '#2d3748' }}>Custom AI Models</h3>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#718096' }}>
                  Register any additional OpenRouter models you plan to use (e.g. <code>google/gemma-2-27b-it</code>).
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <input
                    type="text"
                    placeholder="Display name (e.g. Google Gemma 2‑27B IT)"
                    value={newCustomModelLabel}
                    onChange={(e) => setNewCustomModelLabel(e.target.value)}
                    style={{
                      flex: 1,
                      minWidth: '180px',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e0',
                      fontSize: '0.85rem',
                    }}
                  />
                  <input
                    type="text"
                    placeholder="Model ID (e.g. google/gemma-2-27b-it)"
                    value={newCustomModelId}
                    onChange={(e) => setNewCustomModelId(e.target.value)}
                    style={{
                      flex: 1,
                      minWidth: '220px',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #cbd5e0',
                      fontSize: '0.85rem',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!newCustomModelId.trim()) return;
                      const entry = {
                        label: newCustomModelLabel.trim() || newCustomModelId.trim(),
                        modelId: newCustomModelId.trim(),
                      };
                      setOpenRouterCustomModels((prev) => {
                        if (prev.some((m) => m.modelId === entry.modelId)) return prev;
                        return [...prev, entry];
                      });
                      setNewCustomModelLabel('');
                      setNewCustomModelId('');
                    }}
                    style={{
                      padding: '0.5rem 0.9rem',
                      borderRadius: '6px',
                      border: '1px solid #4c6ef5',
                      backgroundColor: '#4c6ef5',
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      cursor: newCustomModelId.trim() ? 'pointer' : 'not-allowed',
                      opacity: newCustomModelId.trim() ? 1 : 0.6,
                    }}
                  >
                    + Add Model
                  </button>
                </div>

                {openRouterCustomModels.length > 0 && (
                  <div
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '0.75rem 0.9rem',
                      backgroundColor: '#f8fafc',
                    }}
                  >
                    {openRouterCustomModels.map((model) => (
                      <div
                        key={model.modelId}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '0.35rem 0',
                          borderBottom: '1px solid #e2e8f0',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 500, color: '#2d3748' }}>{model.label || model.modelId}</div>
                          <div style={{ fontSize: '0.8rem', color: '#718096' }}>{model.modelId}</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setOpenRouterCustomModels((prev) => prev.filter((m) => m.modelId !== model.modelId));
                            if (openRouterDefaultModel === model.modelId) {
                              setOpenRouterDefaultModel('');
                            }
                          }}
                          style={{
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: '#e53e3e',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => saveOpenRouterSettings()}
                  style={{
                    padding: '0.6rem 1.5rem',
                    borderRadius: '6px',
                    border: '1px solid #4c6ef5',
                    backgroundColor: '#4c6ef5',
                    color: 'white',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Save AI Settings
                </button>
              </div>
            </Card>

            <Card>
              <SectionTitle>Location XLSX Upload</SectionTitle>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#4a5568' }}>
                Upload an Excel (.xlsx) file containing location stretches with the following columns:
                <strong> Sr.No</strong>, <strong> Location</strong>, <strong> CA</strong>, <strong> Area</strong>,
                <strong> Strech Side(LHS/RHS)</strong>, <strong> From</strong>, <strong> Lattitude &amp; Longitude</strong>,
                <strong> To</strong>, <strong> Lattitude &amp; Longitude</strong>,
                <strong> Minimum Plot Size Required (Frontage in Meter)</strong>, <strong> Road NH/STATE/CITY</strong>,
                <strong> Minimum Plot Size Required(Depth in Meter)</strong>.
                Use the template to ensure the header row matches exactly.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={handleDownloadLocationsTemplate}
                  style={{
                    padding: '0.6rem 1.2rem',
                    borderRadius: '6px',
                    border: '1px solid #4c6ef5',
                    backgroundColor: '#4c6ef5',
                    color: 'white',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Download Template
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    onChange={(e) => {
                      const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                      setLocationsUploadFile(file);
                    }}
                    style={{ maxWidth: '260px' }}
                  />
                  <button
                    type="button"
                    onClick={handleUploadLocationsXlsx}
                    disabled={isLocationsUploading}
                    style={{
                      padding: '0.6rem 1.2rem',
                      borderRadius: '6px',
                      border: '1px solid #38a169',
                      backgroundColor: isLocationsUploading ? '#9ae6b4' : '#38a169',
                      color: 'white',
                      fontSize: '0.9rem',
                      fontWeight: 600,
                      cursor: isLocationsUploading ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {isLocationsUploading ? 'Uploading...' : 'Upload & Validate'}
                  </button>
                </div>
              </div>
              {locationsUploadStatus && (
                <div
                  style={{
                    marginTop: '1rem',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    color:
                      locationsUploadStatus.type === 'success'
                        ? '#22543d'
                        : locationsUploadStatus.type === 'error'
                        ? '#742a2a'
                        : '#234e52',
                    backgroundColor:
                      locationsUploadStatus.type === 'success'
                        ? '#f0fff4'
                        : locationsUploadStatus.type === 'error'
                        ? '#fff5f5'
                        : '#e6fffa',
                    border:
                      locationsUploadStatus.type === 'success'
                        ? '1px solid #9ae6b4'
                        : locationsUploadStatus.type === 'error'
                        ? '1px solid #feb2b2'
                        : '1px solid #81e6d9',
                  }}
                >
                  {locationsUploadStatus.message}
                </div>
              )}
            </Card>

            <Card>
              <SectionTitle>Social Media Share Settings</SectionTitle>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#4a5568' }}>
                Configure the URLs that social media share icons link to across the site (homepage, user dashboard).
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                {([
                  { key: 'whatsapp', label: 'WhatsApp Share URL', placeholder: 'https://wa.me/?text=...' },
                  { key: 'facebook', label: 'Facebook Share URL', placeholder: 'https://www.facebook.com/sharer/sharer.php?u=...' },
                  { key: 'twitter', label: 'Twitter / X Share URL', placeholder: 'https://twitter.com/intent/tweet?url=...' },
                  { key: 'reddit', label: 'Reddit Share URL', placeholder: 'https://reddit.com/submit?url=...' },
                  { key: 'instagram', label: 'Instagram Profile URL', placeholder: 'https://instagram.com/yourpage' },
                  { key: 'email', label: 'Email Share (mailto)', placeholder: 'mailto:?subject=...&body=...' },
                ] as { key: keyof typeof socialShareLinks; label: string; placeholder: string }[]).map(item => (
                  <div key={item.key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600, color: '#2d3748' }}>{item.label}</label>
                    <input
                      type="text"
                      value={socialShareLinks[item.key]}
                      onChange={e => setSocialShareLinks(prev => ({ ...prev, [item.key]: e.target.value }))}
                      placeholder={item.placeholder}
                      style={{
                        padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e0',
                        fontSize: '0.85rem', outline: 'none', transition: 'border-color 0.2s',
                      }}
                      onFocus={e => (e.target.style.borderColor = '#4c6ef5')}
                      onBlur={e => (e.target.style.borderColor = '#cbd5e0')}
                    />
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
                <button
                  type="button"
                  disabled={isSavingSocialLinks}
                  onClick={async () => {
                    setIsSavingSocialLinks(true);
                    setSocialLinksSaveMsg(null);
                    try {
                      await axios.post(`${API_BASE}/api/admin/settings`, { socialShareLinks });
                      setSocialLinksSaveMsg({ type: 'success', text: 'Social media share links saved!' });
                      setTimeout(() => setSocialLinksSaveMsg(null), 3000);
                    } catch (err) {
                      setSocialLinksSaveMsg({ type: 'error', text: 'Failed to save. Please try again.' });
                    } finally {
                      setIsSavingSocialLinks(false);
                    }
                  }}
                  style={{
                    padding: '0.6rem 1.4rem', borderRadius: '6px', border: 'none',
                    backgroundColor: isSavingSocialLinks ? '#a0aec0' : '#4c6ef5', color: 'white',
                    fontSize: '0.9rem', fontWeight: 600,
                    cursor: isSavingSocialLinks ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isSavingSocialLinks ? 'Saving...' : 'Save Share Links'}
                </button>
                {socialLinksSaveMsg && (
                  <span style={{
                    fontSize: '0.85rem', fontWeight: 500,
                    color: socialLinksSaveMsg.type === 'success' ? '#22543d' : '#742a2a',
                  }}>
                    {socialLinksSaveMsg.text}
                  </span>
                )}
              </div>
            </Card>

            <Card>
              <SectionTitle>Application Form Controls</SectionTitle>
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '1rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0'
                }}>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#2d3748' }}>
                      Show "Fill Dummy Data" Button
                    </h3>
                    <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#718096' }}>
                      Toggle visibility of the yellow "Fill Dummy Data" button on all application form pages
                    </p>
                  </div>
                  <label style={{ 
                    position: 'relative', 
                    display: 'inline-block', 
                    width: '60px', 
                    height: '30px' 
                  }}>
                    <input 
                      type="checkbox" 
                      checked={showDummyDataButton}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        saveShowDummyDataButtonSetting(newValue);
                      }}
                      style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                      position: 'absolute',
                      cursor: 'pointer',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: showDummyDataButton ? '#4c51bf' : '#cbd5e0',
                      transition: '0.4s',
                      borderRadius: '30px'
                    }}>
                      <span style={{
                        position: 'absolute',
                        content: '',
                        height: '22px',
                        width: '22px',
                        left: showDummyDataButton ? '34px' : '4px',
                        bottom: '4px',
                        backgroundColor: 'white',
                        transition: '0.4s',
                        borderRadius: '50%'
                      }} />
                    </span>
                  </label>
                </div>
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  backgroundColor: showDummyDataButton ? '#e6fffa' : '#fff5f5', 
                  borderRadius: '6px',
                  border: `1px solid ${showDummyDataButton ? '#81e6d9' : '#feb2b2'}`
                }}>
                  <p style={{ 
                    margin: 0, 
                    fontSize: '0.875rem', 
                    color: showDummyDataButton ? '#234e52' : '#742a2a',
                    fontWeight: 500
                  }}>
                    {showDummyDataButton ? '✓ Button is currently VISIBLE on form pages' : '✗ Button is currently HIDDEN on form pages'}
                  </p>
                </div>
                <div style={{ marginTop: '1.5rem' }}>
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '1rem',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#2d3748' }}>
                        Show "Linked Applications" Navigation Item
                      </h3>
                      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#718096' }}>
                        Toggle visibility of the "Linked Applications" option in the user dashboard navigation menu
                      </p>
                    </div>
                    <label style={{ 
                      position: 'relative', 
                      display: 'inline-block', 
                      width: '60px', 
                      height: '30px' 
                    }}>
                      <input 
                        type="checkbox" 
                        checked={showLinkedApplicationsNav}
                        onChange={async (e) => {
                          const newValue = e.target.checked;
                          setShowLinkedApplicationsNav(newValue);
                          try {
                            await axios.post(`${API_BASE}/api/admin/settings`, {
                              showLinkedApplicationsNav: newValue,
                            });
                          } catch (error) {
                            console.error('Error saving Linked Applications navigation setting:', error);
                          }
                        }}
                        style={{ opacity: 0, width: 0, height: 0 }}
                      />
                      <span style={{
                        position: 'absolute',
                        cursor: 'pointer',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: showLinkedApplicationsNav ? '#4c51bf' : '#cbd5e0',
                        transition: '0.4s',
                        borderRadius: '30px'
                      }}>
                        <span style={{
                          position: 'absolute',
                          content: '',
                          height: '22px',
                          width: '22px',
                          left: showLinkedApplicationsNav ? '34px' : '4px',
                          bottom: '4px',
                          backgroundColor: 'white',
                          transition: '0.4s',
                          borderRadius: '50%'
                        }} />
                      </span>
                    </label>
                  </div>
                  <div style={{ 
                    marginTop: '1rem', 
                    padding: '0.75rem', 
                    backgroundColor: showLinkedApplicationsNav ? '#e6fffa' : '#fff5f5', 
                    borderRadius: '6px',
                    border: `1px solid ${showLinkedApplicationsNav ? '#81e6d9' : '#feb2b2'}`
                  }}>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '0.875rem', 
                      color: showLinkedApplicationsNav ? '#234e52' : '#742a2a',
                      fontWeight: 500
                    }}>
                      {showLinkedApplicationsNav 
                        ? '✓ "Linked Applications" is currently VISIBLE in user navigation' 
                        : '✗ "Linked Applications" is currently HIDDEN from user navigation'}
                    </p>
                  </div>
                  <div style={{ 
                    marginTop: '1.5rem',
                    padding: '1rem',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0'
                  }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#2d3748' }}>
                      Application Form Heading & Subheading
                    </h3>
                    <p style={{ margin: '0.5rem 0 1rem 0', fontSize: '0.875rem', color: '#718096' }}>
                      Configure the main heading text shown at the top of the application form.
                    </p>
                    <div style={{ marginBottom: '0.75rem' }}>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#4a5568', marginBottom: '0.25rem' }}>
                        Form Heading
                      </label>
                      <input
                        type="text"
                        value={formHeading}
                        onChange={(e) => setFormHeading(e.target.value)}
                        onBlur={async () => {
                          try {
                            await axios.post(`${API_BASE}/api/admin/settings`, {
                              formHeading,
                              formSubheading,
                            });
                          } catch (error) {
                            console.error('Error saving form heading setting:', error);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '4px',
                          border: '1px solid #cbd5e0',
                          fontSize: '0.9rem',
                          color: '#2d3748',
                        }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#4a5568', marginBottom: '0.25rem' }}>
                        Form Subheading
                      </label>
                      <input
                        type="text"
                        value={formSubheading}
                        onChange={(e) => setFormSubheading(e.target.value)}
                        onBlur={async () => {
                          try {
                            await axios.post(`${API_BASE}/api/admin/settings`, {
                              formHeading,
                              formSubheading,
                            });
                          } catch (error) {
                            console.error('Error saving form subheading setting:', error);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '4px',
                          border: '1px solid #cbd5e0',
                          fontSize: '0.9rem',
                          color: '#2d3748',
                        }}
                      />
                    </div>
                  </div>
                </div>
                </div>
                <LocationManagementContainer>
                <SectionTitle>CNG Station Locations</SectionTitle>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#718096' }}>
                  Manage available locations for the "Applied CNG Station Location" field in application forms
                </p>
                
                <AddLocationForm>
                  <LocationInput
                    type="text"
                    placeholder="Enter new location name (e.g., Mumbai, Delhi)"
                    value={newLocationName}
                    onChange={(e) => setNewLocationName(e.target.value)}
                    onKeyPress={async (e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (newLocationName.trim()) {
                          const newLocation: LocationItem = {
                            id: Date.now().toString(),
                            name: newLocationName.trim(),
                            enabled: true
                          };
                          const updatedLocations = [...locations, newLocation];
                          await saveLocationsSetting(updatedLocations);
                          setNewLocationName('');
                        }
                      }
                    }}
                  />
                  <AddLocationButton
                    onClick={async () => {
                      if (newLocationName.trim()) {
                        const newLocation: LocationItem = {
                          id: Date.now().toString(),
                          name: newLocationName.trim(),
                          enabled: true
                        };
                        const updatedLocations = [...locations, newLocation];
                        await saveLocationsSetting(updatedLocations);
                        setNewLocationName('');
                      }
                    }}
                    disabled={!newLocationName.trim()}
                  >
                    + Add Location
                  </AddLocationButton>
                </AddLocationForm>
                
                <LocationList>
                  {locations.map((location) => (
                    <LocationItem key={location.id}>
                      <LocationName enabled={location.enabled}>
                        {location.name}
                      </LocationName>
                      <LocationActions>
                        <ToggleSwitch>
                          <input
                            type="checkbox"
                            checked={location.enabled}
                            onChange={async (e) => {
                              const updatedLocations = locations.map(loc =>
                                loc.id === location.id ? { ...loc, enabled: e.target.checked } : loc
                              );
                              await saveLocationsSetting(updatedLocations);
                            }}
                          />
                          <ToggleSlider checked={location.enabled} />
                        </ToggleSwitch>
                        <DeleteLocationButton
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to delete "${location.name}"?`)) {
                              const updatedLocations = locations.filter(loc => loc.id !== location.id);
                              await saveLocationsSetting(updatedLocations);
                            }
                          }}
                          disabled={locations.length === 1}
                          title={locations.length === 1 ? 'Cannot delete the last location' : 'Delete this location'}
                        >
                          Delete
                        </DeleteLocationButton>
                      </LocationActions>
                    </LocationItem>
                  ))}
                </LocationList>
                
                {locations.filter(loc => loc.enabled).length === 0 && (
                  <div style={{ 
                    marginTop: '1rem', 
                    padding: '0.75rem', 
                    backgroundColor: '#fff5f5', 
                    borderRadius: '6px',
                    border: '1px solid #feb2b2'
                  }}>
                    <p style={{ 
                      margin: 0, 
                      fontSize: '0.875rem', 
                      color: '#742a2a',
                      fontWeight: 500
                    }}>
                      ⚠️ Warning: No locations are enabled. Users won't be able to select a location in the application form.
                    </p>
                  </div>
                )}
              </LocationManagementContainer>
            </Card>

            {/* Application Lock Settings Card */}
            <Card>
              <SectionTitle>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span role="img" aria-label="Lock">🔒</span> Application Lock Settings
                </span>
              </SectionTitle>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#4a5568' }}>
                Control whether submitted applications (after payment) are locked for editing. Locked applications show all data but prevent modifications.
              </p>

              {/* Auto-lock Toggle */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '1rem',
                backgroundColor: '#f8fafc',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                marginBottom: '1rem'
              }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#2d3748' }}>
                    Auto-lock Paid Applications
                  </h3>
                  <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.875rem', color: '#718096' }}>
                    Automatically lock applications after online payment is complete (status: payment_complete)
                  </p>
                </div>
                <label style={{ 
                  position: 'relative', 
                  display: 'inline-block', 
                  width: '60px', 
                  height: '30px' 
                }}>
                  <input 
                    type="checkbox" 
                    checked={autoLockPaidApplications}
                    onChange={async (e) => {
                      const newValue = e.target.checked;
                      setAutoLockPaidApplications(newValue);
                      try {
                        await axios.post(`${API_BASE}/api/admin/toggle-auto-lock`, {
                          autoLockPaidApplications: newValue,
                        });
                      } catch (error) {
                        console.error('Error saving auto-lock setting:', error);
                      }
                    }}
                    style={{ opacity: 0, width: 0, height: 0 }}
                  />
                  <span style={{
                    position: 'absolute',
                    cursor: 'pointer',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: autoLockPaidApplications ? '#4c51bf' : '#cbd5e0',
                    transition: '0.4s',
                    borderRadius: '30px'
                  }}>
                    <span style={{
                      position: 'absolute',
                      content: '',
                      height: '22px',
                      width: '22px',
                      left: autoLockPaidApplications ? '34px' : '4px',
                      bottom: '4px',
                      backgroundColor: 'white',
                      transition: '0.4s',
                      borderRadius: '50%'
                    }} />
                  </span>
                </label>
              </div>
              <div style={{ 
                marginBottom: '1.5rem',
                padding: '0.75rem', 
                backgroundColor: autoLockPaidApplications ? '#e6fffa' : '#fff5f5', 
                borderRadius: '6px',
                border: `1px solid ${autoLockPaidApplications ? '#81e6d9' : '#feb2b2'}`
              }}>
                <p style={{ 
                  margin: 0, 
                  fontSize: '0.875rem', 
                  color: autoLockPaidApplications ? '#234e52' : '#742a2a',
                  fontWeight: 500
                }}>
                  {autoLockPaidApplications 
                    ? '✓ Applications are automatically LOCKED after payment completion' 
                    : '✗ Applications remain EDITABLE after payment completion'}
                </p>
              </div>

              {/* Manual Lock/Unlock Section */}
              <div style={{ 
                padding: '1rem',
                backgroundColor: '#fef3c7',
                borderRadius: '8px',
                border: '1px solid #fcd34d',
                marginBottom: '1rem'
              }}>
                <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: 600, color: '#92400e' }}>
                  🔐 Manual Lock/Unlock Applications
                </h3>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: '#78350f' }}>
                  Override the auto-lock setting for specific applications. Enter one or more Application IDs (comma-separated).
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <input
                    type="text"
                    value={lockApplicationIds}
                    onChange={(e) => setLockApplicationIds(e.target.value)}
                    placeholder="e.g., CNG-05022026121317-2134, CNG-03022026142626-707H"
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      borderRadius: '6px',
                      border: '1px solid #d97706',
                      fontSize: '0.9rem',
                      backgroundColor: '#fffbeb'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!lockApplicationIds.trim()) return;
                        const ids = lockApplicationIds.split(',').map(id => id.trim()).filter(id => id);
                        try {
                          const response = await axios.post(`${API_BASE}/api/admin/lock-applications`, {
                            applicationIds: ids,
                            action: 'lock'
                          });
                          if (response.data.success) {
                            setLockActionMessage({ type: 'success', text: response.data.message });
                            setLockApplicationIds('');
                            fetchLockSettings();
                          } else {
                            setLockActionMessage({ type: 'error', text: response.data.message });
                          }
                        } catch (error: any) {
                          setLockActionMessage({ type: 'error', text: error.response?.data?.message || 'Error locking applications' });
                        }
                      }}
                      disabled={!lockApplicationIds.trim()}
                      style={{
                        padding: '0.6rem 1.2rem',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: !lockApplicationIds.trim() ? '#d1d5db' : '#dc2626',
                        color: 'white',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        cursor: !lockApplicationIds.trim() ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      🔒 Lock Applications
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!lockApplicationIds.trim()) return;
                        const ids = lockApplicationIds.split(',').map(id => id.trim()).filter(id => id);
                        try {
                          const response = await axios.post(`${API_BASE}/api/admin/lock-applications`, {
                            applicationIds: ids,
                            action: 'unlock'
                          });
                          if (response.data.success) {
                            setLockActionMessage({ type: 'success', text: response.data.message });
                            setLockApplicationIds('');
                            fetchLockSettings();
                          } else {
                            setLockActionMessage({ type: 'error', text: response.data.message });
                          }
                        } catch (error: any) {
                          setLockActionMessage({ type: 'error', text: error.response?.data?.message || 'Error unlocking applications' });
                        }
                      }}
                      disabled={!lockApplicationIds.trim()}
                      style={{
                        padding: '0.6rem 1.2rem',
                        borderRadius: '6px',
                        border: 'none',
                        backgroundColor: !lockApplicationIds.trim() ? '#d1d5db' : '#059669',
                        color: 'white',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        cursor: !lockApplicationIds.trim() ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      🔓 Unlock Applications
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!lockApplicationIds.trim()) return;
                        const ids = lockApplicationIds.split(',').map(id => id.trim()).filter(id => id);
                        try {
                          const response = await axios.post(`${API_BASE}/api/admin/reset-application-lock`, {
                            applicationIds: ids
                          });
                          if (response.data.success) {
                            setLockActionMessage({ type: 'success', text: response.data.message });
                            setLockApplicationIds('');
                            fetchLockSettings();
                          } else {
                            setLockActionMessage({ type: 'error', text: response.data.message });
                          }
                        } catch (error: any) {
                          setLockActionMessage({ type: 'error', text: error.response?.data?.message || 'Error resetting lock status' });
                        }
                      }}
                      disabled={!lockApplicationIds.trim()}
                      style={{
                        padding: '0.6rem 1.2rem',
                        borderRadius: '6px',
                        border: '1px solid #6b7280',
                        backgroundColor: !lockApplicationIds.trim() ? '#f3f4f6' : '#f9fafb',
                        color: !lockApplicationIds.trim() ? '#9ca3af' : '#374151',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        cursor: !lockApplicationIds.trim() ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ↺ Reset to Default
                    </button>
                  </div>
                </div>
                {lockActionMessage && (
                  <div style={{
                    marginTop: '0.75rem',
                    padding: '0.6rem 0.9rem',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    backgroundColor: lockActionMessage.type === 'success' ? '#d1fae5' : '#fee2e2',
                    color: lockActionMessage.type === 'success' ? '#065f46' : '#991b1b',
                    border: `1px solid ${lockActionMessage.type === 'success' ? '#6ee7b7' : '#fca5a5'}`
                  }}>
                    {lockActionMessage.text}
                  </div>
                )}
              </div>

              {/* Currently Locked/Unlocked Lists */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                {/* Manually Locked List */}
                <div style={{ padding: '1rem', backgroundColor: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#991b1b', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    🔒 Manually Locked ({manuallyLockedIds.length})
                  </h4>
                  {manuallyLockedIds.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>No manually locked applications</p>
                  ) : (
                    <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                      {manuallyLockedIds.map(id => (
                        <div key={id} style={{ fontSize: '0.8rem', color: '#7f1d1d', padding: '0.25rem 0', borderBottom: '1px solid #fecaca' }}>
                          {id}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Manually Unlocked List */}
                <div style={{ padding: '1rem', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                  <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600, color: '#065f46', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    🔓 Manually Unlocked ({manuallyUnlockedIds.length})
                  </h4>
                  {manuallyUnlockedIds.length === 0 ? (
                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>No manually unlocked applications</p>
                  ) : (
                    <div style={{ maxHeight: '120px', overflowY: 'auto' }}>
                      {manuallyUnlockedIds.map(id => (
                        <div key={id} style={{ fontSize: '0.8rem', color: '#047857', padding: '0.25rem 0', borderBottom: '1px solid #a7f3d0' }}>
                          {id}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Card>

            {/* MSG91 SMS Integration Card */}
            <Card>
              <SectionTitle>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span role="img" aria-label="SMS">📱</span> SMS & WhatsApp Integration (MSG91)
                </span>
              </SectionTitle>
              <p style={{ margin: '0 0 1rem 0', fontSize: '0.9rem', color: '#4a5568' }}>
                Configure MSG91 API for sending SMS and WhatsApp notifications on payment confirmation and application submission.
              </p>

              {/* API Configuration */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#2d3748' }}>API Configuration</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem', color: '#4a5568' }}>
                      MSG91 Auth Key
                    </label>
                    <input
                      type="password"
                      value={msg91AuthKey}
                      onChange={(e) => setMsg91AuthKey(e.target.value)}
                      placeholder="Enter your MSG91 Auth Key"
                      style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.9rem' }}
                    />
                    <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#718096' }}>
                      Get your Auth Key from <a href="https://msg91.com/help/where-can-i-find-my-authentication-key" target="_blank" rel="noreferrer" style={{ color: '#3182ce' }}>MSG91 Dashboard</a>
                    </p>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem', color: '#4a5568' }}>
                      Sender ID (6 characters)
                    </label>
                    <input
                      type="text"
                      value={msg91SenderId}
                      onChange={(e) => setMsg91SenderId(e.target.value.toUpperCase().slice(0, 6))}
                      placeholder="e.g., CNGDLR"
                      maxLength={6}
                      style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.9rem', textTransform: 'uppercase' }}
                    />
                  </div>
                </div>
              </div>

              {/* Enable/Disable Toggles */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0fff4', borderRadius: '8px', border: '1px solid #9ae6b4' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: 600, color: '#22543d' }}>Notification Toggles</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
                  {/* SMS Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
                      <input type="checkbox" checked={msg91SmsEnabled} onChange={(e) => setMsg91SmsEnabled(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: msg91SmsEnabled ? '#38a169' : '#cbd5e0', transition: '0.3s', borderRadius: '26px' }}>
                        <span style={{ position: 'absolute', height: '20px', width: '20px', left: msg91SmsEnabled ? '26px' : '3px', bottom: '3px', backgroundColor: 'white', transition: '0.3s', borderRadius: '50%' }} />
                      </span>
                    </label>
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#2d3748' }}>SMS Notifications</span>
                  </div>
                  {/* WhatsApp Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
                      <input type="checkbox" checked={msg91WhatsAppEnabled} onChange={(e) => setMsg91WhatsAppEnabled(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: msg91WhatsAppEnabled ? '#25D366' : '#cbd5e0', transition: '0.3s', borderRadius: '26px' }}>
                        <span style={{ position: 'absolute', height: '20px', width: '20px', left: msg91WhatsAppEnabled ? '26px' : '3px', bottom: '3px', backgroundColor: 'white', transition: '0.3s', borderRadius: '50%' }} />
                      </span>
                    </label>
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#2d3748' }}>WhatsApp Notifications</span>
                  </div>
                  {/* Server Load Alert Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <label style={{ position: 'relative', display: 'inline-block', width: '50px', height: '26px' }}>
                      <input type="checkbox" checked={msg91ServerLoadAlertEnabled} onChange={(e) => setMsg91ServerLoadAlertEnabled(e.target.checked)} style={{ opacity: 0, width: 0, height: 0 }} />
                      <span style={{ position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: msg91ServerLoadAlertEnabled ? '#e53e3e' : '#cbd5e0', transition: '0.3s', borderRadius: '26px' }}>
                        <span style={{ position: 'absolute', height: '20px', width: '20px', left: msg91ServerLoadAlertEnabled ? '26px' : '3px', bottom: '3px', backgroundColor: 'white', transition: '0.3s', borderRadius: '50%' }} />
                      </span>
                    </label>
                    <span style={{ fontSize: '0.9rem', fontWeight: 500, color: '#2d3748' }}>Server Load Alerts</span>
                  </div>
                </div>
                {msg91ServerLoadAlertEnabled && (
                  <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <label style={{ fontSize: '0.85rem', color: '#4a5568' }}>Alert Threshold (%):</label>
                    <input
                      type="number"
                      value={msg91ServerLoadThreshold}
                      onChange={(e) => setMsg91ServerLoadThreshold(Math.min(100, Math.max(50, parseInt(e.target.value) || 80)))}
                      min={50}
                      max={100}
                      style={{ width: '80px', padding: '0.35rem 0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.9rem' }}
                    />
                    <span style={{ fontSize: '0.8rem', color: '#718096' }}>Send alert when CPU or RAM exceeds this %</span>
                  </div>
                )}
              </div>

              {/* SMS Templates */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#ebf8ff', borderRadius: '8px', border: '1px solid #90cdf4' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: '#2c5282' }}>📝 SMS Templates</h3>
                <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: '#4a5568' }}>
                  Available variables: <code>{'{applicantName}'}</code>, <code>{'{applicationId}'}</code>, <code>{'{amount}'}</code>, <code>{'{date}'}</code>, <code>{'{cpuUsage}'}</code>, <code>{'{ramUsage}'}</code>
                </p>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem', color: '#4a5568' }}>Payment Confirmation SMS</label>
                  <textarea
                    value={smsPaymentTemplate}
                    onChange={(e) => setSmsPaymentTemplate(e.target.value)}
                    rows={2}
                    placeholder="Dear {applicantName}, your payment of Rs. {amount} for CNG Dealership Application (ID: {applicationId}) has been received successfully."
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', resize: 'vertical' }}
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem', color: '#4a5568' }}>Application Confirmation SMS</label>
                  <textarea
                    value={smsApplicationTemplate}
                    onChange={(e) => setSmsApplicationTemplate(e.target.value)}
                    rows={2}
                    placeholder="Dear {applicantName}, your CNG Dealership Application (ID: {applicationId}) has been submitted successfully."
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem', color: '#4a5568' }}>Server Load Alert SMS</label>
                  <textarea
                    value={smsServerLoadTemplate}
                    onChange={(e) => setSmsServerLoadTemplate(e.target.value)}
                    rows={2}
                    placeholder="ALERT: Server load is high. CPU: {cpuUsage}%, RAM: {ramUsage}%. Please check the server status."
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', resize: 'vertical' }}
                  />
                </div>
              </div>

              {/* WhatsApp Templates */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0fff4', borderRadius: '8px', border: '1px solid #9ae6b4' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: '#22543d' }}>💬 WhatsApp Templates</h3>
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem', color: '#4a5568' }}>Payment Confirmation WhatsApp</label>
                  <textarea
                    value={waPaymentTemplate}
                    onChange={(e) => setWaPaymentTemplate(e.target.value)}
                    rows={3}
                    placeholder="Dear {applicantName},&#10;&#10;Your payment of Rs. {amount} for CNG Dealership Application has been received.&#10;&#10;Application ID: {applicationId}&#10;Date: {date}"
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '0.25rem', color: '#4a5568' }}>Application Confirmation WhatsApp</label>
                  <textarea
                    value={waApplicationTemplate}
                    onChange={(e) => setWaApplicationTemplate(e.target.value)}
                    rows={3}
                    placeholder="Dear {applicantName},&#10;&#10;Your CNG Dealership Application has been submitted successfully.&#10;&#10;Application ID: {applicationId}&#10;Status: Under Review"
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.85rem', resize: 'vertical' }}
                  />
                </div>
              </div>

              {/* Admin Alert Numbers */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#fff5f5', borderRadius: '8px', border: '1px solid #feb2b2' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: 600, color: '#742a2a' }}>🔔 Admin Alert Numbers</h3>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: '#4a5568' }}>
                  Mobile numbers that will receive server load alerts when enabled.
                </p>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <input
                    type="tel"
                    value={newAdminNumber}
                    onChange={(e) => setNewAdminNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                    style={{ flex: 1, maxWidth: '200px', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.9rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (newAdminNumber.length === 10 && !adminAlertNumbers.includes(newAdminNumber)) {
                        setAdminAlertNumbers([...adminAlertNumbers, newAdminNumber]);
                        setNewAdminNumber('');
                      }
                    }}
                    disabled={newAdminNumber.length !== 10}
                    style={{ padding: '0.5rem 1rem', borderRadius: '6px', border: 'none', backgroundColor: newAdminNumber.length === 10 ? '#e53e3e' : '#cbd5e0', color: 'white', fontSize: '0.85rem', fontWeight: 600, cursor: newAdminNumber.length === 10 ? 'pointer' : 'not-allowed' }}
                  >
                    + Add
                  </button>
                </div>
                {adminAlertNumbers.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {adminAlertNumbers.map((num, idx) => (
                      <span key={idx} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.75rem', backgroundColor: '#fed7d7', borderRadius: '20px', fontSize: '0.85rem', color: '#742a2a' }}>
                        +91 {num}
                        <button
                          type="button"
                          onClick={() => setAdminAlertNumbers(adminAlertNumbers.filter((_, i) => i !== idx))}
                          style={{ border: 'none', background: 'none', color: '#c53030', cursor: 'pointer', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1 }}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Test SMS Section */}
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#faf5ff', borderRadius: '8px', border: '1px solid #d6bcfa' }}>
                <h3 style={{ margin: '0 0 0.75rem 0', fontSize: '1rem', fontWeight: 600, color: '#553c9a' }}>🧪 Test SMS</h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#4a5568', marginBottom: '0.25rem' }}>Mobile Number</label>
                    <input
                      type="tel"
                      value={testSmsNumber}
                      onChange={(e) => setTestSmsNumber(e.target.value.replace(/\D/g, '').slice(0, 10))}
                      placeholder="10-digit number"
                      maxLength={10}
                      style={{ width: '150px', padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.9rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.8rem', color: '#4a5568', marginBottom: '0.25rem' }}>Message Type</label>
                    <select
                      value={testSmsType}
                      onChange={(e) => setTestSmsType(e.target.value)}
                      style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #cbd5e0', fontSize: '0.9rem', backgroundColor: 'white' }}
                    >
                      <option value="payment">Payment Confirmation</option>
                      <option value="application">Application Confirmation</option>
                      <option value="serverload">Server Load Alert</option>
                      <option value="default">Default Test</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={handleTestSms}
                    disabled={isTestingSms || testSmsNumber.length < 10}
                    style={{ padding: '0.5rem 1.25rem', borderRadius: '6px', border: 'none', backgroundColor: isTestingSms ? '#b794f4' : '#805ad5', color: 'white', fontSize: '0.9rem', fontWeight: 600, cursor: isTestingSms || testSmsNumber.length < 10 ? 'not-allowed' : 'pointer' }}
                  >
                    {isTestingSms ? 'Sending...' : 'Send Test SMS'}
                  </button>
                </div>
              </div>

              {/* Save Button & Status */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem' }}>
                <button
                  type="button"
                  onClick={saveSmsSettings}
                  disabled={isSavingSmsSettings}
                  style={{ padding: '0.6rem 1.5rem', borderRadius: '6px', border: 'none', backgroundColor: isSavingSmsSettings ? '#9ae6b4' : '#38a169', color: 'white', fontSize: '0.95rem', fontWeight: 600, cursor: isSavingSmsSettings ? 'not-allowed' : 'pointer' }}
                >
                  {isSavingSmsSettings ? 'Saving...' : '💾 Save SMS Settings'}
                </button>
                {smsTestMessage && (
                  <div style={{ padding: '0.5rem 1rem', borderRadius: '6px', fontSize: '0.85rem', backgroundColor: smsTestSuccess ? '#f0fff4' : '#fff5f5', color: smsTestSuccess ? '#22543d' : '#742a2a', border: smsTestSuccess ? '1px solid #9ae6b4' : '1px solid #feb2b2' }}>
                    {smsTestMessage}
                  </div>
                )}
              </div>
            </Card>
          </TabContent>
        )}

        {activeTab === 'security-apis' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="security-apis"
          >
            <SecurityAPIsSettings />
          </TabContent>
        )}

        {activeTab === 'page4-rows' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="page4-rows"
          >
            <Page4RowSelectionsTab />
          </TabContent>
        )}

        {activeTab === 'appendix-logic' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="appendix-logic"
          >
            <AppendixLogicTab />
          </TabContent>
        )}

        {activeTab === 'settings' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="settings"
          >
            <Settings />
          </TabContent>
        )}

        {activeTab === 'docs' && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="docs"
          >
            <AdminDocs />
          </TabContent>
        )}

        {activeTab === 'app-maintenance' && !isSubAdmin && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="app-maintenance"
          >
            <AppMaintenance />
          </TabContent>
        )}

        {activeTab === 's3-settings' && !isSubAdmin && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="s3-settings"
          >
            <S3Settings />
          </TabContent>
        )}

        {activeTab === 'sub-admins' && !isSubAdmin && (
          <TabContent
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            key="sub-admins"
          >
            <div style={{ padding: '20px' }}>
              {/* Success/Error Messages */}
              {subAdminSuccess && (
                <div style={{ padding: '12px 16px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '8px', color: '#166534', marginBottom: '16px' }}>
                  {subAdminSuccess}
                </div>
              )}
              {subAdminError && (
                <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px' }}>
                  {subAdminError}
                </div>
              )}

              {/* Header with Add Button */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: '#1e293b' }}>Sub-Admin Accounts</h2>
                  <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: '#64748b' }}>Manage sub-admin access and permissions</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API_BASE}/api/admin/payment-stats`, { credentials: 'include' });
                        const data = await res.json();
                        if (data.success && data.lastPayment) {
                          setConfettiTestInfo({
                            applicantName: data.lastPayment.applicantName,
                            applicationId: data.lastPayment.applicationId,
                            amount: data.lastPayment.amount,
                            photo: data.lastPayment.photo,
                            locationArea: data.lastPayment.locationArea,
                            locationDistrict: data.lastPayment.locationDistrict,
                          });
                        } else {
                          setConfettiTestInfo({
                            applicantName: 'Sample Applicant',
                            applicationId: 'CNG-21022026143000-001X',
                            amount: 5000,
                            photo: null,
                            locationArea: 'Pune GA',
                            locationDistrict: 'Pune',
                          });
                        }
                      } catch {
                        setConfettiTestInfo({
                          applicantName: 'Sample Applicant',
                          applicationId: 'CNG-21022026143000-001X',
                          amount: 5000,
                          photo: null,
                          locationArea: 'Pune GA',
                          locationDistrict: 'Pune',
                        });
                      }
                      setConfettiTestTrigger(prev => prev + 1);
                    }}
                    style={{
                      padding: '10px 18px',
                      background: 'linear-gradient(135deg, #6A0DAD, #9B59B6)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '0.85rem',
                      boxShadow: '0 2px 8px rgba(106, 13, 173, 0.25)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(106, 13, 173, 0.35)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(106, 13, 173, 0.25)'; }}
                    title="Test the payment celebration confetti animation with the last paid applicant's data"
                  >
                    <span style={{ fontSize: '1rem' }}>🎉</span>
                    Test Payment Confetti
                  </button>
                  <button
                    onClick={() => setShowCreateSubAdminModal(true)}
                    style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #4c6ef5, #3b5bdb)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add Sub-Admin
                  </button>
                </div>
              </div>

              {/* Sub-Admins Table */}
              <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                {isLoadingSubAdmins ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading sub-admins...</div>
                ) : subAdmins.length === 0 ? (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" style={{ marginBottom: '12px' }}>
                      <path d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                    </svg>
                    <p style={{ margin: 0 }}>No sub-admins created yet</p>
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Name</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Email</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Permissions</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Status</th>
                        <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Last Login</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Logins</th>
                        <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, fontSize: '0.875rem', color: '#475569' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subAdmins.map((sa, index) => (
                        <tr key={sa.id || sa._id} style={{ borderBottom: '1px solid #f1f5f9', opacity: 0, animation: `subAdminFadeSlideIn 0.45s ease ${index * 0.08}s forwards` }}>
                          <td style={{ padding: '14px 16px', fontWeight: 500, color: '#1e293b' }}>{sa.name}</td>
                          <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '0.9rem' }}>{sa.email}</td>
                          <td style={{ padding: '14px 16px' }}>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {sa.permissions.slice(0, 3).map(p => (
                                <span key={p} style={{ padding: '2px 8px', background: '#e0e7ff', color: '#4338ca', borderRadius: '4px', fontSize: '0.75rem', fontWeight: 500 }}>{p}</span>
                              ))}
                              {sa.permissions.length > 3 && (
                                <span style={{ padding: '2px 8px', background: '#f1f5f9', color: '#64748b', borderRadius: '4px', fontSize: '0.75rem' }}>+{sa.permissions.length - 3} more</span>
                              )}
                            </div>
                          </td>
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                            <span style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600, background: sa.isActive ? '#dcfce7' : '#fef2f2', color: sa.isActive ? '#166534' : '#dc2626' }}>
                              {sa.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '0.85rem' }}>
                            {sa.lastLogin ? (() => {
                              const d = new Date(sa.lastLogin);
                              const now = new Date();
                              const diffMs = now.getTime() - d.getTime();
                              const diffMins = Math.floor(diffMs / 60000);
                              const diffHrs = Math.floor(diffMs / 3600000);
                              const diffDays = Math.floor(diffMs / 86400000);
                              let relative = '';
                              if (diffMins < 1) relative = 'Just now';
                              else if (diffMins < 60) relative = `${diffMins}m ago`;
                              else if (diffHrs < 24) relative = `${diffHrs}h ago`;
                              else if (diffDays < 7) relative = `${diffDays}d ago`;
                              else relative = d.toLocaleDateString();
                              return (
                                <div>
                                  <div style={{ fontSize: '0.82rem', color: '#1e293b' }}>{d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                                  <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })} &middot; {relative}</div>
                                </div>
                              );
                            })() : <span style={{ color: '#cbd5e1', fontStyle: 'italic' }}>Never</span>}
                          </td>
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '4px 10px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600,
                              background: (sa.loginCount || 0) > 0 ? '#e0f2fe' : '#f8fafc',
                              color: (sa.loginCount || 0) > 0 ? '#0369a1' : '#94a3b8',
                            }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4M10 17l5-5-5-5M15 12H3"/></svg>
                              {sa.loginCount || 0}
                            </span>
                          </td>
                          <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '6px' }}>
                              <button
                                onClick={() => { setEditingSubAdmin(sa); setNewSubAdminPassword(''); setShowEditSubAdminModal(true); }}
                                style={{ padding: '6px 12px', background: '#f1f5f9', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#475569', fontSize: '0.8rem', fontWeight: 500 }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleToggleSubAdminActive(sa)}
                                style={{ padding: '6px 12px', background: sa.isActive ? '#fef9c3' : '#dcfce7', border: 'none', borderRadius: '6px', cursor: 'pointer', color: sa.isActive ? '#92400e' : '#166534', fontSize: '0.8rem', fontWeight: 500, transition: 'all 0.2s ease' }}
                              >
                                {sa.isActive ? 'Deactivate' : 'Activate'}
                              </button>
                              <button
                                onClick={() => handleDeleteSubAdmin(sa.id || sa._id || '')}
                                style={{ padding: '6px 12px', background: '#fef2f2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#dc2626', fontSize: '0.8rem', fontWeight: 500 }}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Security Note */}
              <div style={{ marginTop: '24px', padding: '16px', background: '#fefce8', border: '1px solid #fde047', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ca8a04" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                  <div>
                    <strong style={{ color: '#854d0e', fontSize: '0.9rem' }}>Security Information</strong>
                    <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#a16207' }}>
                      Sub-admins log in via <code style={{ background: '#fef9c3', padding: '2px 6px', borderRadius: '4px' }}>/su-admin/login</code> using OTP sent to their registered email. 
                      They can only access the navigation items you grant them. Sub-admin sessions use separate cookies from super admin and user sessions for complete isolation.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Create Sub-Admin Modal */}
            {showCreateSubAdminModal && (
              <ModalBackdrop>
                <ModalContent style={{ maxWidth: '720px' }}>
                  <ModalTitle>Create New Sub-Admin</ModalTitle>
                  {subAdminError && (
                    <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', marginBottom: '16px', fontSize: '0.9rem' }}>
                      {subAdminError}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '200px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.9rem' }}>Name</label>
                        <input type="text" value={newSubAdminName} onChange={(e) => setNewSubAdminName(e.target.value)} placeholder="Full name" style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.9rem' }}>Email</label>
                        <input type="email" value={newSubAdminEmail} onChange={(e) => setNewSubAdminEmail(e.target.value)} placeholder="email@example.com" style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem' }} />
                      </div>
                      <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '0', lineHeight: 1.4 }}>Sub-admin will receive an OTP on this email to log in.</p>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '0.9rem' }}>Navigation Permissions</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                        {ALL_NAV_PERMISSIONS.map(perm => (
                          <label key={perm.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: newSubAdminPermissions.includes(perm.key) ? '#e0e7ff' : '#f8fafc', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', border: newSubAdminPermissions.includes(perm.key) ? '1px solid #818cf8' : '1px solid transparent' }}>
                            <input type="checkbox" checked={newSubAdminPermissions.includes(perm.key)} onChange={() => toggleSubAdminPermission(perm.key)} style={{ accentColor: '#4c6ef5' }} />
                            {perm.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <ModalButtons style={{ marginTop: '20px' }}>
                    <CancelButton onClick={() => { setShowCreateSubAdminModal(false); setSubAdminError(null); setNewSubAdminEmail(''); setNewSubAdminName(''); setNewSubAdminPassword(''); setNewSubAdminPermissions(['dashboard', 'applications']); }}>Cancel</CancelButton>
                    <DeleteButton onClick={handleCreateSubAdmin} style={{ background: 'linear-gradient(135deg, #4c6ef5, #3b5bdb)' }}>Create Sub-Admin</DeleteButton>
                  </ModalButtons>
                </ModalContent>
              </ModalBackdrop>
            )}

            {/* Edit Sub-Admin Modal */}
            {showEditSubAdminModal && editingSubAdmin && (
              <ModalBackdrop>
                <ModalContent style={{ maxWidth: '720px' }}>
                  <ModalTitle>Edit Sub-Admin: {editingSubAdmin.name}</ModalTitle>
                  {subAdminError && (
                    <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', marginBottom: '16px', fontSize: '0.9rem' }}>
                      {subAdminError}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', minWidth: '200px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.9rem' }}>Name</label>
                        <input type="text" value={editingSubAdmin.name} onChange={(e) => setEditingSubAdmin({ ...editingSubAdmin, name: e.target.value })} style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem' }} />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500, fontSize: '0.9rem' }}>Email (read-only)</label>
                        <input type="email" value={editingSubAdmin.email} disabled style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.95rem', background: '#f8fafc', color: '#64748b' }} />
                      </div>
                      <div>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: editingSubAdmin.isActive ? '#dcfce7' : '#fef2f2', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 500 }}>
                          <input type="checkbox" checked={editingSubAdmin.isActive} onChange={(e) => setEditingSubAdmin({ ...editingSubAdmin, isActive: e.target.checked })} style={{ accentColor: '#22c55e' }} />
                          {editingSubAdmin.isActive ? 'Active' : 'Inactive'}
                        </label>
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '0.9rem' }}>Navigation Permissions</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                        {ALL_NAV_PERMISSIONS.map(perm => (
                          <label key={perm.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', background: editingSubAdmin.permissions.includes(perm.key) ? '#e0e7ff' : '#f8fafc', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', border: editingSubAdmin.permissions.includes(perm.key) ? '1px solid #818cf8' : '1px solid transparent' }}>
                            <input type="checkbox" checked={editingSubAdmin.permissions.includes(perm.key)} onChange={() => toggleSubAdminPermission(perm.key, true)} style={{ accentColor: '#4c6ef5' }} />
                            {perm.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                  <ModalButtons style={{ marginTop: '20px' }}>
                    <CancelButton onClick={() => { setShowEditSubAdminModal(false); setEditingSubAdmin(null); setSubAdminError(null); setNewSubAdminPassword(''); }}>Cancel</CancelButton>
                    <DeleteButton onClick={handleUpdateSubAdmin} style={{ background: 'linear-gradient(135deg, #4c6ef5, #3b5bdb)' }}>Save Changes</DeleteButton>
                  </ModalButtons>
                </ModalContent>
              </ModalBackdrop>
            )}
          </TabContent>
        )}
      </MainContent>
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <ModalBackdrop>
          <ModalContent>
            <ModalTitle>Confirm Deletion</ModalTitle>
            <ModalText>
              Are you sure you want to permanently delete {selectedApplicationIds.length} application(s)?
              This action cannot be undone.
            </ModalText>
            <div style={{ marginTop: '1rem' }}>
              <label htmlFor="deletionReason" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Reason for deletion (required):
              </label>
              <textarea
                id="deletionReason"
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.25rem',
                  border: '1px solid #e2e8f0',
                  minHeight: '80px',
                }}
                placeholder="Please provide a reason for deleting these applications..."
              />
            </div>
            <ModalButtons>
              <CancelButton
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setDeletionReason('');
                }}
                disabled={isProcessingAction}
              >
                Cancel
              </CancelButton>
              <DeleteButton
                onClick={handleDelete}
                disabled={isProcessingAction || !deletionReason.trim()}
              >
                {isProcessingAction ? 'Deleting...' : 'Delete'}
              </DeleteButton>
            </ModalButtons>
          </ModalContent>
        </ModalBackdrop>
      )}
      
      {/* Single Application Delete Confirmation Modal */}
      {showSingleDeleteConfirmation && (
        <ModalBackdrop>
          <ModalContent style={{ maxWidth: deletionPhase === 'complete' ? '550px' : '450px' }}>
            {/* Phase: Idle - Show confirmation form */}
            {deletionPhase === 'idle' && (
              <>
                <ModalTitle style={{ color: '#dc2626' }}>
                  <span style={{ marginRight: '8px' }}>🗑️</span>
                  Confirm Application Deletion
                </ModalTitle>
                <ModalText>
                  Are you sure you want to <strong>permanently delete</strong> application <strong>{applicationNumberToDelete}</strong>?
                  <br /><span style={{ color: '#dc2626', fontSize: '0.9em' }}>This action cannot be undone. All files will be permanently removed.</span>
                </ModalText>
                <div style={{ marginTop: '1rem' }}>
                  <label htmlFor="singleDeletionReason" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                    Reason for deletion (required):
                  </label>
                  <textarea
                    id="singleDeletionReason"
                    value={deletionReason}
                    onChange={(e) => setDeletionReason(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #e2e8f0',
                      minHeight: '80px',
                      fontSize: '0.95rem',
                    }}
                    placeholder="Please provide a reason for deleting this application..."
                  />
                </div>
                <ModalButtons>
                  <CancelButton onClick={resetDeletionModal} disabled={isProcessingAction}>
                    Cancel
                  </CancelButton>
                  <DeleteButton
                    onClick={handleSingleDeleteSubmit}
                    disabled={isProcessingAction || !deletionReason.trim()}
                  >
                    Delete Permanently
                  </DeleteButton>
                </ModalButtons>
              </>
            )}
            
            {/* Phase: Deleting - Show progress bar */}
            {deletionPhase === 'deleting' && (
              <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔄</div>
                <ModalTitle style={{ marginBottom: '1rem' }}>Deleting Application...</ModalTitle>
                <ModalText style={{ marginBottom: '1.5rem' }}>
                  Removing files and database records for <strong>{applicationNumberToDelete}</strong>
                </ModalText>
                {/* Progress Bar */}
                <div style={{
                  width: '100%',
                  height: '12px',
                  backgroundColor: '#e2e8f0',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  marginBottom: '0.5rem'
                }}>
                  <div style={{
                    width: `${deletionProgress}%`,
                    height: '100%',
                    background: 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)',
                    borderRadius: '6px',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                  {Math.round(deletionProgress)}% complete
                </div>
              </div>
            )}
            
            {/* Phase: Complete - Show results */}
            {deletionPhase === 'complete' && deletionResults && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ 
                    width: '70px', 
                    height: '70px', 
                    borderRadius: '50%', 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem',
                    boxShadow: '0 4px 15px rgba(16, 185, 129, 0.3)'
                  }}>
                    <span style={{ fontSize: '2rem', color: 'white' }}>✓</span>
                  </div>
                  <ModalTitle style={{ color: '#10b981', marginBottom: '0.5rem' }}>
                    Application Deleted Successfully
                  </ModalTitle>
                  <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
                    {applicationNumberToDelete}
                  </div>
                </div>
                
                {/* Summary Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '12px',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    padding: '1rem',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '1px solid #86efac'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#16a34a' }}>
                      {deletionResults.filesDeleted}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#15803d', fontWeight: 500 }}>Files Deleted</div>
                  </div>
                  <div style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    padding: '1rem',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '1px solid #fcd34d'
                  }}>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#d97706' }}>
                      {deletionResults.foldersDeleted}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#b45309', fontWeight: 500 }}>Folders Removed</div>
                  </div>
                  <div style={{
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    padding: '1rem',
                    borderRadius: '12px',
                    textAlign: 'center',
                    border: '1px solid #93c5fd'
                  }}>
                    <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#2563eb' }}>
                      {deletionResults.totalSpaceReclaimed}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#1d4ed8', fontWeight: 500 }}>Space Reclaimed</div>
                  </div>
                </div>
                
                {/* File List */}
                {deletionResults.files.length > 0 && (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ 
                      fontSize: '0.85rem', 
                      fontWeight: 600, 
                      color: '#374151', 
                      marginBottom: '0.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <span>📄</span> Deleted Files:
                    </div>
                    <div style={{
                      maxHeight: '150px',
                      overflowY: 'auto',
                      background: '#f8fafc',
                      borderRadius: '8px',
                      padding: '0.5rem',
                      border: '1px solid #e2e8f0'
                    }}>
                      {deletionResults.files.map((file, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 8px',
                          borderRadius: '4px',
                          backgroundColor: idx % 2 === 0 ? 'transparent' : '#f1f5f9',
                          fontSize: '0.8rem'
                        }}>
                          <span style={{ color: '#475569', fontFamily: 'monospace' }}>{file.name}</span>
                          <span style={{ 
                            color: '#64748b', 
                            fontSize: '0.75rem',
                            backgroundColor: '#e2e8f0',
                            padding: '2px 6px',
                            borderRadius: '4px'
                          }}>{file.size}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <ModalButtons style={{ justifyContent: 'center' }}>
                  <CancelButton 
                    onClick={resetDeletionModal}
                    style={{ 
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', 
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    Done
                  </CancelButton>
                </ModalButtons>
              </div>
            )}
            
            {/* Phase: Error */}
            {deletionPhase === 'error' && (
              <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
                <ModalTitle style={{ color: '#dc2626' }}>Deletion Failed</ModalTitle>
                <ModalText>
                  An error occurred while deleting the application. Please try again.
                </ModalText>
                <ModalButtons style={{ justifyContent: 'center', marginTop: '1.5rem' }}>
                  <CancelButton onClick={resetDeletionModal}>
                    Close
                  </CancelButton>
                </ModalButtons>
              </div>
            )}
          </ModalContent>
        </ModalBackdrop>
      )}
      
      {/* Archive Confirmation Modal */}
      {showArchiveConfirmation && (
        <ModalBackdrop>
          <ModalContent>
            <ModalTitle>Confirm Archive</ModalTitle>
            <ModalText>
              Are you sure you want to archive {selectedApplicationIds.length} application(s)?
              Archived applications will be removed from active view.
            </ModalText>
            <div style={{ marginTop: '1rem' }}>
              <label htmlFor="archiveReason" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
                Reason for archiving (required):
              </label>
              <textarea
                id="archiveReason"
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '0.25rem',
                  border: '1px solid #e2e8f0',
                  minHeight: '80px',
                }}
                placeholder="Please provide a reason for archiving these applications..."
              />
            </div>
            <ModalButtons>
              <CancelButton
                onClick={() => {
                  setShowArchiveConfirmation(false);
                  setDeletionReason('');
                }}
                disabled={isProcessingAction}
              >
                Cancel
              </CancelButton>
              <ArchiveButton
                onClick={handleArchive}
                disabled={isProcessingAction || !deletionReason.trim()}
              >
                {isProcessingAction ? 'Archiving...' : 'Archive'}
              </ArchiveButton>
            </ModalButtons>
          </ModalContent>
        </ModalBackdrop>
      )}
      
      {/* Mark as Payment Completed Confirmation Modal */}
      {showMarkPaidConfirmation && (
        <ModalBackdrop>
          <ModalContent>
            <ModalTitle>Confirm Payment Completion</ModalTitle>
            <ModalText>
              Are you sure you want to mark this application as <strong>Payment Completed</strong> with the uploaded proof?
            </ModalText>
            <ModalButtons>
              <CancelButton
                onClick={() => setShowMarkPaidConfirmation(false)}
                disabled={isMarkingPaid}
              >
                Cancel
              </CancelButton>
              <ArchiveButton
                onClick={handleMarkAsPaymentComplete}
                disabled={isMarkingPaid || !paymentProofFile}
              >
                {isMarkingPaid ? 'Marking...' : 'Yes, Mark as Payment Completed'}
              </ArchiveButton>
            </ModalButtons>
          </ModalContent>
        </ModalBackdrop>
      )}

      {/* Payment Proof Preview Modal */}
      {showPaymentProofModal && paymentProofPreviewUrl && paymentProofPreviewType && (
        <ModalBackdrop>
          <ModalContent>
            <ModalTitle>Payment Proof Preview</ModalTitle>
            <div style={{ maxHeight: '70vh', overflow: 'auto' }}>
              {paymentProofPreviewType === 'image' ? (
                <img
                  src={paymentProofPreviewUrl}
                  alt="Payment proof"
                  style={{ maxWidth: '100%', borderRadius: '8px' }}
                />
              ) : (
                <iframe
                  src={paymentProofPreviewUrl}
                  title="Payment proof PDF"
                  style={{ width: '100%', height: '70vh', border: 'none' }}
                />
              )}
            </div>
            <ModalButtons>
              <CancelButton
                onClick={() => {
                  setShowPaymentProofModal(false);
                  setPaymentProofPreviewUrl(null);
                  setPaymentProofPreviewType(null);
                }}
              >
                Close
              </CancelButton>
            </ModalButtons>
          </ModalContent>
        </ModalBackdrop>
      )}

      {/* Payment Toast Notification */}
      <PaymentToast
        show={paymentToast.show}
        applicantName={paymentToast.applicantName}
        amount={paymentToast.amount}
        onClose={handleClosePaymentToast}
      />

      {/* Session Player Modal */}
      {sessionPlayAppId && (
        <SessionPlayerModal
          applicationId={sessionPlayAppId}
          onClose={() => setSessionPlayAppId(null)}
        />
      )}

      {/* Live Session Viewer Modal */}
      {liveSessionAppId && (
        <LiveSessionModal
          applicationId={liveSessionAppId}
          userEmail={liveSessionEmail}
          onClose={() => { setLiveSessionAppId(null); setLiveSessionEmail(''); }}
        />
      )}

      {/* Registered Users - View Live Modal (visitor-based) */}
      {regUsersLiveVisitorId && (
        <VisitorLiveModal
          visitorId={regUsersLiveVisitorId}
          visitorLabel={regUsersLiveLabel}
          onClose={() => { setRegUsersLiveVisitorId(null); setRegUsersLiveLabel(''); }}
        />
      )}

      {/* Live dot animation keyframe */}
      <style>{`
        @keyframes liveDot {
          0%, 100% { opacity: 1; box-shadow: 0 0 4px #22c55e; }
          50% { opacity: 0.4; box-shadow: 0 0 10px #22c55e, 0 0 20px rgba(34,197,94,0.3); }
        }
      `}</style>

      {/* Tile Detail Modal */}
      {activeTileModal && (
        <TileDetailModal
          type={activeTileModal}
          onClose={() => setActiveTileModal(null)}
          applications={applications}
        />
      )}

    </DashboardContainer>
  );
};

export default AdminDashboard;
